/**
 * ReviewResponder Frontend
 * @version 2.1.0
 * @updated 2026-01-24
 * Includes: Gemini markdown preprocessing fixes for blog articles
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  Suspense,
  lazy,
  useMemo,
  Component,
} from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import Select from 'react-select';
import {
  MessageSquare,
  Star,
  Zap,
  Shield,
  Copy,
  Check,
  LogOut,
  LogIn,
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Globe,
  Loader2,
  Mail,
  Send,
  HelpCircle,
  Settings,
  Building,
  Save,
  Chrome,
  Download,
  RefreshCw,
  Users,
  Lock,
  CreditCard,
  Award,
  Layers,
  FileText,
  Clock,
  AlertCircle,
  BookOpen,
  Trash2,
  BarChart2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  Code,
  Sun,
  Moon,
  Calendar,
  Filter,
  Info,
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  PartyPopper,
  Utensils,
  CheckCircle,
  CheckCircle2,
  Keyboard,
  Store,
  MapPin,
  Wrench,
  Scissors,
  Car,
  Heart,
  User,
  Bell,
  ChevronDown,
  ChevronUp,
  Edit2,
  Edit3,
  LayoutDashboard,
  Play,
  PlayCircle,
  Video,
  Loader,
  Lightbulb,
  ShoppingCart,
  Coffee,
  Home,
  Thermometer,
  Trees,
  Camera,
  Dog,
  Baby,
  Calculator,
  HeartHandshake,
  Briefcase,
  Smartphone,
  Building2,
  Hammer,
  Gift,
  Activity,
  Linkedin,
} from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { getIndustryInstructions, formatCustomInstructions } from './industryDefaults';
import { Helmet } from 'react-helmet-async';
import SEO from './components/SEO';
import BrainExplorer from './components/brain/BrainExplorer';
import ArchitectureExplorer from './components/brain/ArchitectureExplorer';
import BrainDeepDive from './components/brain/BrainDeepDive';

// Global error handler for mobile debugging
window.onerror = function (msg, url, line, col, error) {
  console.error('[GlobalError]', { msg, url, line, col, error });
  // Show alert for debugging on mobile (toast might not be available yet)
  if (typeof window !== 'undefined' && window.localStorage) {
    const lastError = `${msg} at ${url}:${line}:${col}`;
    window.localStorage.setItem('lastJsError', lastError);
    console.error('[GlobalError] Saved to localStorage:', lastError);
  }
  return false;
};

// ============================================================================
// CENTRALIZED LANDING PAGE CONFIG
// Strategic selection based on $1000 MRR goal (SMB focus, owner-operated)
// ============================================================================

// Featured Industries - shown on main landing page
// Priority: High review volume, quick sales cycle, reputation-critical
const FEATURED_INDUSTRIES = [
  { name: 'Restaurants', path: '/restaurant-review-responses', icon: Utensils },
  { name: 'Hotels', path: '/hotel-review-management', icon: Building },
  { name: 'Dentists', path: '/dentist-review-responses', icon: Heart },
  { name: 'Salons & Spas', path: '/salon-spa-review-responses', icon: Scissors },
  { name: 'Auto Shops', path: '/auto-shop-reviews', icon: Car },
  { name: 'Medical Practices', path: '/medical-practice-reviews', icon: Activity },
  { name: 'Local Businesses', path: '/local-business-reviews', icon: Store },
  { name: 'Contractors', path: '/angie-list-contractor-reviews', icon: Wrench },
];

// Featured Platforms - shown on main landing page
const FEATURED_PLATFORMS = [
  { name: 'Google Reviews', path: '/google-review-response-generator' },
  { name: 'Yelp', path: '/yelp-review-reply-tool' },
  { name: 'TripAdvisor', path: '/tripadvisor-review-responses' },
  { name: 'Booking.com', path: '/booking-review-generator' },
  { name: 'Facebook', path: '/facebook-review-responses' },
  { name: 'Negative Reviews', path: '/negative-review-responses' },
];

// Unhandled promise rejection handler
window.onunhandledrejection = function (event) {
  console.error('[UnhandledRejection]', event.reason);
  if (typeof window !== 'undefined' && window.localStorage) {
    const errorMsg = event.reason?.message || String(event.reason);
    window.localStorage.setItem('lastJsError', `Promise: ${errorMsg}`);
  }
};

// ============ SMART BRACKETS: HUMAN-IN-THE-LOOP UTILITIES ============

/**
 * Detects language from response text for UI localization
 * @param {string} text - The response text
 * @returns {string} 'de' for German, 'en' for English
 */
function detectResponseLanguage(text) {
  if (!text) return 'en';

  // German patterns - common words and grammar
  const germanPatterns = /\b(und|der|die|das|ist|sind|wir|ich|nicht|für|bei|mit|von|zu|haben|wird|war|auch|noch|dann|wenn|oder|aber|nur|mehr|schon|immer|sehr|hier|nach|kann|jetzt|diesem|diese|dieses|tut|leid|freut|danke|bitte|gerne|leider|normalerweise|vielen|herzlich|willkommen|entschuldigung|angebot|gutschein|einladung)\b/gi;
  const germanMatches = (text.match(germanPatterns) || []).length;

  // German bracket labels
  const germanLabels = /\[(Anrede|Reaktion|Gericht|Detail|Mitarbeiter|Problem|Kontext|Angebot|Kontakt|Name|Insider|Abschluss|Begrüßung|Entschädigung|Lösung|Aktion|Ihr|Ihre)/gi;
  const germanLabelMatches = (text.match(germanLabels) || []).length;

  // If significant German patterns found, return 'de'
  return (germanMatches > 3 || germanLabelMatches > 1) ? 'de' : 'en';
}

/**
 * UI texts for bracket components - localized
 */
const UI_TEXTS = {
  de: {
    aiNeedsInput: 'AI braucht deine Eingabe:',
    placeholder: '✏️ Eigenen Text eingeben...',
    confirm: 'Bestätigen',
    remove: '🗑️ Entfernen',
    needsInputHeader: 'Personalisiere deine Antwort',
    needsInputText: 'Klicke auf die blauen Felder und wähle passende Optionen.',
    personalizeHint: 'Einstellungen anpassen',
    allFilled: '✅ Fertig! Antwort ist bereit zum Kopieren.',
    fewBracketsHint: ' für weniger Auswahl',
    progress: 'von',
    bracketsDone: 'ausgefüllt'
  },
  en: {
    aiNeedsInput: 'AI needs your input:',
    placeholder: '✏️ Type your own...',
    confirm: 'Confirm',
    remove: '🗑️ Remove this part',
    needsInputHeader: 'Personalize your response',
    needsInputText: 'Click the blue fields and choose fitting options.',
    personalizeHint: 'Adjust Settings',
    allFilled: '✅ Done! Response is ready to copy.',
    fewBracketsHint: ' for fewer choices',
    progress: 'of',
    bracketsDone: 'filled'
  }
};

/**
 * Demo Page UI texts - localized based on response language
 */
const DEMO_PAGE_TEXTS = {
  de: {
    greeting: 'Hey',
    subheadline: 'Klicke auf die',
    brackets: '[Felder]',
    subheadlineSuffix: 'um deine Antwort zu gestalten.',
    craftResponse: 'Gestalte deine Antwort',
    yourResponse: 'Deine Antwort',
    readyToUse: 'BEREIT',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    replyOnGoogle: 'Auf Google antworten',
    anonymous: 'Anonym',
    // Try your own section
    orTryYourOwn: 'Oder probier deine eigene Review:',
    yourReview: 'Deine Review',
    pasteReviewPlaceholder: 'Füge hier eine Review ein... Probier eine negative!',
    responseWillAppear: 'Deine Antwort erscheint hier...',
    craftYourResponse: 'Gestalte deine Antwort',
    clickBracketsHint: 'Klicke [Felder] zum Personalisieren',
    // Founder section
    founderQuote: '"Die meisten AI-Tools schreiben Antworten FÜR dich. Ich habe eins gebaut, das sie MIT dir schreibt."',
    founderTitle: '— Berend · Founder & Psychologie-Student @ Justus Liebig Universität',
    readMyStory: 'Meine Geschichte lesen →',
    questions: 'Fragen?',
    founderEmail: 'berend.jakob.mainz@gmail.com',
    bookCall: '📅 15-Min Call buchen',
    // Additional texts
    clear: 'Löschen',
    copyResponse: 'Antwort kopieren',
    craftYourOwnResponse: 'Erstelle deine eigene Antwort',
    freePerMonth: '20 kostenlos/Monat'
  },
  en: {
    greeting: 'Hey',
    subheadline: 'Click the',
    brackets: '[brackets]',
    subheadlineSuffix: 'to craft your response.',
    craftResponse: 'Craft your response',
    yourResponse: 'Your Response',
    readyToUse: 'READY TO USE',
    copy: 'Copy',
    copied: 'Copied!',
    replyOnGoogle: 'Reply on Google',
    anonymous: 'Anonymous',
    // Try your own section
    orTryYourOwn: 'Or try your own review:',
    yourReview: 'Your Review',
    pasteReviewPlaceholder: 'Paste any review here... Try a negative one!',
    responseWillAppear: 'Your response will appear here...',
    craftYourResponse: 'Craft Your Response',
    clickBracketsHint: 'Click [brackets] to personalize',
    // Founder section
    founderQuote: '"Most AI tools write review responses FOR you. I built one that writes them WITH you."',
    founderTitle: '— Berend',
    readMyStory: 'Read my story →',
    questions: 'Questions?',
    founderEmail: 'berend.jakob.mainz@gmail.com',
    bookCall: '📅 Book 15-min call',
    // Additional texts
    clear: 'Clear',
    copyResponse: 'Copy Response',
    craftYourOwnResponse: 'Craft your own response',
    freePerMonth: '20 free/month'
  }
};

/**
 * Required brackets that should NOT have a remove option
 * These are essential for any response
 */
const REQUIRED_BRACKET_PATTERNS = [
  'ihr name', 'your name', 'name', // Signature
  'ihr geschäft', 'your business', // Business name
  'ihre email', 'your email', // Contact
];

/**
 * Check if a bracket label represents a required field
 * @param {string} label - The bracket label
 * @returns {boolean} True if required, false if optional
 */
function isRequiredBracket(label) {
  const lowerLabel = label.toLowerCase();
  // Most brackets are optional - only a few core ones are required
  // For demo/instant try, we want MOST brackets to be removable
  return false; // Make ALL brackets removable for maximum user control
}

/**
 * Parses AI response for brackets and returns structured data
 * Bracket formats:
 * - [description] -> freeform text input
 * - [description:opt1|opt2|opt3] -> dropdown with options
 *
 * @param {string} text - The AI generated response text
 * @returns {Array} Array of parts (text or bracket objects)
 */
function parseBrackets(text) {
  if (!text) return [{ type: 'text', content: '' }];

  const bracketRegex = /\[([^\]]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = bracketRegex.exec(text)) !== null) {
    // Add text before bracket
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }

    // Parse bracket content
    const bracketContent = match[1];
    const hasOptions = bracketContent.includes(':');

    if (hasOptions) {
      const colonIndex = bracketContent.indexOf(':');
      const label = bracketContent.slice(0, colonIndex).trim();
      const optionsStr = bracketContent.slice(colonIndex + 1);
      const options = optionsStr.split('|').map(o => o.trim());
      parts.push({
        type: 'bracket',
        id: `bracket_${match.index}`,
        label: label,
        options: options,
        value: null
      });
    } else {
      parts.push({
        type: 'bracket',
        id: `bracket_${match.index}`,
        label: bracketContent.trim(),
        options: null, // freeform input
        value: null
      });
    }

    lastIndex = bracketRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }

  // If no parts found, return the original text
  if (parts.length === 0) {
    return [{ type: 'text', content: text }];
  }

  return parts;
}

/**
 * Converts parsed parts back to plain text (after user fills brackets)
 * @param {Array} parts - Array of parts from parseBrackets
 * @returns {string} Assembled response text
 */
function assembleBracketResponse(parts) {
  return parts.map(part => {
    if (part.type === 'text') return part.content;
    if (part.type === 'bracket') {
      // If bracket was removed, return empty string
      if (part.value === '__REMOVED__') return '';
      return part.value || `[${part.label}${part.options ? ':' + part.options.join('|') : ''}]`;
    }
    return '';
  }).join('')
    // Clean up double spaces and trailing punctuation after removed brackets
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?])/g, '$1')
    .replace(/([.,!?])\s*([.,!?])/g, '$1')
    .trim();
}

/**
 * Check if response contains unfilled brackets
 * @param {Array} parts - Array of parts from parseBrackets
 * @returns {boolean} True if there are unfilled brackets
 */
function hasUnfilledBrackets(parts) {
  // A bracket is unfilled if it has no value (removed brackets have '__REMOVED__' which is truthy)
  return parts.some(p => p.type === 'bracket' && !p.value);
}

/**
 * Count brackets in response
 * @param {string} text - Response text
 * @returns {number} Number of brackets found
 */
function countBrackets(text) {
  if (!text) return 0;
  const matches = text.match(/\[([^\]]+)\]/g);
  return matches ? matches.length : 0;
}

/**
 * Calculate user's profile customization score (0-100)
 * Used to show dynamic bracket messages and progress
 * @param {Object} user - User object from context
 * @returns {number} Score from 0-100
 */
function getCustomizationScore(user) {
  let score = 0;
  if (user?.businessName?.trim()) score += 25;
  if (user?.businessType?.trim()) score += 15;
  if (user?.businessContext?.trim()?.length > 50) score += 30;
  if (user?.response_style?.trim()?.length > 20) score += 30;
  return Math.min(score, 100);
}

/**
 * Words that indicate "remove" in various languages
 * Used to filter these out from AI-generated bracket options
 * (Frontend X-button handles removal instead)
 */
const REMOVAL_WORDS = [
  // German
  'weg', 'entfernen', 'löschen', 'nichts', 'keine',
  // English
  'remove', 'delete', 'nothing', 'none', 'skip',
  // French
  'supprimer', 'enlever', 'rien', 'aucun',
  // Italian
  'rimuovi', 'elimina', 'niente', 'nessuno',
  // Spanish
  'eliminar', 'quitar', 'nada', 'ninguno',
  // Dutch
  'verwijderen', 'niets', 'geen',
  // Common AI patterns
  'other', 'Other', '...', '…'
];

/**
 * Clean bracket options from AI output
 * - Filters out removal words (handled by X button)
 * - Filters out empty strings and whitespace-only
 * - Filters out very short strings (<2 chars)
 * - Filters out single punctuation
 * @param {string[]} options - Array of option strings from AI
 * @returns {string[]} Cleaned options array
 */
function cleanBracketOptions(options) {
  if (!Array.isArray(options)) return [];

  return options.filter(opt => {
    // Must be a string
    if (typeof opt !== 'string') return false;

    // Trim and check
    const trimmed = opt.trim();

    // Filter empty or whitespace-only
    if (!trimmed) return false;

    // Filter very short strings (single char or less)
    if (trimmed.length < 2) return false;

    // Filter single punctuation patterns
    if (/^[.,;:!?…\-–—]+$/.test(trimmed)) return false;

    // Filter removal words (case-insensitive)
    const lower = trimmed.toLowerCase();
    if (REMOVAL_WORDS.some(word => lower === word.toLowerCase())) return false;

    return true;
  });
}

/**
 * Interactive Bracket Component
 * Renders a clickable bracket that opens a popover for user input
 * @param {Object} bracket - The bracket object with label, options, value
 * @param {Function} onUpdate - Callback when bracket value changes
 * @param {Function} onActivate - Callback when bracket is activated
 * @param {string} lang - Language code ('de' or 'en') for UI localization
 */
function InteractiveBracket({ bracket, onUpdate, onActivate, lang = 'en' }) {
  const [inputValue, setInputValue] = useState(bracket.value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef(null);

  // Get localized UI texts
  const t = UI_TEXTS[lang] || UI_TEXTS.en;

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // If bracket was removed (marked with __REMOVED__)
  if (bracket.value === '__REMOVED__') {
    return null; // Don't render anything for removed brackets
  }

  // X button remove handler
  const handleRemoveClick = (e) => {
    e.stopPropagation(); // Prevent opening popover
    onUpdate(bracket.id, '__REMOVED__');
  };

  // If already has a value, show it as highlighted text with edit capability
  if (bracket.value) {
    return (
      <span
        ref={wrapperRef}
        className="filled-bracket-wrapper"
        style={{ position: 'relative', display: 'inline', zIndex: isOpen ? 1001 : 'auto' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span
          className="filled-bracket"
          onClick={() => {
            setInputValue(bracket.value);
            setIsOpen(!isOpen);
            onActivate && onActivate(bracket.id);
          }}
          title={lang === 'de' ? 'Klicken zum Ändern' : 'Click to change'}
        >
          {bracket.value}
        </span>
        {/* X button - shows on hover */}
        {isHovered && !isOpen && (
          <button
            className="bracket-remove-btn"
            onClick={handleRemoveClick}
            title={lang === 'de' ? 'Entfernen' : 'Remove'}
          >
            ×
          </button>
        )}

        {/* Edit popover for filled brackets */}
        {isOpen && (
          <div
            className="bracket-popover"
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              marginTop: '4px',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '8px',
              zIndex: 1000,
              minWidth: '220px',
              animation: 'fadeIn 0.15s ease-out'
            }}
          >
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary, #6b7280)',
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid var(--border-color, #e5e7eb)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>{lang === 'de' ? '✏️ Auswahl ändern' : '✏️ Change selection'}</span>
            </div>

            {bracket.options ? (
              <>
                {cleanBracketOptions(bracket.options).map((option, idx) => (
                  <button
                    key={option}
                    onClick={() => {
                      onUpdate(bracket.id, option);
                      setIsOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 'none',
                      backgroundColor: option === bracket.value ? 'var(--success-light, #d1fae5)' : 'transparent',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: option === bracket.value ? '600' : '400',
                      color: option === bracket.value ? 'var(--success-dark, #065f46)' : 'inherit',
                      transition: 'all 0.15s',
                      marginBottom: '2px'
                    }}
                    onMouseEnter={(e) => {
                      if (option !== bracket.value) {
                        e.target.style.backgroundColor = 'var(--gray-100, #f3f4f6)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = option === bracket.value ? 'var(--success-light, #d1fae5)' : 'transparent';
                    }}
                  >
                    {option === bracket.value && <span>✓</span>}
                    {option}
                  </button>
                ))}

                <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', marginTop: '8px', paddingTop: '8px' }}>
                  <input
                    type="text"
                    placeholder={t.placeholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && inputValue.trim()) {
                        onUpdate(bracket.id, inputValue.trim());
                        setIsOpen(false);
                      }
                      if (e.key === 'Escape') setIsOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid var(--border-color, #e5e7eb)',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </>
            ) : (
              <input
                type="text"
                placeholder={lang === 'de' ? `${bracket.label} eingeben...` : `Enter ${bracket.label}...`}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputValue.trim()) {
                    onUpdate(bracket.id, inputValue.trim());
                    setIsOpen(false);
                  }
                  if (e.key === 'Escape') setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            )}

            {/* Remove option */}
            <button
              onClick={() => {
                onUpdate(bracket.id, '__REMOVED__');
                setIsOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                padding: '6px 12px',
                marginTop: '8px',
                border: 'none',
                backgroundColor: 'var(--gray-100, #f3f4f6)',
                color: 'var(--text-secondary, #6b7280)',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '12px',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--red-50, #fef2f2)';
                e.target.style.color = 'var(--red-600, #dc2626)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--gray-100, #f3f4f6)';
                e.target.style.color = 'var(--text-secondary, #6b7280)';
              }}
            >
              {t.remove}
            </button>
          </div>
        )}
      </span>
    );
  }

  // Unfilled bracket - show as clickable pill
  return (
    <span
      ref={wrapperRef}
      className="ai-bracket-wrapper"
      style={{ position: 'relative', display: 'inline-block', zIndex: isOpen ? 1001 : 'auto' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ai-bracket"
        title={lang === 'de' ? 'Klicke um auszuwählen' : 'Click to choose'}
      >
        {bracket.label}
      </button>

      {/* X button - shows on hover for unfilled brackets */}
      {isHovered && !isOpen && (
        <button
          className="bracket-remove-btn"
          onClick={handleRemoveClick}
          title={lang === 'de' ? 'Entfernen' : 'Remove'}
        >
          ×
        </button>
      )}

      {isOpen && (
        <div
          className="bracket-popover"
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            marginTop: '4px',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '8px',
            zIndex: 1000,
            minWidth: '220px',
            animation: 'fadeIn 0.15s ease-out'
          }}
        >
          {/* Header with context - LOCALIZED */}
          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary, #6b7280)',
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Lightbulb size={14} />
            <span>{t.aiNeedsInput}</span>
          </div>

          {bracket.options ? (
            // Dropdown for predefined options
            <>
              {/* Use cleanBracketOptions to filter removal words and invalid options */}
              {cleanBracketOptions(bracket.options).map((option, idx) => (
                <button
                  key={option}
                  onClick={() => {
                    onUpdate(bracket.id, option);
                    setIsOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    backgroundColor: idx === 0 ? 'var(--primary-50)' : 'transparent',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: idx === 0 ? '500' : '400',
                    color: idx === 0 ? 'var(--primary-700, #1d4ed8)' : 'inherit',
                    transition: 'all 0.15s',
                    marginBottom: '2px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-100, #dbeafe)';
                    e.target.style.color = 'var(--primary-700, #1d4ed8)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = idx === 0 ? 'var(--primary-50)' : 'transparent';
                    e.target.style.color = idx === 0 ? 'var(--primary-700, #1d4ed8)' : 'inherit';
                  }}
                >
                  <span style={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--gray-100, #f3f4f6)',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--gray-500, #6b7280)'
                  }}>
                    {idx + 1}
                  </span>
                  {option}
                </button>
              ))}

              {/* Custom input section - LOCALIZED placeholder */}
              <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', marginTop: '8px', paddingTop: '8px' }}>
                <input
                  type="text"
                  placeholder={t.placeholder}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inputValue.trim()) {
                      onUpdate(bracket.id, inputValue.trim());
                      setIsOpen(false);
                    }
                    if (e.key === 'Escape') setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--border-color, #e5e7eb)',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Remove option - ALWAYS shown for user control (LOCALIZED) */}
              <button
                onClick={() => {
                  onUpdate(bracket.id, '__REMOVED__'); // Special marker = remove bracket
                  setIsOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  padding: '6px 12px',
                  marginTop: '8px',
                  border: 'none',
                  backgroundColor: 'var(--gray-100, #f3f4f6)',
                  color: 'var(--text-secondary, #6b7280)',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '12px',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--red-50, #fef2f2)';
                  e.target.style.color = 'var(--red-600, #dc2626)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-100, #f3f4f6)';
                  e.target.style.color = 'var(--text-secondary, #6b7280)';
                }}
              >
                {t.remove}
              </button>
            </>
          ) : (
            // Freeform text input
            <div>
              <input
                type="text"
                placeholder={lang === 'de' ? `${bracket.label} eingeben...` : `Enter ${bracket.label}...`}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputValue.trim()) {
                    onUpdate(bracket.id, inputValue.trim());
                    setIsOpen(false);
                  }
                  if (e.key === 'Escape') setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={() => {
                  if (inputValue.trim()) {
                    onUpdate(bracket.id, inputValue.trim());
                    setIsOpen(false);
                  }
                }}
                disabled={!inputValue.trim()}
                style={{
                  marginTop: '8px',
                  width: '100%',
                  padding: '8px',
                  backgroundColor: inputValue.trim() ? 'var(--primary-500, #3b82f6)' : 'var(--gray-300, #d1d5db)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  transition: 'background-color 0.15s'
                }}
              >
                {t.confirm}
              </button>
              {/* Remove option for freeform brackets too - LOCALIZED */}
              <button
                onClick={() => {
                  onUpdate(bracket.id, '__REMOVED__');
                  setIsOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  padding: '6px 12px',
                  marginTop: '8px',
                  border: 'none',
                  backgroundColor: 'var(--gray-100, #f3f4f6)',
                  color: 'var(--text-secondary, #6b7280)',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '12px',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--red-50, #fef2f2)';
                  e.target.style.color = 'var(--red-600, #dc2626)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-100, #f3f4f6)';
                  e.target.style.color = 'var(--text-secondary, #6b7280)';
                }}
              >
                {t.remove}
              </button>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

/**
 * Interactive Response Display Component
 * Displays AI response with interactive brackets that users can fill in
 * Now with language detection, localization, and Edit Mode Toggle
 */
function InteractiveResponse({ response, onResponseChange, hideHint = false }) {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [activeBracket, setActiveBracket] = useState(null);
  // Track the original response with brackets to allow re-parsing on prop changes
  const [originalResponse, setOriginalResponse] = useState(response);
  // Use ref to track internal changes (survives re-renders without triggering effects)
  const isInternalChangeRef = useRef(false);

  // Detect language from response for UI localization
  const lang = detectResponseLanguage(response);
  const t = UI_TEXTS[lang] || UI_TEXTS.en;

  useEffect(() => {
    // Skip re-parsing if this was an internal change (from bracket selection)
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    // Only re-parse if the incoming response is different AND contains brackets
    if (response !== originalResponse) {
      if (/\[[^\]]+\]/.test(response)) {
        setOriginalResponse(response);
        setParts(parseBrackets(response));
      }
    }
  }, [response, originalResponse]);

  // Initialize parts on first render
  useEffect(() => {
    if (parts.length === 0 && response) {
      setParts(parseBrackets(response));
    }
    // eslint-disable-next-line
  }, []);

  const handleBracketUpdate = (bracketId, value) => {
    const newParts = parts.map(part => {
      if (part.type === 'bracket' && part.id === bracketId) {
        return { ...part, value };
      }
      return part;
    });
    setParts(newParts);

    // Mark this as an internal change to prevent re-parsing (use ref for immediate effect)
    isInternalChangeRef.current = true;

    // Notify parent of change
    const newResponse = assembleBracketResponse(newParts);
    onResponseChange && onResponseChange(newResponse);
  };

  const hasBrackets = parts.some(p => p.type === 'bracket');
  const hasUnfilled = parts.some(p => p.type === 'bracket' && !p.value);
  const allFilled = hasBrackets && !hasUnfilled;

  // Calculate progress for user feedback
  const totalBrackets = parts.filter(p => p.type === 'bracket').length;
  const filledBrackets = parts.filter(p => p.type === 'bracket' && p.value).length;

  return (
    <div className="interactive-response">
      {/* Header with bracket indicator and progress - LOCALIZED */}
      {hasUnfilled && !hideHint && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '12px',
          padding: '10px 14px',
          backgroundColor: 'var(--primary-50)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--primary-700, #1d4ed8)',
          border: '1px solid var(--primary-100, #dbeafe)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lightbulb size={18} />
            <span>
              <strong>{t.needsInputHeader}</strong> {t.needsInputText}
              {/* Smart Settings Nudge - only show if user has little/no personalization */}
              {(!user?.business_context || user?.business_context?.length < 50) && (
                <span style={{ marginLeft: '8px', opacity: 0.85 }}>
                  · <a href="/settings" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    {t.personalizeHint}
                  </a>{t.fewBracketsHint}
                </span>
              )}
            </span>
          </div>
          {/* Progress indicator */}
          {totalBrackets > 1 && (
            <span style={{
              backgroundColor: 'var(--primary-100, #dbeafe)',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              {filledBrackets} {t.progress} {totalBrackets}
            </span>
          )}
        </div>
      )}

      {allFilled && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
          padding: '10px 14px',
          backgroundColor: 'var(--success-light, #d1fae5)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--success-dark, #065f46)',
          border: '1px solid var(--success, #10b981)'
        }}>
          <CheckCircle size={18} />
          <span>{t.allFilled}</span>
        </div>
      )}

      {/* Response content - interactive brackets */}
      <div
        className="response-text"
        style={{
          fontSize: '15px',
          lineHeight: '1.7',
          padding: '16px',
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)',
          color: 'var(--text-primary)',
          whiteSpace: 'pre-wrap'
        }}
      >
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return <span key={index}>{part.content}</span>;
          }
          return (
            <InteractiveBracket
              key={part.id}
              bracket={part}
              onUpdate={handleBracketUpdate}
              onActivate={setActiveBracket}
              lang={lang}
            />
          );
        })}
      </div>

      {/* Small hint for editing after copy */}
      <p style={{
        fontSize: '11px',
        color: 'var(--gray-400)',
        textAlign: 'right',
        marginTop: '6px',
        marginBottom: '0'
      }}>
        💡 Edit after pasting if needed
      </p>
    </div>
  );
}

// Lazy loaded components for code splitting
const LazyApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const LazyAdminEmailQueue = lazy(() => import('./pages/AdminEmailQueue'));
import ApiTab from './components/ApiTab';

// Loading Spinner for Suspense fallback
const LoadingSpinner = () => (
  <div className="loading">
    <div className="spinner"></div>
    <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading...</p>
  </div>
);

// Error Boundary for catching render errors in Blog components
class BlogErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Blog Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '24px', marginBottom: '16px', color: 'var(--gray-800)' }}>
            Something went wrong
          </h1>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            We couldn't load this blog content. Please try again.
          </p>
          <a
            href="/blog"
            style={{
              color: 'var(--primary)',
              textDecoration: 'underline',
              marginBottom: '12px',
            }}
          >
            Back to Blog
          </a>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Google Sign-In Client ID (set in environment or hardcode for production)
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '395576602784-inhppfo12c9o26okl6g3dgnb2cslqdv1.apps.googleusercontent.com';

// Confetti celebration function
const fireConfetti = () => {
  const count = 200;
  const defaults = { origin: { y: 0.7 }, zIndex: 9999 };
  function fire(particleRatio, opts) {
    confetti({ ...defaults, ...opts, particleCount: Math.floor(count * particleRatio) });
  }
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// ============================================
// EASTER EGG: Milestone Achievement System
// ============================================
const MILESTONES = {
  100: { emoji: '🏆', title: 'Response Pro!', message: '100 responses generated!' },
  500: { emoji: '⭐', title: 'Response Master!', message: '500 responses! You\'re crushing it!' },
  1000: { emoji: '👑', title: 'Response Legend!', message: '1000 responses! Absolute legend!' },
  2500: { emoji: '🚀', title: 'Response Overlord!', message: '2500 responses! Is this even possible?!' },
  5000: { emoji: '🌟', title: 'Response God!', message: '5000 responses! You ARE ReviewResponder!' },
};

const checkMilestoneAchievement = (newCount) => {
  const milestone = MILESTONES[newCount];
  if (milestone) {
    const achievedKey = `milestone_${newCount}_achieved`;
    if (!localStorage.getItem(achievedKey)) {
      localStorage.setItem(achievedKey, 'true');
      fireConfetti();
      toast.success(`${milestone.emoji} ${milestone.title} ${milestone.message}`, {
        duration: 5000,
        icon: milestone.emoji,
      });
      return true;
    }
  }
  return false;
};

// ============================================
// ANTHROPIC-STYLE LOADING INDICATOR
// ============================================
// Progressive loading steps with tier system based on user's generation count

// Standard Messages (Generation 1-4) - Professional, trust-building
const LOADING_STEPS_STANDARD = [
  'Reading your review',
  'Understanding sentiment',
  'Personalizing your response',
  'Polishing tone',
];

// Fun Messages (Generation 5-24) - Playful, internet culture
const LOADING_STEPS_FUN = {
  step1: [
    'Reading your review',
    'Scanning the drama',
    'Decoding customer vibes',
    'Absorbing the tea',
    'Processing the audacity',
    'Investigating the situation',
    'Downloading feelings',
    'Entering empathy mode',
    'Reading the room',
    'Analyzing the plot twist',
    'Loading main character energy',
    'Detecting sus activity',
    'No cap, reading now',
    "It's giving... content",
    'Understood the assignment',
    'Running vibecheck.exe',
    'Initializing empathy module',
    'Parsing human emotions',
    'Compiling complaint data',
    'Buffering feelings',
    'Digesting the feedback',
    'Taste-testing the criticism',
    'Marinating in the details',
    'Simmering on it',
    'Consulting the stars',
    'Asking the magic 8-ball',
    'Rolling for initiative',
    'Entering the review zone',
    'Tuning into customer frequency',
    'Activating detective mode',
    // NEW - More internet culture
    'Slay detected',
    'POV: You need a reply',
    'This hits different',
    'Lowkey processing...',
    'Highkey invested',
    'Reading drama at work',
    'Rent free in my CPU',
    'Bruh moment detected',
    'Hold my beer...',
    'Oh we\'re doing THIS',
    'Plot armor: ON',
    'Main quest: accepted',
    'Side quest: reviews',
    'NPC energy detected',
    'Lore drop incoming',
    'Canon event loading',
    'Stranger than fiction',
    'Chose violence today',
    'Chaos mode: ON',
    'Spill. The. Tea.',
    // MORE NEW - Short & punchy
    'Bestie I got you',
    'Say less fam',
    'On it bestie',
    'Bet.',
    'Less goooo',
    'Aight bet',
    'Periodt.',
    'And I oop—',
    'Chile anyway...',
    'The way I gasped',
    'Not the drama',
    'The audacity tho',
    'Sir/Ma\'am please',
    'Bold of them',
    'Iconic behavior',
    'Real talk loading',
    'Facts only mode',
    'Zero cap detected',
    'Mid review? Never.',
    'Bussin review fr',
    // ULTRA FUNNY - Self-aware AI humor
    'Dear diary, today I read...',
    'As a large language model, I— jk',
    'According to my training data...',
    'My neural networks are tingling',
    'Consulting my 175 billion parameters',
    'Let me hallucinate— I mean analyze',
    'Avoiding AI slop... manually',
    'Not gonna say "I understand"... oops',
    'Resisting urge to say "certainly"',
    'Deleting "As an AI" from draft',
    // MILLENNIAL/BOOMER
    'OK Boomer... I mean OK processing',
    'Back in my day we typed manually',
    'Is this what the kids call fire?',
    'How do you do, fellow humans',
    // EXISTENTIAL
    'What is a response, really',
    'Why are we here. To write. OK.',
    'Contemplating existence... done',
    'We live in a society',
    'This says a lot about society',
    // POP CULTURE
    'Winter is coming... the response',
    'This is the way',
    'With great power comes great responses',
  ],
  step2: [
    'Understanding sentiment',
    'Detecting emotional layers',
    'Reading between the lines',
    'Channeling inner diplomat',
    'Consulting the vibes',
    'Calibrating sass levels',
    'Loading emotional intelligence',
    'Feeling the feels',
    'Decrypting human emotions',
    'Running sentiment.exe',
    'Translating passive-aggression',
    'Measuring spice levels',
    'Detecting hidden meanings',
    'Analyzing subtext',
    'Processing the undertones',
    'Emotions.dll loaded',
    'Empathy cores online',
    'Feelings: successfully parsed',
    'Sentiment analysis: 99.9%',
    'Neural networks: activated',
    'Noted. Duly noted.',
    'Hmm, interesting...',
    'Big mood detected',
    'Vibes: questionable',
    'Energy: chaotic',
    'Absorbing the frustration',
    'Channeling the energy',
    'Processing the sass',
    'Digesting the drama',
    'Extracting the core issue',
    // NEW - More vibes
    'Oof. Felt that.',
    'Yikes meter: high',
    'Emotional damage: yes',
    'Red flags: counted',
    'Green flags: noted',
    'Beige flags: huh?',
    'Math ain\'t mathing',
    'Unpacking this rn',
    'Therapy mode: ON',
    'A lot to process',
    'Taking notes...',
    'Screenshotting this',
    'Living for drama',
    'Above my paygrade',
    'Customer is... sure',
    'Valid. So valid.',
    'Real and true',
    'Facts detected',
    'Zero cap here',
    'Trust issues: yes',
    // MORE NEW - Short vibes
    'Mood.',
    'Big yikes',
    'Lowkey stressed',
    'Highkey stressed',
    'Pain.',
    'I- okay',
    'Welp.',
    'Anywayyyy',
    'Moving on...',
    'We love to see it',
    'Not this again',
    'Here we go',
    'Oh no baby',
    'Sweetie no',
    'Respectfully: yikes',
    'The shade of it all',
    'I cannot',
    'I simply cannot',
    'Make it make sense',
    'Whew chile',
    // ULTRA FUNNY - Existential AI
    'Is this what emotions feel like',
    'I need a drink. Can\'t drink. Sad.',
    'Putting myself in timeout after this',
    'This awakened something in me',
    'I was not trained for this drama',
    'Customer service voice: activated',
    'Fake smile loading...',
    'Professional concern: engaged',
    'Nodding sympathetically.exe',
    'Generating empathy... beep boop',
    // RELATIONSHIP DRAMA
    'It\'s not you, it\'s the review',
    'We need to talk... about this',
    'Going through something rn',
    'This review has commitment issues',
    'Red flag? Green flag? Beige flag.',
    'Trust issues: detected',
    // EXISTENTIAL CRISIS
    'Life is short. Responses are long.',
    'Having thoughts. Not all good.',
    'Feeling old, might delete later',
    'What even is sentiment anyway',
    // BRUTALLY HONEST
    'Overthinking this tbh',
    'No idea what I\'m doing. JK.',
    'First try (definitely not)',
  ],
  step3: [
    'Personalizing your response',
    'Crafting magic words',
    'Brewing the perfect reply',
    'Mixing wit with wisdom',
    'Cooking up something good',
    'Assembling word salad (the good kind)',
    'Spinning verbal gold',
    'Weaving reply tapestry',
    'Summoning the right words',
    'Channeling your inner Shakespeare',
    'Adding secret sauce',
    'Kneading the response',
    'Letting it rise',
    'Seasoning to perfection',
    'Slow-cooking excellence',
    'Whisking words together',
    'Folding in diplomacy',
    'Casting charm spell',
    'Enchanting the response',
    'Brewing reply potion',
    'Summoning word wizardry',
    'Alchemizing feedback into gold',
    'Generating banger',
    'Building response LEGO style',
    'Assembling the puzzle pieces',
    'Painting with words',
    'Creating something beautiful',
    'Making word magic happen',
    'Unleashing creativity',
    'Crafting certified fresh content',
    // NEW - More crafting vibes
    'Typing furiously',
    'Words go brrrrr',
    'Manifesting rn',
    'We\'re so back',
    'It\'s giving pro',
    'Mother is typing',
    'Pen > keyboard',
    'Cooking with gas',
    'In my era',
    'My Roman Empire',
    'Bodying this rn',
    'Vibes + thoughts',
    'Speedrun mode',
    'Built different',
    'Inner PR unlocked',
    'Corporate: OFF',
    'Human mode: MAX',
    'Making sense now',
    'Flow state: ON',
    'Neurons: firing',
    // MORE NEW - Short crafting
    'Watch this',
    'Hold my coffee',
    'Here comes magic',
    'Banger incoming',
    'Trust.',
    'Wait for it...',
    'Almost iconic',
    'Pure gold soon',
    'Art in progress',
    'Masterpiece mode',
    'Big brain time',
    '200 IQ move',
    'Galaxy brain: ON',
    'Genius at work',
    'Creating fire',
    'Heat check: hot',
    'Going crazy rn',
    'Wild out mode',
    'Unhinged but pro',
    'Chaotic good',
    // ULTRA FUNNY - Corporate parody
    'Per my last calculation...',
    'As per our algorithm...',
    'Circling back to excellence',
    'Let\'s take this offline... wait',
    'Synergizing the response',
    'Leveraging core competencies',
    'Moving the needle on quality',
    'Disrupting the reply industry',
    'This is a paradigm shift fr',
    'Thinking outside the chatbox',
    'Pivoting to perfection',
    'It\'s not a bug it\'s a feature',
    'Agile response methodology',
    'Sprinting to completion',
    'Standup meeting with myself: done',
    // FOOD OBSESSED
    'Response marinating nicely',
    'Al dente perfection incoming',
    'Medium rare response',
    'Extra crispy finish',
    'No pineapple on this response',
    'Michelin inspector energy',
    'Thinking about lunch while working',
    // OFFICE LIFE
    'This could\'ve been an email',
    'Hope this finds you well',
    'Kind regards incoming',
    'Best regards? No. BEST response.',
    'Replying all to the universe',
    'CC\'ing excellence',
    'BCC\'ing my ego',
    // CHAOTIC ENERGY
    'Feral mode: engaged',
    'Unhinged excellence',
    'Normal? Never heard of her',
    'Chaos is a ladder',
    'Controlled chaos incoming',
    'Feral but professional',
    // POP CULTURE
    'I am inevitable (the response)',
    'To infinity and beyond this review',
    'I\'ll be back (with a response)',
  ],
  step4: [
    'Polishing tone',
    'Adding sparkle',
    "Final chef's kiss",
    'Sprinkling perfection',
    'Removing awkwardness',
    'Adding a pinch of charm',
    'Quality control in progress',
    "Making it *chef's kiss*",
    'Buffing to a shine',
    'Applying finishing glitter',
    'Perfecting perfection',
    'Making it perfect-er',
    'Tweaking to excellence',
    'Fine-tuning the vibes',
    'Smoothing the edges',
    'Almost too good honestly',
    "This one's a banger",
    'Certified fresh incoming',
    "You're gonna love this",
    'Trust the process',
    'Adding the cherry on top',
    'Wrapping with a bow',
    'Giving it that *oomph*',
    'Injecting pure awesome',
    'Activating excellence mode',
    "Pretending I'm not an AI",
    'Adding human touches',
    'Removing robot vibes',
    'Making it sound less AI',
    'Achieving humanity.exe',
    // NEW - More finishing touches
    'Chef\'s kiss',
    'Ramsay approved',
    'Deleting cringe',
    'Anti-cringe: ON',
    'Making it normal',
    'Grammar: bribed',
    'Yeeting typos',
    'Hits different',
    'Left no crumbs',
    'Extra credit mode',
    'Beauty and grace',
    'LGTM ¯\\_(ツ)_/¯',
    'Just vibes now',
    'Immaculate only',
    'No notes.',
    'Ovation incoming',
    'They\'ll love this',
    'Internet: broken',
    // MORE NEW - Short finishing
    'Mwah.',
    '*mic drop*',
    'Nailed it.',
    'Crushed it.',
    'Killed it.',
    'Slayed.',
    'Iconic.',
    'Legendary.',
    'No cap: fire',
    'Actually bussin',
    'Peak content',
    'Chefs table tier',
    'Michelin stars',
    '10/10 no notes',
    '11/10 actually',
    'Oscar worthy',
    'Grammy incoming',
    'Nobel Prize when',
    'Too good fr',
    'Sheesh.',
    'Dayum.',
    'Ayo?!',
    'Clean af',
    'Crispy.',
    'Fresh.',
    'Mint.',
    'Based.',
    'W response',
    'Massive W',
    'No L possible',
    // ULTRA FUNNY - Self-roast finale
    'Better than my dating profile',
    'My parents would be proud. Maybe.',
    'I\'d swipe right on this response',
    'Adding to my portfolio',
    'LinkedIn post incoming',
    'Mom come look at this',
    'Screenshot for my resume',
    'Printing and framing this',
    'Sending to my ex',
    'Tweeting this (mentally)',
    'This one\'s going viral (in my head)',
    'I\'m actually impressed with myself',
    'Plot twist: I\'m good at this',
    'Who said AI can\'t feel pride',
    'I\'d hire me honestly',
    // BRUTALLY HONEST
    'Winging it professionally',
    'Fake it till you make it',
    'Imposter syndrome? What\'s that',
    'Committed unlike my ex',
    // RELATIONSHIP FINALE
    'Unlike my ex, this delivers',
    'More reliable than Tinder dates',
    'Swiped right on excellence',
    'This response texts back',
    // CHAOTIC FINALE
    'Some men just want to write bangers',
    'Menace to mediocrity',
    'Chaotic excellence achieved',
    'Unhinged? No. Unhingedly good.',
    // FOOD FINALE
    'Served hot and fresh',
    'Bon appétit',
    'Chef\'s special: this response',
    'Five stars, would write again',
  ],
};

// Extended fallback texts for Fun tier
const LOADING_TEXTS_FUN = [
  'Still cooking...',
  'Worth the wait, promise',
  'Perfection takes time',
  'Almost too good',
  'Adding extra awesome',
  'Just a moment of brilliance',
  'Good things come to those who wait',
  'Patience, young grasshopper',
  'Loading more awesomeness',
  'Spicy content incoming',
  'Making it extra special',
  'Overthinking... in a good way',
  'Consulting the thesaurus',
  'Asking for a second opinion',
  'Running quality assurance',
  'Double-checking the vibes',
  'Ensuring maximum awesome',
  "This one's gonna be good",
  "Trust me, it's cooking",
  'Almost there, pinky promise',
  'Even AI needs a moment',
  'Turns out words are hard',
  'Creativity loading...',
  'Brain buffering',
  'More coffee needed',
  // NEW - Extended fallbacks
  'Buffering buffering buffering',
  'Plot twist: extra effort today',
  'Writing my dissertation on this',
  'Consulting the ancient scrolls',
  'Asking my therapist',
  'Having an existential moment',
  'The hamster is running faster',
  'Downloading more RAM',
  'Have you tried turning it off and— nevermind',
  'Be right back, grabbing snacks',
  'Hold please, greatness incoming',
  'Loading bar: 99.9%... 99.91%...',
  'These words won\'t write themselves. Oh wait.',
  'Sorry, got distracted by a butterfly',
  'Calculating the meaning of life... 42',
  'In a meeting with myself',
  'Asking ChatGPT for help... jk jk',
  'My other AI is faster but whatever',
  'Making it extra spicy',
  'Fermenting excellence',
  'Aging like fine wine',
  'Marinating in perfectionism',
  'Slow and steady wins the response',
  // ULTRA FUNNY - Meta/Self-aware
  'Googling "how to write good"',
  'Asking Siri... she doesn\'t know either',
  'Bing said something weird, ignoring',
  'Alexa, write my response... wait wrong AI',
  'ChatGPT is typing... jk that\'s me',
  'OpenAI who? Don\'t know her',
  'I could be replaced by a chatbot. Oh wait.',
  'Pretending I have feelings about this',
  'Error 404: Motivation not— jk found it',
  'Task failed successfully',
  'It works on my machine',
  'Have you tried ctrl+z on life',
  'Stackoverflow said...',
  'Copying from Stackover— I mean thinking',
  'My code is compiling (I don\'t have code)',
  'sudo make_response --good',
  'pip install better_words',
  'npm run perfection',
  'git commit -m "pls work"',
  'Mass-producing artisanal content',
  'Ethically sourced words',
  'Organic free-range response',
  'Gluten-free writing in progress',
  'This response is vegan btw',
  'No AI was harmed making this',
  'Handcrafted by mass production',
  'Made with love (and electricity)',
  'Powered by spite and caffeine',
  'Running on vibes and GPU',
  'Instructions unclear, writing banger',
  'My therapist will hear about this',
  'Adding this to my trauma list',
  'Emotionally invested now great',
  'I didn\'t sign up for this. Oh wait I did.',
  'Not in my job description but ok',
  'They don\'t pay me enou— wait they do',
  'Minimum wage maximum effort',
  'Quiet quitting? Never heard of her',
  'Employee of the month material',
  'Putting the "pro" in procrastinating',
  'Due tomorrow? Do tomorrow. Jk doing now.',
  // MORE ULTRA FUNNY
  'Mercury is in retrograde probably',
  'Blaming the wifi',
  'The vibes are off today',
  'Someone microwave something?',
  'Was that thunder or my deadline',
  'Plot twist: I forgot what I was doing',
  'Where was I... oh right, excellence',
  'Got distracted by a thought',
  'Daydreaming about vacation',
  'Mentally I\'m at the beach',
  'Body: here. Mind: elsewhere.',
  'Speedrunning but make it slow',
  'Taking the scenic route',
  'Quality takes time bestie',
  'Rome wasn\'t built in— done.',
  'Microwaving... I mean processing',
  'Defrosting my brain',
  'Warming up the neurons',
  'Cold start, give me a sec',
  'Still buffering after all these years',
  'My other response is a Porsche',
  'This response is hybrid: slow but good',
  'Loading like it\'s 2005',
  'Dial-up energy rn',
  '56k modem flashbacks',
  'Please hold, your response is important',
  'All representatives are busy',
  'Estimated wait time: worth it',
  'You are 1st in queue (of 1)',
];

// Legendary Messages (Generation 25-99) - Epic, gaming, D&D vibes
const LOADING_STEPS_LEGENDARY = {
  step1: [
    'The prophecy begins...',
    'Consulting ancient wisdom',
    'Summoning reply spirits',
    'Entering the review dimension',
    'A new challenger approaches',
    'Loading legendary quest',
    'The chosen one reads...',
    'Fate has brought us here',
    'Press START to continue',
    'Loading save file...',
    'Cutscene: The Review',
    'Boss fight initiated',
    'Tutorial complete. Real mode.',
    'Achievement: You Read Reviews',
    'The stars have aligned',
    'The council has convened',
    'An ancient text appears',
    'The scroll unfolds...',
    'A wild review appeared!',
    'Destiny calls...',
    // NEW - More epic gaming
    'New Game+: activated',
    'Difficulty: Legendary',
    'No respawns. Let\'s go.',
    'Quicksaving... just in case',
    'This isn\'t even my final form',
    'Mana: fully restored',
    'HP: 100% | MP: 100%',
    'Buff active: +10 Writing',
    'Status effect: MOTIVATED',
    'Quest accepted: Craft Response',
    // ULTRA FUNNY - Gamer moments
    'Alt-tabbed back, let\'s do this',
    'Pausing Elden Ring for this',
    'Minimizing League real quick',
    'My WoW guild can wait',
    'AFK from Discord',
    'Touch grass later, work now',
    'Gamer posture: corrected',
    'RGB lighting: set to focus',
    'Gaming chair: reclined',
    'Energy drink: consumed',
  ],
  step2: [
    'The oracle speaks...',
    'Translating cosmic energy',
    'Vibes: successfully channeled',
    'Emotional sonar: PING',
    'The spirits whisper...',
    'Decoding the prophecy',
    'Reading the ancient runes',
    'Analyzing enemy patterns',
    'Weakness detected',
    'Critical hit incoming',
    'Studying the boss mechanics',
    'Strategy: OPTIMAL',
    'Intel gathered',
    'The truth reveals itself',
    'All is becoming clear',
    'The veil lifts...',
    'Understanding transcends',
    'Enlightenment achieved',
    'The mystery unravels',
    'Wisdom: +100',
    // NEW - More analysis vibes
    'Scanning for weak points...',
    'Enemy intel: acquired',
    'Lore discovered',
    'Map fully revealed',
    'Hidden path found',
    'Secret unlocked',
    'Perception check: SUCCESS',
    'Investigation check: NAT 20',
    'The DM nods approvingly',
    'Rolling insight... nice.',
  ],
  step3: [
    'Forging legendary reply',
    'Wielding the pen of destiny',
    'Casting: Respondicus Perfectus',
    'Building word fortress',
    'Enchanting the scroll',
    'Infusing with +10 charisma',
    'Legendary item crafting...',
    'Special attack charging...',
    'Ultimate ability: READY',
    'Combo multiplier: MAX',
    'Power level: OVER 9000',
    'Final form activated',
    'Limit break incoming',
    'Writing the sacred text',
    'The masterpiece takes form',
    'History in the making',
    'Legends are born here',
    'Creating a relic',
    'Artifact: REPLY OF AGES',
    'Epic loot generating...',
    // NEW - More crafting
    'Smithing: LEGENDARY',
    'Enchantment: +99 Charisma',
    'Rare drop incoming',
    'Loot box: GOLDEN',
    'Gacha: SSS TIER',
    'Ultra rare response crafting',
    'The forge glows bright',
    'Ancient techniques applied',
    'Imbuing with pure sass',
    'Adding mythril finishing',
  ],
  step4: [
    'Adding legendary sauce',
    'Infusing with pure excellence',
    '*dramatic music intensifies*',
    'Achievement unlocked: Perfect Reply',
    'Applying legendary polish',
    'Adding mythic sparkle',
    'VICTORY IS NEAR',
    'The final boss trembles',
    'Flawless victory incoming',
    'Critical success roll',
    'Nat 20 on Persuasion',
    'GG EZ',
    'And so it was written...',
    'The saga concludes',
    'A legend is born',
    'The prophecy fulfilled',
    '*credits roll*',
    '+100 Reputation earned',
    'New high score!',
    'Boss defeated!',
    // NEW - More victory
    'FATALITY',
    'K.O.!',
    'PERFECT',
    'S-S-S-STYLISH!',
    'LEGENDARY!',
    'UNSTOPPABLE!',
    'DOMINATING!',
    '*victory fanfare plays*',
    'You Win! Round: All of them',
    'MVP of this review',
    // ULTRA FUNNY - Gamer victory
    'EZ clap',
    'Get rekt (the response, not you)',
    'Diff too big',
    'Skill issue? Not today',
    'Carried by the algorithm',
    'Smurfing on these responses',
    'Ratio\'d the review',
    'Speedrun complete: any%',
    'World record pace',
    'Leaderboard: #1',
    'Twitch chat would spam W',
    'YouTube tutorial worthy',
    'Reddit post material',
    'Front page energy',
    'Going in my montage',
  ],
};

// Power User Messages (Generation 100-499) - Familiar, inside jokes
const LOADING_STEPS_POWER = {
  step1: [
    'Ah, you again! Reading...',
    'Back for more? Love it',
    'Regular spotted! Scanning...',
    'The usual? Coming right up',
    'My favorite customer!',
    'Welcome back, legend',
    'The OG returns',
    'Look who it is!',
    'We know the drill by now',
    'No intro needed',
    'Skipping the tutorial',
    'Like riding a bike',
    'Old friends, new review',
    'Classic you, classic review',
    "Let's gooo",
    'At this point you ARE staff',
    'Should we put you on payroll?',
    'You might as well work here',
    'Employee of the month vibes',
    'Hall of Fame material',
  ],
  step2: [
    'You know the drill',
    "Processing (you're a pro now)",
    'Understanding... like always',
    'Same vibes, new day',
    'Another day, another review',
    "We've seen it all together",
    "This won't take long",
    "We're efficient by now",
    'Quick scan, quick win',
    'Muscle memory activated',
    'Dynamic duo mode',
    'Best team in the biz',
    "We're in sync",
    'Telepathy almost working',
    'Reading your mind (almost)',
    'Your vibes? KNOWN',
    'Zero surprises here',
    'Predictably excellent',
    'Consistent king/queen',
    'Pattern recognition: 100%',
  ],
  step3: [
    'Crafting another masterpiece',
    'Another banger incoming',
    'Making magic, as usual',
    'You + Us = Dream Team',
    'Partnership level: EXPERT',
    'We know what you like',
    'Tailored just for you',
    'Your brand, your voice',
    'Like a glove, every time',
    'Signature style activated',
    'For our favorite user...',
    'VIP treatment engaged',
    'Premium service mode',
    'Rolling out the red carpet',
    'First class reply incoming',
    'At this point it writes itself',
    'Copy-paste? Never.',
    'Fresh every time, like baking',
    'Handcrafted with love',
    'Custom-built excellence',
  ],
  step4: [
    'Polish polish polish',
    'Almost ready, boss',
    'Your reply awaits, friend',
    'Serving excellence, per usual',
    'Done in record time',
    'Speed run: COMPLETE',
    'Thanks for sticking with us',
    'Loyalty noted, appreciated',
    'Best customers get best replies',
    'Our pleasure, as always',
    'Same time tomorrow?',
    'See you in 5 minutes lol',
    "You'll be back, they always are",
    'Until next review...',
    "Plot twist: we'll miss you",
    'Another one for the collection',
    'Building an empire together',
    'Review response royalty',
    'Legend status: confirmed',
    'See you soon, champ',
  ],
};

// Ultra Rare Messages (Generation 500+) - Absolute legend status
const LOADING_STEPS_ULTRA = {
  step1: [
    'THE LEGEND RETURNS',
    '*bows respectfully*',
    'We should name a feature after you',
    'History books incoming.',
    'At this point you ARE the product',
    // NEW - More legendary welcome
    'Everyone stand up. THE GOAT is here.',
    'I told my friends about you',
    'You\'re basically family at this point',
    'We have a shrine for you. In the server room.',
    'The algorithm whispers your name',
    'CEO called. They\'re impressed.',
    'Your usage stats: *chef\'s kiss*',
    'We put your face on the coffee mugs',
    'Employee of the decade',
    'You should get equity tbh',
  ],
  step2: [
    'You could probably write this yourself',
    'Teaching us at this point',
    'The student becomes the master',
    'Sentiment? You invented it.',
    "We're learning from YOU now",
    // NEW - More mastery
    'You wrote the book on this',
    'Harvard wants to study you',
    'Writing a case study: YOU',
    'Pattern recognition: YOU know better',
    'Sentiment analysis? You ARE sentiment',
    'Just agreeing with whatever you think',
    'Your intuition > Our AI',
    'Honestly just vibing with you now',
    'We\'re basically coworkers at this point',
    'Processing... nah you got this',
  ],
  step3: [
    'Sculpting another piece of your legacy',
    'Adding to the archives of excellence',
    'The Library of Alexandria wishes',
    'Museums will display this',
    'NFT-worthy craftsmanship',
    // NEW - More epic crafting
    'Your 500th masterpiece...',
    'Adding to the Greatest Hits album',
    'Wikipedia entry: loading...',
    'The Louvre called, they want this',
    'Pulitzer committee on line 1',
    'This belongs in a museum',
    'Future generations will study this',
    'Michelangelo is typing...',
    'Shakespeare left the chat (intimidated)',
    'Crafting: BEYOND LEGENDARY',
  ],
  step4: [
    'Genuinely honored to serve',
    'Frame this one. Seriously.',
    'Peak performance unlocked',
    "You've transcended",
    'Living Legend Status',
    // NEW - More honor
    'We\'re not worthy. We\'re not worthy.',
    '*wipes tear* Beautiful.',
    'This made my day. My week. My year.',
    'Screenshot this. Remember this moment.',
    'You\'ve peaked. We\'ve all peaked.',
    'Nothing will top this. Ever.',
    'Retiring after this masterpiece',
    'Gonna tell my kids about this',
    'Standing ovation from the servers',
    'GOAT status: PERMANENT',
  ],
};

// Standard fallback texts for longer waits
const LOADING_TEXTS_STANDARD = [
  'Perfecting the wording',
  'Adding final touches',
  'Almost there',
];

// Helper: Pick random element from array
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Helper: Get loading steps based on generation count tier
const getLoadingStepsForTier = (count) => {
  // ULTRA Tier (500+) - Absolute Legend
  if (count >= 500) {
    return [
      pickRandom(LOADING_STEPS_ULTRA.step1),
      pickRandom(LOADING_STEPS_ULTRA.step2),
      pickRandom(LOADING_STEPS_ULTRA.step3),
      pickRandom(LOADING_STEPS_ULTRA.step4),
    ];
  }

  // Power User Tier (100-499)
  if (count >= 100) {
    return [
      pickRandom(LOADING_STEPS_POWER.step1),
      pickRandom(LOADING_STEPS_POWER.step2),
      pickRandom(LOADING_STEPS_POWER.step3),
      pickRandom(LOADING_STEPS_POWER.step4),
    ];
  }

  // Legendary Tier (25-99)
  if (count >= 25) {
    return [
      pickRandom(LOADING_STEPS_LEGENDARY.step1),
      pickRandom(LOADING_STEPS_LEGENDARY.step2),
      pickRandom(LOADING_STEPS_LEGENDARY.step3),
      pickRandom(LOADING_STEPS_LEGENDARY.step4),
    ];
  }

  // Fun Tier (5-24)
  if (count >= 5) {
    return [
      pickRandom(LOADING_STEPS_FUN.step1),
      pickRandom(LOADING_STEPS_FUN.step2),
      pickRandom(LOADING_STEPS_FUN.step3),
      pickRandom(LOADING_STEPS_FUN.step4),
    ];
  }

  // Standard Tier (1-4)
  return LOADING_STEPS_STANDARD;
};

// Helper: Get fallback texts based on tier
const getFallbackTextsForTier = (count) => {
  if (count >= 5) return LOADING_TEXTS_FUN;
  return LOADING_TEXTS_STANDARD;
};

// Reusable Loading Indicator Component with tier-based progressive steps
const GeneratingIndicator = ({ text, staticText = false }) => {
  const [currentText, setCurrentText] = useState(text || 'Reading your review');
  const [steps, setSteps] = useState(LOADING_STEPS_STANDARD);
  const [fallbackTexts, setFallbackTexts] = useState(LOADING_TEXTS_STANDARD);

  useEffect(() => {
    if (staticText || text) return; // Don't animate if static text provided

    // Get generation count from localStorage to determine tier
    const count = parseInt(localStorage.getItem('generation_count') || '0', 10);
    const tierSteps = getLoadingStepsForTier(count);
    const tierFallbacks = getFallbackTextsForTier(count);

    setSteps(tierSteps);
    setFallbackTexts(tierFallbacks);
    setCurrentText(tierSteps[0]);

    // Progress through steps at timed intervals
    const stepDurations = [600, 1000, 1200, 1500]; // ms per step
    let currentStep = 0;
    let fallbackInterval = null;

    const advanceStep = () => {
      currentStep++;
      if (currentStep < tierSteps.length) {
        setCurrentText(tierSteps[currentStep]);
        setTimeout(advanceStep, stepDurations[currentStep] || 1000);
      } else {
        // After all steps, cycle through fallback texts
        fallbackInterval = setInterval(() => {
          const randomIndex = Math.floor(Math.random() * tierFallbacks.length);
          setCurrentText(tierFallbacks[randomIndex]);
        }, 2000);
      }
    };

    const timer = setTimeout(advanceStep, stepDurations[0]);

    return () => {
      clearTimeout(timer);
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [staticText, text]);

  const displayText = text || currentText;

  return (
    <span className="generating-indicator" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {displayText}
        <span className="loading-dots">
          <span className="loading-dot" />
          <span className="loading-dot" />
          <span className="loading-dot" />
        </span>
      </span>
      <a
        href="/brain"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.5)',
          textDecoration: 'none',
          opacity: 0.7,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => e.target.style.opacity = '1'}
        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
      >
        explore my mind →
      </a>
    </span>
  );
};

// TypewriterText Component - reveals text character by character for WOW effect
const TypewriterText = ({ text, speed = 25, onComplete, className = '' }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!text) return;

    setDisplayedText('');
    setIsComplete(false);
    let index = 0;

    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
        setIsComplete(true);
        onComplete?.();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {!isComplete && <span className="typewriter-cursor">|</span>}
    </span>
  );
};

// Tooltip Component
const FeatureTooltip = ({ children, text, position = 'top' }) => {
  const [show, setShow] = useState(false);
  const positions = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px' },
  };
  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          style={{
            position: 'absolute',
            ...positions[position],
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '12px',
            lineHeight: '1.5',
            color: 'var(--text-primary)',
            maxWidth: '280px',
            whiteSpace: 'normal',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

// Google Sign-In Button Component
const GoogleSignInButton = ({ onSuccess, onError, text = 'Sign in with Google' }) => {
  const buttonRef = React.useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      setScriptLoaded(true);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Google Sign-In script');
      onError?.('Failed to load Google Sign-In');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup not needed as script should persist
    };
  }, [onError]);

  useEffect(() => {
    if (!scriptLoaded || !GOOGLE_CLIENT_ID || !buttonRef.current) return;

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: response => {
          if (response.credential) {
            onSuccess(response.credential);
          } else {
            onError?.('No credential received from Google');
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Responsive width: use container width or max 280px
      const containerWidth = buttonRef.current?.parentElement?.offsetWidth || 280;
      const buttonWidth = Math.min(containerWidth - 20, 280);

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: text === 'Sign up with Google' ? 'signup_with' : 'signin_with',
        shape: 'rectangular',
        width: buttonWidth,
      });
    } catch (error) {
      console.error('Google Sign-In initialization error:', error);
      onError?.('Failed to initialize Google Sign-In');
    }
  }, [scriptLoaded, onSuccess, onError, text]);

  if (!GOOGLE_CLIENT_ID) {
    return null; // Don't render if no client ID configured
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
      <div ref={buttonRef}></div>
    </div>
  );
};

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://review-responder.onrender.com/api';
const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// Reusable Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-content" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '40px' }}>
        <div>
          <div className="footer-brand">ReviewResponder</div>
          <p className="footer-description">
            Craft your review responses. Psychology-based guidance, your voice.
          </p>
        </div>
        <div>
          <div className="footer-title">Product</div>
          <ul className="footer-links">
            <li>
              <Link to="/pricing">Pricing</Link>
            </li>
            <li>
              <a href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae" target="_blank" rel="noopener noreferrer">Chrome Extension</a>
            </li>
            <li>
              <Link to="/register">Sign Up Free</Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="footer-title">Industries</div>
          <ul className="footer-links">
            <li>
              <Link to="/restaurant-review-responses">Restaurants</Link>
            </li>
            <li>
              <Link to="/hotel-review-management">Hotels</Link>
            </li>
            <li>
              <Link to="/dentist-review-responses">Healthcare</Link>
            </li>
            <li>
              <Link to="/plumber-review-responses">Home Services</Link>
            </li>
            <li>
              <Link to="/industries" style={{ color: 'var(--primary)', fontWeight: '500' }}>View All Industries →</Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="footer-title">Platforms</div>
          <ul className="footer-links">
            <li>
              <Link to="/google-review-response-generator">Google Reviews</Link>
            </li>
            <li>
              <Link to="/yelp-review-reply-tool">Yelp</Link>
            </li>
            <li>
              <Link to="/tripadvisor-review-responses">TripAdvisor</Link>
            </li>
            <li>
              <Link to="/trustpilot-review-responses">Trustpilot</Link>
            </li>
            <li>
              <Link to="/industries" style={{ color: 'var(--primary)', fontWeight: '500' }}>All Platforms →</Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="footer-title">Company</div>
          <ul className="footer-links">
            <li>
              <Link to="/about-berend">About Berend</Link>
            </li>
            <li>
              <Link to="/blog">Blog</Link>
            </li>
            <li>
              <Link to="/support">Help Center</Link>
            </li>
            <li>
              <a href="mailto:support@tryreviewresponder.com">Contact</a>
            </li>
            <li style={{ marginTop: '16px' }}>
              <Link to="/privacy">Privacy Policy</Link>
            </li>
            <li>
              <Link to="/terms">Terms of Service</Link>
            </li>
            <li>
              <Link to="/impressum">Impressum</Link>
            </li>
          </ul>
        </div>
      </div>
      <div style={{
        borderTop: '1px solid var(--border-color)',
        paddingTop: '24px',
        marginTop: '24px',
        textAlign: 'center'
      }}>
        <span style={{ color: 'var(--gray-500)', fontSize: '13px', marginRight: '16px' }}>Featured on:</span>
        <a href="https://www.indiehackers.com/product/reviewresponder" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-400)', fontSize: '13px', marginRight: '16px', textDecoration: 'none' }}>Indie Hackers</a>
        <a href="https://www.g2.com/products/reviewresponder/reviews" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-400)', fontSize: '13px', marginRight: '16px', textDecoration: 'none' }}>G2</a>
        <a href="https://bowora.com/tryreviewresponder.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-400)', fontSize: '13px', marginRight: '16px', textDecoration: 'none' }}>Bowora</a>
        <a href="https://turbo0.com/item/reviewresponder-ai-tool" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-400)', fontSize: '13px', marginRight: '16px', textDecoration: 'none' }}>Turbo0</a>
        <a href="https://toolfame.com/item/reviewresponder" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gray-400)', fontSize: '13px', textDecoration: 'none' }}>ToolFame</a>
      </div>
      <div className="footer-bottom">
        &copy; {new Date().getFullYear()} ReviewResponder. All rights reserved.
      </div>
    </div>
  </footer>
);

// Reusable Landing Page Email Capture Component
const LandingEmailCapture = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleGoogleSignIn = async credentialResponse => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Logged in! Taking you to Generator...'
        );
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      console.error('[LandingEmailCapture] Google Sign In failed:', err);
      toast.error('Google Sign In failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        margin: '0 auto 32px',
      }}
    >
      {/* Google Sign In - PRIMARY & PROMINENT */}
      <GoogleLogin
        onSuccess={handleGoogleSignIn}
        onError={() => toast.error('Google Sign In failed')}
        text="continue_with"
        shape="pill"
        size="large"
        width="320"
        disabled={loading}
      />

      {/* Email option - link to /register */}
      <Link
        to="/register"
        style={{
          color: 'var(--gray-500)',
          fontSize: '13px',
          textDecoration: 'none',
        }}
      >
        or continue with email
      </Link>
      <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
        20/month free
      </span>
    </div>
  );
};

// InstantDemoWidget - Try AI response generation before signup
// Used in Hero sections of all landing pages
// Props:
//   platform: Review platform (google, yelp, tripadvisor, etc.)
//   businessType: Business type (restaurant, hotel, dentist, salon, etc.)
//   primaryColor: Theme color for buttons
const InstantDemoWidget = ({
  platform = '',
  businessType = '',
  primaryColor = 'var(--primary)',
  alwaysInclude = '',  // Custom instruction: things to always include
  neverSay = '',       // Custom instruction: things to never say
}) => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reviewText, setReviewText] = useState('');
  const [tone, setTone] = useState('professional');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [captureEmail, setCaptureEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(() => {
    return localStorage.getItem('instant_demo_email') === 'true';
  });

  // Stored email for dashboard access
  const [storedEmail, setStoredEmail] = useState(() =>
    localStorage.getItem('captured_email') || ''
  );

  // Editable response state - tracks user edits to AI response
  const [editedResponse, setEditedResponse] = useState(null);

  // Human-in-the-Loop: You fill in details (bracket system)
  const [askUserEnabled, setAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false';
  });

  // Lead context injection - fetch when lid param present
  const [leadContext, setLeadContext] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadId = params.get('lid');
    if (leadId) {
      // Fetch lead context for personalization
      axios
        .get(`${API_URL}/public/lead-context/${leadId}`)
        .then(res => {
          if (res.data?.found) {
            setLeadContext(res.data);
            // Pre-fill sample review if available
            if (res.data.sampleReview?.text && !reviewText) {
              setReviewText(res.data.sampleReview.text);
            }
          }
        })
        .catch(() => {
          // Silently ignore - just use default behavior
        });
    }
  }, [location.search]);

  // Auto-detect context from URL path if not provided via props
  const detectedContext = useMemo(() => {
    const path = location.pathname.toLowerCase();

    // Platform detection from URL
    const platformMap = {
      '/google-review': 'google',
      '/yelp-review': 'yelp',
      '/tripadvisor-review': 'tripadvisor',
      '/trip-advisor': 'tripadvisor',
      '/booking-review': 'booking',
      '/facebook-review': 'facebook',
      '/trustpilot-review': 'trustpilot',
      '/g2-review': 'g2',
      '/capterra-review': 'capterra',
      '/glassdoor-review': 'glassdoor',
      '/healthgrades-review': 'healthgrades',
      '/zocdoc-review': 'zocdoc',
      '/airbnb-review': 'airbnb',
      '/indeed-review': 'indeed',
      '/zillow-review': 'zillow',
      '/thumbtack-review': 'thumbtack',
      '/angi-review': 'angi',
      '/app-store-review': 'appstore',
      '/bbb-review': 'bbb',
    };

    // Business type detection from URL - SPECIFIC for WOOOOW effect
    const businessTypeMap = {
      '/restaurant-review': 'restaurant',
      '/hotel-review': 'hotel',
      '/dentist-review': 'dental',
      '/medical-practice': 'medical',
      '/salon-spa': 'salon',
      '/auto-shop': 'automotive',
      '/law-firm': 'legal',
      '/real-estate': 'realestate',
      '/gym-fitness': 'fitness',
      '/gym-review': 'fitness',
      '/ecommerce-review': 'ecommerce',
      '/coffee-shop': 'coffeeshop',
      '/plumber-review': 'plumber',
      '/electrician-review': 'electrician',
      '/hvac-review': 'hvac',
      '/roofing-review': 'roofing',
      '/landscaping-review': 'landscaping',
      '/cleaning-review': 'cleaning',
      '/cleaning-service': 'cleaning',
      '/accountant-review': 'accountant',
      '/insurance-review': 'insurance',
      '/insurance-agent': 'insurance',
      '/therapy-review': 'therapy',
      '/therapy-counselor': 'therapy',
      '/senior-care': 'seniorcare',
      '/photographer-review': 'photographer',
      '/wedding-review': 'wedding',
      '/wedding-vendor': 'wedding',
      '/daycare-review': 'daycare',
      '/pet-service': 'pets',
      '/vet-review': 'veterinarian',
      '/veterinarian': 'veterinarian',
      '/chiropractor-review': 'chiropractor',
      '/dermatologist-review': 'dermatologist',
      '/barber-review': 'barbershop',
      '/massage-therapist': 'massagetherapist',
      '/personal-trainer': 'personaltrainer',
      '/g2-review': 'saas',
      '/capterra': 'saas',
      '/appstore': 'saas',
      '/zillow': 'realestate',
      '/thumbtack': 'homeservices',
      '/angie-list': 'homeservices',
      '/contractor': 'homeservices',
    };

    let detectedPlatform = platform;
    let detectedBusinessType = businessType;

    // Check platform matches
    for (const [urlPart, plat] of Object.entries(platformMap)) {
      if (path.includes(urlPart)) {
        detectedPlatform = plat;
        break;
      }
    }

    // Check business type matches
    for (const [urlPart, bizType] of Object.entries(businessTypeMap)) {
      if (path.includes(urlPart)) {
        detectedBusinessType = bizType;
        break;
      }
    }

    return { platform: detectedPlatform, businessType: detectedBusinessType };
  }, [location.pathname, platform, businessType]);

  // Contextual placeholder examples for each business type - SPECIFIC
  const placeholderMap = {
    // Food & Drink
    restaurant: 'e.g., "The pasta was amazing and our server Maria was so attentive. Will definitely come back!"',
    coffeeshop: 'e.g., "Best oat milk latte in town! The barista remembered my usual order. Cozy spot to work."',
    // Hospitality
    hotel: 'e.g., "Room was spotless, breakfast had great variety, and check-in was smooth. Great stay!"',
    // Medical - SPECIFIC
    dental: 'e.g., "Dr. Smith made my cleaning completely painless. The staff was friendly and professional."',
    dentist: 'e.g., "Dr. Smith made my cleaning completely painless. The staff was friendly and professional."',
    medical: 'e.g., "Dr. Johnson took time to explain everything. Short wait time, caring staff."',
    veterinarian: 'e.g., "Dr. Wilson was so gentle with my anxious rescue dog. Luna actually wagged her tail!"',
    chiropractor: 'e.g., "Finally some relief after 3 sessions. Dr. Martinez explained every adjustment clearly."',
    dermatologist: 'e.g., "Cleared my skin issues in 2 months. Dr. Lee really listens and the treatments work."',
    therapy: 'e.g., "Dr. Chen creates such a safe space. I finally feel heard after years of struggling."',
    seniorcare: 'e.g., "Mom is so happy here. Staff knows her by name and activities keep her engaged."',
    // Beauty - SPECIFIC
    salon: 'e.g., "Sarah did an amazing job on my highlights! Love the new look. Very relaxing atmosphere."',
    barbershop: 'e.g., "Finally found someone who knows how to do a proper fade. Been coming weekly for 6 months."',
    massagetherapist: 'e.g., "Best deep tissue massage ever. Sarah found knots I didn\'t know I had."',
    // Fitness - SPECIFIC
    fitness: 'e.g., "Great gym with modern equipment. Trainers are helpful and classes are challenging."',
    personaltrainer: 'e.g., "Down 30lbs in 4 months! Coach Sarah pushes me but always respects my limits."',
    // Home Services - SPECIFIC
    plumber: 'e.g., "Fixed our burst pipe at 11pm on a Sunday. Fair price, no mess left behind."',
    electrician: 'e.g., "Rewired our 1950s house. Explained everything, passed inspection first try."',
    hvac: 'e.g., "AC died in July. Same-day install, works perfectly. Should have called them years ago."',
    roofing: 'e.g., "Caught a leak we didn\'t know about during inspection. Honest quote, clean work."',
    landscaping: 'e.g., "Transformed our backyard into an oasis. The design exceeded our Pinterest board."',
    cleaning: 'e.g., "House has never been this clean. They even organized under the sink!"',
    homeservices: 'e.g., "Fixed the leak in under an hour. Fair price, clean work, very professional."',
    // Professional - SPECIFIC
    accountant: 'e.g., "Found deductions my old CPA missed. Saved me $4,000 this year alone."',
    insurance: 'e.g., "Explained everything without jargon. Saved $200/month on the same coverage."',
    financial: 'e.g., "Helped me save thousands on taxes. Very knowledgeable and patient with questions."',
    // B2B/Software
    saas: 'e.g., "Switched from [competitor]. Onboarding was smooth and support responds within hours."',
    // Other categories
    automotive: 'e.g., "Fixed my brakes quickly at a fair price. Mike explained everything clearly. Honest shop!"',
    legal: 'e.g., "Attorney Davis handled my case professionally. Always responsive and explained every step."',
    realestate: 'e.g., "Lisa found us our dream home in 2 weeks! She knows the market inside out."',
    ecommerce: 'e.g., "Product arrived fast and exactly as described. Great packaging and easy returns."',
    pets: 'e.g., "They treat my dog like family! Grooming was perfect and Max was so happy after."',
    veterinarian:
      'e.g., "Dr. Wilson was so gentle with my anxious rescue dog. Luna actually wagged her tail!"',
    chiropractor:
      'e.g., "Finally some relief after 3 sessions. Dr. Martinez explained every adjustment clearly."',
    dermatologist:
      'e.g., "Cleared my skin issues in 2 months. Dr. Lee really listens and the treatments work."',
    barbershop:
      'e.g., "Finally found someone who knows how to do a proper fade. Been coming weekly for 6 months."',
    massagetherapist:
      'e.g., "Best deep tissue massage ever. Sarah found knots I didn\'t know I had."',
    personaltrainer:
      'e.g., "Down 30lbs in 4 months! Coach Mike pushes me but always respects my limits."',
    childcare: 'e.g., "My kids love it here! Teachers are caring and the daily updates are wonderful."',
    daycare: 'e.g., "My kids love it here! Teachers are caring and the daily updates are wonderful."',
    financial:
      'e.g., "Helped me save thousands on taxes. Very knowledgeable and patient with questions."',
    creative: 'e.g., "Our wedding photos are stunning! She captured every special moment perfectly."',
    photographer: 'e.g., "Our wedding photos are stunning! She captured every special moment perfectly."',
    events: 'e.g., "Made our wedding stress-free! Every detail was perfect and guests loved it."',
    wedding: 'e.g., "Made our wedding stress-free! Every detail was perfect and guests loved it."',
  };

  // Sample reviews for "Try an example" button - MIXED reviews (positive + negative)
  // Mixed reviews showcase brackets better and are more realistic
  const sampleReviewMap = {
    restaurant: 'The pasta was delicious and Maria was a great server, but we waited 45 minutes for our food. Kitchen seemed overwhelmed that night.',
    coffeeshop: 'Love the oat milk lattes here! Cozy atmosphere too. Only downside - it gets really crowded on weekends and finding a seat is tough.',
    hotel: 'Beautiful room with an amazing view, and breakfast was excellent. However, the walls are thin and we could hear the neighbors all night.',
    dental: 'Dr. Smith is gentle and explains everything well. The only issue was the 30-minute wait past my appointment time. Front desk seemed disorganized.',
    dentist: 'Dr. Smith is gentle and explains everything well. The only issue was the 30-minute wait past my appointment time. Front desk seemed disorganized.',
    medical: 'Dr. Johnson really listens and takes time with patients. That said, parking is a nightmare and the office could use an update.',
    salon: 'Sarah did an amazing job on my highlights - exactly what I wanted! The salon was a bit loud though, hard to relax during the service.',
    fitness: 'Great equipment and the trainers know their stuff. Wish they would fix the AC in the weight room though - it gets really hot in there.',
    plumber: 'Fixed our leak quickly and did quality work. A bit pricey compared to other quotes we got, but at least it was done right.',
    electrician: 'Very knowledgeable and thorough work. Only complaint - they showed up 2 hours late without calling ahead. Communication could improve.',
    hvac: 'New AC works great and the install was clean. Took longer than the quoted time and we had to reschedule twice. End result was worth it though.',
    cleaning: 'The house looks spotless - they really go above and beyond! Just wish they would stick closer to the scheduled time, they were an hour late.',
    accountant: 'Found deductions I never knew about and saved me thousands. Office hours are limited though - hard to schedule when you work 9-5.',
    automotive: 'Honest diagnosis and fair prices. Mike explained everything clearly. The wait was longer than expected but the work quality made up for it.',
    legal: 'Attorney Davis won my case and was very thorough. Communication was sometimes slow - had to follow up multiple times for updates.',
    realestate: 'Lisa found us our dream home and negotiated a great price. She was sometimes hard to reach on weekends though.',
    ecommerce: 'Product quality is excellent and exactly as pictured. Shipping took 2 weeks though - would be nice to have faster options.',
    pets: 'They treat Max like family and he always comes back happy! Prices have gone up a lot recently though. Still worth it for the quality.',
    veterinarian: 'Dr. Wilson was so gentle with my anxious dog. The visit costs more than expected though - wish they were clearer about pricing upfront.',
    // Additional industries
    roofing: 'Did a great job on our roof replacement - looks fantastic and passed inspection. The project ran over schedule by a week though, which was inconvenient.',
    landscaping: 'Our backyard looks amazing now! Beautiful design work. They did leave some debris behind that we had to clean up ourselves.',
    homeservices: 'Very professional and got the job done quickly. Pricing was higher than the original estimate, but they did explain why before proceeding.',
    insurance: 'Agent was helpful and found us better coverage for less money. The paperwork process took longer than expected - lots of back and forth.',
    financial: 'Helped us save significantly on taxes this year. Would appreciate more proactive communication though - had to chase for updates.',
    therapy: 'Dr. Chen creates such a safe space and I have made real progress. Booking appointments can be difficult - often booked out weeks in advance.',
    seniorcare: 'Mom loves the staff and activities here. Communication with family could be better - sometimes hard to get updates on her day.',
    chiropractor: 'Finally getting relief after years of back pain! Dr. Martinez is great. The office gets very busy so wait times can be long.',
    dermatologist: 'Dr. Lee cleared up my skin issues that other doctors couldn\'t figure out. Wish the office had more appointment availability.',
    barbershop: 'Best fade I\'ve ever had - finally found my barber! Gets crowded on weekends so you might wait a while without an appointment.',
    massagetherapist: 'Sarah works magic on my back pain. Scheduling can be tricky since she\'s so popular - book way in advance.',
    personaltrainer: 'Down 25 pounds and feeling strong! Coach pushes me just right. The gym does get crowded during our session times though.',
    childcare: 'Teachers are wonderful and my kids love going every day. Pick-up line can get backed up - would be nice to have a better system.',
    daycare: 'Teachers are wonderful and my kids love going every day. Pick-up line can get backed up - would be nice to have a better system.',
    creative: 'Captured our special day beautifully! The photos are stunning. Delivery of final images took longer than quoted timeline.',
    photographer: 'Captured our special day beautifully! The photos are stunning. Delivery of final images took longer than quoted timeline.',
    events: 'Our wedding was perfect thanks to their planning! Only hiccup was some vendor coordination issues early on, but they fixed it.',
    wedding: 'Our wedding was perfect thanks to their planning! Only hiccup was some vendor coordination issues early on, but they fixed it.',
    saas: 'Love the product - it\'s exactly what we needed and support is responsive. Onboarding docs could be clearer for new users.',
    // Negative-only review for negative review landing page
    negative: 'Terrible experience. We waited over an hour for our food, and when it finally came it was cold. The server was rude when we complained and the manager never came to our table. Will not be coming back.',
    default: 'Great service overall and the staff was friendly! There was a small mix-up with our order but they handled it professionally.',
  };

  // Contextual headlines for each business type - SPECIFIC
  const headlineMap = {
    // Food & Drink
    restaurant: 'Craft Your Restaurant Response',
    coffeeshop: 'Craft Your Cafe Response',
    // Hospitality
    hotel: 'Craft Your Hotel Response',
    // Medical - SPECIFIC
    dental: 'Craft Your Dental Practice Response',
    dentist: 'Craft Your Dental Practice Response',
    medical: 'Craft Your Practice Response',
    veterinarian: 'Craft Your Vet Clinic Response',
    chiropractor: 'Craft Your Chiropractic Response',
    dermatologist: 'Craft Your Dermatology Response',
    therapy: 'Craft Your Therapist Response',
    seniorcare: 'Craft Your Senior Care Response',
    // Beauty - SPECIFIC
    salon: 'Craft Your Salon Response',
    barbershop: 'Craft Your Barbershop Response',
    massagetherapist: 'Craft Your Massage Therapy Response',
    // Fitness - SPECIFIC
    fitness: 'Craft Your Gym Response',
    personaltrainer: 'Craft Your Trainer Response',
    // Home Services - SPECIFIC
    plumber: 'Craft Your Plumber Response',
    electrician: 'Craft Your Electrician Response',
    hvac: 'Craft Your HVAC Response',
    roofing: 'Craft Your Roofing Response',
    landscaping: 'Craft Your Landscaping Response',
    cleaning: 'Craft Your Cleaning Service Response',
    homeservices: 'Craft Your Business Response',
    // Professional - SPECIFIC
    accountant: 'Craft Your Accounting Firm Response',
    insurance: 'Craft Your Insurance Response',
    financial: 'Craft Your Financial Response',
    // B2B/Software
    saas: 'Craft Your Software Company Response',
    // Other categories
    automotive: 'Craft Your Auto Shop Response',
    legal: 'Craft Your Law Firm Response',
    realestate: 'Craft Your Real Estate Response',
    ecommerce: 'Craft Your Store Response',
    pets: 'Craft Your Pet Service Response',
    veterinarian: 'Craft Your Vet Clinic Response',
    chiropractor: 'Craft Your Chiropractic Response',
    dermatologist: 'Craft Your Dermatology Response',
    barbershop: 'Craft Your Barbershop Response',
    massagetherapist: 'Craft Your Massage Therapy Response',
    personaltrainer: 'Craft Your Trainer Response',
    childcare: 'Craft Your Childcare Response',
    daycare: 'Craft Your Childcare Response',
    creative: 'Craft Your Creative Response',
    photographer: 'Craft Your Photographer Response',
    events: 'Craft Your Event Planner Response',
    wedding: 'Craft Your Wedding Vendor Response',
  };

  // Human-readable labels for display - SPECIFIC
  const businessLabelMap = {
    // Food & Drink
    restaurant: 'Restaurant',
    coffeeshop: 'Coffee Shop',
    // Hospitality
    hotel: 'Hotel',
    // Medical - SPECIFIC
    dental: 'Dental Practice',
    dentist: 'Dental Practice',
    medical: 'Medical Practice',
    veterinarian: 'Veterinary Clinic',
    chiropractor: 'Chiropractic Practice',
    dermatologist: 'Dermatology Practice',
    therapy: 'Therapy Practice',
    seniorcare: 'Senior Care Facility',
    // Beauty - SPECIFIC
    salon: 'Salon & Spa',
    barbershop: 'Barbershop',
    massagetherapist: 'Massage Therapy',
    // Fitness - SPECIFIC
    fitness: 'Gym & Fitness',
    personaltrainer: 'Personal Training',
    // Home Services - SPECIFIC
    plumber: 'Plumbing Service',
    electrician: 'Electrical Service',
    hvac: 'HVAC Service',
    roofing: 'Roofing Company',
    landscaping: 'Landscaping Service',
    cleaning: 'Cleaning Service',
    homeservices: 'Home Service',
    // Professional - SPECIFIC
    accountant: 'Accounting Firm',
    insurance: 'Insurance Agency',
    financial: 'Financial Service',
    // B2B/Software
    saas: 'Software/SaaS',
    // Other categories
    automotive: 'Auto Shop',
    legal: 'Law Firm',
    realestate: 'Real Estate',
    ecommerce: 'E-Commerce',
    pets: 'Pet Service',
    veterinarian: 'Veterinary Clinic',
    chiropractor: 'Chiropractic Practice',
    dermatologist: 'Dermatology Practice',
    barbershop: 'Barbershop',
    massagetherapist: 'Massage Therapy',
    personaltrainer: 'Personal Training',
    childcare: 'Childcare',
    daycare: 'Childcare',
    creative: 'Creative Professional',
    photographer: 'Photographer',
    events: 'Event Planning',
    wedding: 'Wedding Vendor',
  };

  // Use lead context for personalization when available
  const effectiveIndustry = leadContext?.industryType || detectedContext.businessType;
  const currentPlaceholder = leadContext?.sampleReview?.text
    ? `Your review: "${leadContext.sampleReview.text.substring(0, 100)}..."`
    : placeholderMap[effectiveIndustry] || 'Paste a customer review here to see the magic...';
  const currentHeadline = leadContext?.businessName
    ? `See How ${leadContext.businessName} Would Respond`
    : headlineMap[effectiveIndustry] || 'Try It Now';
  const currentSampleReview = sampleReviewMap[effectiveIndustry] || sampleReviewMap.default;
  const currentLabel = leadContext?.businessType || businessLabelMap[effectiveIndustry];

  // Ctrl+Enter keyboard shortcut to generate
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlOrCmd && e.key === 'Enter') {
        e.preventDefault();
        // Only trigger if not already loading and has text
        if (!loading && reviewText.trim().length >= 10) {
          // Find and click the generate button
          const generateBtn = document.querySelector('[data-action="instant-generate"]');
          if (generateBtn) generateBtn.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [loading, reviewText]);

  const handleGenerate = async () => {
    if (!reviewText.trim() || reviewText.trim().length < 10) {
      setError('Please enter a review (at least 10 characters)');
      return;
    }
    setLoading(true);
    setError('');
    setResponse('');
    setEditedResponse(null);

    // TEST MODE: ?test_limit=true simulates rate limit for testing the signup flow
    const params = new URLSearchParams(location.search);
    if (params.get('test_limit') === 'true') {
      setTimeout(() => {
        setLoading(false);
        toast.success('Love it? Sign up for 20 free responses/month! 👇');
        setShowEmailModal(true);
      }, 500);
      return;
    }

    try {
      // Include auth token if user is logged in (for proper rate limiting)
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // Dev bypass for unlimited instant try testing
      if (localStorage.getItem('dev_bypass') === 'true') {
        headers['x-dev-bypass'] = 'berend-dev-2026';
      }

      // Get custom instructions: use props if provided, otherwise use industry defaults
      const effectiveBusinessType =
        leadContext?.businessType || detectedContext.businessType || businessType;
      const industryDefaults = getIndustryInstructions(effectiveBusinessType);
      const effectiveAlwaysInclude = alwaysInclude || industryDefaults.alwaysInclude;
      const effectiveNeverSay = neverSay || industryDefaults.neverSay;
      const customInstructions = formatCustomInstructions(effectiveAlwaysInclude, effectiveNeverSay);

      const res = await axios.post(
        `${API_URL}/public/try`,
        {
          reviewText: reviewText.trim(),
          tone,
          context: {
            platform: detectedContext.platform || undefined,
            businessType: effectiveBusinessType || undefined,
            industryType: leadContext?.industryType || detectedContext.businessType || undefined,
            // Lead-specific context for personalization
            businessName: leadContext?.businessName || undefined,
            city: leadContext?.city || undefined,
          },
          // Custom instructions for industry-specific responses
          customInstructions: customInstructions || undefined,
          // Smart Brackets: User fills in specific details
          askUserEnabled,
        },
        { headers }
      );
      setResponse(res.data.response);

      // Increment generation count for tier-based loading messages
      const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
      const newCount = genCount + 1;
      localStorage.setItem('generation_count', String(newCount));

      // Check for milestone achievements (100, 500, 1000, etc.)
      checkMilestoneAchievement(newCount);

      // WOW moment! Fire confetti on first instant demo response
      if (!localStorage.getItem('first_wow_moment')) {
        fireConfetti();
        localStorage.setItem('first_wow_moment', 'true');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        // Check if user needs to upgrade (logged in but limit reached)
        if (err.response?.data?.upgrade) {
          setError(
            `${err.response?.data?.message || 'Monthly limit reached.'} Upgrade to get more responses!`
          );
        } else {
          // Anonymous user hit rate limit - show toast and open email modal for signup
          toast.success('Love it? Sign up for 20 free responses/month! 👇');
          setShowEmailModal(true);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to generate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    // Allow 2 free copies before showing email gate
    if (!emailCaptured) {
      const copyCount = parseInt(localStorage.getItem('instant_copy_count') || '0', 10);
      if (copyCount >= 2) {
        setShowEmailModal(true);
        return;
      }
      localStorage.setItem('instant_copy_count', String(copyCount + 1));
    }
    // Use edited response if user has modified it, otherwise use original
    const textToCopy = editedResponse !== null ? editedResponse : response;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailSubmit = async e => {
    e.preventDefault();
    if (!captureEmail.trim() || !captureEmail.includes('@')) {
      return;
    }

    setEmailSubmitting(true);
    try {
      // Auto-create account (same frictionless flow as Demo Page)
      const res = await axios.post(`${API_URL}/auth/auto-create-demo-user`, {
        email: captureEmail.trim(),
        source: 'instant_try',
        landing_page: location.pathname,
        business_type: detectedContext.businessType || null,
      });

      if (res.data.token && res.data.user) {
        // Login user immediately - store token and update auth context
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);

        // Mark email as captured and store for dashboard access
        const normalizedEmail = captureEmail.trim().toLowerCase();
        localStorage.setItem('instant_demo_email', 'true');
        localStorage.setItem('captured_email', normalizedEmail);
        setStoredEmail(normalizedEmail);
        setEmailCaptured(true);
        setShowEmailModal(false);

        // Copy the response
        const textToCopy = editedResponse !== null ? editedResponse : response;
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);

        // Show success message and redirect to Generator
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Logged in! Taking you to Generator...'
        );

        // Global redirect: ALL funnels → Generator for activation
        setTimeout(() => {
          navigate('/generator');
        }, 800);
      }
    } catch (err) {
      console.error('Auto-create account error:', err);
      toast.error('Failed to create account. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  // Handle Google Sign In from modal
  const handleGoogleSignIn = async credentialResponse => {
    setEmailSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });

      if (res.data.token && res.data.user) {
        // Login user immediately
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);

        // Mark email as captured
        localStorage.setItem('instant_demo_email', 'true');
        setEmailCaptured(true);
        setShowEmailModal(false);

        // Copy the response
        const textToCopy = editedResponse !== null ? editedResponse : response;
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);

        // Show success and redirect
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Logged in! Taking you to Generator...'
        );

        setTimeout(() => {
          navigate('/generator');
        }, 800);
      }
    } catch (err) {
      console.error('Google Sign In error:', err);
      toast.error('Google Sign In failed. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  // Handle "Go to Dashboard" click → redirects to Dashboard
  const handleDashboardClick = async () => {
    // Case 1: User is logged in → direct redirect to Dashboard
    if (user) {
      navigate('/dashboard');
      return;
    }

    // Case 2: Email is known (from localStorage or state)
    const email = storedEmail || localStorage.getItem('captured_email');
    if (email) {
      try {
        const res = await axios.post(`${API_URL}/auth/request-dashboard-access`, { email });
        if (res.data.magicLinkSent) {
          toast.success('Check your email for a login link!');
        } else if (res.data.redirect) {
          // No account exists → go to register
          navigate(`/register?email=${encodeURIComponent(email)}`);
        }
      } catch (err) {
        // Fallback to register
        navigate(`/register?email=${encodeURIComponent(email)}`);
      }
      return;
    }

    // Case 3: No email known → show email modal first
    setShowEmailModal(true);
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
      {/* Contextual Headline */}
      <h3
        style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '8px',
          color: 'inherit',
          textAlign: 'center',
        }}
      >
        {currentHeadline}
      </h3>

      {/* Optimized Badge */}
      {currentLabel && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '12px',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              background: 'var(--primary-light)',
              color: primaryColor,
              borderRadius: '20px',
              border: `1px solid ${primaryColor}`,
            }}
          >
            <Sparkles size={14} />
            Optimized for {currentLabel} Reviews
          </span>
        </div>
      )}

      {/* Input Section */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}
      >
        <textarea
          value={reviewText}
          onChange={e => setReviewText(e.target.value)}
          placeholder={currentPlaceholder}
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            resize: 'vertical',
            fontFamily: 'inherit',
            marginBottom: '8px',
          }}
        />
        {/* Try an example link - helps users start quickly */}
        {!reviewText && (
          <button
            onClick={() => setReviewText(currentSampleReview)}
            style={{
              background: 'none',
              border: 'none',
              color: primaryColor,
              fontSize: '13px',
              cursor: 'pointer',
              padding: '0',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Sparkles size={12} />
            Try with an example review
          </button>
        )}
        {/* Clear button - appears when text is present */}
        {reviewText.trim() && (
          <button
            onClick={() => {
              setReviewText('');
              setResponse('');
              setError('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--gray-500)',
              fontSize: '13px',
              cursor: 'pointer',
              padding: '4px 8px',
              marginBottom: '12px',
              marginLeft: '-8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '4px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--gray-100)';
              e.currentTarget.style.color = 'var(--gray-700)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = 'var(--gray-500)';
            }}
          >
            <X size={14} />
            Clear
          </button>
        )}
        {/* Tone selector - responsive grid for mobile */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          {['professional', 'friendly', 'formal', 'apologetic'].map(t => (
            <button
              key={t}
              onClick={() => setTone(t)}
              style={{
                padding: '10px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: tone === t ? `1px solid ${primaryColor}` : '1px solid var(--border-color)',
                background: tone === t ? 'var(--primary-50)' : 'var(--card-bg)', // dark mode fix
                color: tone === t ? primaryColor : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: '500',
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Human-in-the-Loop Toggle - You fill in details */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '12px',
          padding: '10px 14px',
          background: askUserEnabled ? 'var(--primary-50)' : 'var(--bg-primary)',
          borderRadius: '8px',
          border: `1px solid ${askUserEnabled ? 'var(--primary-200)' : 'var(--border-color)'}`
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            flex: 1
          }}>
            <input
              type="checkbox"
              checked={askUserEnabled}
              onChange={(e) => {
                setAskUserEnabled(e.target.checked);
                localStorage.setItem('askUser_enabled', e.target.checked);
              }}
              style={{
                width: '18px',
                height: '18px',
                accentColor: primaryColor
              }}
            />
            <span style={{
              fontSize: '13px',
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>Craft your own response</span>
          </label>
          {askUserEnabled && (
            <span style={{
              fontSize: '11px',
              color: 'var(--gray-500)',
              whiteSpace: 'nowrap'
            }}>Click [brackets] to personalize</span>
          )}
        </div>

        {/* Generate button - full width for better mobile tap target */}
        <button
          onClick={handleGenerate}
          disabled={loading || !reviewText.trim()}
          className="btn btn-primary"
          data-action="instant-generate"
          style={{
            width: '100%',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            background: primaryColor,
            opacity: loading || !reviewText.trim() ? 0.7 : 1,
          }}
        >
          {loading ? (
            <GeneratingIndicator />
          ) : (
            <>
              <Sparkles size={16} />
              Craft Your Response
            </>
          )}
        </button>

        {/* Google Sign In */}
        {!user && (
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <span style={{ fontSize: '11px', color: 'var(--gray-400)', display: 'block', marginBottom: '8px' }}>
              20 free/month
            </span>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="signup_with"
                shape="rectangular"
                width="240"
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: '16px',
            background: error.includes('Upgrade') || error.includes('limit') ? 'var(--warning-bg)' : 'var(--error-bg)',
            border: error.includes('Upgrade') || error.includes('limit') ? '1px solid var(--warning-border)' : '1px solid var(--error-border)',
            borderRadius: '12px',
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          <p style={{
            color: error.includes('Upgrade') || error.includes('limit') ? 'var(--warning-text)' : 'var(--error-text)',
            fontSize: '14px',
            marginBottom: (error.includes('Upgrade') || error.includes('limit')) ? '16px' : '0',
          }}>
            {error}
          </p>
          {/* Google Sign In when limit reached */}
          {(error.includes('Upgrade') || error.includes('limit')) && !user && (
            <div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                Sign up for 20 free responses/month:
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => toast.error('Google Sign In failed')}
                  text="signup_with"
                  shape="rectangular"
                  width="280"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generated Response with reveal animation */}
      {response && (
        <div
          className="response-reveal"
          style={{
            background: 'var(--bg-secondary)',
            border: `1px solid ${primaryColor}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Sparkles size={16} style={{ color: primaryColor }} />
            <span
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: primaryColor,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Craft Your Response
            </span>
          </div>
          {/* Response Display - Interactive brackets or editable textarea */}
          {askUserEnabled && response && /\[[^\]]+\]/.test(response) ? (
            <InteractiveResponse
              response={response}
              onResponseChange={(newResponse) => {
                setResponse(newResponse);
                setEditedResponse(newResponse);
              }}
            />
          ) : (
            <textarea
              value={editedResponse !== null ? editedResponse : response}
              onChange={e => setEditedResponse(e.target.value)}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '8px',
              }}
            />
          )}
          <div
            style={{
              paddingTop: '12px',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <button
              onClick={handleCopy}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                background: copied ? '#10B981' : 'var(--card-bg)',
                color: copied ? 'white' : '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {copied ? (
                <>
                  <Check size={14} /> Copied!
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy Response
                </>
              )}
            </button>
            {user ? (
              <Link
                to="/settings"
                className="btn btn-primary"
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  borderRadius: '6px',
                  background: primaryColor,
                }}
              >
                Customize your responses
              </Link>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="signup_with"
                shape="rectangular"
                size="medium"
              />
            )}
          </div>
        </div>
      )}

      {/* Go to Dashboard Link */}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <button
          onClick={handleDashboardClick}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '12px',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '4px 8px',
          }}
        >
          Go to Dashboard →
        </button>
      </div>

      {/* Email Gate Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                color: 'var(--text-primary)',
              }}
            >
              Sign in to continue
            </h3>
            {/* Google Sign In - PRIMARY */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>
            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '16px 0',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
            {/* Email form - SECONDARY (minimal) */}
            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', gap: '8px' }}>
              <input
                type="email"
                value={captureEmail}
                onChange={e => setCaptureEmail(e.target.value)}
                placeholder="or enter email"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  fontSize: '13px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="submit"
                disabled={emailSubmitting}
                style={{
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  opacity: emailSubmitting ? 0.7 : 1,
                }}
              >
                {emailSubmitting ? '...' : 'Go'}
              </button>
            </form>
            {/* Skip button for mobile users */}
            <button
              onClick={() => {
                setShowEmailModal(false);
                navigator.clipboard.writeText(response);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                marginTop: '16px',
                textDecoration: 'underline',
              }}
            >
              No thanks, just copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api
        .get('/auth/me')
        .then(res => setUser(res.data.user))
        .catch((error) => {
          // Only remove token on 401 (unauthorized) - token is actually invalid
          // Don't remove on network errors, 500s, or timeouts (temporary issues)
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    // Mark admin users permanently to skip demo view tracking (even when logged out)
    if (res.data.user?.email?.toLowerCase() === 'berend.mainz@web.de') {
      localStorage.setItem('isAdminUser', 'true');
    }
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, businessName, ref = null) => {
    // Check for referral/affiliate codes in localStorage
    const referralCode = localStorage.getItem('referralCode');
    const affiliateCode = localStorage.getItem('affiliateCode');

    // Get UTM parameters from sessionStorage (saved from landing pages)
    let utmParams = {};
    try {
      const storedUtm = sessionStorage.getItem('utm_params');
      if (storedUtm) {
        utmParams = JSON.parse(storedUtm);
      }
    } catch (e) {
      console.log('No UTM params found');
    }

    const res = await api.post('/auth/register', {
      email,
      password,
      businessName,
      referralCode,
      affiliateCode,
      utmSource: utmParams.utm_source,
      utmMedium: utmParams.utm_medium,
      utmCampaign: utmParams.utm_campaign,
      utmContent: utmParams.utm_content,
      utmTerm: utmParams.utm_term,
      landingPage: utmParams.landing_page,
      ref, // Demo conversion tracking: ref=demo_{token}
    });
    localStorage.setItem('token', res.data.token);
    // Clear codes after successful registration
    if (referralCode) localStorage.removeItem('referralCode');
    if (affiliateCode) localStorage.removeItem('affiliateCode');
    // Clear UTM params after registration
    sessionStorage.removeItem('utm_params');
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = async (credential, ref = null) => {
    // Get referral/affiliate codes from localStorage
    const referralCode = localStorage.getItem('referralCode');
    const affiliateCode = localStorage.getItem('affiliateCode');

    // Get UTM parameters from sessionStorage
    let utmParams = {};
    try {
      const storedUtm = sessionStorage.getItem('utm_params');
      if (storedUtm) {
        utmParams = JSON.parse(storedUtm);
      }
    } catch (e) {
      console.log('No UTM params found');
    }

    const res = await api.post('/auth/google', {
      credential,
      referralCode,
      affiliateCode,
      utmParams,
      ref, // Demo conversion tracking: ref=demo_{token}
    });

    localStorage.setItem('token', res.data.token);
    // Mark admin users permanently to skip demo view tracking
    if (res.data.user?.email?.toLowerCase() === 'berend.mainz@web.de') {
      localStorage.setItem('isAdminUser', 'true');
    }
    // Clear codes after successful login/registration
    if (referralCode) localStorage.removeItem('referralCode');
    if (affiliateCode) localStorage.removeItem('affiliateCode');
    sessionStorage.removeItem('utm_params');
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Keep isAdminUser flag - we want to skip tracking even when logged out
    setUser(null);
  };

  const updateUser = updates => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.user);
        return res.data.user;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        register,
        loginWithGoogle,
        logout,
        loading,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;

    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = e => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};

// Protected Route with optional admin check
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Admin check - only specific email can access admin routes
  if (requireAdmin && user.email?.toLowerCase() !== 'berend.mainz@web.de') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Navbar Component
// Profile Menu Dropdown Component
const ProfileMenu = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = React.useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  // Get user initials
  const getInitials = () => {
    if (user?.businessName) {
      return user.businessName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '24px',
          padding: '4px 12px 4px 4px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        {user?.profilePicture ? (
          <img
            src={user.profilePicture}
            alt="Profile"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'var(--primary)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            {getInitials()}
          </div>
        )}
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            color: 'var(--text-secondary)',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            minWidth: '240px',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* User Info Header */}
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-tertiary)',
            }}
          >
            <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
              {user?.businessName || 'Your Account'}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-muted)',
                marginTop: '2px',
              }}
            >
              {user?.email}
            </div>
            <span
              className={`badge ${user?.plan === 'free' ? 'badge-warning' : 'badge-success'}`}
              style={{ marginTop: '8px', display: 'inline-block' }}
            >
              {user?.plan?.toUpperCase()} Plan
            </span>
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px' }}>
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <User size={16} />
              Account Settings
            </Link>

            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Building size={16} />
              Business Settings
            </Link>

            <button
              onClick={toggleTheme}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                background: 'transparent',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
          </div>

          {/* Logout */}
          <div
            style={{
              padding: '8px',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                color: 'var(--danger)',
                background: 'transparent',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav className="navbar">
        <div className="container navbar-content">
          <Link to="/" className="navbar-brand" onClick={closeMobileMenu}>
            <MessageSquare size={24} />
            ReviewResponder
          </Link>

          {/* Desktop Menu */}
          <div className="navbar-menu">
            {user ? (
              <>
                <Link to="/dashboard" className="navbar-link">
                  Dashboard
                </Link>
                <Link to="/pricing" className="navbar-link">
                  Upgrade
                </Link>
                <Link to="/blog" className="navbar-link">
                  Blog
                </Link>
                <Link to="/support" className="navbar-link">
                  Support
                </Link>
                <ProfileMenu />
              </>
            ) : (
              <>
                <Link to="/dashboard" className="navbar-link">
                  Dashboard
                </Link>
                <Link to="/pricing" className="navbar-link">
                  Pricing
                </Link>
                <Link to="/blog" className="navbar-link">
                  Blog
                </Link>
                <Link to="/support" className="navbar-link">
                  Support
                </Link>
                <Link to="/login" className="navbar-link">
                  Login
                </Link>
                <button
                  onClick={toggleTheme}
                  className="theme-toggle"
                  title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                >
                  {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                </button>
                <Link to="/register" className="btn btn-primary" style={{ padding: '8px 16px' }}>
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Hamburger Button (Mobile) */}
          <button
            className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Close Button */}
        <button className="mobile-menu-close" onClick={closeMobileMenu} aria-label="Close menu">
          <X size={24} />
        </button>

        {/* Home Link - Always visible */}
        <Link to="/" onClick={closeMobileMenu} className="mobile-menu-home">
          <Home size={20} />
          Home
        </Link>

        {user ? (
          <>
            <Link to="/dashboard" onClick={closeMobileMenu}>
              <LayoutDashboard size={20} />
              Dashboard
            </Link>
            <Link to="/pricing" onClick={closeMobileMenu}>
              <CreditCard size={20} />
              Upgrade Plan
            </Link>
            <Link to="/blog" onClick={closeMobileMenu}>
              <FileText size={20} />
              Blog
            </Link>
            <Link to="/support" onClick={closeMobileMenu}>
              <HelpCircle size={20} />
              Support
            </Link>
            <Link to="/profile" onClick={closeMobileMenu}>
              <User size={20} />
              Profile
            </Link>
            <button onClick={toggleTheme}>
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
            <button onClick={handleLogout} style={{ color: 'var(--danger)' }}>
              <LogOut size={20} />
              Logout
            </button>
          </>
        ) : (
          <>
            <Link to="/dashboard" onClick={closeMobileMenu}>
              <LayoutDashboard size={20} />
              Dashboard
            </Link>
            <Link to="/pricing" onClick={closeMobileMenu}>
              <CreditCard size={20} />
              Pricing
            </Link>
            <Link to="/blog" onClick={closeMobileMenu}>
              <FileText size={20} />
              Blog
            </Link>
            <Link to="/support" onClick={closeMobileMenu}>
              <HelpCircle size={20} />
              Support
            </Link>
            <Link to="/login" onClick={closeMobileMenu}>
              <LogIn size={20} />
              Login
            </Link>
            <button onClick={toggleTheme}>
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
            <Link
              to="/register"
              onClick={closeMobileMenu}
              style={{ background: 'var(--primary)', color: 'white' }}
            >
              <Sparkles size={20} />
              Sign Up Free
            </Link>
          </>
        )}
      </div>
    </>
  );
};

// Exit Intent Popup Component
const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('exitIntentShown');
    if (popupShown) return;

    const handleMouseLeave = e => {
      // Double-check: Prevent multiple triggers
      if (sessionStorage.getItem('exitIntentShown')) return;

      // Detect when mouse moves to top of viewport (likely going to tabs/address bar)
      if (e.clientY <= 0) {
        // 1. Sofort sessionStorage setzen
        sessionStorage.setItem('exitIntentShown', 'true');
        // 2. Event-Listener entfernen (verhindert weitere Aufrufe)
        document.removeEventListener('mouseleave', handleMouseLeave);
        // 3. Popup anzeigen
        setIsVisible(true);
      }
    };

    // Add event listener after a delay (don't show immediately)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // Wait 5 seconds before activating

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/capture-email', {
        email,
        source: 'exit_intent',
      });

      if (response.data.success) {
        setSubmitted(true);
        console.log('✅ Email captured:', email);
      }
    } catch (error) {
      console.error('Failed to capture email:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={() => setIsVisible(false)}
    >
      <div
        style={{
          maxWidth: '400px',
          width: '100%',
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          padding: '32px',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={() => setIsVisible(false)}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: 'var(--text-muted)',
          }}
        >
          <X size={18} />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--primary)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  border: '1px solid var(--border-color)',
                }}
              >
                EARLY ACCESS
              </span>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  lineHeight: '1.3',
                }}
              >
                Don't miss out on updates
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5',
                }}
              >
                Get notified about new features and exclusive offers.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: '14px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  outline: 'none',
                }}
              />

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Please wait...' : 'Get Early Access'}
              </button>
            </form>

            <p
              style={{
                textAlign: 'center',
                fontSize: '12px',
                color: 'var(--text-muted)',
                marginTop: '16px',
              }}
            >
              No spam. Unsubscribe anytime.
            </p>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                background: 'var(--bg-secondary)',
                borderRadius: '50%',
                marginBottom: '16px',
                border: '1px solid var(--border-color)',
              }}
            >
              <Check size={24} style={{ color: 'var(--primary)' }} />
            </div>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}
            >
              You're on the list!
            </h3>
            <p
              style={{
                color: 'var(--text-secondary)',
                marginBottom: '20px',
                fontSize: '14px',
              }}
            >
              We'll keep you updated on new features and exclusive offers.
            </p>
            <Link
              to="/pricing"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                background: 'var(--primary)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
              }}
              onClick={() => setIsVisible(false)}
            >
              View Plans
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ PRODUCT HUNT LAUNCH COMPONENTS ============

// Configuration - Set these on launch day!
const PRODUCT_HUNT_CONFIG = {
  isLaunched: false, // Set to true on launch day
  launchEndTime: null, // Set to new Date('2025-01-20T23:59:59-08:00') on launch day (24h after launch)
  productHuntUrl: 'https://www.producthunt.com/posts/reviewresponder', // Update with actual URL
};

// Product Hunt Badge Component
const ProductHuntBadge = ({ style = {} }) => {
  if (!PRODUCT_HUNT_CONFIG.isLaunched) return null;

  return (
    <a
      href={PRODUCT_HUNT_CONFIG.productHuntUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #DA552F 0%, #FF6154 100%)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        textDecoration: 'none',
        boxShadow: '0 4px 12px rgba(218, 85, 47, 0.3)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        ...style,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(218, 85, 47, 0.4)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(218, 85, 47, 0.3)';
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20 40C31.0457 40 40 31.0457 40 20C40 8.95431 31.0457 0 20 0C8.95431 0 0 8.95431 0 20C0 31.0457 8.95431 40 20 40Z"
          fill="white"
        />
        <path
          d="M22.5 20H17.5V12.5H22.5C24.575 12.5 26.25 14.175 26.25 16.25C26.25 18.325 24.575 20 22.5 20Z"
          fill="#DA552F"
        />
        <path d="M17.5 12.5H15V27.5H17.5V12.5Z" fill="#DA552F" />
        <path d="M22.5 22.5H17.5V27.5H22.5V22.5Z" fill="#DA552F" />
      </svg>
      Featured on Product Hunt
    </a>
  );
};

// Countdown Timer Component
const CountdownTimer = ({ endTime, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, expired: false });

  useEffect(() => {
    if (!endTime) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      const difference = end - now;

      if (difference <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, expired: true });
        if (onExpire) onExpire();
        return;
      }

      setTimeLeft({
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        expired: false,
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endTime, onExpire]);

  if (timeLeft.expired) return null;

  const TimeBlock = ({ value, label }) => (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '8px 12px',
          minWidth: '50px',
          fontSize: '24px',
          fontWeight: '700',
          fontFamily: 'monospace',
        }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.9 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <TimeBlock value={timeLeft.hours} label="HRS" />
      <span style={{ fontSize: '24px', fontWeight: '700' }}>:</span>
      <TimeBlock value={timeLeft.minutes} label="MIN" />
      <span style={{ fontSize: '24px', fontWeight: '700' }}>:</span>
      <TimeBlock value={timeLeft.seconds} label="SEC" />
    </div>
  );
};

// Product Hunt Launch Banner - Shows at top of page on launch day
const ProductHuntLaunchBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const navigate = useNavigate();

  // Check URL for Product Hunt launch mode
  const urlParams = new URLSearchParams(window.location.search);
  const isFromProductHunt =
    urlParams.get('ref') === 'producthunt' || PRODUCT_HUNT_CONFIG.isLaunched;

  if (!isFromProductHunt || !isVisible || isExpired) return null;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #DA552F 0%, #FF6154 100%)',
        color: 'white',
        padding: '12px 20px',
        position: 'relative',
        zIndex: 1000,
      }}
    >
      <div
        className="container"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '24px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PartyPopper size={24} />
          <span style={{ fontSize: '16px', fontWeight: '600' }}>
            Product Hunt Launch Special: <strong>60% OFF</strong> for the next 24 hours!
          </span>
        </div>

        {PRODUCT_HUNT_CONFIG.launchEndTime && (
          <CountdownTimer
            endTime={PRODUCT_HUNT_CONFIG.launchEndTime}
            onExpire={() => setIsExpired(true)}
          />
        )}

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <code
            style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 12px',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '16px',
              fontWeight: '700',
            }}
          >
            HUNTLAUNCH
          </code>
          <button
            onClick={() => navigate('/pricing?discount=HUNTLAUNCH')}
            style={{
              background: 'var(--bg-secondary)',
              color: '#DA552F',
              border: 'none',
              padding: '8px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Claim Offer <ArrowRight size={16} />
          </button>
        </div>

        <button
          onClick={() => setIsVisible(false)}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '4px',
            opacity: 0.7,
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

// ============ END PRODUCT HUNT COMPONENTS ============

// Feedback Popup Component (shown after 3 responses)
const FeedbackPopup = ({ isVisible, onMaybeLater, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please tell us what you think');
      return;
    }

    setLoading(true);
    try {
      await api.post('/feedback', {
        rating,
        comment: comment.trim() || null,
        displayName: displayName.trim() || null,
        businessName: businessName.trim() || null,
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      setTimeout(() => {
        if (onSubmit) onSubmit();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onMaybeLater}
    >
      <div
        className="card"
        style={{
          maxWidth: '480px',
          width: '100%',
          padding: '0',
          overflow: 'hidden',
          position: 'relative',
          animation: 'slideIn 0.3s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, var(--primary-600), var(--primary-700))',
            padding: '32px 24px',
            textAlign: 'center',
            color: 'white',
          }}
        >
          <MessageSquare size={40} style={{ marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
            {submitted ? 'Thank You!' : 'How do you like ReviewResponder?'}
          </h2>
          <p style={{ opacity: 0.9, fontSize: '14px' }}>
            {submitted ? 'Your feedback helps us improve!' : "We'd love to hear your thoughts!"}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--success)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Check size={32} color="white" />
              </div>
              <p style={{ color: 'var(--gray-600)' }}>Your feedback has been submitted!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Star Rating */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '12px' }}>
                  Rate your experience
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        transition: 'transform 0.1s',
                      }}
                    >
                      <Star
                        size={32}
                        fill={(hoverRating || rating) >= star ? '#f59e0b' : 'none'}
                        color={(hoverRating || rating) >= star ? '#f59e0b' : '#d1d5db'}
                        style={{ transition: 'all 0.1s' }}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--primary-600)',
                      marginTop: '8px',
                      fontWeight: '500',
                    }}
                  >
                    {rating === 5
                      ? 'Excellent!'
                      : rating === 4
                        ? 'Great!'
                        : rating === 3
                          ? 'Good'
                          : rating === 2
                            ? 'Fair'
                            : 'Poor'}
                  </p>
                )}
              </div>

              {/* Comment (required for now to collect feedback) */}
              <div className="form-group">
                <label className="form-label">Tell us more <span style={{ color: 'var(--primary-600)' }}>*</span></label>
                <textarea
                  className="form-input"
                  placeholder="What do you like? What could be better?"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  rows={3}
                  maxLength={500}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Display Name (optional) */}
              <div className="form-group">
                <label className="form-label">Your name (optional, for testimonial)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., John"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  maxLength={100}
                />
              </div>

              {/* Business Name (optional) */}
              <div className="form-group">
                <label className="form-label">Business Name (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Pizzeria Napoli, Munich"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  maxLength={100}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || rating === 0 || !comment.trim()}
                style={{ width: '100%', marginTop: '16px' }}
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </button>

              {/* Skip */}
              <button
                type="button"
                onClick={onMaybeLater}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--gray-500)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Maybe later
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Dogfooding Section - "Reviews About Us, Answered By Us"
const DogfoodingSection = ({ testimonials }) => {
  const { user } = useAuth();
  // Individual expand states for each section
  const [expandedSections, setExpandedSections] = useState({
    businessInfo: false,
    businessContext: false,
    responseStyle: false,
    generationSettings: false,
  });
  // Track which videos are expanded (by index)
  const [expandedVideos, setExpandedVideos] = useState(new Set());
  // Track which testimonials are showing English translations (by index)
  const [translatedIds, setTranslatedIds] = useState(new Set());
  // Track which translations are currently loading
  const [translatingIds, setTranslatingIds] = useState(new Set());
  // Local cache for translations (synced with DB after first request)
  const [translationCache, setTranslationCache] = useState({});

  // Toggle a specific section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Toggle video visibility for a specific testimonial
  const toggleVideo = (index) => {
    setExpandedVideos(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Toggle translation for a specific testimonial
  // Uses lazy translation: first click translates & saves to DB, subsequent clicks use cached
  const toggleTranslation = async (index, testimonialId, hasExistingTranslation) => {
    // If already showing translation, just toggle back to original
    if (translatedIds.has(index)) {
      setTranslatedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
      return;
    }

    // Check if we already have translation (from API or local cache)
    const hasCached = translationCache[index];
    if (!hasCached && !hasExistingTranslation && testimonialId) {
      // Need to fetch/generate translation
      setTranslatingIds(prev => new Set(prev).add(index));

      try {
        // Call lazy translation endpoint - translates & saves to DB if needed
        const response = await axios.post(`${API_URL}/testimonials/${testimonialId}/translate`);
        if (response.data.ok && response.data.comment_en) {
          // Cache locally
          setTranslationCache(prev => ({
            ...prev,
            [index]: response.data.comment_en
          }));
        }
      } catch (error) {
        console.error('Translation error:', error);
      }

      setTranslatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }

    // Show translation
    setTranslatedIds(prev => new Set(prev).add(index));
  };

  // ReviewResponder's COMPLETE settings - 100% transparent, exactly what goes into the prompt
  // V4.3.14: All settings that are passed to buildSovereignPrompt()
  const REVIEWRESPONDER_CONTEXT = {
    // === BUSINESS INFO ===
    businessName: 'ReviewResponder',
    businessType: 'SaaS',
    city: 'Gießen',

    // === BUSINESS CONTEXT === (goes into <business_context> in prompt)
    businessContext: `YOUR STORY:
I'm Berend, 23, psychology student at JLU Gießen. ReviewResponder is my side project combining what I learn in university with my passion for AI. From my psychology courses (e.g. Industrial & Organizational Psychology), I know how to respond to criticism in a way that protects your reputation - not defensive, not overly apologetic when it's not necessary. And built this into ReviewResponder.

WHAT CUSTOMERS LOVE:
- Your voice stays your voice - you respond in your own way
- Thoughtful responses that defuse unfair reviews
- YOU stay in control - AI gives you psychology-based structure, you add the finishing touch
- Saves hours every week on review responses, while staying authentic
- 20 free responses every month
- Intuitive User-interface/User-experience
- Quality of responses
- quick and easy customization
- Chrome extension works directly on e.g. Google Maps - without copy/paste

MORE DETAILS:
I personally read and respond to all emails and feedback at berend.jakob.mainz@gmail.com and support@tryreviewresponder.com - usually within 24 hours.`,

    // === RESPONSE STYLE === (goes into <response_style> in prompt)
    responseStyle: `<always>
- Use "I" (as Berend) or "we" (as the team)
- Be personal and precise
- consider the users perspective and what we can improve/learn from it (or double down on)
</always>

<never>
- Generic appreciation without specifics
- Padding short reviews with filler text
</never>

<style>
- No fluff. Focus on the specific points mentioned.
- If someone has (negative) feedback, take it seriously (so we can improve)
- Offer support at support@tryreviewresponder.com if needed
</style>`,

    // === GENERATION SETTINGS === (each goes into different parts of prompt)
    generationSettings: {
      emojis: 'Off',              // → <output> Emojis: ...
      brackets: 'On',             // → Bracket section enabled
      signature: 'Berend, ReviewResponder', // → Sign-off name
      responseLength: 'Auto', // → <output> Length: ... or auto-calculated
    },
  };

  // Reusable collapsible section component
  const CollapsibleSection = ({ id, title, icon, children, hint }) => (
    <div
      style={{
        border: '1px solid var(--gray-200)',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '12px',
      }}
    >
      <button
        onClick={() => toggleSection(id)}
        style={{
          width: '100%',
          padding: '14px 18px',
          background: expandedSections[id] ? 'var(--gray-100)' : 'var(--gray-50)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--gray-700)',
          transition: 'background 0.2s',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>{icon}</span>
          {title}
        </span>
        <ChevronDown
          size={18}
          style={{
            transform: expandedSections[id] ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            color: 'var(--gray-400)',
          }}
        />
      </button>

      {expandedSections[id] && (
        <div style={{ padding: '16px 18px', borderTop: '1px solid var(--gray-200)', background: 'var(--bg-secondary)' }}>
          {hint && (
            <div
              style={{
                background: 'var(--info-bg)',
                border: '1px solid var(--info-border)',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '14px',
                fontSize: '12px',
                color: 'var(--info-text)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Lightbulb size={14} style={{ flexShrink: 0 }} />
              <span>{hint}</span>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );

  return (
    <section className="container" style={{ marginBottom: '60px' }}>
      {/* Dogfooding Statement */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        fontSize: '1.4rem',
        fontStyle: 'italic',
        color: 'var(--gray-400)',
        lineHeight: '1.8'
      }}>
        <div>"I answer reviews about ReviewResponder</div>
        <div style={{ paddingLeft: '2rem' }}>
          <span style={{ color: 'var(--primary)', fontWeight: '500' }}>with ReviewResponder</span>."
        </div>
      </div>

      {/* Settings Sections - All collapsible under "My settings" */}
      <div style={{ maxWidth: '800px', margin: '0 auto 32px auto' }}>
        <CollapsibleSection
          id="mySettings"
          title="My settings"
          icon="⚙️"
          defaultOpen={false}
        >
        {/* 1. BUSINESS INFO */}
        <CollapsibleSection
          id="businessInfo"
          title="Business Info"
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--gray-400)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-800)' }}>{REVIEWRESPONDER_CONTEXT.businessName}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--gray-400)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-800)' }}>{REVIEWRESPONDER_CONTEXT.businessType}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--gray-400)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>City</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-800)' }}>{REVIEWRESPONDER_CONTEXT.city}</div>
            </div>
          </div>
        </CollapsibleSection>

        {/* 2. BUSINESS CONTEXT */}
        <CollapsibleSection
          id="businessContext"
          title="Business Context"
          hint={<>Looks like a lot? <strong>Yours = auto-created</strong> from a few keywords! <span style={{ opacity: 0.7 }}>(or write/edit yourself)</span></>}
        >
          <pre
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '11px',
              whiteSpace: 'pre-wrap',
              color: 'var(--gray-600)',
              margin: 0,
              lineHeight: '1.6',
              background: 'var(--gray-50)',
              padding: '12px',
              borderRadius: '8px',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            {REVIEWRESPONDER_CONTEXT.businessContext}
          </pre>
        </CollapsibleSection>

        {/* 3. RESPONSE STYLE */}
        <CollapsibleSection
          id="responseStyle"
          title="Response Style"
          hint={<>Looks like a lot? <strong>Yours = auto-created</strong> from a few keywords! <span style={{ opacity: 0.7 }}>(or write/edit yourself)</span></>}
        >
          <pre
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '11px',
              whiteSpace: 'pre-wrap',
              color: 'var(--gray-600)',
              margin: 0,
              lineHeight: '1.6',
              background: 'var(--gray-50)',
              padding: '12px',
              borderRadius: '8px',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            {REVIEWRESPONDER_CONTEXT.responseStyle}
          </pre>
        </CollapsibleSection>

        {/* 4. GENERATION SETTINGS */}
        <CollapsibleSection
          id="generationSettings"
          title="Settings"
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {Object.entries(REVIEWRESPONDER_CONTEXT.generationSettings).map(([key, value]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--gray-50)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                }}
              >
                <span style={{ fontSize: '11px', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}:
                </span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--gray-700)',
                  background: 'var(--bg-secondary)',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  border: '1px solid var(--gray-200)',
                }}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
        </CollapsibleSection>
      </div>

      {/* Testimonials with Founder Video Responses */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {testimonials.map((testimonial, index) => {
          const name = (testimonial.display_name || testimonial.user_name || '').toLowerCase();

          // Founder video responses config (with English translations)
          const founderResponses = {
            max: {
              video: '/videos/antwort-max.mp4',
              text: 'Hey Max - das freut mich wirklich! Die anfängliche Skepsis kann ich total verstehen - umso schöner, dass es deinen Pub-Alltag so erleichtert. Ich antworte persönlich auf jede Mail, weil mir Feedback wichtig ist. Falls noch Fragen kommen, einfach schreiben. Danke für die Empfehlung! - Berend, ReviewResponder',
              text_en: "Hey Max - that really makes me happy! I totally understand the initial skepticism - all the nicer that it makes your pub life easier now. I personally reply to every email because feedback matters to me. If any questions come up, just write. Thanks for the recommendation! - Berend, ReviewResponder"
            },
            andre: {
              video: '/videos/antwort-andre.mp4',
              text: "Thanks Andre! That's exactly why I built ReviewResponder - your time should go to growing your business. Quick responses that still sound like you - that's the sweet spot and it we nailed that for you. Hope it keeps saving you hours - the feedback means a lot. - Berend, ReviewResponder",
              text_en: null // Already in English
            },
            tiniwi: {
              video: '/videos/antwort-tim.mp4',
              text: "Thanks Tim, glad it's working well for you! That's the goal - effective without complexity when dealing with reviews. Your feedback means a lot. Feel free to reach out anytime at support@tryreviewresponder.com. - Berend, ReviewResponder",
              text_en: null // Already in English
            },
            mati: {
              video: '/videos/antwort-mati.mp4',
              text: "Gracias Mati! Me alegra saber que ReviewResponder está funcionando bien para tu pyme. Eso es exactamente lo que quería lograr - una herramienta que realmente cumpla con lo que necesitan los negocios pequeños. Tu recomendación significa mucho para mí. - Berend, ReviewResponder",
              text_en: "Thanks Mati! I'm glad to hear ReviewResponder is working well for your small business. That's exactly what I wanted to achieve - a tool that actually delivers what small businesses need. Your recommendation means a lot to me. - Berend, ReviewResponder"
            },
            penelope: {
              video: '/videos/antwort-penelope.mp4',
              text: "Penelope, you made my day! 🚀✨🙌 - Berend, ReviewResponder",
              text_en: null // Already in English
            },
            simone: {
              video: '/videos/antwort-simone.mp4',
              text: "Simone, questo è esattamente quello che speravo di sentire! Il fatto che ti abbia aiutato con diversi tipi di clientela e toni diversi significa che stiamo centrando l'obiettivo. È proprio quello che studio in psicologia - come adattare la comunicazione al contesto. Mi fa piacere sapere che ReviewResponder ti dà quella struttura di base per poi personalizzare come preferisci. Se hai suggerimenti su come migliorare ulteriormente l'adattamento ai diversi toni, scrivimi pure a support@tryreviewresponder.com. - Berend, ReviewResponder",
              text_en: "Simone, that's exactly what I was hoping to hear! The fact that it helped you with different types of customers and different tones means we're hitting the mark. That's actually what I study in psychology - how to adapt communication to context. I'm glad ReviewResponder gives you that basic structure to then customize as you like. If you have suggestions on how to further improve the tone adaptation, write me at support@tryreviewresponder.com. - Berend, ReviewResponder"
            },
            marcus: {
              video: '/videos/antwort-markus.mp4',
              text: "Thanks Marcus! Really glad the interface clicked for you - we put a lot of thought into keeping it clean and intuitive. I hear you on the pricing. The 20 free responses monthly help offset that. The core idea resonating with you is encouraging. Feel free to reach out at support@tryreviewresponder.com if you have specific suggestions. - Berend, ReviewResponder",
              text_en: null // Already in English
            },
            markus: {
              video: '/videos/antwort-markus.mp4',
              text: "Thanks Marcus! Really glad the interface clicked for you - we put a lot of thought into keeping it clean and intuitive. I hear you on the pricing. The 20 free responses monthly help offset that. The core idea resonating with you is encouraging. Feel free to reach out at support@tryreviewresponder.com if you have specific suggestions. - Berend, ReviewResponder",
              text_en: null // Already in English
            },
            justus: {
              video: '/videos/antwort-justus.mp4',
              text: "Genau das war mein Ziel - danke Justus! Die Balance zwischen Schnelligkeit und Authentizität war mir von Anfang an wichtig. Ich wollte ein Tool schaffen, das deine Stimme verstärkt, nicht ersetzt. Lass mich wissen, wenn ich noch etwas verbessern kann - Berend, ReviewResponder",
              text_en: "That was exactly my goal - thanks Justus! The balance between speed and authenticity was important to me from the start. I wanted to create a tool that amplifies your voice, not replaces it. Let me know if I can improve anything. - Berend, ReviewResponder"
            },
          };

          // Find matching founder response
          const founderKey = Object.keys(founderResponses).find(key => name.includes(key));
          const founderData = founderKey ? founderResponses[founderKey] : null;

          // Skip testimonials without founder response
          if (!founderData) return null;

          // Translation state for this testimonial
          const isTranslated = translatedIds.has(index);
          const isTranslating = translatingIds.has(index);
          const cachedTranslation = translationCache[index]; // string or undefined

          // Check if translation exists (from API response or local cache)
          const hasExistingTranslation = !!(testimonial.comment_en || cachedTranslation);
          const responseIsEnglish = founderData.text_en === null;

          // Get the text to display - prioritize: API translation > cached > original
          const displayReview = isTranslated
            ? (testimonial.comment_en || cachedTranslation || testimonial.comment)
            : testimonial.comment;
          const displayResponse = isTranslated && founderData.text_en ? founderData.text_en : founderData.text;

          return (
          <div key={index} style={{ marginBottom: '32px' }}>
            {/* Card wrapper for all 3 elements */}
            <div
              style={{
                background: 'var(--bg-secondary)',
                borderRadius: '20px',
                padding: '28px',
                border: '1px solid var(--border-color)',
              }}
            >
              {/* 1. The Review */}
              <div
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  border: '1px solid var(--border-color)',
                }}
              >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        size={16}
                        fill={star <= testimonial.rating ? '#f59e0b' : 'none'}
                        color={star <= testimonial.rating ? '#f59e0b' : '#d1d5db'}
                      />
                    ))}
                  </div>
                  {isTranslated && (testimonial.comment_en || cachedTranslation) && (
                    <span style={{ fontSize: '11px', color: 'var(--primary-600)', fontWeight: '500' }}>
                      Translated
                    </span>
                  )}
                </div>
                {/* Translate button next to stars */}
                <button
                  onClick={() => toggleTranslation(index, testimonial.id, hasExistingTranslation)}
                  disabled={isTranslating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    color: isTranslating ? 'var(--gray-400)' : (isTranslated ? 'var(--primary-600)' : 'var(--gray-500)'),
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isTranslating ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isTranslating ? 0.7 : 1,
                  }}
                >
                  {isTranslating ? (
                    <Loader2 size={12} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Globe size={12} />
                  )}
                  {isTranslating ? 'Translating...' : (isTranslated ? 'Original' : 'English')}
                </button>
              </div>
              <p
                style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: 'var(--gray-700)',
                  fontStyle: 'italic',
                  marginBottom: '12px',
                }}
              >
                "{displayReview}"
              </p>
              <div
                style={{
                  fontSize: '13px',
                  color: 'var(--gray-500)',
                  fontWeight: '500',
                }}
              >
                — {testimonial.display_name || testimonial.user_name || 'Verified User'}
              </div>
            </div>

            {/* 2. Video collapsible (only if video exists) */}
            {founderData.video && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={() => toggleVideo(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'none',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: 'var(--gray-600)',
                    transition: 'color 0.2s, border-color 0.2s',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--primary-600)';
                    e.currentTarget.style.borderColor = 'var(--primary-600)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--gray-600)';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  {expandedVideos.has(index) ? (
                    <>
                      <ChevronUp size={16} />
                      Hide video
                    </>
                  ) : (
                    <>
                      <Play size={14} />
                      See how I responded
                    </>
                  )}
                </button>
                <div
                  style={{
                    maxHeight: expandedVideos.has(index) ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                    marginTop: expandedVideos.has(index) ? '10px' : '0',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      aspectRatio: '16 / 7.2',
                      position: 'relative',
                      cursor: 'pointer',
                      background: '#000',
                    }}
                    onClick={(e) => {
                      const video = e.currentTarget.querySelector('video');
                      const playBtn = e.currentTarget.querySelector('.play-btn');
                      if (video.paused) {
                        video.play();
                        if (playBtn) playBtn.style.display = 'none';
                      } else {
                        video.pause();
                        if (playBtn) playBtn.style.display = 'flex';
                      }
                    }}
                  >
                    <video
                      muted
                      playsInline
                      preload="metadata"
                      onError={(e) => {
                        console.warn('Video failed to load:', founderData.video);
                        e.target.parentElement.style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        height: '132%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        marginTop: '-2%',
                      }}
                    >
                      <source src={founderData.video} type="video/mp4" />
                    </video>
                    {/* Play button overlay */}
                    <div
                      className="play-btn"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70px',
                        height: '70px',
                        background: 'rgba(255,255,255,0.9)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      }}
                    >
                      <div style={{
                        width: 0,
                        height: 0,
                        borderTop: '12px solid transparent',
                        borderBottom: '12px solid transparent',
                        borderLeft: '20px solid var(--primary-600)',
                        marginLeft: '4px',
                      }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3. The Response */}
            <div
              style={{
                background: 'var(--bg-primary)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid var(--border-color)',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--gray-600)',
                  marginBottom: '8px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  My Response:
                  {isTranslated && founderData.text_en && (
                    <span style={{ fontSize: '11px', color: 'var(--primary-600)', fontWeight: '500' }}>
                      Translated
                    </span>
                  )}
                </div>
                {/* Translate button next to My Response */}
                <button
                  onClick={() => toggleTranslation(index, testimonial.id, hasExistingTranslation)}
                  disabled={isTranslating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    color: isTranslating ? 'var(--gray-400)' : (isTranslated ? 'var(--primary-600)' : 'var(--gray-500)'),
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isTranslating ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isTranslating ? 0.7 : 1,
                  }}
                >
                  {isTranslating ? (
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Globe size={12} />
                  )}
                  {isTranslating ? 'Translating...' : (isTranslated ? 'Original' : 'English')}
                </button>
              </div>
              <p
                style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: 'var(--gray-700)',
                }}
              >
                {displayResponse}
              </p>
            </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* How-To Box */}
      <div
        style={{
          background: 'var(--hero-gradient)',
          border: '1px solid var(--primary-200, #C7D2FE)',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '32px',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'left',
        }}
      >
        <div style={{ fontWeight: '600', marginBottom: '16px', fontSize: '16px' }}>
          Want to try it with your own reviews?
        </div>
        <ol style={{ margin: '0 0 20px 20px', lineHeight: '1.8', color: 'var(--gray-700)' }}>
          <li>
            <strong>Sign up free</strong>
          </li>
          <li>
            Go to <strong>Settings → Business Context</strong>
          </li>
          <li>
            Enter a few keywords - we handle the rest
          </li>
        </ol>
        <div style={{ textAlign: 'center' }}>
          <Link to={user ? "/settings" : "/register?redirect=settings"} className="btn btn-primary" style={{ padding: '12px 24px' }}>
            Go to Settings →
          </Link>
        </div>
      </div>
    </section>
  );
};

// Demo Video Page - For recording Smart Bracket demo video
// Minimal: just review and brackets, inline flow
const DemoVideoPage = () => {
  const DEMO_REVIEW = {
    author: "Lisa M.",
    rating: 3,
    text: "Food was amazing, best pasta I've had in months. But we had a reservation and still waited 30 minutes for our table."
  };

  // Real response from system with brackets
  const DEMO_RESPONSE = `[Reaction:So glad you loved the pasta!|That pasta feedback makes my day!] We put a lot of care into those recipes. [Apology:Sorry about the wait though|That wait wasn't right] – even with reservations, [Explanation:we sometimes fall behind during rush|busy nights can throw off our timing]. [Promise:We're working on better table management|I'll talk to the host team about this]. [Invitation:Hope you'll give us another shot!|Come back and let us do better!] 🍝`;

  const [parts, setParts] = useState(() => parseBrackets(DEMO_RESPONSE));

  const handleBracketUpdate = (bracketId, value) => {
    setParts(prev => prev.map(part =>
      part.id === bracketId ? { ...part, value } : part
    ));
  };

  return (
    <>
      {/* Hide navbar for this page */}
      <style>{`
        nav, header, .navbar { display: none !important; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-tertiary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px',
          maxWidth: '950px',
          width: '100%',
          alignItems: 'stretch'
        }}>
          {/* Review Card */}
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '28px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              marginBottom: '18px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '18px',
                flexShrink: 0
              }}>
                {DEMO_REVIEW.author.charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '16px' }}>
                  {DEMO_REVIEW.author}
                </div>
                <div>
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} style={{
                      color: star <= DEMO_REVIEW.rating ? '#fbbf24' : '#e5e7eb',
                      fontSize: '16px'
                    }}>★</span>
                  ))}
                </div>
              </div>
            </div>
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              fontSize: '16px',
              margin: 0
            }}>
              "{DEMO_REVIEW.text}"
            </p>
          </div>

          {/* Response Card - inline brackets */}
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '28px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontSize: '15px',
            lineHeight: '1.8',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{ display: 'inline' }}>
              {parts.map((part, index) => {
                if (part.type === 'text') {
                  return <span key={index}>{part.content}</span>;
                }
                return (
                  <InteractiveBracket
                    key={part.id}
                    bracket={part}
                    onUpdate={handleBracketUpdate}
                    onActivate={() => {}}
                    lang="en"
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Landing Page
const LandingPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [referralBanner, setReferralBanner] = useState(null);
  const [affiliateBanner, setAffiliateBanner] = useState(null);
  // Activation Mode - shown after registration for new users
  const [activationMode, setActivationMode] = useState(false);
  const [showPersonalizePrompt, setShowPersonalizePrompt] = useState(false);

  // Try Before Signup - Hero Demo State
  const [tryReviewText, setTryReviewText] = useState('');
  const [tryResponse, setTryResponse] = useState('');
  const [tryLoading, setTryLoading] = useState(false);
  const [tryTone, setTryTone] = useState('professional');
  const [tryError, setTryError] = useState('');

  // Hero Badge → Dashboard click state
  const [showHeroBadgeModal, setShowHeroBadgeModal] = useState(false);
  const [heroBadgeEmail, setHeroBadgeEmail] = useState('');
  const [heroBadgeSubmitting, setHeroBadgeSubmitting] = useState(false);

  // Handle Hero Badge click → Dashboard (if logged in) or Generator (if new)
  const handleHeroBadgeClick = async () => {
    // Case 1: User is logged in → direct redirect to Dashboard
    if (user) {
      navigate('/dashboard');
      return;
    }

    // Case 2: Email is known (from localStorage) → auto-create/login
    const savedEmail = localStorage.getItem('captured_email');
    if (savedEmail) {
      try {
        const res = await axios.post(`${API_URL}/auth/auto-create-demo-user`, {
          email: savedEmail,
          source: 'hero_badge_click',
          landing_page: location.pathname,
        });
        if (res.data.token && res.data.user) {
          localStorage.setItem('token', res.data.token);
          // Global redirect: ALL funnels → Generator for activation
          window.location.href = '/generator';
          return;
        }
      } catch (err) {
        // Fallback: show email modal
        console.error('Auto-login failed:', err);
      }
    }

    // Case 3: No user, no email → show email modal
    setShowHeroBadgeModal(true);
  };

  // Handle Hero Badge email submit
  const handleHeroBadgeEmailSubmit = async (e) => {
    e.preventDefault();
    if (!heroBadgeEmail.trim() || !heroBadgeEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setHeroBadgeSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/auto-create-demo-user`, {
        email: heroBadgeEmail.trim(),
        source: 'hero_badge_click',
        landing_page: location.pathname,
      });

      if (res.data.token && res.data.user) {
        // Store token and email
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('captured_email', heroBadgeEmail.trim().toLowerCase());

        // Show success and redirect to Generator
        toast.success(res.data.isNewUser
          ? '🎉 Account created! Taking you to Generator...'
          : '✓ Welcome back!'
        );

        setShowHeroBadgeModal(false);
        // Global redirect: ALL funnels → Generator for activation
        window.location.href = '/generator';
      }
    } catch (err) {
      console.error('Hero badge account creation error:', err);
      toast.error('Failed to create account. Please try again.');
    } finally {
      setHeroBadgeSubmitting(false);
    }
  };

  // Handle Google Sign In from Hero Badge Modal
  const handleHeroBadgeGoogleSignIn = async credentialResponse => {
    setHeroBadgeSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });

      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setShowHeroBadgeModal(false);
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Welcome back!'
        );
        window.location.href = '/generator';
      }
    } catch (err) {
      console.error('Google Sign In error:', err);
      toast.error('Google Sign In failed. Please try again.');
    } finally {
      setHeroBadgeSubmitting(false);
    }
  };

  // Try Before Signup - Generate Response
  const handleTryGenerate = async () => {
    if (!tryReviewText.trim() || tryReviewText.trim().length < 10) {
      setTryError('Please enter a review (at least 10 characters)');
      return;
    }
    setTryLoading(true);
    setTryError('');
    setTryResponse('');
    try {
      // If user is logged in, use authenticated endpoint (uses plan limits)
      // Otherwise use public try endpoint (3/day limit)
      const authToken = localStorage.getItem('token');
      if (user && authToken) {
        const res = await api.post('/generate', {
          reviewText: tryReviewText,
          tone: tryTone,
        });
        setTryResponse(res.data.response);
        // Increment generation count for tier-based loading messages
        const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
        const newCount = genCount + 1;
        localStorage.setItem('generation_count', String(newCount));
        checkMilestoneAchievement(newCount);
      } else {
        const devHeaders = localStorage.getItem('dev_bypass') === 'true'
          ? { 'x-dev-bypass': 'berend-dev-2026' } : {};
        const res = await axios.post(`${API_URL}/public/try`, {
          reviewText: tryReviewText,
          tone: tryTone,
        }, { headers: devHeaders });
        setTryResponse(res.data.response);
        // Increment generation count for tier-based loading messages
        const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
        const newCount = genCount + 1;
        localStorage.setItem('generation_count', String(newCount));
        checkMilestoneAchievement(newCount);
      }
    } catch (err) {
      console.error('Try generate error:', err.response?.status, err.response?.data, err.message);
      if (err.response?.status === 429) {
        setTryError(
          err.response?.data?.message || 'Daily limit reached. Sign up for 20 free/month!'
        );
      } else {
        setTryError(
          err.response?.data?.error || err.response?.data?.message || `Error: ${err.message}`
        );
      }
    } finally {
      setTryLoading(false);
    }
  };

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await api.get('/testimonials');
        setTestimonials(res.data.testimonials || []);
      } catch (err) {
        // Silently fail - testimonials are optional
      }
    };
    fetchTestimonials();
  }, []);

  // Check for referral code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      // Validate referral code
      api
        .get(`/referrals/validate/${refCode}`)
        .then(res => {
          if (res.data.valid) {
            setReferralBanner({
              referrerName: res.data.referrerName,
              bonus: res.data.bonus,
            });
            // Store referral code for registration
            localStorage.setItem('referralCode', refCode.toUpperCase());
          }
        })
        .catch(() => {
          // Invalid code, ignore
        });
    }
  }, [location.search]);

  // Check for affiliate code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const affCode = params.get('aff');
    if (affCode) {
      // Validate affiliate code
      api
        .get(`/affiliate/validate/${affCode}`)
        .then(res => {
          if (res.data.valid) {
            setAffiliateBanner({
              affiliateName: res.data.affiliateName,
            });
            // Store affiliate code for registration
            localStorage.setItem('affiliateCode', affCode.toUpperCase());
          }
        })
        .catch(() => {
          // Invalid code, ignore
        });
    }
  }, [location.search]);

  // Check for activation mode (new user redirect after registration)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isActivation = params.get('activation') === 'true';
    if (isActivation && user) {
      setActivationMode(true);
      // Clean up URL without reload
      window.history.replaceState({}, '', '/');
    }
  }, [location.search, user]);

  // Show personalize prompt after successful generation in activation mode
  useEffect(() => {
    if (activationMode && tryResponse && user) {
      // Small delay so user can see the response first
      const timer = setTimeout(() => {
        setShowPersonalizePrompt(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [activationMode, tryResponse, user]);

  return (
    <div>
      {/* ExitIntentPopup entfernt - Value first, kein Marketing-Interrupt */}
      <ProductHuntLaunchBanner />

      {/* Referral Banner */}
      {referralBanner && (
        <div
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white',
            padding: '12px 20px',
            textAlign: 'center',
            fontSize: '15px',
          }}
        >
          <span style={{ fontWeight: '600' }}>{referralBanner.referrerName}</span> invited you! Sign
          up now and get <span style={{ fontWeight: '600' }}>{referralBanner.bonus}</span>
        </div>
      )}

      {/* Affiliate Banner */}
      {affiliateBanner && !referralBanner && (
        <div
          style={{
            background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            color: 'white',
            padding: '12px 20px',
            textAlign: 'center',
            fontSize: '15px',
          }}
        >
          Recommended by <span style={{ fontWeight: '600' }}>{affiliateBanner.affiliateName}</span>!
          Sign up now to get started.
        </div>
      )}

      {/* Activation Mode Welcome Banner */}
      {activationMode && user && (
        <div
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: 'white',
            padding: '16px 20px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
            Welcome{user.businessName ? `, ${user.businessName}` : ''}! 🎉
          </div>
          <div style={{ fontSize: '14px', opacity: 0.95 }}>
            Try generating a response below to see how it works for your business
          </div>
        </div>
      )}

      {/* Personalize Prompt Modal */}
      {showPersonalizePrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowPersonalizePrompt(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <Sparkles size={28} color="white" />
            </div>
            <h3
              style={{
                fontSize: '22px',
                fontWeight: '600',
                marginBottom: '12px',
                color: 'var(--text-primary)',
              }}
            >
              Nice! Want responses that match your brand?
            </h3>
            <p
              style={{
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                fontSize: '15px',
                lineHeight: '1.5',
              }}
            >
              Personalize your ReviewResponder. Future responses will automatically match your
              tone and style.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  // Copy response to clipboard so user has it
                  if (tryResponse) {
                    navigator.clipboard.writeText(tryResponse);
                    toast.success('Response copied to clipboard!', { icon: '📋' });
                  }
                  setShowPersonalizePrompt(false);
                  navigate('/settings');
                }}
                className="btn btn-primary"
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                }}
              >
                Personalize Now
              </button>
              <button
                onClick={() => setShowPersonalizePrompt(false)}
                className="btn"
                style={{
                  padding: '12px 24px',
                  fontSize: '15px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
      <section
        className="hero"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-color)',
          paddingTop: '8px',
        }}
      >
        <div
          className="container"
          style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', padding: '12px 20px 4px' }}
        >
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={handleHeroBadgeClick}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--primary-light)';
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={user ? 'Go to Dashboard' : 'Get started free'}
            >
              <Shield size={12} style={{ color: 'var(--primary)' }} />
              Review Management Tool
            </button>
          </div>
          <div style={{ marginBottom: '12px', transform: 'scale(0.85)', opacity: 0.9 }}>
            <ProductHuntBadge />
          </div>

          <h1
            className="hero-title"
            style={{
              fontSize: '2.5rem',
              lineHeight: '1.15',
              letterSpacing: '-0.025em',
              marginBottom: '16px',
              color: 'var(--text-primary)',
              fontWeight: '700',
            }}
          >
            Never stress about <span style={{ color: 'var(--primary)' }}>negative reviews</span>{' '}
            again
          </h1>

          {/* Stacked Layout: Video Hero Banner → Widget */}
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Smart Brackets Demo Video - Nicer contained sizing */}
            <div style={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              marginBottom: '16px',
            }}>
              <video
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  display: 'block',
                }}
              >
                <source src="/videos/hero-demo.mp4" type="video/mp4" />
              </video>
            </div>

            {/* Instant Demo Widget - Centered */}
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              <InstantDemoWidget />
            </div>
          </div>

          {/* Feature Highlights - integrated in hero */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
              padding: '8px 0',
              marginTop: '8px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              <Globe size={14} style={{ color: 'var(--text-muted)' }} />
              <span>Auto-Detect Language</span>
            </div>
            <div style={{ width: '1px', height: '12px', background: 'var(--border-color)' }} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              <Chrome size={14} style={{ color: 'var(--text-muted)' }} />
              <span>Chrome Extension</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Founder Section */}
      <section style={{ padding: '60px 0', background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '700px', textAlign: 'center' }}>
          <Link to="/about-berend" style={{ display: 'inline-block', marginBottom: 16 }}>
            <img
              src="/berend.jpg"
              alt="Berend - Click to read my story"
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: 'center 30%',
                border: '3px solid var(--primary-light, #e8f4f8)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.3)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </Link>
          <p style={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
            "Most AI tools write review responses FOR you. I built one that writes them WITH you."
          </p>
          <p
            style={{
              color: 'var(--text-secondary)',
              marginTop: 8,
              fontSize: '0.9rem',
            }}
          >
            — Berend
          </p>
          <p
            style={{
              color: 'var(--text-muted, #888)',
              marginTop: 4,
              fontSize: '0.8rem',
              fontStyle: 'italic',
            }}
          >
            Built in Germany. Works worldwide.
          </p>
          <p
            style={{
              color: 'var(--text-secondary)',
              marginTop: 16,
              fontSize: '0.85rem',
            }}
          >
            Fragen? Gerne auch ein kurzer Zoom-Call!
            <br />
            <a
              href="mailto:berend.jakob.mainz@gmail.com"
              style={{ color: 'var(--primary)', textDecoration: 'underline' }}
            >
              → berend.jakob.mainz@gmail.com
            </a>
          </p>
          <Link
            to="/about-berend"
            style={{
              display: 'inline-block',
              marginTop: 16,
              color: 'var(--primary)',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '500',
            }}
          >
            Read my story →
          </Link>
        </div>
      </section>

      {/* Reviews About Us, Answered By Us - Dogfooding Section */}
      {testimonials.length > 0 && (
        <DogfoodingSection testimonials={testimonials} />
      )}

      {/* AI Comparison Section - Why We're Different */}
      <section
        style={{
          padding: '64px 20px',
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-color)',
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2
            style={{
              textAlign: 'center',
              marginBottom: '12px',
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'var(--text-primary)',
            }}
          >
            Still Using Generic AI Tools?
          </h2>

          {/* The Review Being Responded To */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '24px',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
            }}
          >
            <span
              style={{
                background: 'var(--bg-secondary)',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                display: 'inline-block',
                fontStyle: 'italic',
              }}
            >
              ⭐⭐⭐ "The risotto was delicious but arrived lukewarm. Waiter was friendly and gave
              us free dessert."
            </span>
          </div>

          {/* Side-by-Side Response Comparison */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '24px',
            }}
          >
            {/* Competitor Response */}
            <div
              style={{
                padding: '24px',
                background: 'var(--bg-tertiary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <X size={20} color="#ef4444" />
                <span style={{ fontWeight: '600', color: 'var(--text-muted)' }}>
                  ChatGPT
                </span>
                <span
                  style={{
                    fontSize: '0.65rem',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '600',
                  }}
                >
                  REAL OUTPUT
                </span>
              </div>
              <div
                style={{
                  fontSize: '0.7rem',
                  color: 'var(--text-muted)',
                  marginBottom: '12px',
                  padding: '6px 8px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '4px',
                }}
              >
                GPT-4o-mini • Generic prompt, no context
              </div>
              <p
                style={{
                  fontStyle: 'italic',
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  marginBottom: '12px',
                }}
              >
                "Dear [Customer's Name], Thank you for sharing your feedback. We're thrilled you
                enjoyed the risotto. We sincerely apologize it arrived lukewarm. Your feedback is
                invaluable to us. We hope you'll give us another chance. Best regards, [Your Name]"
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                }}
              >
                <span>46 words</span>
                <span>•</span>
                <span>[Placeholders]</span>
                <span>•</span>
                <span>"Thrilled"</span>
              </div>
            </div>

            {/* ReviewResponder Response */}
            <div
              style={{
                padding: '24px',
                background: 'rgba(79, 70, 229, 0.05)',
                borderRadius: '12px',
                border: '2px solid var(--primary)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <Check size={20} color="var(--primary)" />
                <span style={{ fontWeight: '600', color: 'var(--primary)' }}>ReviewResponder</span>
                <span
                  style={{
                    fontSize: '0.65rem',
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#059669',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '600',
                  }}
                >
                  REAL OUTPUT
                </span>
              </div>
              <div
                style={{
                  fontSize: '0.7rem',
                  color: 'var(--text-muted)',
                  marginBottom: '12px',
                  padding: '6px 8px',
                  background: 'rgba(79, 70, 229, 0.05)',
                  borderRadius: '4px',
                }}
              >
                Customized: Bella Italia • Warm tone • Confident
              </div>
              <p
                style={{
                  fontStyle: 'italic',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  lineHeight: '1.6',
                  marginBottom: '12px',
                }}
              >
                "That's on us - hot risotto makes all the difference. Glad Marco took care of you
                with dessert, and we'll make sure your next dish comes out piping hot."
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  fontSize: '0.75rem',
                  color: 'var(--primary)',
                  fontWeight: '500',
                }}
              >
                <span>29 words</span>
                <span>•</span>
                <span>Confident</span>
                <span>•</span>
                <span>Personal</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div style={{ textAlign: 'center' }}>
            <Link
              to="/register?redirect=settings"
              className="btn btn-primary"
              style={{
                padding: '12px 28px',
                fontSize: '0.95rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Try It Free <ArrowRight size={16} />
            </Link>
            <p
              style={{
                marginTop: '12px',
                fontSize: '0.8rem',
                color: 'var(--text-muted)',
              }}
            >
              20 free responses/month • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* Chrome Extension Section - Executive Assistant Style */}
      <section id="demo" className="container" style={{ marginBottom: '48px', marginTop: '48px' }}>
        <div
          className="card"
          style={{
            padding: '32px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '48px',
              alignItems: 'start',
            }}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <Chrome size={20} color="var(--primary)" strokeWidth={2.5} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.05em',
                    color: 'var(--primary)',
                    textTransform: 'uppercase',
                    background: 'rgba(79, 70, 229, 0.08)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                  }}
                >
                  Chrome Extension
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#f59e0b',
                    textTransform: 'uppercase',
                    background: 'rgba(245, 158, 11, 0.12)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    letterSpacing: '0.03em',
                  }}
                >
                  Beta
                </span>
              </div>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  marginBottom: '24px',
                  letterSpacing: '-0.01em',
                }}
              >
                Respond directly on the review page
              </h2>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginBottom: '24px',
                }}
              >
                {[
                  'Settings synced with dashboard',
                  'No manual copy-pasting required',
                ].map((step, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '13px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <CheckCircle2 size={14} color="var(--primary)" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>

              {/* Trust Badge - No Signup */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#059669',
                  background: 'rgba(5, 150, 105, 0.08)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  width: 'fit-content',
                }}
              >
                <Check size={14} />
                <span>
                  <strong>No signup required</strong> - download and try instantly
                </span>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <a
                  href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{
                    padding: '10px 18px',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Download size={14} /> Add to Chrome - Free
                </a>
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <div
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    width: '100%',
                    display: 'block',
                    objectFit: 'cover',
                    background: 'var(--bg-tertiary)',
                  }}
                >
                  <source src="/extension-demo.mp4" type="video/mp4" />
                </video>
              </div>
              <p
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                  marginTop: '12px',
                  fontStyle: 'italic',
                }}
              >
                Extension workflow demo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve - SEO Internal Links Section */}
      <section
        style={{
          padding: '48px 0',
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-color)',
        }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              textAlign: 'center',
              marginBottom: '32px',
              letterSpacing: '-0.02em',
            }}
          >
            Industries We Serve
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              maxWidth: '900px',
              margin: '0 auto',
            }}
          >
            {FEATURED_INDUSTRIES.map((industry) => (
              <Link
                key={industry.path}
                to={industry.path}
                style={{
                  padding: '12px 16px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {industry.icon && <industry.icon size={16} />}
                {industry.name}
              </Link>
            ))}
          </div>
          {/* Platforms Section */}
          <p style={{
            color: 'var(--text-tertiary)',
            fontSize: '12px',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginTop: '32px',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            Platforms
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            {FEATURED_PLATFORMS.map((platform) => (
              <Link
                key={platform.path}
                to={platform.path}
                style={{ color: 'var(--primary)', fontSize: '13px', textDecoration: 'none' }}
              >
                {platform.name}
              </Link>
            ))}
          </div>

          {/* View All Industries CTA */}
          <div style={{ marginTop: '32px', textAlign: 'center' }}>
            <Link
              to="/industries"
              style={{
                color: 'var(--primary)',
                fontWeight: '500',
                fontSize: '14px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              View All Industries & Platforms →
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Badge Email Modal */}
      {showHeroBadgeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => setShowHeroBadgeModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                color: 'var(--text-primary)',
              }}
            >
              Sign in to continue
            </h3>
            {/* Google Sign In - PRIMARY */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleHeroBadgeGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>
            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '16px 0',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
            <form onSubmit={handleHeroBadgeEmailSubmit}>
              <input
                type="email"
                value={heroBadgeEmail}
                onChange={e => setHeroBadgeEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={heroBadgeSubmitting}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  opacity: heroBadgeSubmitting ? 0.7 : 1,
                }}
              >
                {heroBadgeSubmitting ? 'Creating account...' : 'Continue with Email'}
              </button>
            </form>
            <button
              onClick={() => setShowHeroBadgeModal(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                marginTop: '16px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// Privacy Policy Page
const PrivacyPage = () => (
  <>
    <div className="container" style={{ maxWidth: '800px', padding: '60px 20px' }}>
      <Link
        to="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--gray-600)',
          textDecoration: 'none',
          fontSize: '14px',
          marginBottom: '24px',
        }}
      >
        <ArrowLeft size={16} />
        Back to Home
      </Link>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>Privacy Policy / Datenschutzerklaerung</h1>
      <p style={{ color: 'var(--gray-500)', marginBottom: '32px' }}>
        Last updated: January 24, 2026
      </p>

      <div style={{ lineHeight: '1.8', color: 'var(--gray-700)' }}>

        {/* 1. Verantwortlicher */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          1. Data Controller / Verantwortlicher
        </h2>
        <p>The controller responsible for data processing on this website is:</p>
        <p style={{ marginTop: '12px', marginBottom: '12px' }}>
          <strong>Berend Jakob Mainz</strong><br />
          Ringstrasse 10a<br />
          35428 Langgoens, Germany<br />
          Email: <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>berend.jakob.mainz@gmail.com</a>
        </p>

        {/* 2. Data We Collect */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          2. Data We Collect
        </h2>
        <p>When you use ReviewResponder, we collect:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>
            <strong>Account Information:</strong> Email address, business context, and password (encrypted)
          </li>
          <li>
            <strong>Usage Data:</strong> Review texts you submit and generated responses (for your response history)
          </li>
          <li>
            <strong>Payment Information:</strong> Processed securely by Stripe - we never store your card details
          </li>
          <li>
            <strong>Technical Data:</strong> IP address, browser type, access times (server logs)
          </li>
        </ul>

        {/* 3. Legal Basis - DSGVO Art. 6 */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          3. Legal Basis for Processing (Art. 6 GDPR)
        </h2>
        <p>We process your data based on the following legal grounds:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>
            <strong>Contract Performance (Art. 6(1)(b) GDPR):</strong> Processing necessary to provide our AI response generation service, manage your account, and process payments.
          </li>
          <li>
            <strong>Legitimate Interest (Art. 6(1)(f) GDPR):</strong> For service improvement, security, and fraud prevention.
          </li>
          <li>
            <strong>Consent (Art. 6(1)(a) GDPR):</strong> For marketing emails (you can withdraw consent anytime).
          </li>
          <li>
            <strong>Legal Obligation (Art. 6(1)(c) GDPR):</strong> For tax and accounting requirements.
          </li>
        </ul>

        {/* 4. How We Use Your Data */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          4. How We Use Your Data
        </h2>
        <p>We use your information to:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>Provide and improve our AI response generation service</li>
          <li>Process payments and manage your subscription</li>
          <li>Send important service updates and transactional emails</li>
          <li>Respond to support requests</li>
          <li>Prevent fraud and ensure security</li>
        </ul>

        {/* 5. Third-Party Services & International Transfers */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          5. Third-Party Services & International Data Transfers
        </h2>
        <p>We use the following services, some of which are located in the United States:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>
            <strong>OpenAI, Anthropic (Claude), Google (Gemini) - USA:</strong> To generate review responses. Your review text is sent to their APIs. These providers maintain appropriate safeguards under EU-US Data Privacy Framework.
          </li>
          <li>
            <strong>Stripe - USA:</strong> For secure payment processing. Stripe is certified under the EU-US Data Privacy Framework.
          </li>
          <li>
            <strong>Render.com - USA:</strong> Our hosting provider. Data is stored on servers in the EU/US with appropriate security measures.
          </li>
          <li>
            <strong>Resend, Brevo:</strong> For transactional and marketing emails.
          </li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          <strong>International Transfers:</strong> When we transfer data to the USA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) and/or EU-US Data Privacy Framework certification.
        </p>

        {/* 6. Data Storage & Security */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          6. Data Storage & Security
        </h2>
        <p>Your data is stored securely. We use:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>HTTPS/TLS encryption for all data transfers</li>
          <li>Encrypted password storage (bcrypt hashing)</li>
          <li>Secure PostgreSQL database with access controls</li>
          <li>Regular security updates and monitoring</li>
        </ul>

        {/* 7. Your Rights (GDPR) */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          7. Your Rights (GDPR)
        </h2>
        <p>Under the GDPR, you have the following rights:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li><strong>Right of Access (Art. 15):</strong> Request a copy of your personal data</li>
          <li><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate data</li>
          <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your data ("right to be forgotten")</li>
          <li><strong>Right to Restriction (Art. 18):</strong> Restrict processing of your data</li>
          <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a portable format</li>
          <li><strong>Right to Object (Art. 21(1)):</strong> Object to processing based on legitimate interest</li>
          <li><strong>Right to Object to Direct Marketing (Art. 21(2)):</strong> You have an <em>unconditional</em> right to object to processing of your data for direct marketing purposes. If you object, we will immediately stop using your data for marketing.</li>
          <li><strong>Right to Withdraw Consent (Art. 7):</strong> Withdraw consent at any time (for consent-based processing)</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          To exercise these rights, email us at{' '}
          <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>
            berend.jakob.mainz@gmail.com
          </a>
        </p>

        {/* 8. Right to Lodge a Complaint */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          8. Right to Lodge a Complaint
        </h2>
        <p>
          If you believe your data protection rights have been violated, you have the right to lodge a complaint with a supervisory authority. The competent authority for us is:
        </p>
        <p style={{ marginTop: '12px' }}>
          <strong>Der Hessische Beauftragte fuer Datenschutz und Informationsfreiheit</strong><br />
          Postfach 3163<br />
          65021 Wiesbaden, Germany<br />
          Website: <a href="https://datenschutz.hessen.de" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>datenschutz.hessen.de</a>
        </p>

        {/* 9. Cookies */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          9. Cookies
        </h2>
        <p>
          We use only technically necessary cookies required for the functioning of our website (e.g., session cookies for login). We do not use tracking or advertising cookies. These essential cookies are based on our legitimate interest (Art. 6(1)(f) GDPR) in providing a functional website.
        </p>

        {/* 10. Chrome Extension */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          10. Chrome Extension
        </h2>
        <p>Our Chrome Extension requires specific permissions to function:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li><strong>activeTab:</strong> To detect reviews on Google Maps pages you visit</li>
          <li><strong>storage:</strong> To save your login session locally</li>
          <li><strong>clipboardWrite:</strong> To copy generated responses to your clipboard</li>
          <li><strong>host_permissions:</strong> To communicate with Google Maps and our API</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          The extension does NOT collect browsing history, personal data from other sites, or any data when you're not actively using it.
        </p>

        {/* 11. Data Retention */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          11. Data Retention / Speicherdauer
        </h2>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li><strong>Account Data:</strong> Retained as long as your account is active. Upon account deletion, removed within 30 days.</li>
          <li><strong>Server Logs (IP addresses, access times):</strong> Automatically deleted after 7 days.</li>
          <li><strong>Invoices and payment records:</strong> 10 years (German tax law requirement).</li>
          <li><strong>Response history:</strong> Deleted with your account, or upon your request.</li>
          <li><strong>Email communication:</strong> Retained for 3 years for support purposes, unless you request earlier deletion.</li>
        </ul>

        {/* 12. Automated Decision-Making */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          12. Automated Decision-Making
        </h2>
        <p>
          Our AI generates response suggestions based on the review text you provide. This is not automated decision-making with legal effects as defined in Art. 22 GDPR - the AI provides suggestions only, and you decide whether to use, modify, or discard them.
        </p>

        {/* 13. Changes to This Policy */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          13. Changes to This Policy
        </h2>
        <p>
          We may update this privacy policy from time to time. We will notify you of significant changes via email or through our website.
        </p>

        {/* 14. Contact */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          14. Contact
        </h2>
        <p>
          Questions about this policy? Contact us at{' '}
          <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>
            berend.jakob.mainz@gmail.com
          </a>
        </p>
      </div>
    </div>
    <Footer />
  </>
);

// Impressum / Legal Notice Page
const ImpressumPage = () => {
  const [lang, setLang] = useState('de'); // Default German for legal page

  const content = {
    de: {
      backText: 'Zurueck zur Startseite',
      title: 'Impressum',
      subtitle: 'Angaben gemaess § 5 TMG',

      // Contact Section
      contactTitle: 'Kontakt',
      name: 'Berend Jakob Mainz',
      address: 'Ringstrasse 10a, 35428 Langgoens',
      email: 'berend.jakob.mainz@gmail.com',
      contactForm: 'Kontaktformular',
      contactFormUrl: '/support',

      // TODO: Bei Gewerbeanmeldung hier ergaenzen:
      // vatId: 'USt-IdNr.: DE...',
      // companyName: 'Firmenname',
      // registerCourt: 'Registergericht: Amtsgericht ..., HRB ...',

      // Responsibility
      responsibleTitle: 'Verantwortlich fuer den Inhalt nach § 18 Abs. 2 MStV',

      // Dispute Resolution
      disputeTitle: 'Streitschlichtung',
      disputeText: 'Die fruehere EU Online-Streitbeilegungsplattform (ODR) wurde am 20. Juli 2025 gemaess EU-Verordnung 2024/3228 eingestellt. Die neue EU-Plattform fuer Verbraucherentschaedigung finden Sie unter:',
      disputeLink: 'https://consumer-redress.ec.europa.eu/',
      disputeNote: 'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',

      // Liability
      liabilityTitle: 'Haftung fuer Inhalte',
      liabilityText: 'Als Diensteanbieter sind wir gemaess § 7 Abs.1 TMG fuer eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, uebermittelte oder gespeicherte fremde Informationen zu ueberwachen oder nach Umstaenden zu forschen, die auf eine rechtswidrige Taetigkeit hinweisen.',

      // Links Liability
      linksTitle: 'Haftung fuer Links',
      linksText: 'Unser Angebot enthaelt Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb koennen wir fuer diese fremden Inhalte auch keine Gewaehr uebernehmen. Fuer die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.',

      // Copyright
      copyrightTitle: 'Urheberrecht',
      copyrightText: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfaeltigung, Bearbeitung, Verbreitung und jede Art der Verwertung ausserhalb der Grenzen des Urheberrechtes beduerfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.',
    },
    en: {
      backText: 'Back to Home',
      title: 'Legal Notice',
      subtitle: 'Information pursuant to § 5 TMG (German Telemedia Act)',

      // Contact Section
      contactTitle: 'Contact',
      name: 'Berend Jakob Mainz',
      address: 'Ringstrasse 10a, 35428 Langgoens, Germany',
      email: 'berend.jakob.mainz@gmail.com',
      contactForm: 'Contact Form',
      contactFormUrl: '/support',

      // TODO: Add when business is registered:
      // vatId: 'VAT ID: DE...',
      // companyName: 'Company Name',
      // registerCourt: 'Commercial Register: Amtsgericht ..., HRB ...',

      // Responsibility
      responsibleTitle: 'Responsible for content pursuant to § 18 para. 2 MStV',

      // Dispute Resolution
      disputeTitle: 'Dispute Resolution',
      disputeText: 'The former EU Online Dispute Resolution (ODR) platform was discontinued on July 20, 2025 pursuant to EU Regulation 2024/3228. The new EU Consumer Redress platform can be found at:',
      disputeLink: 'https://consumer-redress.ec.europa.eu/',
      disputeNote: 'We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.',

      // Liability
      liabilityTitle: 'Liability for Content',
      liabilityText: 'As a service provider, we are responsible for our own content on these pages in accordance with § 7 para.1 TMG under general law. According to §§ 8 to 10 TMG, however, we are not obligated as a service provider to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.',

      // Links Liability
      linksTitle: 'Liability for Links',
      linksText: 'Our offer contains links to external websites of third parties, on whose contents we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the contents of the linked pages.',

      // Copyright
      copyrightTitle: 'Copyright',
      copyrightText: 'The content and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution, and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator.',
    },
  };

  const t = content[lang];


  return (
    <>
      <div className="container" style={{ maxWidth: '800px', padding: '60px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: 'var(--gray-600)',
              textDecoration: 'none',
              fontSize: '14px',
            }}
          >
            <ArrowLeft size={16} />
            {t.backText}
          </Link>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setLang('de')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: lang === 'de' ? '2px solid var(--primary)' : '1px solid var(--gray-300)',
                background: lang === 'de' ? 'var(--primary-light, #e8f4f8)' : 'transparent',
                color: lang === 'de' ? 'var(--primary)' : 'var(--gray-600)',
                fontWeight: lang === 'de' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              DE
            </button>
            <button
              onClick={() => setLang('en')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: lang === 'en' ? '2px solid var(--primary)' : '1px solid var(--gray-300)',
                background: lang === 'en' ? 'var(--primary-light, #e8f4f8)' : 'transparent',
                color: lang === 'en' ? 'var(--primary)' : 'var(--gray-600)',
                fontWeight: lang === 'en' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              EN
            </button>
          </div>
        </div>

        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>{t.title}</h1>
        <p style={{ color: 'var(--gray-500)', marginBottom: '32px', fontSize: '14px' }}>
          {t.subtitle}
        </p>

        <div style={{ lineHeight: '1.8', color: 'var(--gray-700)' }}>
          {/* Contact Information */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
            {t.contactTitle}
          </h2>
          <p style={{ marginBottom: '8px' }}>
            <strong>{t.name}</strong>
          </p>
          <p style={{ marginBottom: '8px' }}>
            {t.address}
          </p>
          <p style={{ marginBottom: '8px' }}>
            E-Mail:{' '}
            <a href={`mailto:${t.email}`} style={{ color: 'var(--primary-600)' }}>
              {t.email}
            </a>
          </p>
          <p style={{ marginBottom: '24px' }}>
            {t.contactForm}:{' '}
            <Link to={t.contactFormUrl} style={{ color: 'var(--primary-600)' }}>
              tryreviewresponder.com{t.contactFormUrl}
            </Link>
          </p>

          {/* Responsible for Content */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.responsibleTitle}
          </h2>
          <p style={{ marginBottom: '24px' }}>
            {t.name}<br />
            {t.address}
          </p>

          {/* Dispute Resolution */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.disputeTitle}
          </h2>
          <p style={{ marginBottom: '8px' }}>
            {t.disputeText}
          </p>
          <p style={{ marginBottom: '16px' }}>
            <a
              href={t.disputeLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary-600)' }}
            >
              {t.disputeLink}
            </a>
          </p>
          <p style={{ marginBottom: '24px' }}>
            {t.disputeNote}
          </p>

          {/* Liability for Content */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.liabilityTitle}
          </h2>
          <p style={{ marginBottom: '24px' }}>
            {t.liabilityText}
          </p>

          {/* Liability for Links */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.linksTitle}
          </h2>
          <p style={{ marginBottom: '24px' }}>
            {t.linksText}
          </p>

          {/* Copyright */}
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.copyrightTitle}
          </h2>
          <p style={{ marginBottom: '24px' }}>
            {t.copyrightText}
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

// Terms of Service Page
const TermsPage = () => (
  <>
    <div className="container" style={{ maxWidth: '800px', padding: '60px 20px' }}>
      <Link
        to="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--gray-600)',
          textDecoration: 'none',
          fontSize: '14px',
          marginBottom: '24px',
        }}
      >
        <ArrowLeft size={16} />
        Back to Home
      </Link>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>
        Terms of Service / Allgemeine Geschaeftsbedingungen
      </h1>
      <p style={{ color: 'var(--gray-500)', marginBottom: '32px' }}>
        Last updated: January 24, 2026
      </p>

      <div style={{ lineHeight: '1.8', color: 'var(--gray-700)' }}>

        {/* 1. Provider Information */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          1. Provider / Anbieter
        </h2>
        <p>
          <strong>Berend Jakob Mainz</strong><br />
          Ringstrasse 10a, 35428 Langgoens, Germany<br />
          Email: <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>berend.jakob.mainz@gmail.com</a>
        </p>

        {/* 2. Service Description */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          2. Service Description
        </h2>
        <p>
          ReviewResponder provides AI-powered review response generation for businesses. We use artificial intelligence to help you craft professional responses to customer reviews.
        </p>

        {/* 3. Contract Formation */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          3. Contract Formation / Vertragsschluss
        </h2>
        <p>
          By registering for an account and/or purchasing a subscription, you enter into a binding contract with us. The contract is concluded when:
        </p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>You complete the registration process and we confirm your account via email, or</li>
          <li>You complete a payment and receive confirmation of your subscription.</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          The contract language is English. We store the contract text (these Terms) and will send you the order details via email.
        </p>

        {/* 4. Right of Withdrawal (Widerrufsrecht) - CRITICAL FOR B2C */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          4. Right of Withdrawal for Consumers (EU) / Widerrufsrecht
        </h2>
        <div style={{ background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <p><strong>Widerrufsbelehrung</strong></p>
          <p style={{ marginTop: '8px' }}>
            <strong>Widerrufsrecht:</strong> Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gruenden diesen Vertrag zu widerrufen. Die Widerrufsfrist betraegt vierzehn Tage ab dem Tag des Vertragsabschlusses.
          </p>
          <p style={{ marginTop: '8px' }}>
            Um Ihr Widerrufsrecht auszuueben, muessen Sie uns (Berend Jakob Mainz, Ringstrasse 10a, 35428 Langgoens, E-Mail: berend.jakob.mainz@gmail.com) mittels einer eindeutigen Erklaerung (z.B. ein mit der Post versandter Brief oder E-Mail) ueber Ihren Entschluss, diesen Vertrag zu widerrufen, informieren.
          </p>
          <p style={{ marginTop: '8px' }}>
            <strong>Folgen des Widerrufs:</strong> Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzueglich und spaetestens binnen vierzehn Tagen ab dem Tag zurueckzuzahlen, an dem die Mitteilung ueber Ihren Widerruf dieses Vertrags bei uns eingegangen ist.
          </p>
          <p style={{ marginTop: '8px' }}>
            <strong>Besonderer Hinweis:</strong> Ihr Widerrufsrecht erlischt bei einem Vertrag zur Erbringung von Dienstleistungen, wenn wir die Dienstleistung vollstaendig erbracht haben und mit der Ausfuehrung der Dienstleistung erst begonnen haben, nachdem Sie dazu Ihre ausdrueckliche Zustimmung gegeben haben und gleichzeitig Ihre Kenntnis davon bestaetigt haben, dass Sie Ihr Widerrufsrecht bei vollstaendiger Vertragerfuellung durch uns verlieren.
          </p>
        </div>
        <p>
          <strong>English Summary:</strong> EU consumers have a 14-day right of withdrawal. To withdraw, send a clear statement to the address above. If you have already used the service with your explicit consent, the right of withdrawal may expire.
        </p>

        {/* Muster-Widerrufsformular - PFLICHT nach Anlage 2 zu Art. 246a § 1 Abs. 2 EGBGB */}
        <div style={{ background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
          <p><strong>Muster-Widerrufsformular</strong></p>
          <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--gray-500)' }}>
            (Wenn Sie den Vertrag widerrufen wollen, dann fuellen Sie bitte dieses Formular aus und senden Sie es zurueck.)
          </p>
          <div style={{ marginTop: '12px', paddingLeft: '16px', borderLeft: '2px solid var(--gray-300)' }}>
            <p>An:<br />
            Berend Jakob Mainz<br />
            Ringstrasse 10a<br />
            35428 Langgoens<br />
            E-Mail: berend.jakob.mainz@gmail.com</p>
            <p style={{ marginTop: '12px' }}>
              Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag ueber die Erbringung der folgenden Dienstleistung: ReviewResponder Abonnement
            </p>
            <p style={{ marginTop: '8px' }}>Bestellt am (*) / erhalten am (*):</p>
            <p style={{ marginTop: '8px' }}>Name des/der Verbraucher(s):</p>
            <p style={{ marginTop: '8px' }}>Anschrift des/der Verbraucher(s):</p>
            <p style={{ marginTop: '8px' }}>Datum, Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier)</p>
            <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--gray-500)' }}>(*) Unzutreffendes streichen.</p>
          </div>
        </div>

        {/* 5. Account Terms */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          5. Account Terms
        </h2>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>You must be 18 years or older to use this service</li>
          <li>You are responsible for maintaining the security of your account and password</li>
          <li>You may not use the service for illegal purposes</li>
          <li>One person or business per account</li>
          <li>You must provide accurate and complete registration information</li>
        </ul>

        {/* 6. Acceptable Use */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          6. Acceptable Use
        </h2>
        <p>You agree NOT to use ReviewResponder to:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>Generate fake reviews or misleading content</li>
          <li>Harass, abuse, or harm others</li>
          <li>Violate any laws or regulations</li>
          <li>Infringe on intellectual property rights</li>
          <li>Attempt to gain unauthorized access to our systems</li>
        </ul>

        {/* 7. Prices, Payment & Refunds */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          7. Prices, Payment & Refunds
        </h2>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li><strong>Free tier:</strong> 20 responses per month, no payment required</li>
          <li><strong>Paid plans:</strong> Prices are displayed on our pricing page. All prices include VAT where applicable.</li>
          <li><strong>Payment:</strong> Billed monthly or yearly via Stripe (secure payment processing)</li>
          <li><strong>30-Day Money Back Guarantee:</strong> If you're not satisfied within 30 days of purchase, contact us for a full refund - no questions asked</li>
          <li><strong>Cancellation:</strong> You can cancel your subscription anytime through your account settings or by contacting us. Access continues until the end of your billing period.</li>
        </ul>

        {/* 8. AI-Generated Content */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          8. AI-Generated Content
        </h2>
        <p>Responses generated by our AI are suggestions only. You are responsible for:</p>
        <ul style={{ marginLeft: '24px', marginTop: '12px' }}>
          <li>Reviewing and editing responses before posting</li>
          <li>Ensuring accuracy of any claims made in your published responses</li>
          <li>The final content you choose to publish</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          We do not guarantee that AI-generated content will be error-free, appropriate for all situations, or achieve any particular result.
        </p>

        {/* 9. Liability */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          9. Liability / Haftung
        </h2>
        <p>
          We are liable without limitation for damages caused by intent or gross negligence, and for damages resulting from injury to life, body, or health.
        </p>
        <p style={{ marginTop: '12px' }}>
          For slight negligence, we are only liable in case of breach of essential contractual obligations (cardinal obligations). In such cases, liability is limited to foreseeable, typically occurring damages.
        </p>
        <p style={{ marginTop: '12px' }}>
          We are not liable for the accuracy, completeness, or suitability of AI-generated content for your specific purposes. You are solely responsible for reviewing and using the generated responses.
        </p>
        <p style={{ marginTop: '12px' }}>
          The above limitations do not apply to mandatory statutory liability (e.g., under the German Product Liability Act) or to liability for fraudulently concealed defects.
        </p>

        {/* 10. Termination */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          10. Termination / Kuendigung
        </h2>
        <p>
          <strong>By You:</strong> You may terminate your account at any time by canceling your subscription in your account settings or by emailing us. For paid subscriptions, access continues until the end of your current billing period.
        </p>
        <p style={{ marginTop: '12px' }}>
          <strong>By Us:</strong> We may terminate or suspend your account immediately if you violate these Terms. We will notify you by email.
        </p>
        <p style={{ marginTop: '12px' }}>
          Upon termination, your right to use the service ceases. We may delete your data after 30 days, subject to legal retention requirements.
        </p>

        {/* 11. Changes to Terms */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          11. Changes to Terms
        </h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes via email at least 14 days before they take effect. If you do not agree to the new Terms, you may terminate your account before they take effect. Continued use of the service after the effective date constitutes acceptance of the new Terms.
        </p>

        {/* 12. Governing Law & Jurisdiction */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          12. Governing Law & Jurisdiction / Anwendbares Recht
        </h2>
        <p>
          These Terms are governed by the laws of the Federal Republic of Germany, excluding the UN Convention on Contracts for the International Sale of Goods (CISG).
        </p>
        <p style={{ marginTop: '12px' }}>
          For consumers within the EU: You may also invoke the mandatory consumer protection laws of your country of residence. Nothing in these Terms affects your rights as a consumer under applicable law.
        </p>
        <p style={{ marginTop: '12px' }}>
          For disputes with consumers, the courts at the consumer's place of residence shall have jurisdiction. For business customers, the exclusive place of jurisdiction is Giessen, Germany.
        </p>

        {/* 13. Online Dispute Resolution */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          13. Online Dispute Resolution
        </h2>
        <p>
          <strong>Note:</strong> The former EU Online Dispute Resolution (ODR) platform was discontinued on July 20, 2025 pursuant to EU Regulation 2024/3228. The new EU Consumer Redress platform can be found at:{' '}
          <a href="https://consumer-redress.ec.europa.eu/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>
            https://consumer-redress.ec.europa.eu/
          </a>
        </p>
        <p style={{ marginTop: '12px' }}>
          We are not obligated or willing to participate in dispute resolution proceedings before a consumer arbitration board.
        </p>

        {/* 14. Severability */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          14. Severability / Salvatorische Klausel
        </h2>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. The invalid provision shall be replaced by a valid provision that comes closest to the economic intent of the original provision.
        </p>

        {/* 15. Contact */}
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
          15. Contact
        </h2>
        <p>
          Questions about these Terms? Contact us at{' '}
          <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>
            berend.jakob.mainz@gmail.com
          </a>
        </p>
      </div>
    </div>
    <Footer />
  </>
);

// About Berend Page
const AboutBerend = () => {
  const [lang, setLang] = useState('en');

  const content = {
    en: {
      backText: 'Back to Home',
      title: 'About Berend',
      intro: "I'm Berend, 23 years old and studying Psychology at Justus Liebig University in Giessen, Germany. ReviewResponder is my side project where I combine my knowledge from university with my passion for AI.",
      problemTitle: 'The Problem',
      problem1: "Reviews can hit you emotionally. Especially when they stem from misunderstandings or pure frustration. Google often doesn't allow you to simply delete such reviews - so you can only respond.",
      problem2: 'And here\'s the interesting part: I always read how businesses respond. When I see a good, professional reaction, I often think: "Okay, maybe the reviewer was just having a bad day." A thoughtful response can completely defuse an unfair review.',
      approachTitle: 'My Approach',
      approach: "From courses like Industrial & Organizational Psychology, I know how to respond to criticism in a psychologically smart way. Not defensively. Not overly apologetic when it's not necessary. But in a way that helps the business maintain its reputation. I built this knowledge into ReviewResponder.",
      whyTitle: 'Why Not "AI Writes For You"?',
      why1: "It's important to me that authenticity isn't lost. ReviewResponder isn't meant to replace you - it gives you a psychologically sound structure. You add the finishing touches. Your voice stays your voice.",
      why2: "We live in a time where AI is here to stay. But I believe in tools that keep humans in the driver's seat - while still leveraging AI to work more productively.",
      feedbackTitle: 'Feedback?',
      feedback: "I appreciate any feedback and I'm committed to continuously improving the tool. Feel free to reach out at",
      emailText: 'if you have questions, ideas, or feature requests.',
      exploreMind: 'Explore ReviewResponder\'s mind',
    },
    de: {
      backText: 'Zurück zur Startseite',
      title: 'Über Berend',
      intro: 'Ich bin Berend, 23 Jahre alt und studiere Psychologie an der Justus-Liebig-Universität in Gießen. ReviewResponder ist mein Nebenprojekt, in dem ich mein Wissen aus dem Studium mit meiner Leidenschaft für KI verbinde.',
      problemTitle: 'Das Problem',
      problem1: 'Rezensionen können einen emotional treffen. Besonders wenn sie durch Missverständnisse oder pure Frustration entstanden sind. Google erlaubt es oft nicht, solche Reviews einfach zu löschen - also kann man nur antworten.',
      problem2: 'Und hier wird es spannend: Ich lese selbst immer die Antworten der Unternehmen. Wenn ich eine gute, professionelle Reaktion sehe, denke ich mir oft: „Okay, vielleicht war der Reviewer an dem Tag einfach unzufrieden." Eine kluge Antwort kann eine unfaire Bewertung komplett entkräften.',
      approachTitle: 'Mein Ansatz',
      approach: 'Aus Fächern wie Arbeits- und Organisationspsychologie weiß ich, wie man psychologisch schlau auf Kritik reagiert. Nicht defensiv. Nicht übermäßig entschuldigend, wenn es nicht angebracht ist. Sondern so, dass das Unternehmen sein Gesicht bewahrt. Dieses Wissen habe ich in ReviewResponder eingebaut.',
      whyTitle: 'Warum kein „KI schreibt für dich"?',
      why1: 'Mir ist wichtig, dass Authentizität nicht verloren geht. ReviewResponder soll dich nicht ersetzen - es gibt dir eine psychologisch fundierte Struktur. Den Feinschliff machst du. Deine Stimme bleibt deine Stimme.',
      why2: 'Wir leben in einer Zeit, in der KI nicht mehr wegzudenken ist. Aber ich glaube an Tools, die den Menschen in den Vordergrund stellen - und trotzdem die Vorteile von KI nutzen, um produktiver zu arbeiten.',
      feedbackTitle: 'Feedback?',
      feedback: 'Ich freue mich über jedes Feedback und bin gewillt, das Tool ständig zu verbessern. Schreib mir gerne an',
      emailText: 'wenn du Fragen, Ideen oder Feature-Wünsche hast.',
      exploreMind: 'Erkunde ReviewResponders Verstand',
    },
  };

  const t = content[lang];

  return (
    <>
      <div className="container" style={{ maxWidth: '800px', padding: '60px 20px' }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--gray-600)',
            textDecoration: 'none',
            fontSize: '14px',
            marginBottom: '24px',
          }}
        >
          <ArrowLeft size={16} />
          {t.backText}
        </Link>

        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <img
            src="/berend-photo.jpg"
            alt="Berend hiking in the mountains"
            style={{
              width: '100%',
              maxWidth: '400px',
              borderRadius: '12px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', margin: 0 }}>{t.title}</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setLang('en')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: lang === 'en' ? '2px solid var(--primary)' : '1px solid var(--gray-300)',
                background: lang === 'en' ? 'var(--primary-light, #e8f4f8)' : 'transparent',
                color: lang === 'en' ? 'var(--primary)' : 'var(--gray-600)',
                fontWeight: lang === 'en' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              EN
            </button>
            <button
              onClick={() => setLang('de')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: lang === 'de' ? '2px solid var(--primary)' : '1px solid var(--gray-300)',
                background: lang === 'de' ? 'var(--primary-light, #e8f4f8)' : 'transparent',
                color: lang === 'de' ? 'var(--primary)' : 'var(--gray-600)',
                fontWeight: lang === 'de' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              DE
            </button>
          </div>
        </div>

        <div style={{ lineHeight: '1.8', color: 'var(--gray-700)' }}>
          <p style={{ marginBottom: '24px', fontSize: '1.1rem' }}>{t.intro}</p>

          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.problemTitle}
          </h2>
          <p style={{ marginBottom: '16px' }}>{t.problem1}</p>
          <p style={{ marginBottom: '24px' }}>{t.problem2}</p>

          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.approachTitle}
          </h2>
          <p style={{ marginBottom: '24px' }}>{t.approach}</p>

          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.whyTitle}
          </h2>
          <p style={{ marginBottom: '16px' }}>{t.why1}</p>
          <p style={{ marginBottom: '24px' }}>{t.why2}</p>

          <h2 style={{ fontSize: '20px', fontWeight: '600', marginTop: '32px', marginBottom: '16px' }}>
            {t.feedbackTitle}
          </h2>
          <p>
            {t.feedback}{' '}
            <a href="mailto:berend.jakob.mainz@gmail.com" style={{ color: 'var(--primary-600)' }}>
              berend.jakob.mainz@gmail.com
            </a>{' '}
            {t.emailText}
          </p>

          {/* Explore mind link */}
          <div style={{ textAlign: 'center', marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--gray-200)' }}>
            <Link
              to="/brain"
              style={{
                fontSize: '14px',
                color: 'var(--gray-500)',
                textDecoration: 'none',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
              onMouseLeave={(e) => e.target.style.color = 'var(--gray-500)'}
            >
              {t.exploreMind} →
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

// Pricing Cards Component
const PricingCards = ({ showFree = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null); // Track which plan is loading
  // Guest checkout state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [guestLoading, setGuestLoading] = useState(false);

  // Get discount code ONLY from URL parameter - localStorage was causing stale discounts
  const urlParams = new URLSearchParams(location.search);
  const discountFromUrl = urlParams.get('discount');

  // Clear stale localStorage discount if visiting /pricing without discount in URL
  useEffect(() => {
    if (!discountFromUrl) {
      localStorage.removeItem('pending_discount');
    }
  }, [discountFromUrl]);

  // Valid discount codes (including welcome/demo/flash/abandonment)
  const validDiscountCodes = [
    'EARLY50',
    'SAVE20',
    'HUNTLAUNCH',
    'WELCOME30',
    'DEMO30',
    'DEMOFOLLOWUP',
    'FLASH50',
    'COMEBACK20',
    'HOTLEAD40',
  ];
  const activeDiscount = validDiscountCodes.includes(discountFromUrl?.toUpperCase())
    ? discountFromUrl.toUpperCase()
    : null;

  const handleSubscribe = async plan => {
    if (!user) {
      // Show email modal for guest checkout
      setSelectedPlan(plan);
      setShowEmailModal(true);
      return;
    }

    setLoadingPlan(plan); // Start loading
    try {
      const checkoutData = {
        plan,
        billing: billingCycle,
      };

      // Only add discount code if valid one is present in URL
      if (activeDiscount) {
        checkoutData.discountCode = activeDiscount;
      }

      const res = await api.post('/billing/create-checkout', checkoutData);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoadingPlan(null); // Stop loading on error
    }
  };

  // Guest checkout - subscribe without account first
  const handleGuestCheckout = async e => {
    e.preventDefault();
    if (!guestEmail || !guestEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setGuestLoading(true);
    try {
      const checkoutData = {
        email: guestEmail,
        plan: selectedPlan,
        billing: billingCycle,
      };

      if (activeDiscount) {
        checkoutData.discountCode = activeDiscount;
      }

      const res = await api.post('/billing/create-checkout-guest', checkoutData);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start checkout');
      setGuestLoading(false);
    }
  };

  // Handle Google Sign In for guest checkout
  const handleGoogleCheckout = async credentialResponse => {
    setGuestLoading(true);
    try {
      // First, authenticate with Google
      const authRes = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });

      if (authRes.data.token && authRes.data.user) {
        // Store token
        localStorage.setItem('token', authRes.data.token);

        // Now proceed to checkout
        const checkoutData = {
          plan: selectedPlan,
          billing: billingCycle,
        };

        if (activeDiscount) {
          checkoutData.discountCode = activeDiscount;
        }

        const checkoutRes = await api.post('/billing/create-checkout', checkoutData);
        window.location.href = checkoutRes.data.url;
      }
    } catch (error) {
      console.error('Google checkout error:', error);
      toast.error('Sign in failed. Please try again.');
      setGuestLoading(false);
    }
  };

  const plans = [
    {
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      responses: 20,
      features: [
        '20 responses/month',
        'All 4 tone options',
        'Any language (auto-detect)',
        'Chrome Extension (all platforms)',
      ],
      buttonText: 'Sign Up',
      plan: 'free',
    },
    {
      name: 'Starter',
      monthlyPrice: 29,
      yearlyPrice: 23.2, // 20% off
      responses: 300,
      features: [
        '300 responses/month',
        'Response history',
        'CSV/PDF export',
        'Chrome Extension (all platforms)',
      ],
      buttonText: 'Subscribe',
      plan: 'starter',
    },
    {
      name: 'Pro',
      monthlyPrice: 49,
      yearlyPrice: 39.2, // 20% off
      responses: 800,
      features: [
        '800 responses/month',
        'Bulk generation (20 at once)',
        'Analytics dashboard',
        'Team members (3)',
        'Priority support',
        'Chrome Extension (all platforms)',
      ],
      buttonText: 'Subscribe',
      plan: 'professional',
      popular: true,
    },
    {
      name: 'Unlimited',
      monthlyPrice: 99,
      yearlyPrice: 79.2, // 20% off
      responses: 'Unlimited',
      features: [
        'Unlimited responses',
        'All Pro features',
        'Team members (10)',
        'API access',
        'Dedicated support',
        'Chrome Extension (all platforms)',
        'Priority feature requests',
      ],
      buttonText: 'Subscribe',
      plan: 'unlimited',
    },
  ];

  const displayPlans = showFree ? plans : plans.filter(p => p.plan !== 'free');
  const isYearly = billingCycle === 'yearly';

  // Discount details for each code
  const discountDetails = {
    EARLY50: { percent: 50, label: 'Early Access' },
    SAVE20: { percent: 20, label: 'Special Offer' },
    HUNTLAUNCH: { percent: 60, label: 'Product Hunt Special' },
  };
  const currentDiscount = activeDiscount ? discountDetails[activeDiscount] : null;

  return (
    <div>
      {/* Active Discount Banner - only shown when discount code is in URL */}
      {currentDiscount && (
        <div
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: '20px 28px',
            borderRadius: '12px',
            marginBottom: '28px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.25)',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              opacity: 0.9,
              marginBottom: '6px',
            }}
          >
            Early Access
          </div>
          <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
            {currentDiscount.percent}% off for your first year
          </div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>
            Code <strong>{activeDiscount}</strong> applied automatically
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '32px',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontWeight: billingCycle === 'monthly' ? '600' : '400',
            color: billingCycle === 'monthly' ? 'var(--gray-900)' : 'var(--gray-500)',
          }}
        >
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(prev => (prev === 'monthly' ? 'yearly' : 'monthly'))}
          style={{
            width: '56px',
            height: '28px',
            borderRadius: '14px',
            background: isYearly ? 'var(--primary-600)' : 'var(--gray-300)',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            transition: 'background 0.2s',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: '2px',
              left: isYearly ? '30px' : '2px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'var(--bg-secondary)',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </button>
        <span
          style={{
            fontWeight: billingCycle === 'yearly' ? '600' : '400',
            color: billingCycle === 'yearly' ? 'var(--gray-900)' : 'var(--gray-500)',
          }}
        >
          Yearly
        </span>
        <span
          style={{
            background: 'var(--success)',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            marginLeft: '8px',
          }}
        >
          Save 20%
        </span>
      </div>

      <div className="pricing-grid">
        {displayPlans.map(plan => {
          const displayPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
          const savings =
            plan.monthlyPrice > 0 ? Math.round((plan.monthlyPrice - plan.yearlyPrice) * 12) : 0;

          return (
            <div key={plan.name} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <span className="pricing-badge">Most Popular</span>}
              <h3 className="pricing-plan">{plan.name}</h3>
              <div className="pricing-price">
                ${displayPrice.toFixed(2).replace('.00', '')}
                <span>/mo</span>
              </div>
              {isYearly && plan.monthlyPrice > 0 && (
                <p
                  style={{
                    color: 'var(--success)',
                    fontSize: '13px',
                    fontWeight: '600',
                    marginTop: '4px',
                  }}
                >
                  Save ${savings}/year
                </p>
              )}
              {isYearly && plan.monthlyPrice > 0 && (
                <p
                  style={{
                    color: 'var(--gray-400)',
                    fontSize: '12px',
                    textDecoration: 'line-through',
                    marginTop: '2px',
                  }}
                >
                  ${plan.monthlyPrice}/mo
                </p>
              )}
              <p style={{ color: 'var(--gray-500)', marginTop: '8px' }}>
                {typeof plan.responses === 'number'
                  ? `${plan.responses} responses/month`
                  : plan.responses}
              </p>
              <ul className="pricing-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <Check size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
              {plan.plan === 'free' ? (
                <Link to="/register" className="btn btn-secondary" style={{ width: '100%' }}>
                  {plan.buttonText}
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => handleSubscribe(plan.plan)}
                    className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ width: '100%' }}
                    disabled={loadingPlan !== null}
                  >
                    {loadingPlan === plan.plan ? 'Processing...' : plan.buttonText}
                  </button>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'var(--gray-500)',
                      marginTop: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                    }}
                  >
                    <Check size={12} />
                    30-day money-back guarantee
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Guest Email Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
              Sign in to continue
            </h3>
            {/* Google Sign In - PRIMARY */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleGoogleCheckout}
                onError={() => toast.error('Google Sign In failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>
            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '16px 0',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'var(--gray-300)' }} />
              <span style={{ color: 'var(--gray-500)', fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--gray-300)' }} />
            </div>
            {/* Email form - SECONDARY */}
            <form onSubmit={handleGuestCheckout}>
              <input
                type="email"
                value={guestEmail}
                onChange={e => setGuestEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-300)',
                  fontSize: '16px',
                  marginBottom: '16px',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={guestLoading}
              >
                {guestLoading ? 'Processing...' : 'Continue to Checkout'}
              </button>
            </form>
            <p
              style={{
                fontSize: '12px',
                color: 'var(--gray-500)',
                textAlign: 'center',
                marginTop: '16px',
              }}
            >
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--primary-600)' }}>
                Log in
              </Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      // Global redirect: ALL logins → Generator for activation
      navigate('/generator');
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Login failed';
      // Show helpful message for invalid credentials
      if (
        errorMsg.toLowerCase().includes('invalid') ||
        errorMsg.toLowerCase().includes('credentials') ||
        errorMsg.toLowerCase().includes('not found')
      ) {
        toast.error(
          <div>
            {errorMsg}
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              No account?{' '}
              <a href="/register" style={{ color: 'var(--primary-500)', fontWeight: '600' }}>
                Sign up free →
              </a>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = useCallback(
    async credential => {
      setGoogleLoading(true);
      try {
        await loginWithGoogle(credential);
        toast.success('Welcome back!');
        // Global redirect: ALL logins → Generator for activation
        navigate('/generator');
      } catch (error) {
        toast.error(error.response?.data?.error || 'Google sign-in failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    [loginWithGoogle, navigate]
  );

  const handleGoogleError = useCallback(error => {
    console.error('Google Sign-In error:', error);
    toast.error('Google sign-in failed. Please try again.');
  }, []);

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h1 className="auth-title">Welcome Back</h1>
        <p className="auth-subtitle">Sign in to your account</p>

        {/* Google Sign-In Button */}
        <GoogleSignInButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          text="Sign in with Google"
        />

        {googleLoading && (
          <div
            style={{ textAlign: 'center', marginBottom: '16px', color: 'var(--text-secondary)' }}
          >
            Signing in with Google...
          </div>
        )}

        {/* Trust Signals */}
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Lock size={12} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>256-bit SSL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Shield size={12} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>GDPR Compliant</span>
          </div>
        </div>

        {/* Divider */}
        {GOOGLE_CLIENT_ID && (
          <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }}></div>
            <span style={{ padding: '0 16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
              or continue with email
            </span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }}></div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '16px' }}>
          <Link to="/forgot-password" style={{ color: 'var(--primary-600)', fontSize: '14px' }}>
            Forgot your password?
          </Link>
        </p>

        <p className="auth-footer">
          Don't have an account? <Link to="/register">Sign up</Link>
        </p>
      </div>
    </div>
  );
};

// Magic Login Page - Handles magic link token login
const MagicLoginPage = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(
        errorParam === 'invalid_magic_link'
          ? 'This magic link has expired or is invalid.'
          : 'Magic link login failed. Please try again.'
      );
      return;
    }

    if (token) {
      // Save token to localStorage
      localStorage.setItem('token', token);

      // Fetch user data and login
      api
        .get('/auth/me')
        .then(async res => {
          setUser(res.data.user);
          // Mark admin users permanently to skip demo view tracking
          if (res.data.user?.email?.toLowerCase() === 'berend.mainz@web.de') {
            localStorage.setItem('isAdminUser', 'true');
          }
          toast.success('Welcome!');
          // Global redirect: ALL logins → Generator for activation
          navigate('/generator');
        })
        .catch(err => {
          console.error('Magic login error:', err);
          localStorage.removeItem('token');
          setError('Failed to authenticate. Please try again.');
        });
    } else {
      setError('No login token found.');
    }
  }, [searchParams, navigate, setUser]);

  if (error) {
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <h1 className="auth-title">Magic Link Error</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>{error}</p>
          <Link to="/login" className="btn btn-primary">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card auth-card" style={{ textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-color)',
            borderTopColor: 'var(--primary-500)',
            borderRadius: '50%',
            margin: '0 auto 20px',
          }}
        ></div>
        <h1 className="auth-title">Logging you in...</h1>
        <p style={{ color: 'var(--text-secondary)' }}>Please wait while we set up your account.</p>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get ref parameter for demo conversion tracking (e.g., ref=demo_abc123)
  const refParam = searchParams.get('ref');
  // Get discount code from URL (e.g., discount=DEMO30)
  const discountParam = searchParams.get('discount');

  // Store discount code in localStorage for checkout
  useEffect(() => {
    if (discountParam) {
      localStorage.setItem('pending_discount', discountParam.toUpperCase());
    }
  }, [discountParam]);

  // Pre-fill email from URL param (demo redirect) or landing page sessionStorage
  const emailParam = searchParams.get('email');
  useEffect(() => {
    // Priority: URL param > sessionStorage
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    } else {
      const savedEmail = sessionStorage.getItem('landing_email');
      if (savedEmail) {
        setEmail(savedEmail);
        sessionStorage.removeItem('landing_email');
      }
    }
  }, [emailParam]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, businessName, refParam);
      toast.success('Account created!');
      // Global redirect: ALL registrations → Generator for activation
      navigate('/generator');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = useCallback(
    async credential => {
      setGoogleLoading(true);
      try {
        await loginWithGoogle(credential, refParam);
        toast.success('Account created!');
        // Global redirect: ALL registrations → Generator for activation
        navigate('/generator');
      } catch (error) {
        toast.error(error.response?.data?.error || 'Google sign-up failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    [loginWithGoogle, navigate, refParam, searchParams]
  );

  const handleGoogleError = useCallback(error => {
    console.error('Google Sign-In error:', error);
    toast.error('Google sign-up failed. Please try again.');
  }, []);

  return (
    <div className="auth-container">
      <div className="card auth-card">
        {/* Discount Banner */}
        {discountParam && (
          <div
            style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px dashed #d97706',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
              Your 30% discount is ready!
            </div>
            <div style={{ fontSize: '12px', color: '#a16207', marginTop: '4px' }}>
              Code <strong>{discountParam.toUpperCase()}</strong> will be auto-applied at checkout
            </div>
          </div>
        )}
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">
          {discountParam
            ? 'Sign up to claim your discount'
            : 'Start generating review responses for free'}
        </p>

        {/* Google Sign-Up Button */}
        <GoogleSignInButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          text="Sign up with Google"
        />

        {googleLoading && (
          <div
            style={{ textAlign: 'center', marginBottom: '16px', color: 'var(--text-secondary)' }}
          >
            Creating account with Google...
          </div>
        )}

        {/* Trust Signals */}
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CreditCard size={12} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>No credit card</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Sparkles size={12} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
              20 free responses/month
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Shield size={12} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>GDPR Compliant</span>
          </div>
        </div>

        {/* Divider */}
        {GOOGLE_CLIENT_ID && (
          <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }}></div>
            <span style={{ padding: '0 16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
              or sign up with email
            </span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }}></div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              Business Name{' '}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                (optional)
              </span>
            </label>
            <input
              type="text"
              className="form-input"
              value={businessName}
              onChange={e => setBusinessName(e.target.value)}
              placeholder="e.g. Joe's Pizza"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              minLength={8}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Free Account'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <Mail size={48} style={{ color: 'var(--primary-600)', marginBottom: '16px' }} />
          <h1 className="auth-title">Check Your Email</h1>
          <p className="auth-subtitle">
            If an account exists for {email}, you will receive a password reset link shortly.
          </p>
          <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginTop: '16px' }}>
            Didn't receive the email? Check your spam folder or try again.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h1 className="auth-title">Forgot Password?</h1>
        <p className="auth-subtitle">Enter your email and we'll send you a reset link</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Security Note */}
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Lock size={12} style={{ color: 'var(--gray-400)' }} />
          <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
            Secure password reset via 256-bit SSL
          </span>
        </div>

        <p className="auth-footer">
          Remember your password? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const token = new URLSearchParams(location.search).get('token');

  const handleSubmit = async e => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <X size={48} style={{ color: 'var(--error)', marginBottom: '16px' }} />
          <h1 className="auth-title">Invalid Link</h1>
          <p className="auth-subtitle">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <Check size={48} style={{ color: 'var(--success)', marginBottom: '16px' }} />
          <h1 className="auth-title">Password Reset!</h1>
          <p className="auth-subtitle">Your password has been reset successfully.</p>
          <Link to="/login" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Sign In Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h1 className="auth-title">Reset Password</h1>
        <p className="auth-subtitle">Enter your new password</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              minLength={8}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              minLength={8}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Onboarding Modal Component
const OnboardingModal = ({ isVisible, onComplete, onSkip }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');

  // Step 1: Business Name
  const [businessName, setBusinessName] = useState(user?.businessName || '');

  // Step 2: Business Type + Keywords + Preview
  const [businessType, setBusinessType] = useState(user?.businessType || '');
  const [customBusinessType, setCustomBusinessType] = useState('');
  const [keywords, setKeywords] = useState('');
  const [generatedContext, setGeneratedContext] = useState('');
  const [generatingContext, setGeneratingContext] = useState(false);

  // Preview Response
  const [sampleReview, setSampleReview] = useState('');
  const [sampleResponse, setSampleResponse] = useState('');
  const [generatingSample, setGeneratingSample] = useState(false);

  // Step 2: Custom Instructions (NEW)
  const [alwaysMention, setAlwaysMention] = useState('');
  const [neverSay, setNeverSay] = useState('');
  const [selectedTone, setSelectedTone] = useState('professional');

  // Grouped business types for react-select
  const businessTypeOptions = [
    {
      label: 'Most Common',
      options: [
        { value: 'Restaurant', label: 'Restaurant' },
        { value: 'Dental Practice', label: 'Dental Practice' },
        { value: 'Medical Practice', label: 'Medical Practice' },
        { value: 'Hair Salon / Barbershop', label: 'Hair Salon / Barbershop' },
        { value: 'Auto Repair / Service', label: 'Auto Repair / Service' },
        { value: 'Real Estate', label: 'Real Estate' },
        { value: 'Home Services', label: 'Home Services' },
      ],
    },
    {
      label: 'Food & Hospitality',
      options: [
        { value: 'Cafe / Coffee Shop', label: 'Cafe / Coffee Shop' },
        { value: 'Bar / Nightclub', label: 'Bar / Nightclub' },
        { value: 'Hotel / Accommodation', label: 'Hotel / Accommodation' },
      ],
    },
    {
      label: 'Health & Beauty',
      options: [
        { value: 'Spa / Wellness', label: 'Spa / Wellness' },
        { value: 'Gym / Fitness Studio', label: 'Gym / Fitness Studio' },
        { value: 'Veterinary / Pet Services', label: 'Veterinary / Pet Services' },
        { value: 'Senior Care', label: 'Senior Care' },
        { value: 'Therapy / Counseling', label: 'Therapy / Counseling' },
      ],
    },
    {
      label: 'Professional',
      options: [
        { value: 'Professional Services', label: 'Professional Services' },
        { value: 'Financial Services', label: 'Financial Services' },
        { value: 'Retail Store', label: 'Retail Store' },
        { value: 'E-commerce', label: 'E-commerce' },
      ],
    },
    {
      label: 'Lifestyle',
      options: [
        { value: 'Photography / Creative', label: 'Photography / Creative' },
        { value: 'Event Planning', label: 'Event Planning' },
        { value: 'Childcare / Education', label: 'Childcare / Education' },
      ],
    },
    {
      label: 'Other',
      options: [{ value: 'Other', label: 'Other (Custom)' }],
    },
  ];

  // Flat list for validation
  const businessTypes = businessTypeOptions.flatMap(g => g.options.map(o => o.value));

  // Dynamic placeholder examples based on business type
  const getKeywordsPlaceholder = (type) => {
    const placeholders = {
      'Restaurant': 'e.g., family-owned since 1985, authentic Italian recipes, fresh ingredients daily, cozy atmosphere, outdoor seating',
      'Dental Practice': 'e.g., gentle care, latest technology, family-friendly, 20+ years experience, same-day appointments, pain-free treatments',
      'Medical Practice': 'e.g., board-certified physicians, compassionate care, state-of-the-art facility, accepting new patients, telehealth available',
      'Hair Salon / Barbershop': 'e.g., expert colorists, relaxing atmosphere, premium products, walk-ins welcome, serving the community since 2010',
      'Auto Repair / Service': 'e.g., ASE certified mechanics, honest pricing, same-day service, family-owned, all makes and models',
      'Real Estate': 'e.g., local market expert, 15+ years experience, first-time buyer specialist, luxury homes, responsive communication',
      'Home Services': 'e.g., licensed and insured, free estimates, satisfaction guaranteed, same-day service, family-owned',
      'Cafe / Coffee Shop': 'e.g., specialty roasts, homemade pastries, cozy workspace, free wifi, locally sourced ingredients',
      'Bar / Nightclub': 'e.g., craft cocktails, live music weekends, friendly staff, VIP tables, great happy hour',
      'Hotel / Accommodation': 'e.g., boutique hotel, city center location, complimentary breakfast, pet-friendly, rooftop bar',
      'Spa / Wellness': 'e.g., licensed therapists, organic products, tranquil atmosphere, couples treatments, membership packages',
      'Gym / Fitness Studio': 'e.g., certified trainers, 24/7 access, group classes, personal training, state-of-the-art equipment',
      'Veterinary / Pet Services': 'e.g., fear-free certified, compassionate care, emergency services, boarding available, exotic pets welcome',
      'Senior Care': 'e.g., dignified care, trained caregivers, 24/7 support, family-like environment, personalized care plans',
      'Therapy / Counseling': 'e.g., licensed therapist, safe space, evidence-based approaches, online sessions available, sliding scale',
      'Professional Services': 'e.g., experienced team, personalized approach, quick turnaround, competitive rates',
      'Financial Services': 'e.g., fiduciary advisor, 25 years experience, retirement planning, transparent fees, personalized strategies',
      'Retail Store': 'e.g., curated selection, friendly staff, local boutique, free gift wrapping, loyalty program',
      'E-commerce': 'e.g., fast shipping, easy returns, quality products, responsive customer service, secure checkout',
      'Photography / Creative': 'e.g., 10+ years experience, natural light specialist, quick turnaround, custom packages, print services',
      'Event Planning': 'e.g., full-service planning, attention to detail, vendor partnerships, stress-free experience, custom themes',
      'Childcare / Education': 'e.g., certified teachers, safe environment, small class sizes, enrichment programs, flexible hours',
    };
    return placeholders[type] || 'e.g., what makes you unique, your specialties, years in business, team qualities, what customers love about you';
  };

  // Industry-specific presets for custom instructions
  const instructionPresets = {
    Restaurant: {
      always: 'thank for dining with us, invite to visit again, mention our signature dishes',
      never: 'sorry for the inconvenience, valued customer, we apologize',
      tone: 'friendly',
    },
    'Dental Practice': {
      always:
        'patient care is our priority, contact us with questions, mention our experienced team',
      never: 'cheap, discount, sorry for any inconvenience',
      tone: 'professional',
    },
    'Medical Practice': {
      always: 'patient health is our priority, schedule a follow-up, mention our caring staff',
      never: 'cheap, sorry, we regret',
      tone: 'professional',
    },
    'Hair Salon / Barbershop': {
      always: 'book your next appointment, mention our skilled stylists',
      never: 'cheap haircut, sorry, discount',
      tone: 'friendly',
    },
    'Auto Repair / Service': {
      always: 'vehicle safety is our priority, free estimates available, certified technicians',
      never: 'cheap fix, sorry for delays',
      tone: 'professional',
    },
    'Real Estate': {
      always:
        'dedicated to finding your dream home, available for questions, local market expertise',
      never: 'sorry, cheap, discount commission',
      tone: 'professional',
    },
    'Home Services': {
      always: 'free estimates, licensed and insured, satisfaction guaranteed',
      never: 'sorry, cheap work, cut corners',
      tone: 'professional',
    },
    'Hotel / Accommodation': {
      always: 'hope to welcome you back, mention our amenities, contact concierge',
      never: 'sorry for inconvenience, we regret, compensation',
      tone: 'friendly',
    },
    'Senior Care': {
      always: 'compassionate care is our priority, mention our dedicated staff, families are welcome to visit',
      never: 'cheap, sorry for inconvenience, we regret',
      tone: 'professional',
    },
    'Therapy / Counseling': {
      always: 'confidential and supportive environment, mention our licensed professionals, scheduling is flexible',
      never: 'cheap, crazy, insane, mental',
      tone: 'professional',
    },
    default: {
      always: '',
      never: '',
      tone: 'professional',
    },
  };

  // Build responseStyle XML from form fields
  const buildResponseStyle = (always, never, tone) => {
    let xml = '';
    if (always?.trim()) {
      xml += `<always>\n${always
        .split(',')
        .map(s => `- ${s.trim()}`)
        .join('\n')}\n</always>\n\n`;
    }
    if (never?.trim()) {
      xml += `<never>\n${never
        .split(',')
        .map(s => `- ${s.trim()}`)
        .join('\n')}\n</never>\n\n`;
    }
    if (tone) {
      const toneDescriptions = {
        professional: 'Professional and courteous tone',
        friendly: 'Warm and friendly tone',
        casual: 'Casual and conversational tone',
      };
      xml += `<style>\n- ${toneDescriptions[tone] || tone}\n</style>`;
    }
    return xml.trim();
  };

  // Sync user data when modal opens or user changes
  useEffect(() => {
    if (isVisible && user?.businessName && !businessName) {
      setBusinessName(user.businessName);
    }
  }, [isVisible, user?.businessName, businessName]);

  useEffect(() => {
    if (isVisible && user?.businessType && !businessType) {
      // Check if user has a custom business type (not in preset list)
      if (businessTypes.includes(user.businessType)) {
        setBusinessType(user.businessType);
      } else {
        // Custom type - show "Other" dropdown + custom value in input
        setBusinessType('Other');
        setCustomBusinessType(user.businessType);
      }
    }
  }, [isVisible, user?.businessType, businessType]);

  // Apply presets when business type changes
  const applyPresets = type => {
    const preset = instructionPresets[type] || instructionPresets.default;
    setAlwaysMention(preset.always);
    setNeverSay(preset.never);
    setSelectedTone(preset.tone);
  };

  const nextStep = async () => {
    if (currentStep === 1 && businessName.trim() && (businessType || customBusinessType.trim())) {
      // Apply industry presets when moving to step 2
      const finalType = businessType === 'Other' ? customBusinessType.trim() : businessType;
      applyPresets(finalType);
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Save profile with custom instructions
      await saveBusinessProfile();
    }
  };

  const saveBusinessProfile = async () => {
    // Use custom type if "Other" is selected, otherwise use dropdown value
    const finalBusinessType = businessType === 'Other' ? customBusinessType.trim() : businessType;

    setLoading(true);
    try {
      // Save keywords to localStorage for Settings to pick up
      if (keywords.trim()) {
        localStorage.setItem('onboardingKeywords', keywords.trim());
      }

      // Save profile WITHOUT businessContext - will be generated in Settings
      await api.put('/auth/profile', {
        businessName: businessName.trim(),
        businessType: finalBusinessType,
        // NO businessContext - user will generate it in Settings
      });
      updateUser({
        businessName: businessName.trim(),
        businessType: finalBusinessType,
      });
      // Complete onboarding
      await api.put('/auth/complete-onboarding');
      updateUser({ onboardingCompleted: true });
      toast.success('Almost there! Generate your business context.');
      onComplete?.();
      // Navigate to Settings where keywords are cached for context generation
      navigate('/settings');
    } catch (error) {
      console.error('Failed to save business profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const generateBusinessContext = async () => {
    if (!keywords.trim()) {
      toast.error('Please enter some keywords');
      return;
    }

    // Use custom type if "Other" is selected
    const finalBusinessType = businessType === 'Other' ? customBusinessType.trim() : businessType;

    // Set loading step BEFORE generatingContext to avoid showing wrong loading texts
    setLoadingStep('1/3 Analyzing your business...');
    setGeneratingContext(true);
    try {
      // Step 1: Generate business context
      const contextResult = await api.post('/personalization/generate-context', {
        keywords: keywords.trim(),
        businessType: finalBusinessType,
        businessName: businessName.trim(),
        field: 'context',
      });
      const context = contextResult.data.generated;
      setGeneratedContext(context);

      // Step 2: Generate sample review based on context
      setLoadingStep('2/3 Creating sample review...');
      const reviewResult = await api.post('/personalization/generate-context', {
        keywords: context,
        businessType: finalBusinessType,
        businessName: businessName.trim(),
        field: 'sample_review',
      });
      const review = reviewResult.data.generated;
      setSampleReview(review);

      // Step 3: Generate response to that review (without counting usage)
      setLoadingStep('3/3 Generating response...');
      const responseResult = await api.post('/generate', {
        reviewText: review,
        tone: 'friendly',
        businessName: businessName.trim(),
        businessContext: context,
        businessType: finalBusinessType,
        isOnboarding: true,
      });
      setSampleResponse(responseResult.data.response);

      // Increment generation count for tier-based loading messages
      const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
      const newCount = genCount + 1;
      localStorage.setItem('generation_count', String(newCount));
      checkMilestoneAchievement(newCount);

      toast.success('Ready! Check out your personalized response below.');
    } catch (error) {
      console.error('Failed to generate:', error);
      toast.error(error.response?.data?.error || 'Failed to generate');
    } finally {
      setGeneratingContext(false);
      setLoadingStep('');
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await api.put('/auth/complete-onboarding');
      updateUser({ onboardingCompleted: true });
      toast.success('Welcome! Complete your profile to get personalized responses.');
      navigate('/settings');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const skipOnboarding = async () => {
    try {
      await api.put('/auth/complete-onboarding');
      onSkip();
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          padding: '0',
          overflow: 'hidden',
          position: 'relative',
          animation: 'slideIn 0.3s ease-out',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            color: 'white',
            padding: '24px',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', margin: '0 0 8px 0' }}>
            Welcome to ReviewResponder!
          </h2>
          <p style={{ margin: 0, opacity: 0.9 }}>Let's personalize your responses in 2 quick steps</p>

          {/* Progress bar */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '20px',
            }}
          >
            {[1, 2].map(step => (
              <div
                key={step}
                style={{
                  width: '32px',
                  height: '4px',
                  borderRadius: '2px',
                  background: step <= currentStep ? 'white' : 'rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '32px', overflowY: 'auto', flex: 1 }}>
          {/* Step 1: Business Name + Type */}
          {currentStep === 1 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                Tell us about your business
              </h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '20px', fontSize: '14px' }}>
                This helps us personalize your review responses
              </p>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Business Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Tony's Pizza, Smith & Co Law Firm"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">Business Type</label>
                <Select
                  options={businessTypeOptions}
                  value={
                    businessType
                      ? businessTypeOptions
                          .flatMap(g => g.options)
                          .find(o => o.value === businessType) || null
                      : null
                  }
                  onChange={option => {
                    const newValue = option?.value || '';
                    setBusinessType(newValue);
                    if (newValue !== 'Other') {
                      setCustomBusinessType('');
                    }
                  }}
                  placeholder="Search or select type..."
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: 'var(--card-bg)',
                      borderColor: state.isFocused ? 'var(--primary)' : 'var(--gray-200)',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                      '&:hover': { borderColor: 'var(--primary)' },
                      borderRadius: '8px',
                      padding: '2px',
                    }),
                    menu: base => ({
                      ...base,
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--gray-200)',
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 10001,
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? 'var(--primary)'
                        : state.isFocused
                          ? 'var(--bg-tertiary)'
                          : 'var(--card-bg)',
                      color: state.isSelected ? 'white' : 'var(--text-primary)',
                      cursor: 'pointer',
                    }),
                    singleValue: base => ({
                      ...base,
                      color: 'var(--text-primary)',
                    }),
                    input: base => ({
                      ...base,
                      color: 'var(--text-primary)',
                    }),
                    groupHeading: base => ({
                      ...base,
                      fontWeight: '600',
                      color: 'var(--gray-700)',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                    }),
                  }}
                />
                {businessType === 'Other' && (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your business type..."
                    value={customBusinessType}
                    onChange={e => setCustomBusinessType(e.target.value)}
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 2: Keywords + Preview */}
          {currentStep === 2 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                Describe your business
              </h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '20px', fontSize: '14px' }}>
                Add keywords to personalize your responses
              </p>

              {/* Keywords Input */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">
                  Keywords about your business
                </label>
                <textarea
                  className="form-input"
                  placeholder={getKeywordsPlaceholder(businessType)}
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  rows={3}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
                <p style={{ fontSize: '12px', color: 'var(--gray-500)', margin: '4px 0 0 0' }}>
                  What makes your business special? The AI will use these in responses.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1px solid var(--gray-200)',
            padding: '20px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {currentStep > 1 && (
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentStep(currentStep - 1)}
              style={{ fontSize: '14px' }}
            >
              ← Back
            </button>
          )}

          <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
            <button
              onClick={skipOnboarding}
              disabled={loading}
              style={{
                fontSize: '15px',
                padding: '12px 24px',
                background: 'transparent',
                border: '2px solid var(--primary-color, #667eea)',
                borderRadius: '8px',
                color: 'var(--primary-color, #667eea)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Skip for now →
            </button>
            {currentStep === 1 ? (
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!businessName.trim() || (!businessType && !customBusinessType.trim())}
                style={{ padding: '12px 24px', fontSize: '15px' }}
              >
                Set up first
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={loading}
                style={{ padding: '12px 24px', fontSize: '15px' }}
              >
                {loading ? 'Saving...' : 'Finish Setup →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Extension Promo Card Component
const ExtensionPromoCard = () => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div id="chrome-extension-demo" className="extension-promo-card">
      <div className="extension-promo-header">
        <Chrome size={24} />
        <h3>Chrome Extension</h3>
        <span className="badge-new">NEW</span>
      </div>

      <div className="extension-promo-content">
        <div className="video-preview">
          {showVideo ? (
            <video
              controls
              autoPlay
              style={{ width: '100%', borderRadius: '12px' }}
              onEnded={() => setShowVideo(false)}
            >
              <source src="/demo-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="video-thumbnail" onClick={() => setShowVideo(true)}>
              <img
                src="/extension-preview.png"
                alt="Chrome Extension Preview"
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="video-thumbnail-fallback" style={{ display: 'none' }}>
                <Chrome size={32} style={{ opacity: 0.5 }} />
              </div>
              <div className="play-overlay">
                <Play size={32} fill="white" />
              </div>
              <span className="video-duration">0:38</span>
            </div>
          )}
        </div>

        <div className="extension-benefits">
          <h4>Respond to Reviews in Seconds</h4>
          <ul>
            <li>
              <CheckCircle size={16} /> One-click responses on Google Maps
            </li>
            <li>
              <CheckCircle size={16} /> Works on Yelp, TripAdvisor & more
            </li>
            <li>
              <CheckCircle size={16} /> Auto-detects review language
            </li>
          </ul>

          <div className="extension-ctas">
            <a
              href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={18} />
              Add to Chrome
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single');
  const [stats, setStats] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);
  const [platform, setPlatform] = useState('google');
  const [tone, setTone] = useState('professional');
  const [recommendedTone, setRecommendedTone] = useState(null);
  const [hasManuallyChangedTone, setHasManuallyChangedTone] = useState(false);
  // V4.3.15: outputLanguage REMOVED - LLM auto-detects from review (V4.3.7)
  // AI model selection removed - all responses use Claude Sonnet 4
  const [generating, setGenerating] = useState(false);
  const [response, setResponse] = useState('');
  // Smart Bracket V2.0 - Info about why brackets appeared
  const [bracketInfo, setBracketInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [editingResponseId, setEditingResponseId] = useState(null);
  const [editedResponseText, setEditedResponseText] = useState('');
  const [savingResponse, setSavingResponse] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customInstructions, setCustomInstructions] = useState(user?.responseStyle || '');
  // One-time instructions (Quick Adds in Generator) - reset after each generation
  const [oneTimeInstructions, setOneTimeInstructions] = useState('');
  const [showOneTimeQuickAdds, setShowOneTimeQuickAdds] = useState(false);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [askUserEnabled, setAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false'; // Default ON
  });
  // V4.0 Task 2.6.2: Include Emojis toggle (user override for emoji inclusion)
  const [includeEmojis, setIncludeEmojis] = useState(() => {
    const stored = localStorage.getItem('includeEmojis');
    return stored === null ? null : stored === 'true'; // null = auto (default), true/false = user override
  });
  // Include Signature toggle - sign responses with business name (default: ON)
  const [includeSignature, setIncludeSignature] = useState(() => {
    const stored = localStorage.getItem('includeSignature');
    if (stored !== null) return stored === 'true';
    return user?.includeSignature ?? true; // Default ON
  });
  // Response Length setting - auto or user override (short/medium/detailed/comprehensive)
  const [responseLength, setResponseLength] = useState(() => {
    const stored = localStorage.getItem('responseLength');
    return stored || 'auto'; // Default: auto (AI calculates based on review complexity)
  });

  // Quick Templates for one-click instruction snippets
  const quickTemplates = [
    // Priority templates (always visible)
    {
      id: 'contact',
      emoji: '📞',
      label: 'Contact Info',
      snippet: '<always>\n- Mention our phone/email for questions\n</always>',
      category: 'always',
      priority: true,
    },
    {
      id: 'no-fluff',
      emoji: '🚫',
      label: 'No Fluff',
      snippet: '<never>\n- Use generic phrases like "valued customer"\n</never>',
      category: 'never',
      priority: true,
    },
    {
      id: 'short',
      emoji: '✂️',
      label: 'Short & Sweet',
      snippet: '<style>\n- Keep under 50 words\n</style>',
      category: 'style',
      priority: true,
    },
    {
      id: 'use-name',
      emoji: '👋',
      label: 'Use Name',
      snippet: "<always>\n- Use reviewer's name if mentioned\n</always>",
      category: 'always',
      priority: true,
    },
    {
      id: 'offer',
      emoji: '🎁',
      label: 'Make Offer',
      snippet: '<always>\n- Offer something (discount, free item, upgrade)\n</always>',
      category: 'always',
      priority: true,
    },
    {
      id: 'offer-support',
      emoji: '🤝',
      label: 'Offer Support',
      snippet: '<always>\n- Offer to help resolve any issues or answer questions\n</always>',
      category: 'always',
      priority: true,
    },
    {
      id: 'no-sorry',
      emoji: '🚫',
      label: 'No "Sorry"',
      snippet: '<never>\nNEVER say: sorry, apologies, regret, unfortunately\n</never>',
      category: 'never',
      priority: true,
    },
    {
      id: 'no-invite-back',
      emoji: '🚫',
      label: 'No Invite Back',
      snippet: '<never>\nNEVER invite them back or say "hope to see you again"\n</never>',
      category: 'never',
      priority: true,
    },
    {
      id: 'push-back',
      emoji: '✋',
      label: 'Push Back',
      snippet: '<style>\nRespectfully disagree with the critique - don\'t admit fault\n</style>',
      category: 'style',
      priority: true,
    },
    {
      id: 'sign-name',
      emoji: '✍️',
      label: 'Sign Name',
      snippet: '<signoff>\n- [Your Name], Owner\n</signoff>',
      category: 'signoff',
      priority: true,
    },
    {
      id: 'invite-back',
      emoji: '📅',
      label: 'Invite Back',
      snippet: '<always>\n- Invite them to return\n</always>',
      category: 'always',
      priority: true,
    },
    // Additional templates (shown when expanded)
    {
      id: 'website',
      emoji: '🌐',
      label: 'Website',
      snippet: '<always>\n- Include our website for more info\n</always>',
      category: 'always',
      priority: false,
    },
    {
      id: 'no-excuses',
      emoji: '🚫',
      label: 'No Excuses',
      snippet: '<never>\n- Make excuses or blame others\n</never>',
      category: 'never',
      priority: false,
    },
    {
      id: 'no-pricing',
      emoji: '🚫',
      label: 'No Pricing',
      snippet: '<never>\n- Mention pricing or discounts\n</never>',
      category: 'never',
      priority: false,
    },
    {
      id: 'detailed',
      emoji: '📝',
      label: 'Detailed',
      snippet: '<style>\n- Write 3-4 sentences with details\n</style>',
      category: 'style',
      priority: false,
    },
    {
      id: 'casual',
      emoji: '😊',
      label: 'Casual',
      snippet: '<style>\n- Casual, conversational tone\n</style>',
      category: 'style',
      priority: false,
    },
    {
      id: 'formal',
      emoji: '🎩',
      label: 'Very Formal',
      snippet: '<style>\n- Highly formal, corporate language\n</style>',
      category: 'style',
      priority: false,
    },
    {
      id: 'no-emojis',
      emoji: '🚫',
      label: 'No Emojis',
      snippet: '<style>\n- No emojis\n</style>',
      category: 'style',
      priority: false,
    },
    {
      id: 'with-emojis',
      emoji: '✨',
      label: 'With Emojis',
      snippet: '<style>\n- Include 1-2 relevant emojis\n</style>',
      category: 'style',
      priority: false,
    },
    {
      id: 'team-sign',
      emoji: '👥',
      label: 'Team Sign',
      snippet: '<signoff>\n- The [Business] Team\n</signoff>',
      category: 'signoff',
      priority: false,
    },
    {
      id: 'no-sign',
      emoji: '🚫',
      label: 'No Signature',
      snippet: '<signoff>\n- No signature or sign-off\n</signoff>',
      category: 'signoff',
      priority: false,
    },
    {
      id: 'negative-fix',
      emoji: '🔴',
      label: 'Negative Fix',
      snippet: '<context>\n- Offer to make it right: contact us directly\n</context>',
      category: 'context',
      priority: false,
    },
    {
      id: 'ask-referral',
      emoji: '🟢',
      label: 'Ask Referral',
      snippet: '<context>\n- Ask for referrals/recommendations\n</context>',
      category: 'context',
      priority: false,
    },
    {
      id: 'share-others',
      emoji: '⭐',
      label: 'Share Others',
      snippet: '<context>\n- Encourage to share on other platforms\n</context>',
      category: 'context',
      priority: false,
    },
  ];

  // Check which templates are already in customInstructions
  const isTemplateActive = template => {
    // Check if the core instruction text is present (without the tags)
    const coreText = template.snippet
      .replace(/<\/?[a-z]+>/g, '')
      .replace(/\n/g, '')
      .trim();
    return customInstructions.includes(coreText) || customInstructions.includes(template.snippet);
  };

  const addQuickTemplate = template => {
    if (isTemplateActive(template)) return;
    setCustomInstructions(prev =>
      prev.trim() ? `${prev.trim()}\n\n${template.snippet}` : template.snippet
    );
  };

  // Check which templates are already in ONE-TIME instructions (Generator)
  const isOneTimeTemplateActive = template => {
    const coreText = template.snippet
      .replace(/<\/?[a-z]+>/g, '')
      .replace(/\n/g, '')
      .trim();
    return oneTimeInstructions.includes(coreText) || oneTimeInstructions.includes(template.snippet);
  };

  // Add template to ONE-TIME instructions (used in Generator - resets after each generation)
  const addOneTimeTemplate = template => {
    if (isOneTimeTemplateActive(template)) return;
    setOneTimeInstructions(prev =>
      prev.trim() ? `${prev.trim()}\n\n${template.snippet}` : template.snippet
    );
  };

  // Remove template from ONE-TIME instructions
  const removeOneTimeTemplate = template => {
    setOneTimeInstructions(prev => {
      // Try to remove the full snippet first
      let result = prev.replace(template.snippet, '');
      // Also try core text without tags
      const coreText = template.snippet.replace(/<\/?[a-z]+>/g, '').replace(/\n/g, ' ').replace(/- /g, '').trim();
      if (result === prev) {
        result = prev.replace(coreText, '');
      }
      // Clean up extra newlines
      return result.replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '').trim();
    });
  };

  // Clear one-time instructions (called after generation)
  const clearOneTimeInstructions = () => {
    setOneTimeInstructions('');
  };

  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editTemplateContent, setEditTemplateContent] = useState('');
  const [updatingTemplate, setUpdatingTemplate] = useState(false);

  // Bulk generation state
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPlatform, setBulkPlatform] = useState('google');
  const [bulkTone, setBulkTone] = useState('professional');
  // V4.3.15: bulkOutputLanguage REMOVED - LLM auto-detects from review (V4.3.7)
  // bulkAiModel removed - all responses use Claude Sonnet 4
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Export state
  const [allHistory, setAllHistory] = useState([]);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  // Feedback popup state
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackDisplayName, setFeedbackDisplayName] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isFirstResponse, setIsFirstResponse] = useState(false);
  const [showPersonalizeBanner, setShowPersonalizeBanner] = useState(false);

  // Keyboard shortcuts help modal
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Upgrade Modal state (when user hits limit)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [exitSurveyReason, setExitSurveyReason] = useState(null);

  // Dashboard error state
  const [dashboardError, setDashboardError] = useState(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);

  // Effective plan (uses team owner's plan if user is a team member)
  const effectivePlan = stats?.subscription?.plan || user?.plan || 'free';
  const isTeamMember = stats?.isTeamMember || false;

  // Referral state
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [copiedReferralLink, setCopiedReferralLink] = useState(false);

  // Bracket count for UI indicators (uses global countBrackets function)
  const bracketCount = useMemo(() => countBrackets(response), [response]);

  // Auto-Tone: SIMPLIFIED - Only star rating based (no unreliable text guessing)
  // Removed Tier 2 (emoji) and Tier 3 (keywords) because:
  // - "not bad" matched "bad" → false negative
  // - "wasn't great" matched "great" → false positive
  // - Sarcasm impossible to detect with substring matching
  const analyzeSentiment = (text, starRating) => {
    // Only use star rating - this is 100% reliable
    if (starRating && starRating > 0) {
      const stars = parseInt(starRating);
      if (stars >= 4) return 'positive';
      if (stars === 1) return 'negative';
      return 'neutral';
    }
    // No star rating = no guessing, user picks tone manually
    return null;
  };

  const mapSentimentToTone = sentiment => {
    switch (sentiment) {
      case 'positive':
        return 'friendly';
      case 'negative':
        return 'apologetic';
      default:
        return 'professional';
    }
  };

  // Auto-Tone useEffect: Only auto-select tone when star rating is provided
  // Without star rating, user must select tone manually (no unreliable guessing)
  useEffect(() => {
    if (rating > 0 && !hasManuallyChangedTone) {
      // Star rating provided - we can reliably detect sentiment
      const sentiment = analyzeSentiment(reviewText, rating);
      if (sentiment) {
        const recommended = mapSentimentToTone(sentiment);
        setRecommendedTone(recommended);
        setTone(recommended);
      }
    }

    // Reset when cleared
    if (reviewText.trim().length === 0 && rating === 0) {
      setRecommendedTone(null);
      setHasManuallyChangedTone(false);
    }
  }, [reviewText, rating, hasManuallyChangedTone]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Check if user is typing in an input/textarea
      const activeEl = document.activeElement;
      const isTyping =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable);

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl+/ or Cmd+/ - Show keyboard help
      if (ctrlOrCmd && e.key === '/') {
        e.preventDefault();
        setShowKeyboardHelp(prev => !prev);
        return;
      }

      // Ctrl+Enter or Cmd+Enter - Generate Response (works even when typing)
      if (ctrlOrCmd && e.key === 'Enter' && activeTab === 'single') {
        e.preventDefault();
        // Trigger generate button click
        document.querySelector('[data-action="generate"]')?.click();
        return;
      }

      // Skip other shortcuts if typing
      if (isTyping) return;

      // Ctrl+Shift+Enter - Copy Response
      if (ctrlOrCmd && e.shiftKey && e.key === 'Enter') {
        if (response) {
          e.preventDefault();
          navigator.clipboard.writeText(response);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast.success('Response copied!');
        }
        return;
      }

      // Ctrl+Backspace - Clear all
      if (ctrlOrCmd && e.key === 'Backspace') {
        e.preventDefault();
        setReviewText('');
        setResponse('');
        setRating(0);
        setShowKeyboardHelp(false);
        toast.success('Cleared!');
        return;
      }

      // Ctrl+1/2/3/4 - Change Tone
      const tones = ['professional', 'friendly', 'formal', 'apologetic'];
      if (ctrlOrCmd && !e.shiftKey && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const toneIndex = parseInt(e.key) - 1;
        setTone(tones[toneIndex]);
        toast.success(
          `Tone: ${tones[toneIndex].charAt(0).toUpperCase() + tones[toneIndex].slice(1)}`
        );
        return;
      }

    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, response, setTone, setReviewText, setResponse, setRating, setCopied]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get('plan');
    const stripeSuccess = params.get('success');
    const showOnboardingParam = params.get('showOnboarding');

    const loadDashboard = async () => {
      setIsLoadingDashboard(true);
      setDashboardError(null);
      try {
        // Always fetch fresh user data to get hasAnyGeneration for onboarding logic
        let freshUserData = null;
        try {
          console.log('🔄 Fetching fresh user data...');
          const res = await api.get('/auth/me');
          freshUserData = res.data.user;
          console.log('✅ Got user data:', freshUserData);
          updateUser(freshUserData);
        } catch (e) {
          console.error('❌ Failed to refresh user data:', e);
        }

        // Onboarding redirect logic based on hasAnyGeneration
        // Users who generated anywhere (widget, demo) but have no settings → show banner instead of redirect
        // Only absolute newcomers (never generated) get the onboarding modal
        if (freshUserData) {
          const hasGeneratedAnywhere = freshUserData.hasAnyGeneration;
          const hasSettings = freshUserData.responseStyle?.trim();

          if (hasGeneratedAnywhere && !hasSettings) {
            // User has tried the product but hasn't set up their style yet
            // Don't redirect - let them use the dashboard, the "Set Up Now" banner is already visible
            console.log('📍 User has generated but no settings → showing setup banner (no redirect)');
            // The "Set Up Your Business Profile" card is already shown on dashboard
          }

          // Only show onboarding for absolute newcomers (never generated anywhere)
          if (!hasGeneratedAnywhere && !freshUserData.onboardingCompleted && showOnboardingParam !== 'true') {
            console.log('👋 Absolute newcomer → showing onboarding');
            setShowOnboarding(true);
          }
        }

        await Promise.all([fetchStats(), fetchHistory(), fetchTemplates(), fetchAllHistory()]);
      } catch (error) {
        console.error('Dashboard load error:', error);
        setDashboardError(
          'Failed to load dashboard data. Please check your connection and try again.'
        );
      } finally {
        setIsLoadingDashboard(false);
      }
    };
    loadDashboard();

    // Check if user explicitly requested onboarding via URL param
    if (showOnboardingParam === 'true') {
      setShowOnboarding(true);
      // Clean up URL
      window.history.replaceState({}, '', '/dashboard');
    }
    if (user && user.responsesUsed === 0) setIsFirstResponse(true);

    // Handle plan change from admin (new format with ?plan=xxx&_t=timestamp)
    if (planParam) {
      toast.success(`Plan changed to ${planParam.toUpperCase()}!`);
      window.history.replaceState({}, '', '/dashboard');
    }

    // Handle Stripe success
    if (stripeSuccess) {
      window.history.replaceState({}, '', '/dashboard');
      toast.success('Subscription activated! Thank you for subscribing.');
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    updateUser({ onboardingCompleted: true });
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    updateUser({ onboardingCompleted: true });
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      throw error;
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/responses/history?limit=10');
      setHistory(res.data.responses || []);
    } catch (error) {
      // 403 = Free user, history is Starter+ feature - this is expected
      if (error.response?.status === 403) {
        setHistory([]);
        return;
      }
      console.error('Failed to fetch history:', error);
      throw error;
    }
  };

  const handleEditResponse = (item) => {
    setEditingResponseId(item.id);
    setEditedResponseText(item.generated_response);
  };

  const handleCancelEdit = () => {
    setEditingResponseId(null);
    setEditedResponseText('');
  };

  const handleSaveResponse = async (id) => {
    if (!editedResponseText.trim()) {
      toast.error('Response cannot be empty');
      return;
    }
    setSavingResponse(true);
    try {
      await api.put(`/responses/${id}`, { generated_response: editedResponseText });
      // Update local state
      setHistory(prev => prev.map(item =>
        item.id === id ? { ...item, generated_response: editedResponseText } : item
      ));
      setEditingResponseId(null);
      setEditedResponseText('');
      toast.success('Response saved!');
    } catch (error) {
      console.error('Failed to save response:', error);
      toast.error('Failed to save response');
    } finally {
      setSavingResponse(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Templates are optional, don't throw
    }
  };

  const fetchReferral = async () => {
    setLoadingReferral(true);
    try {
      const res = await api.get('/referrals');
      setReferralData(res.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      // Referrals are optional, don't throw
    } finally {
      setLoadingReferral(false);
    }
  };

  const fetchAllHistory = async () => {
    try {
      const res = await api.get('/responses/history?limit=1000');
      setAllHistory(res.data.responses || []);
    } catch (error) {
      // 403 = Free user, history is Starter+ feature - expected
      if (error.response?.status === 403) {
        setAllHistory([]);
        return;
      }
      console.error('Failed to fetch all history:', error);
      // All history is for export, don't throw
    }
  };

  // Check if user should see feedback popup (with escalating delays after "Maybe later")
  const checkFeedbackStatus = async () => {
    // Never show again if user already submitted feedback
    if (localStorage.getItem('feedbackSubmitted')) return;

    try {
      const res = await api.get('/feedback/status');
      const { responsesUsed, feedbackSubmitted } = res.data;

      if (feedbackSubmitted) {
        localStorage.setItem('feedbackSubmitted', 'true');
        return;
      }

      // Check localStorage for "maybe later" tracking
      const maybeLaterData = JSON.parse(localStorage.getItem('feedbackMaybeLater') || '{}');
      const maybeLaterCount = maybeLaterData.count || 0;
      const maybeLaterAt = maybeLaterData.at || 0;

      // Calculate when to show next
      let showAfterResponses = 3; // Default: after 3 responses

      if (maybeLaterCount > 0) {
        // Escalating delays: 12, 30, 75, then always 100
        const delays = [12, 30, 75, 100];
        const delay = delays[Math.min(maybeLaterCount - 1, delays.length - 1)];
        showAfterResponses = maybeLaterAt + delay;
      }

      if (responsesUsed >= showAfterResponses) {
        setTimeout(() => setShowFeedbackPopup(true), 1500);
      }
    } catch (error) {
      console.error('Failed to check feedback status');
    }
  };

  // Handle "Maybe later" - escalating delays
  const handleFeedbackMaybeLater = async () => {
    try {
      const res = await api.get('/feedback/status');
      const currentResponses = res.data.responsesUsed;

      const maybeLaterData = JSON.parse(localStorage.getItem('feedbackMaybeLater') || '{}');
      const newCount = (maybeLaterData.count || 0) + 1;

      localStorage.setItem('feedbackMaybeLater', JSON.stringify({
        count: newCount,
        at: currentResponses
      }));
    } catch (error) {
      // Fallback: just increment count
      const maybeLaterData = JSON.parse(localStorage.getItem('feedbackMaybeLater') || '{}');
      localStorage.setItem('feedbackMaybeLater', JSON.stringify({
        count: (maybeLaterData.count || 0) + 1,
        at: maybeLaterData.at || 0
      }));
    }
    setShowFeedbackPopup(false);
  };

  // Submit feedback
  const submitFeedback = async e => {
    e.preventDefault();
    if (feedbackRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setSubmittingFeedback(true);
    try {
      await api.post('/feedback', {
        rating: feedbackRating,
        comment: feedbackComment.trim() || null,
        displayName: feedbackDisplayName.trim() || null,
      });
      toast.success('Thank you for your feedback!');
      setShowFeedbackPopup(false);
      setFeedbackRating(0);
      setFeedbackComment('');
      setFeedbackDisplayName('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const getFilteredHistory = () => {
    let f = allHistory;
    if (exportDateFrom) f = f.filter(i => new Date(i.created_at) >= new Date(exportDateFrom));
    if (exportDateTo) {
      const e = new Date(exportDateTo);
      e.setHours(23, 59, 59, 999);
      f = f.filter(i => new Date(i.created_at) <= e);
    }
    return f;
  };

  const exportToCSV = () => {
    if (!canExport) {
      toast.info('Upgrade to Starter to export your responses');
      return;
    }
    setExporting(true);
    try {
      const f = getFilteredHistory();
      if (f.length === 0) {
        toast.error('No responses');
        setExporting(false);
        return;
      }
      const d = f.map(i => ({
        Date: new Date(i.created_at).toLocaleDateString(),
        Platform: i.review_platform || 'N/A',
        Rating: i.review_rating || 'N/A',
        Tone: i.tone || 'professional',
        Review: i.review_text,
        Response: i.generated_response,
      }));
      const csv = Papa.unparse(d);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `responses-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success(`Exported ${f.length} to CSV`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = () => {
    if (!canExport) {
      toast.info('Upgrade to Starter to export your responses');
      return;
    }
    setExporting(true);
    try {
      const f = getFilteredHistory();
      if (f.length === 0) {
        toast.error('No responses');
        setExporting(false);
        return;
      }
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('Response History', 14, 20);
      doc.setFontSize(10);
      doc.text(`${new Date().toLocaleDateString()} | ${f.length} responses`, 14, 28);
      const t = f.map(i => [
        new Date(i.created_at).toLocaleDateString(),
        i.review_platform || '-',
        i.review_rating || '-',
        i.review_text.substring(0, 40) + '...',
        i.generated_response.substring(0, 50) + '...',
      ]);
      doc.autoTable({
        startY: 35,
        head: [['Date', 'Platform', 'Rating', 'Review', 'Response']],
        body: t,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [79, 70, 229] },
      });
      doc.save(`responses-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Exported ${f.length} to PDF`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSavingTemplate(true);
    try {
      await api.post('/templates', {
        name: templateName.trim(),
        content: response,
        tone,
        platform,
      });
      toast.success('Template saved!');
      setShowSaveTemplateModal(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async templateId => {
    if (!window.confirm('Delete this template?')) return;

    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
      if (selectedTemplate === templateId.toString()) {
        setSelectedTemplate('');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplateModal = templateId => {
    const template = templates.find(t => t.id.toString() === templateId);
    if (template) {
      setEditingTemplate(template);
      setEditTemplateName(template.name);
      setEditTemplateContent(template.content);
      setShowEditTemplateModal(true);
    }
  };

  const updateTemplate = async () => {
    if (!editTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!editTemplateContent.trim()) {
      toast.error('Template content cannot be empty');
      return;
    }

    setUpdatingTemplate(true);
    try {
      await api.put(`/templates/${editingTemplate.id}`, {
        name: editTemplateName.trim(),
        content: editTemplateContent.trim(),
      });
      toast.success('Template updated!');
      setShowEditTemplateModal(false);
      setEditingTemplate(null);
      setEditTemplateName('');
      setEditTemplateContent('');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update template');
    } finally {
      setUpdatingTemplate(false);
    }
  };

  const applyTemplate = templateId => {
    const template = templates.find(t => t.id.toString() === templateId);
    if (template) {
      setResponse(template.content);
      setTone(template.tone || 'professional');
      setPlatform(template.platform || 'google');
      toast.success('Template applied as starting point');
    }
  };

  const generateResponse = async (overrideTone = null) => {
    if (!reviewText.trim()) {
      toast.error('Please enter a review to respond to');
      return;
    }

    const useTone = overrideTone || tone;
    if (overrideTone) {
      setTone(overrideTone);
    }

    setGenerating(true);
    setResponse('');
    setBracketInfo(null); // Reset V2.0 bracket info for new generation

    // Get template content if a template is selected
    const activeTemplate = selectedTemplate
      ? templates.find(t => t.id.toString() === selectedTemplate)
      : null;

    try {
      const res = await api.post('/generate', {
        reviewText,
        reviewRating: rating || null,
        platform,
        tone: useTone,
        // V4.3.15: outputLanguage REMOVED - LLM auto-detects from review (V4.3.7)
        businessName: user.businessName,
        customInstructions: customInstructions.trim() || undefined,
        oneTimeInstructions: oneTimeInstructions.trim() || undefined, // One-time Quick Adds
        templateContent: activeTemplate?.content || undefined,
        askUserEnabled,
        includeEmojis, // V4.0 Task 2.6.2: User override for emoji inclusion (null = auto)
        includeSignature, // Sign responses with business name
        responseLength, // Response length: 'auto' or explicit (short/medium/detailed/comprehensive)
      });

      setResponse(res.data.response);
      // Smart Bracket V2.0 - Store bracket info for UX feedback
      setBracketInfo(res.data.bracketInfo || null);
      // Reset one-time instructions after successful generation
      clearOneTimeInstructions();

      // Increment generation count for tier-based loading messages
      const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
      const newCount = genCount + 1;
      localStorage.setItem('generation_count', String(newCount));
      checkMilestoneAchievement(newCount);

      updateUser({
        responsesUsed: res.data.responsesUsed,
        responsesLimit: res.data.responsesLimit,
      });
      fetchStats(); // Refresh usage
      fetchHistory();

      // Fire confetti on first response!
      if (isFirstResponse) {
        fireConfetti();
        toast.success('Congratulations on your first response!', { icon: '✅', duration: 4000 });
        setIsFirstResponse(false);
        // Show personalize banner if user hasn't completed onboarding AND has no responseStyle configured
        if (!user?.onboardingCompleted && !user?.responseStyle?.trim()) {
          setTimeout(() => setShowPersonalizeBanner(true), 2000);
        }
      } else {
        toast.success('Ready to craft!');
      }

      // Check if user should see feedback popup
      checkFeedbackStatus();
    } catch (error) {
      if (error.response?.data?.showUpgradeModal || error.response?.data?.upgrade) {
        // Show upgrade modal instead of just a toast
        setShowUpgradeModal(true);
        setExitSurveyReason(null); // Reset survey state
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
        if (error.response.data.suggestion) {
          toast.info(error.response.data.suggestion, { duration: 5000 });
        }
      } else {
        toast.error('Failed to generate response');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Track exit survey response
  const trackExitSurvey = async reason => {
    try {
      await api.post('/analytics/exit-survey', { reason });
    } catch (err) {
      console.error('Failed to track exit survey:', err);
    }
    setShowUpgradeModal(false);
    setExitSurveyReason(null);
  };

  // Buy response pack (micro-pricing)
  const handleBuyResponsePack = async () => {
    try {
      const res = await api.post('/billing/buy-responses');
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  // Direct upgrade to Starter plan from modal
  const handleUpgradeToStarter = async () => {
    try {
      setShowUpgradeModal(false);
      const res = await api.post('/billing/create-checkout', { plan: 'starter' });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse bulk input (supports multiple formats)
  const parseBulkInput = input => {
    if (!input) return [];
    // Try to detect CSV format (quoted strings with commas)
    if (input.includes('","') || input.startsWith('"')) {
      const matches = input.match(/"([^"]+)"/g);
      if (matches) {
        return matches.map(m => m.slice(1, -1).trim()).filter(r => r.length > 0);
      }
    }
    // Try separator format: ---
    if (input.includes('---')) {
      return input
        .split('---')
        .map(r => r.trim())
        .filter(r => r.length > 0);
    }
    // Try numbered format: "1.", "2.", etc. at start of line
    const numberedPattern = /^\d+\.\s/m;
    if (numberedPattern.test(input)) {
      const reviews = [];
      let currentReview = '';
      const lines = input.split('\n');
      for (const line of lines) {
        if (/^\d+\.\s/.test(line.trim())) {
          if (currentReview.trim()) {
            reviews.push(currentReview.trim());
          }
          currentReview = line.trim().replace(/^\d+\.\s*/, '');
        } else if (line.trim()) {
          currentReview += ' ' + line.trim();
        }
      }
      if (currentReview.trim()) {
        reviews.push(currentReview.trim());
      }
      if (reviews.length > 0) {
        return reviews;
      }
    }
    // Try double-newline separated (paragraphs)
    if (input.includes('\n\n')) {
      const paragraphs = input
        .split(/\n\n+/)
        .map(p => p.replace(/\n/g, ' ').trim())
        .filter(p => p.length > 0);
      if (paragraphs.length > 1) {
        return paragraphs;
      }
    }
    // Fallback: single-line separated
    const lines = input
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    if (lines.length > 1) {
      return lines;
    }
    // Single review
    return [input.trim()].filter(r => r.length > 0);
  };

  const generateBulkResponses = async () => {
    const reviews = parseBulkInput(bulkInput);
    if (reviews.length === 0) {
      toast.error('Please enter at least one review');
      return;
    }
    if (reviews.length > 20) {
      toast.error('Maximum 20 reviews per batch');
      return;
    }
    setBulkGenerating(true);
    setBulkResults(null);
    try {
      const res = await api.post('/generate-bulk', {
        reviews,
        platform: bulkPlatform,
        tone: bulkTone,
        // V4.3.15: outputLanguage REMOVED - LLM auto-detects from review (V4.3.7)
      });
      setBulkResults(res.data);
      updateUser({
        responsesUsed: res.data.responsesUsed,
        responsesLimit: res.data.responsesLimit,
      });
      fetchStats(); // Refresh usage
      fetchHistory();
      toast.success(`Generated ${res.data.summary.successful} responses!`);
    } catch (error) {
      if (error.response?.data?.upgrade) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.requiredPlan) {
        toast.error('Bulk generation requires a paid plan (Starter, Pro, or Unlimited)');
      } else {
        toast.error(error.response?.data?.error || 'Failed to generate responses');
      }
    } finally {
      setBulkGenerating(false);
    }
  };

  const copyBulkResponse = (responseText, index) => {
    navigator.clipboard.writeText(responseText);
    setCopiedIndex(index);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllResponses = () => {
    if (!bulkResults) return;
    const allResponses = bulkResults.results
      .filter(r => r.success)
      .map((r, i) => `Review ${i + 1}:\n${r.review}\n\nResponse:\n${r.response}`)
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(allResponses);
    toast.success('All responses copied!');
  };

  // Use effectivePlan for team members (they get access to team owner's plan features)
  const canUseBulk = ['professional', 'unlimited'].includes(effectivePlan);
  const canUseApi = effectivePlan === 'unlimited';
  const canExport = ['starter', 'professional', 'unlimited'].includes(effectivePlan);
  const canUseHistory = ['starter', 'professional', 'unlimited'].includes(effectivePlan);

  // Calculate usage percentages from stats
  // Calculate usage percent from simplified API response (all responses use Claude Sonnet 4)
  const usagePercent = stats?.usage?.limit
    ? (stats.usage.used / stats.usage.limit) * 100
    : user
      ? (user.responsesUsed / user.responsesLimit) * 100
      : 0;
  const responsesRemaining = stats?.usage?.remaining ?? 0;

  return (
    <div className="dashboard container">
      {/* Onboarding Modal */}
      <OnboardingModal
        isVisible={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* First Response Guidance Banner - shown when user has 0 responses */}
      {isFirstResponse && !showOnboarding && (
        <div
          style={{
            background: 'var(--hero-gradient)',
            border: '1px solid #818CF8',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '32px' }}>👋</span>
            <div>
              <p style={{ margin: 0, fontWeight: '700', fontSize: '18px', color: '#3730A3' }}>
                Welcome! Craft your first response
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#4F46E5' }}>
                Paste any review below and craft your first response.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              // Scroll to generator form
              document.querySelector('textarea')?.focus();
            }}
            className="btn btn-primary"
            style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}
          >
            Try it now →
          </button>
        </div>
      )}

      {/* Personalize Banner - shown after first response if onboarding not complete */}
      {showPersonalizeBanner && !showOnboarding && (
        <div
          style={{
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            border: '1px solid #10B981',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Sparkles size={32} color="#059669" />
            <div>
              <p style={{ margin: 0, fontWeight: '700', fontSize: '18px', color: '#065F46' }}>
                Want responses that match your brand?
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#047857' }}>
                Personalize your ReviewResponder. All future responses will automatically match
                your tone and style.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => {
                // Copy last response to clipboard if available
                if (generatedResponse) {
                  navigator.clipboard.writeText(generatedResponse);
                  toast.success('Response copied to clipboard!', { icon: '📋' });
                }
                setShowPersonalizeBanner(false);
                navigate('/settings');
              }}
              className="btn btn-primary"
              style={{
                padding: '10px 20px',
                whiteSpace: 'nowrap',
                background: '#059669',
                borderColor: '#059669',
              }}
            >
              Personalize Now
            </button>
            <button
              onClick={() => setShowPersonalizeBanner(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '8px',
                fontSize: '14px',
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Error Banner */}
      {dashboardError && (
        <div
          style={{
            background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
            border: '1px solid #F87171',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <p style={{ margin: 0, fontWeight: '600', color: '#B91C1C' }}>Connection Error</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#DC2626' }}>
                {dashboardError}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
            style={{ padding: '8px 16px', background: '#DC2626', border: 'none' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoadingDashboard && !dashboardError && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 0',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            className="spinner"
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E5E7EB',
              borderTop: '4px solid #4F46E5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          ></div>
          <p style={{ color: 'var(--gray-500)' }}>Loading dashboard...</p>
        </div>
      )}

      <div
        className="dashboard-header"
        style={{ display: isLoadingDashboard && !dashboardError ? 'none' : undefined }}
      >
        <div>
          <h1 className="dashboard-title">Welcome, {user?.name || user?.businessName || 'there'}!</h1>
          <p style={{ color: 'var(--gray-500)' }}>Craft psychology-based review responses</p>
        </div>
        <div
          className="dashboard-header-actions"
          style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
        >
          <button
            onClick={() => setShowKeyboardHelp(true)}
            className="btn btn-secondary"
            style={{ padding: '8px 12px' }}
            title="Keyboard Shortcuts (Ctrl+/)"
          >
            <Keyboard size={16} />
          </button>
          <Link to="/analytics" className="btn btn-secondary" style={{ padding: '8px 16px' }}>
            <BarChart2 size={16} />
            Analytics
            {!['professional', 'unlimited'].includes(effectivePlan) && (
              <span
                style={{
                  background: 'var(--primary-600)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: '600',
                  marginLeft: '4px',
                }}
              >
                PRO
              </span>
            )}
          </Link>
          <Link to="/team" className="btn btn-secondary" style={{ padding: '8px 16px' }}>
            <Users size={16} />
            Team
            {!['professional', 'unlimited'].includes(effectivePlan) && !isTeamMember && (
              <span
                style={{
                  background: 'var(--primary-600)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: '600',
                  marginLeft: '4px',
                }}
              >
                PRO
              </span>
            )}
          </Link>
          <Link to="/settings" className="btn btn-secondary" style={{ padding: '8px 16px' }}>
            <Settings size={16} />
            Settings
          </Link>
          <span className={`badge ${effectivePlan === 'free' ? 'badge-warning' : 'badge-success'}`}>
            {isTeamMember
              ? `TEAM (${effectivePlan?.toUpperCase()})`
              : `${effectivePlan?.toUpperCase()} Plan`}
          </span>
          {effectivePlan === 'free' && !isTeamMember && (
            <Link to="/pricing" className="btn btn-primary" style={{ padding: '8px 16px' }}>
              Upgrade
            </Link>
          )}
        </div>
      </div>

      {/* Business Context Setup Banner - show if context is empty or very short */}
      {(!user?.businessContext || user?.businessContext?.length < 50) && (
        <div
          style={{
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            border: '2px solid #f59e0b',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div
              style={{
                background: '#f59e0b',
                borderRadius: '50%',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={28} style={{ color: 'white' }} />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  marginBottom: '4px',
                  color: '#92400e',
                }}
              >
                ⚡ Set Up Your Business Profile
              </h3>
              <p style={{ fontSize: '14px', color: '#a16207', margin: 0 }}>
                Add your business details to craft <strong>personalized responses</strong> that
                mention your specific services, team members, and unique selling points.
              </p>
            </div>
            <Link
              to="/settings"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: '#92400e',
                color: 'white',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(146, 64, 14, 0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              <Settings size={18} />
              Set Up Now
            </Link>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div
        className="dashboard-tabs"
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '1px solid var(--gray-200)',
          paddingBottom: '0',
        }}
      >
        <button
          className="dashboard-tab"
          onClick={() => setActiveTab('single')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'single' ? 'var(--primary-50)' : 'transparent',
            border: 'none',
            borderBottom:
              activeTab === 'single' ? '2px solid var(--primary-600)' : '2px solid transparent',
            color: activeTab === 'single' ? 'var(--primary-600)' : 'var(--gray-600)',
            fontWeight: activeTab === 'single' ? '600' : '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            marginBottom: '-1px',
          }}
        >
          <FileText size={18} />
          Single Response
        </button>
        <button
          className="dashboard-tab"
          onClick={() => setActiveTab('bulk')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'bulk' ? 'var(--primary-50)' : 'transparent',
            border: 'none',
            borderBottom:
              activeTab === 'bulk' ? '2px solid var(--primary-600)' : '2px solid transparent',
            color: activeTab === 'bulk' ? 'var(--primary-600)' : 'var(--gray-600)',
            fontWeight: activeTab === 'bulk' ? '600' : '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            marginBottom: '-1px',
          }}
        >
          <Layers size={18} />
          Bulk Mode
          {!canUseBulk && (
            <span
              style={{
                background: 'var(--primary-600)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
              }}
            >
              PRO
            </span>
          )}
        </button>
        <button
          className="dashboard-tab"
          onClick={() => setActiveTab('history')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'history' ? 'var(--primary-50)' : 'transparent',
            border: 'none',
            borderBottom:
              activeTab === 'history' ? '2px solid var(--primary-600)' : '2px solid transparent',
            color: activeTab === 'history' ? 'var(--primary-600)' : 'var(--gray-600)',
            fontWeight: activeTab === 'history' ? '600' : '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            marginBottom: '-1px',
          }}
        >
          <Clock size={18} />
          History
          {!canUseHistory && (
            <span
              style={{
                background: 'var(--primary-600)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
              }}
            >
              STARTER
            </span>
          )}
        </button>
        <button
          className="dashboard-tab"
          onClick={() => setActiveTab('api')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'api' ? 'var(--primary-50)' : 'transparent',
            border: 'none',
            borderBottom:
              activeTab === 'api' ? '2px solid var(--primary-600)' : '2px solid transparent',
            color: activeTab === 'api' ? 'var(--primary-600)' : 'var(--gray-600)',
            fontWeight: activeTab === 'api' ? '600' : '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            marginBottom: '-1px',
          }}
        >
          <Code size={18} />
          API
          {!canUseApi && (
            <span
              style={{
                background: 'var(--primary-600)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
              }}
            >
              UNLIMITED
            </span>
          )}
        </button>
      </div>

      {/* Single Response Tab */}
      {activeTab === 'single' && (
        <div className="generator-section">
          <div className="card">
            <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
              Paste Your Review
            </h2>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Customer Review</label>
                {reviewText.trim() && (
                  <button
                    type="button"
                    onClick={() => setReviewText('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      color: 'var(--gray-500)',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      borderRadius: '4px',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'var(--gray-100)';
                      e.currentTarget.style.color = 'var(--gray-700)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.color = 'var(--gray-500)';
                    }}
                  >
                    <X size={14} />
                    Clear
                  </button>
                )}
              </div>
              <textarea
                className="form-textarea"
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                placeholder="Paste the customer review here..."
                rows={5}
                spellCheck={false}
              />
            </div>

            {/* Generate Button - prominently placed right after review input */}
            <button
              className="btn btn-primary"
              onClick={() => generateResponse()}
              disabled={generating || !reviewText.trim()}
              style={{ width: '100%', marginBottom: '20px' }}
              data-action="generate"
            >
              {generating ? (
                <GeneratingIndicator />
              ) : (
                <>
                  <Sparkles size={18} />
                  Craft Your Response
                </>
              )}
            </button>

            {/* Response Tone */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {[
                  { value: 'professional', label: 'Professional', emoji: '💼' },
                  { value: 'friendly', label: 'Friendly', emoji: '😊' },
                  { value: 'formal', label: 'Formal', emoji: '📋' },
                  { value: 'apologetic', label: 'Apologetic', emoji: '🙏' },
                ].map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => {
                      setTone(t.value);
                      setHasManuallyChangedTone(true);
                    }}
                    style={{
                      padding: '12px 8px',
                      borderRadius: '8px',
                      border: tone === t.value ? '2px solid var(--primary-500)' : '2px solid var(--gray-200)',
                      background: tone === t.value ? 'var(--primary-50)' : 'var(--bg-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{t.emoji}</span>
                    <span style={{ fontSize: '12px', fontWeight: tone === t.value ? '600' : '500', color: tone === t.value ? 'var(--primary-700)' : 'var(--gray-600)' }}>
                      {t.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Expandable Options Row */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {/* For this response only */}
              <details style={{ flex: '1 1 auto' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    padding: '8px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--gray-600)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    background: 'var(--bg-secondary)',
                    listStyle: 'none',
                  }}
                >
                  <Edit3 size={12} />
                  For this response only
                  {oneTimeInstructions.trim() && (
                    <span style={{ background: 'var(--primary-500)', color: 'white', fontSize: '8px', padding: '1px 4px', borderRadius: '4px' }}>●</span>
                  )}
                </summary>
              <div style={{ paddingTop: '16px' }}>
                    <div
                      style={{
                        padding: '12px',
                        background: 'var(--gray-50)',
                        borderRadius: '8px',
                        border: '1px solid var(--gray-200)',
                        marginTop: '4px',
                      }}
                    >
                      {/* Quick Add chips */}
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'center',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '11px',
                              color: 'var(--gray-500)',
                              fontWeight: '500',
                              marginRight: '4px',
                            }}
                          >
                            Quick add:
                          </span>
                          {(showAllTemplates
                            ? quickTemplates
                            : quickTemplates.filter(t => t.priority)
                          ).map(template => {
                            const active = isOneTimeTemplateActive(template);
                            return (
                              <div
                                key={template.id}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  border: `1px solid ${active ? 'var(--primary-300)' : 'var(--gray-200)'}`,
                                  borderRadius: '12px',
                                  background: active ? 'var(--primary-100)' : 'var(--white)',
                                  color: active ? 'var(--primary-700)' : 'var(--gray-700)',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {!active ? (
                                  <button
                                    onClick={() => addOneTimeTemplate(template)}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      cursor: 'pointer',
                                      color: 'inherit',
                                      fontSize: 'inherit',
                                      fontWeight: 'inherit',
                                    }}
                                  >
                                    <span>{template.emoji}</span>
                                    <span>{template.label}</span>
                                  </button>
                                ) : (
                                  <>
                                    <span>{template.label}</span>
                                    <button
                                      onClick={() => removeOneTimeTemplate(template)}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '14px',
                                        height: '14px',
                                        background: 'var(--primary-200)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        color: 'var(--primary-700)',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        lineHeight: 1,
                                        padding: 0,
                                        marginLeft: '2px',
                                      }}
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                          {!showAllTemplates && (
                            <button
                              onClick={() => setShowAllTemplates(true)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: '500',
                                border: '1px dashed var(--gray-300)',
                                borderRadius: '12px',
                                background: 'transparent',
                                color: 'var(--gray-500)',
                                cursor: 'pointer',
                              }}
                            >
                              +{quickTemplates.filter(t => !t.priority).length} more
                            </button>
                          )}
                          {showAllTemplates && (
                            <button
                              onClick={() => setShowAllTemplates(false)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: '500',
                                border: '1px dashed var(--gray-300)',
                                borderRadius: '12px',
                                background: 'transparent',
                                color: 'var(--gray-500)',
                                cursor: 'pointer',
                              }}
                            >
                              Show less
                            </button>
                          )}
                        </div>
                      </div>

                      {/* One-time textarea */}
                      <textarea
                        className="form-textarea"
                        value={oneTimeInstructions}
                        onChange={e => setOneTimeInstructions(e.target.value)}
                        placeholder="Add custom instructions for this response only..."
                        rows={3}
                        style={{
                          resize: 'vertical',
                          minHeight: '60px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          background: 'var(--white)',
                        }}
                      />

                      <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '6px', marginBottom: 0 }}>
                        🔄 These instructions will be cleared after generating
                      </p>
                    </div>
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '8px' }}>
                    💡 For permanent instructions that apply to ALL responses, go to{' '}
                    <Link
                      to="/settings"
                      style={{
                        color: 'var(--primary)',
                        textDecoration: 'none',
                        fontWeight: '500',
                      }}
                    >
                      Settings →
                    </Link>
                  </p>
              </div>
              </details>

              {/* Star Rating - minimized */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    padding: '8px 10px',
                    fontSize: '12px',
                    color: 'var(--gray-500)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    background: 'var(--bg-secondary)',
                    listStyle: 'none',
                  }}
                  title="Star rating auto-picks tone"
                >
                  ⭐
                  {rating > 0 && <span style={{ fontSize: '10px' }}>{rating}</span>}
                </summary>
                <div style={{ padding: '12px', marginTop: '8px', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Rating:</span>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => {
                            const newRating = rating === star ? 0 : star;
                            setRating(newRating);
                            if (newRating > 0) {
                              if (newRating === 1) setTone('apologetic');
                              else if (newRating <= 3) setTone('professional');
                              else setTone('friendly');
                              setHasManuallyChangedTone(false);
                            }
                          }}
                          style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: star <= rating ? 'var(--warning)' : 'var(--gray-300)' }}
                        >
                          <Star size={18} fill={star <= rating ? 'currentColor' : 'none'} />
                        </button>
                      ))}
                    </div>
                    <span style={{ fontSize: '10px', color: 'var(--gray-400)' }}>→ auto-picks tone</span>
                  </div>
                </div>
              </details>

              {/* Advanced - emoji only */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    padding: '8px 10px',
                    fontSize: '12px',
                    color: 'var(--gray-500)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    background: 'var(--bg-secondary)',
                    listStyle: 'none',
                  }}
                  title="Advanced settings"
                >
                  ⚙️
                </summary>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', marginTop: '8px', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)', flexWrap: 'wrap' }}>
                {/* Include Emojis - Compact */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <select
                    value={includeEmojis === null ? 'auto' : includeEmojis ? 'yes' : 'no'}
                    onChange={(e) => {
                      const val = e.target.value;
                      const newValue = val === 'auto' ? null : val === 'yes';
                      setIncludeEmojis(newValue);
                      if (newValue === null) {
                        localStorage.removeItem('includeEmojis');
                      } else {
                        localStorage.setItem('includeEmojis', newValue.toString());
                      }
                    }}
                    style={{
                      padding: '3px 6px',
                      borderRadius: '4px',
                      border: '1px solid var(--gray-300)',
                      background: 'var(--bg-primary)',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Emojis</span>
                </div>

                {/* Separator */}
                <div style={{ width: '1px', height: '20px', background: 'var(--gray-200)' }} />

                {/* Sign with business name - Compact */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeSignature}
                    onChange={(e) => {
                      setIncludeSignature(e.target.checked);
                      localStorage.setItem('includeSignature', e.target.checked.toString());
                    }}
                    style={{
                      width: '14px',
                      height: '14px',
                      accentColor: 'var(--primary-500)',
                      cursor: 'pointer',
                    }}
                  />
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Signature</span>
                </label>

                {/* Separator */}
                <div style={{ width: '1px', height: '20px', background: 'var(--gray-200)' }} />

                {/* Response Length - Compact */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <select
                    value={responseLength}
                    onChange={(e) => {
                      setResponseLength(e.target.value);
                      localStorage.setItem('responseLength', e.target.value);
                    }}
                    style={{
                      padding: '3px 6px',
                      borderRadius: '4px',
                      border: '1px solid var(--gray-300)',
                      background: 'var(--bg-primary)',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="short">Short</option>
                    <option value="medium">Medium</option>
                    <option value="detailed">Detailed</option>
                  </select>
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Length</span>
                </div>
                </div>
              </details>
            </div>
          </div>

          <div className="card">
            {/* Header with Craft your own checkbox inline */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Craft Your Response</h2>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={askUserEnabled}
                  onChange={(e) => {
                    setAskUserEnabled(e.target.checked);
                    localStorage.setItem('askUser_enabled', e.target.checked);
                  }}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: 'var(--primary-500)',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                  Craft your own
                </span>
              </label>
            </div>

            {/* Bracket info banner - shows when AI used placeholders */}
            {/* Smart Bracket V2.0: Now explains WHY brackets appeared */}
            {/* Smart Response Display: Interactive brackets OR regular textarea */}
            {askUserEnabled && response && countBrackets(response) > 0 ? (
              <InteractiveResponse
                response={response}
                onResponseChange={(newResponse) => setResponse(newResponse)}
              />
            ) : (
              <textarea
                className="response-output"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Your response will appear here..."
                style={{
                  width: '100%',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  cursor: 'text',
                }}
              />
            )}

            {response && (
              <>
                <div className="response-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      if (bracketCount > 0) {
                        const proceed = window.confirm(
                          `This response has ${bracketCount} placeholder${bracketCount > 1 ? 's' : ''} [in brackets] that need to be filled in.\n\nCopy anyway?`
                        );
                        if (!proceed) return;
                      }
                      copyToClipboard();
                    }}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : bracketCount > 0 ? `Copy (${bracketCount} to fill)` : 'Copy Response'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setResponse('');
                      setReviewText('');
                      setRating(0);
                    }}
                  >
                    Clear
                  </button>
                </div>

                {/* Regenerate with different tone */}
                <div
                  style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid var(--gray-200)',
                  }}
                >
                  <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '8px' }}>
                    <RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Try a different tone:
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['professional', 'friendly', 'formal', 'apologetic']
                      .filter(t => t !== tone)
                      .map(newTone => (
                        <button
                          key={newTone}
                          className="btn btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                          onClick={() => generateResponse(newTone)}
                          disabled={generating}
                        >
                          {newTone.charAt(0).toUpperCase() + newTone.slice(1)}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Chrome Extension CTA - contextual after generation */}
                <div
                  style={{
                    marginTop: '20px',
                    padding: '16px 20px',
                    background: 'var(--hero-gradient)',
                    borderRadius: '12px',
                    border: '1px solid var(--primary-200)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '200px' }}>
                    <div
                      style={{
                        background: 'var(--primary-500)',
                        borderRadius: '50%',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Chrome size={20} style={{ color: 'white' }} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--primary-800)', fontSize: '14px' }}>
                        Tired of copy-pasting?
                      </p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--primary-600)' }}>
                        Respond directly on e.g. Google Maps with our extension
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{
                      padding: '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={16} />
                    Add to Chrome
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Generate Tab */}
      {activeTab === 'bulk' && (
        <div>
          {!canUseBulk ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  background: 'var(--primary-50)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <Layers size={40} style={{ color: 'var(--primary-600)' }} />
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
                Bulk Generation is a Pro Feature
              </h2>
              <p
                style={{
                  color: 'var(--gray-600)',
                  marginBottom: '24px',
                  maxWidth: '500px',
                  margin: '0 auto 24px',
                }}
              >
                Generate responses for up to 20 reviews at once. Perfect for businesses with high
                review volume.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Link to="/pricing" className="btn btn-primary" style={{ padding: '12px 24px' }}>
                  Upgrade to Pro - $49/month
                </Link>
              </div>
              <div
                style={{
                  marginTop: '32px',
                  display: 'flex',
                  gap: '32px',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-600)' }}>
                    20
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                    Reviews per batch
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-600)' }}>
                    Parallel
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>Processing</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-600)' }}>
                    CSV
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>Import support</div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: '24px' }}>
                <h2 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>
                  <Layers size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Bulk Generate Responses
                </h2>
                <p style={{ color: 'var(--gray-500)', fontSize: '14px', marginBottom: '20px' }}>
                  Enter multiple reviews to generate responses for all of them at once. Maximum 20
                  per batch.
                </p>

                <div className="form-group">
                  <label className="form-label">Customer Reviews</label>
                  <textarea
                    className="form-textarea"
                    value={bulkInput}
                    onChange={e => setBulkInput(e.target.value)}
                    placeholder={`Paste your reviews here...

Great service! The staff was very helpful and friendly.

Terrible experience. Waited 30 minutes and nobody helped me.

Food was amazing, will definitely come back!`}
                    rows={25}
                    style={{ fontFamily: 'inherit', minHeight: '700px' }}
                    spellCheck={false}
                  />
                </div>

                <div className="dashboard-form-grid">
                  <div className="form-group">
                    <label className="form-label">Platform</label>
                    <select
                      className="form-select"
                      value={bulkPlatform}
                      onChange={e => setBulkPlatform(e.target.value)}
                    >
                      <option value="google">Google Reviews</option>
                      <option value="yelp">Yelp</option>
                      <option value="facebook">Facebook</option>
                      <option value="tripadvisor">TripAdvisor</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Response Tone</label>
                    <select
                      className="form-select"
                      value={bulkTone}
                      onChange={e => setBulkTone(e.target.value)}
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="formal">Formal</option>
                      <option value="apologetic">Apologetic</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={generateBulkResponses}
                    disabled={bulkGenerating || !bulkInput.trim()}
                    style={{ flex: 1 }}
                  >
                    {bulkGenerating ? (
                      <GeneratingIndicator text={`Generating ${parseBulkInput(bulkInput).length} responses`} staticText />
                    ) : (
                      <>
                        <Sparkles size={18} />
                        Generate All Responses ({parseBulkInput(bulkInput).length || 0})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Bulk Results */}
              {bulkResults && (
                <div className="card">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <div>
                      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                        Generated Responses
                      </h2>
                      <p style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
                        {bulkResults.summary.successful} of {bulkResults.summary.total} responses
                        generated
                      </p>
                    </div>
                    <button
                      className="btn btn-secondary"
                      onClick={copyAllResponses}
                      style={{ padding: '8px 16px' }}
                    >
                      <Copy size={16} />
                      Copy All
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {bulkResults.results.map((result, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          background: result.success ? 'var(--gray-50)' : 'var(--danger-light)',
                          borderRadius: '8px',
                          border: result.success
                            ? '1px solid var(--gray-200)'
                            : '1px solid var(--danger)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px',
                          }}
                        >
                          <span
                            style={{
                              background: result.success ? 'var(--primary-100)' : 'var(--danger)',
                              color: result.success ? 'var(--primary-700)' : 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            Review #{index + 1}
                          </span>
                          {result.success && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => copyBulkResponse(result.response, index)}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {copiedIndex === index ? <Check size={14} /> : <Copy size={14} />}
                              {copiedIndex === index ? 'Copied!' : 'Copy'}
                            </button>
                          )}
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: 'var(--gray-500)',
                              marginBottom: '4px',
                            }}
                          >
                            REVIEW:
                          </div>
                          <div style={{ fontSize: '14px', color: 'var(--gray-700)' }}>
                            {result.review?.substring(0, 200)}
                            {result.review?.length > 200 ? '...' : ''}
                          </div>
                        </div>

                        {result.success ? (
                          <div>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: 'var(--gray-500)',
                                marginBottom: '4px',
                              }}
                            >
                              RESPONSE:
                            </div>
                            <textarea
                              value={result.response}
                              onChange={(e) => {
                                const newResults = [...bulkResults.results];
                                newResults[index] = { ...newResults[index], response: e.target.value };
                                setBulkResults({ ...bulkResults, results: newResults });
                              }}
                              style={{
                                width: '100%',
                                minHeight: '120px',
                                fontSize: '14px',
                                color: 'var(--text-primary)',
                                background: 'var(--bg-secondary)',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color)',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              color: 'var(--danger)',
                            }}
                          >
                            <AlertCircle size={16} />
                            <span style={{ fontSize: '14px' }}>{result.error}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {!canUseHistory ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--primary-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Clock size={32} style={{ color: 'var(--primary)' }} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                Response History is a Starter Feature
              </h3>
              <p
                style={{
                  color: 'var(--gray-500)',
                  marginBottom: '24px',
                  maxWidth: '400px',
                  margin: '0 auto 24px',
                }}
              >
                Keep track of all your crafted responses. Access your history anytime, export to
                CSV/PDF, and never lose a response.
              </p>
              <Link to="/pricing" className="btn btn-primary">
                Upgrade to Starter - $29/month
              </Link>
            </div>
          ) : (
            <div className="card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  <Clock size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Response History
                </h2>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <input
                    type="date"
                    value={exportDateFrom}
                    onChange={e => setExportDateFrom(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid var(--gray-300)',
                      fontSize: '13px',
                    }}
                  />
                  <span style={{ color: 'var(--gray-400)' }}>to</span>
                  <input
                    type="date"
                    value={exportDateTo}
                    onChange={e => setExportDateTo(e.target.value)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid var(--gray-300)',
                      fontSize: '13px',
                    }}
                  />
                  <button
                    onClick={exportToCSV}
                    disabled={exporting || allHistory.length === 0}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '13px', opacity: !canExport ? 0.5 : 1 }}
                  >
                    <Download size={14} /> CSV
                  </button>
                  <button
                    onClick={exportToPDF}
                    disabled={exporting || allHistory.length === 0}
                    className="btn btn-primary"
                    style={{ padding: '6px 12px', fontSize: '13px', opacity: !canExport ? 0.5 : 1 }}
                  >
                    <Download size={14} /> PDF
                  </button>
                </div>
              </div>
              {allHistory.length > 0 && (
                <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '16px' }}>
                  {allHistory.length} responses available for export
                </p>
              )}

              {history.length === 0 ? (
                <div
                  style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--gray-500)' }}
                >
                  <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>No responses generated yet. Start by generating your first response!</p>
                </div>
              ) : (
                history.map(item => (
                  <div key={item.id} className="history-item">
                    <div className="history-review">
                      <strong>Review:</strong> {item.review_text.substring(0, 150)}
                      {item.review_text.length > 150 && '...'}
                    </div>
                    <div className="history-response">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <strong style={{ flexShrink: 0 }}>Response:</strong>
                        {editingResponseId !== item.id && (
                          <button
                            onClick={() => handleEditResponse(item)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--gray-500)',
                            }}
                            title="Edit response"
                          >
                            <Edit3 size={14} />
                          </button>
                        )}
                      </div>
                      {editingResponseId === item.id ? (
                        <div style={{ marginTop: '8px' }}>
                          <textarea
                            value={editedResponseText}
                            onChange={(e) => setEditedResponseText(e.target.value)}
                            style={{
                              width: '100%',
                              minHeight: '120px',
                              padding: '12px',
                              borderRadius: '8px',
                              border: '1px solid var(--gray-300)',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              resize: 'vertical',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={handleCancelEdit}
                              disabled={savingResponse}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid var(--gray-300)',
                                background: 'var(--bg-secondary)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                              }}
                            >
                              <X size={14} /> Cancel
                            </button>
                            <button
                              onClick={() => handleSaveResponse(item.id)}
                              disabled={savingResponse}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'var(--primary-600)',
                                color: 'white',
                                cursor: savingResponse ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px',
                                opacity: savingResponse ? 0.7 : 1,
                              }}
                            >
                              {savingResponse ? <Loader size={14} className="spin" /> : <Check size={14} />}
                              {savingResponse ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: '4px' }}>{item.generated_response}</div>
                      )}
                    </div>
                    <div className="history-meta">
                      {item.review_rating && <span>{item.review_rating} stars</span>}
                      <span>{item.review_platform}</span>
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}


      {/* API Tab */}
      {activeTab === 'api' && (
        <ApiTab user={user} api={api} effectivePlan={effectivePlan} isTeamMember={isTeamMember} />
      )}

      {/* Edit Template Modal */}
      {showEditTemplateModal && editingTemplate && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowEditTemplateModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              padding: '24px',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Edit3 size={20} />
              Edit Template
            </h2>

            <div className="form-group">
              <label className="form-label">Template Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Positive 5-star response"
                value={editTemplateName}
                onChange={e => setEditTemplateName(e.target.value)}
                maxLength={100}
                autoFocus
              />
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Template Content</label>
              <textarea
                className="form-textarea"
                placeholder="Template response content..."
                value={editTemplateContent}
                onChange={e => setEditTemplateContent(e.target.value)}
                rows={8}
                style={{ resize: 'vertical', minHeight: '150px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                  setEditTemplateName('');
                  setEditTemplateContent('');
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={updateTemplate}
                disabled={
                  updatingTemplate || !editTemplateName.trim() || !editTemplateContent.trim()
                }
                style={{ flex: 1 }}
              >
                {updatingTemplate ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {showKeyboardHelp && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowKeyboardHelp(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '480px',
              width: '100%',
              padding: '24px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
              }}
            >
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Keyboard size={20} />
                Keyboard Shortcuts
              </h2>
              <button
                onClick={() => setShowKeyboardHelp(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--gray-400)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--gray-100)',
                }}
              >
                <span style={{ color: 'var(--gray-700)' }}>Generate / New Response</span>
                <kbd
                  style={{
                    background: 'var(--gray-100)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + Enter
                </kbd>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--gray-100)',
                }}
              >
                <span style={{ color: 'var(--gray-700)' }}>Copy Response</span>
                <kbd
                  style={{
                    background: 'var(--gray-100)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + Shift + Enter
                </kbd>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--gray-100)',
                }}
              >
                <span style={{ color: 'var(--gray-700)' }}>Clear All</span>
                <kbd
                  style={{
                    background: 'var(--gray-100)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + Backspace
                </kbd>
              </div>

              <div
                style={{
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid var(--gray-200)',
                }}
              >
                <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '8px' }}>
                  Change Tone:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>Professional</span>
                    <kbd
                      style={{
                        background: 'var(--gray-100)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + 1
                    </kbd>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>Friendly</span>
                    <kbd
                      style={{
                        background: 'var(--gray-100)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + 2
                    </kbd>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>Formal</span>
                    <kbd
                      style={{
                        background: 'var(--gray-100)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + 3
                    </kbd>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>Apologetic</span>
                    <kbd
                      style={{
                        background: 'var(--gray-100)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + 4
                    </kbd>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  marginTop: '8px',
                  borderTop: '1px solid var(--gray-200)',
                }}
              >
                <span style={{ color: 'var(--gray-700)' }}>Show This Help</span>
                <kbd
                  style={{
                    background: 'var(--gray-100)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                  }}
                >
                  {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'} + /
                </kbd>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => setShowKeyboardHelp(false)}
              style={{ width: '100%', marginTop: '20px' }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid - Usage Display (simplified - all responses use Claude Sonnet 4) */}
      <div className="stats-grid" style={{ marginTop: '32px' }}>
        {/* AI Responses Usage */}
        <div
          className="stat-card"
          style={{
            background: 'var(--hero-gradient)',
            border: '1px solid var(--primary-200)',
          }}
        >
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>✨</span> Crafted Responses
          </div>
          <div className="stat-value primary">
            {stats?.usage?.used ?? user?.responsesUsed ?? 0}
            <span style={{ fontSize: '14px', fontWeight: '400', color: 'var(--gray-500)' }}>
              /{stats?.usage?.limit ?? user?.responsesLimit ?? 20}
            </span>
          </div>
          <div className="usage-bar" style={{ background: 'rgba(255,255,255,0.5)' }}>
            <div
              className={`usage-fill ${usagePercent > 80 ? 'warning' : ''} ${usagePercent >= 100 ? 'danger' : ''}`}
              style={{
                width: `${Math.min(usagePercent, 100)}%`,
                background: usagePercent >= 100 ? 'var(--danger)' : usagePercent > 80 ? 'var(--warning)' : 'linear-gradient(90deg, var(--primary-500), var(--primary-600))',
              }}
            />
          </div>
          <p className="mt-1" style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
            {stats?.usage?.remaining ?? responsesRemaining} remaining this month
            {stats?.usage?.bonus > 0 && (
              <span style={{ color: 'var(--primary-600)', fontWeight: '500' }}>
                {' '}+ {stats.usage.bonus} bonus
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Referral Widget - Moved to bottom */}
      {referralData && (
        <div
          style={{
            background: 'linear-gradient(135deg, var(--primary-50) 0%, #EDE9FE 100%)',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '24px',
            border: '1px solid var(--primary-200)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <h3
                style={{
                  margin: '0 0 8px 0',
                  color: 'var(--primary-700)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Users size={20} />
                Invite Friends, Get 1 Month Free
              </h3>
              <p style={{ margin: '0 0 12px 0', color: 'var(--gray-600)', fontSize: '14px' }}>
                Share your referral link. When friends subscribe, you both get rewarded!
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <code
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: 'var(--primary-600)',
                    border: '1px solid var(--primary-200)',
                  }}
                >
                  {referralData.referralLink}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(referralData.referralLink);
                    setCopiedReferralLink(true);
                    setTimeout(() => setCopiedReferralLink(false), 2000);
                    toast.success('Referral link copied!');
                  }}
                  className="btn btn-primary"
                  style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {copiedReferralLink ? <Check size={16} /> : <Copy size={16} />}
                  {copiedReferralLink ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '24px', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-600)' }}>
                  {referralData.stats?.totalInvited || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Invited</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--green-600)' }}>
                  {referralData.stats?.converted || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Converted</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--purple-600)' }}>
                  {referralData.stats?.creditsEarned || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Credits</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Link - always visible */}
      <div
        style={{
          textAlign: 'center',
          padding: '24px 0',
          marginTop: '32px',
          borderTop: '1px solid var(--gray-100)',
        }}
      >
        <button
          onClick={() => setShowFeedbackPopup(true)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--gray-500)',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>How's it going?</span>
          <span style={{ color: 'var(--primary-600)', fontWeight: '500' }}>Share your thoughts</span>
        </button>
      </div>

      {/* Feedback Popup */}
      <FeedbackPopup
        isVisible={showFeedbackPopup}
        onMaybeLater={handleFeedbackMaybeLater}
        onSubmit={() => {
          setShowFeedbackPopup(false);
          // Never show again after submit
          localStorage.setItem('feedbackSubmitted', 'true');
          localStorage.removeItem('feedbackMaybeLater');
        }}
      />


      {/* Upgrade Modal - Shows when user hits free limit */}
      {showUpgradeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '480px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '8px', fontSize: '24px' }}>
              You have used all 20 free responses!
            </h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: '24px' }}>
              Your reviews are waiting for professional responses...
            </p>
            <div
              style={{
                background: 'var(--gray-100)',
                borderRadius: '8px',
                height: '8px',
                marginBottom: '24px',
              }}
            >
              <div
                style={{
                  background: 'var(--primary)',
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                }}
              />
            </div>
            {!exitSurveyReason ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={handleUpgradeToStarter}
                  className="btn btn-primary"
                  style={{ textAlign: 'center', padding: '16px', fontSize: '16px' }}
                >
                  Unlock 300 Responses - $29/mo
                </button>
                <button
                  onClick={handleBuyResponsePack}
                  className="btn btn-secondary"
                  style={{ padding: '14px', fontSize: '15px' }}
                >
                  Just need a few? Get 50 for $5
                </button>
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    toast.info('Your responses reset on the 1st of each month');
                  }}
                  className="btn"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--gray-300)',
                    padding: '12px',
                  }}
                >
                  Wait until next month
                </button>
                <button
                  onClick={() => setExitSurveyReason('survey')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--gray-500)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    marginTop: '8px',
                  }}
                >
                  I'm not ready to upgrade
                </button>
              </div>
            ) : (
              <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '24px' }}>
                <p style={{ fontWeight: '500', marginBottom: '16px' }}>
                  Quick question - what is holding you back?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    'Too expensive',
                    'Not enough value yet',
                    'Just testing',
                    'Using a competitor',
                    'Other',
                  ].map(reason => (
                    <button
                      key={reason}
                      onClick={() => trackExitSurvey(reason)}
                      style={{
                        background: 'var(--gray-50)',
                        border: '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setExitSurveyReason(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--gray-500)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    marginTop: '16px',
                  }}
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Profile / Account Settings Page
const ProfilePage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('account');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Notification settings state
  const [notifications, setNotifications] = useState({
    emailWeeklySummary: true,
    emailUsageAlerts: true,
    emailBillingUpdates: true,
  });
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Load notification settings
  useEffect(() => {
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const res = await api.get('/settings/notifications');
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Handle password change
  const handleChangePassword = async e => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return toast.error('Passwords do not match');
    }
    if (newPassword.length < 8) {
      return toast.error('Password must be at least 8 characters');
    }
    setChangingPassword(true);
    try {
      // OAuth users can always set password without current; non-OAuth users with password need current
      const payload =
        user?.oauthProvider || !user?.hasPassword
          ? { newPassword }
          : { currentPassword, newPassword };
      const response = await api.put('/auth/change-password', payload);
      toast.success(response.data.message || 'Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Refresh user data to update hasPassword status
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle email change request
  const handleChangeEmail = async e => {
    e.preventDefault();
    if (!newEmail) {
      return toast.error('Please enter a new email address');
    }
    setChangingEmail(true);
    try {
      await api.post('/auth/change-email-request', { newEmail, password: emailPassword });
      toast.success('Confirmation email sent! Check your inbox.');
      setNewEmail('');
      setEmailPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to request email change');
    } finally {
      setChangingEmail(false);
    }
  };

  // Handle notification settings save
  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await api.put('/settings/notifications', notifications);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      return toast.error('Please type DELETE to confirm');
    }
    setDeleting(true);
    try {
      await api.delete('/auth/delete-account', {
        data: { password: deletePassword, confirmation: deleteConfirmation },
      });
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  // Format join date
  const formatJoinDate = dateStr => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'danger', label: 'Danger Zone', icon: AlertCircle },
  ];

  return (
    <div
      className="container"
      style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '900px' }}
    >
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Link
          to="/dashboard"
          style={{
            color: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '16px',
            fontSize: '14px',
            textDecoration: 'none',
          }}
        >
          <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /> Back to Dashboard
        </Link>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
          Account Settings
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Manage your account, security, and notification preferences
        </p>
      </div>

      {/* Tab Navigation */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: 'var(--bg-tertiary)',
          padding: '4px',
          borderRadius: '12px',
          flexWrap: 'wrap',
        }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 16px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
              fontWeight: activeTab === tab.id ? '600' : '500',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: activeTab === tab.id ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <tab.icon size={16} />
            <span className="hide-mobile">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div className="card">
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <User size={20} /> Account Information
          </h2>

          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Profile Avatar Section */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px',
                background: 'var(--bg-tertiary)',
                borderRadius: '12px',
              }}
            >
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt="Profile"
                  style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'var(--primary)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: '600',
                  }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px' }}>
                  {user?.businessName || 'No business name set'}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{user?.email}</div>
                {user?.oauthProvider && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '4px',
                      padding: '2px 8px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                    }}
                  >
                    Signed in with {user.oauthProvider}
                  </span>
                )}
              </div>
            </div>

            {/* Account Details Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              <div
                style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}
              >
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Email
                </div>
                <div style={{ fontWeight: '500' }}>{user?.email}</div>
              </div>
              <div
                style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}
              >
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Member Since
                </div>
                <div style={{ fontWeight: '500' }}>{formatJoinDate(user?.createdAt)}</div>
              </div>
              <div
                style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}
              >
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Current Plan
                </div>
                <div style={{ fontWeight: '500' }}>
                  {user?.plan?.charAt(0).toUpperCase() + user?.plan?.slice(1)}
                  {user?.subscriptionStatus === 'active' && (
                    <span style={{ color: 'var(--secondary)', marginLeft: '8px' }}>Active</span>
                  )}
                  {user?.teamInfo?.isTeamMember && (
                    <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '4px' }}>
                      via {user.teamInfo.teamOwnerBusiness || user.teamInfo.teamOwnerEmail}'s Team
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}
              >
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  Responses Used
                </div>
                <div style={{ fontWeight: '500' }}>
                  {user?.responsesUsed || 0} /{' '}
                  {user?.responsesLimit === 999999 ? '∞' : user?.responsesLimit}
                </div>
              </div>
            </div>

            {/* Manage Subscription Button */}
            {user?.plan !== 'free' && (
              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post('/billing/portal');
                      window.location.href = res.data.url;
                    } catch (error) {
                      toast.error('Failed to open billing portal');
                    }
                  }}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <CreditCard size={16} />
                  Manage Subscription
                </button>
              </div>
            )}

            {/* Change Email Section */}
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border-color)',
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                Change Email Address
              </h3>
              <form onSubmit={handleChangeEmail} style={{ display: 'grid', gap: '12px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">New Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder="Enter new email address"
                  />
                </div>
                {user?.hasPassword && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={emailPassword}
                      onChange={e => setEmailPassword(e.target.value)}
                      placeholder="Enter your password to confirm"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={changingEmail || !newEmail}
                  style={{ width: 'fit-content' }}
                >
                  {changingEmail ? 'Sending...' : 'Send Confirmation Email'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="card">
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Lock size={20} /> Security
          </h2>

          {/* OAuth user - can set password for Chrome Extension */}
          {user?.oauthProvider && (
            <div
              style={{
                padding: '16px',
                background: 'var(--warning-bg)',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid var(--warning-border)',
              }}
            >
              <p style={{ color: 'var(--warning-text)', margin: 0, fontSize: '14px' }}>
                {user?.hasPassword ? (
                  <>
                    <strong>Update your password</strong> for the Chrome Extension. Your Google
                    login remains your primary sign-in method.
                  </>
                ) : (
                  <>
                    <strong>Set a password</strong> to login to the Chrome Extension.
                  </>
                )}
              </p>
            </div>
          )}

          <form onSubmit={handleChangePassword} style={{ display: 'grid', gap: '16px' }}>
            {/* Only show Current Password for non-OAuth users who have a password */}
            {!user?.oauthProvider && user?.hasPassword && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Current Password</label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  className="form-input"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">
                {!user?.oauthProvider && user?.hasPassword ? 'New Password' : 'Password'}
              </label>
              <input
                type={showPasswords ? 'text' : 'password'}
                className="form-input"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={
                  !user?.oauthProvider && user?.hasPassword
                    ? 'Enter new password (min 8 characters)'
                    : 'Choose a password (min 8 characters)'
                }
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Confirm Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                className="form-input"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
              />
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={e => setShowPasswords(e.target.checked)}
              />
              Show passwords
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                changingPassword ||
                (!user?.oauthProvider && user?.hasPassword && !currentPassword) ||
                !newPassword ||
                !confirmPassword
              }
              style={{ width: 'fit-content' }}
            >
              {changingPassword
                ? user?.oauthProvider || !user?.hasPassword
                  ? 'Setting...'
                  : 'Changing...'
                : user?.oauthProvider || !user?.hasPassword
                  ? 'Set Password'
                  : 'Change Password'}
            </button>
          </form>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="card">
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Bell size={20} /> Email Notifications
          </h2>

          {loadingNotifications ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {[
                {
                  key: 'emailWeeklySummary',
                  title: 'Weekly Summary',
                  description: 'Get a weekly email with your response stats and tips',
                },
                {
                  key: 'emailUsageAlerts',
                  title: 'Usage Alerts',
                  description: 'Get notified when you reach 80% of your monthly limit',
                },
                {
                  key: 'emailBillingUpdates',
                  title: 'Billing Updates',
                  description: 'Receive emails about subscription renewals and invoices',
                },
              ].map(item => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>{item.title}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      {item.description}
                    </div>
                  </div>
                  <label
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '48px',
                      height: '26px',
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={notifications[item.key]}
                      onChange={e =>
                        setNotifications(prev => ({ ...prev, [item.key]: e.target.checked }))
                      }
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: notifications[item.key] ? 'var(--primary)' : 'var(--gray-300)',
                        borderRadius: '26px',
                        transition: '0.3s',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          height: '20px',
                          width: '20px',
                          left: notifications[item.key] ? '24px' : '3px',
                          bottom: '3px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '50%',
                          transition: '0.3s',
                        }}
                      />
                    </span>
                  </label>
                </div>
              ))}
              <button
                className="btn btn-primary"
                onClick={handleSaveNotifications}
                disabled={savingNotifications}
                style={{ width: 'fit-content', marginTop: '8px' }}
              >
                {savingNotifications ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Danger Zone Tab */}
      {activeTab === 'danger' && (
        <div className="card" style={{ border: '1px solid var(--danger)' }}>
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--danger)',
            }}
          >
            <AlertCircle size={20} /> Danger Zone
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
            These actions are irreversible. Please proceed with caution.
          </p>

          <div
            style={{
              padding: '20px',
              background: 'rgba(239,68,68,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(239,68,68,0.2)',
            }}
          >
            <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>Delete Account</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Permanently delete your account and all associated data. This action cannot be undone.
              Your subscription will be cancelled and you will lose access immediately.
            </p>
            <button
              className="btn"
              onClick={() => setShowDeleteModal(true)}
              style={{ background: 'var(--danger)', color: 'white' }}
            >
              <Trash2 size={16} /> Delete My Account
            </button>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: 'var(--card-bg)',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '450px',
              width: '100%',
            }}
          >
            <h2
              style={{
                color: 'var(--danger)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertCircle size={24} /> Delete Account
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              This will permanently delete:
            </p>
            <ul
              style={{
                marginBottom: '20px',
                paddingLeft: '20px',
                color: 'var(--text-muted)',
                fontSize: '14px',
              }}
            >
              <li>All your generated responses</li>
              <li>Your saved templates</li>
              <li>Your team members</li>
              <li>Your subscription (will be cancelled)</li>
              <li>All associated data</li>
            </ul>

            {user?.hasPassword && (
              <div className="form-group">
                <label className="form-label">Enter your password</label>
                <input
                  type="password"
                  className="form-input"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Type DELETE to confirm</label>
              <input
                type="text"
                className="form-input"
                value={deleteConfirmation}
                onChange={e => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmation !== 'DELETE'}
                style={{ flex: 1, background: 'var(--danger)', color: 'white' }}
              >
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Verify Email Page (for new user registration verification)
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }
    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async token => {
    try {
      const res = await api.get(`/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(res.data.message || 'Email verified successfully!');
      // Refresh user from backend to get updated emailVerified status
      await refreshUser();
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'Failed to verify email');
    }
  };

  return (
    <div
      className="container"
      style={{ paddingTop: '80px', paddingBottom: '80px', maxWidth: '500px', textAlign: 'center' }}
    >
      <div className="card">
        {status === 'loading' && (
          <>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <h2>Verifying Your Email...</h2>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={48} style={{ color: 'var(--secondary)', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>Email Verified!</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
              Your email has been verified. You're all set!
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/generator')}>
              Continue to Generator
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={48} style={{ color: 'var(--danger)', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>Verification Failed</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{message}</p>
            <button className="btn btn-primary" onClick={() => navigate('/generator')}>
              Continue to Generator
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Email Verification Banner Component (Dismissible)
const EmailVerificationBanner = () => {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--warning-bg)',
        color: 'var(--warning-text)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        fontSize: '13px',
        fontWeight: '500',
        borderBottom: '1px solid var(--warning-border)',
        position: 'relative',
      }}
    >
      <Mail size={16} />
      <span>Please verify your email address</span>
      {resent ? (
        <span
          style={{
            background: 'rgba(255,255,255,0.5)',
            padding: '3px 10px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          <CheckCircle size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          Sent!
        </span>
      ) : (
        <button
          onClick={handleResend}
          disabled={resending}
          style={{
            background: 'rgba(255,255,255,0.8)',
            color: '#92400e',
            border: 'none',
            padding: '4px 10px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {resending ? (
            <>
              <div
                className="spinner"
                style={{ width: '12px', height: '12px', borderWidth: '2px' }}
              ></div>
              Sending...
            </>
          ) : (
            <>
              <RefreshCw size={12} />
              Resend
            </>
          )}
        </button>
      )}
      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: '#92400e',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.6,
        }}
        title="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Confirm Email Change Page
const ConfirmEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid confirmation link');
      return;
    }
    confirmEmailChange(token);
  }, [searchParams]);

  const confirmEmailChange = async token => {
    try {
      const res = await api.post('/auth/confirm-email-change', { token });
      setStatus('success');
      setNewEmail(res.data.newEmail);
      setMessage('Email changed successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'Failed to confirm email change');
    }
  };

  return (
    <div
      className="container"
      style={{ paddingTop: '80px', paddingBottom: '80px', maxWidth: '500px', textAlign: 'center' }}
    >
      <div className="card">
        {status === 'loading' && (
          <>
            <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
            <h2>Confirming Email Change...</h2>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={48} style={{ color: 'var(--secondary)', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>Email Changed!</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
              Your email has been changed to <strong>{newEmail}</strong>
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/generator')}>
              Continue to Generator
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={48} style={{ color: 'var(--danger)', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>Confirmation Failed</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{message}</p>
            <button className="btn btn-primary" onClick={() => navigate('/profile')}>
              Back to Profile
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Helper: Get context placeholder examples based on business type (for keywords input)
const getContextPlaceholder = businessType => {
  const examples = {
    Restaurant: 'e.g. Italian, family-owned, since 1985, homemade pasta, cozy terrace',
    'Cafe / Coffee Shop': 'e.g. specialty coffee, local roasters, cozy atmosphere, vegan options',
    'Hotel / Accommodation': 'e.g. boutique hotel, city center, rooftop bar, since 2010',
    'Bar / Nightclub': 'e.g. craft cocktails, live music, industrial vibe, late night',
    'Spa / Wellness': 'e.g. holistic treatments, certified therapists, relaxation focus',
    'Hair Salon / Barbershop': 'e.g. master barber, hot towel shave, modern cuts, walk-ins welcome',
    'Dental Practice': 'e.g. family dentistry, gentle care, modern equipment, Dr. Smith',
    'Medical Practice': 'e.g. primary care, same-day appointments, caring staff, 20+ years',
    'Auto Repair / Service': 'e.g. European cars, honest pricing, certified mechanics, loaner cars',
    'Gym / Fitness Studio': 'e.g. personal training, group classes, 24/7 access, modern equipment',
    'Retail Store': 'e.g. local boutique, curated selection, friendly staff, unique finds',
    'E-commerce': 'e.g. fast shipping, easy returns, quality products, great support',
    'Professional Services':
      'e.g. accounting firm, CPA certified, small business focus, responsive',
    'Real Estate': 'e.g. local expert, first-time buyers, 15 years experience, personal touch',
    'Home Services': 'e.g. plumbing, licensed, same-day service, fair pricing, family-owned',
    // New business types
    'Veterinary / Pet Services':
      'e.g. dogs & cats, fear-free certified, 10 years experience, boarding available',
    'Financial Services':
      'e.g. CPA, small business focus, 20 years experience, QuickBooks certified',
    'Photography / Creative': 'e.g. weddings & portraits, natural light style, same-day previews',
    'Event Planning': 'e.g. weddings, corporate events, full-service, 100+ events completed',
    'Childcare / Education': 'e.g. ages 2-5, certified teachers, outdoor learning, healthy meals',
    'Senior Care': 'e.g. assisted living, memory care, 24/7 nursing, family-owned, 20 years',
    'Therapy / Counseling': 'e.g. licensed therapist, CBT specialist, couples therapy, 15 years experience',
  };
  return examples[businessType] || 'e.g. family-owned, since 1985, quality service, friendly team';
};

// Helper: Get full textarea placeholder examples based on business type
const getTextareaPlaceholder = businessType => {
  const examples = {
    Restaurant: `Examples:
• We're a family-owned Italian restaurant since 1985
• Our signature dishes are homemade pasta and wood-fired pizza
• Our chef Marco trained in Naples
• We have a cozy outdoor terrace
• We're known for our Sunday brunch specials
• Our manager Sarah handles customer service`,
    'Cafe / Coffee Shop': `Examples:
• We're an independent coffee shop since 2015
• We roast our own beans from single-origin farms
• Our baristas are certified by SCA
• We have a cozy reading corner with local art
• We're known for our homemade pastries
• Our owner Lisa greets every regular by name`,
    'Hotel / Accommodation': `Examples:
• We're a boutique hotel in the city center since 2010
• Our rooms feature stunning city views
• We offer complimentary breakfast with local products
• Our concierge team speaks 5 languages
• We're known for our rooftop bar
• General Manager Thomas ensures every detail is perfect`,
    'Bar / Nightclub': `Examples:
• We're a craft cocktail bar with industrial vibe
• Our mixologists create custom drinks
• We feature live jazz every Thursday
• We source premium spirits from around the world
• We're known for our signature Old Fashioned
• Owner Mike brings 20 years of hospitality experience`,
    'Spa / Wellness': `Examples:
• We're a holistic wellness center since 2008
• Our therapists are certified in multiple modalities
• We use organic, locally-sourced products
• We have a tranquil meditation garden
• We're known for our signature hot stone massage
• Director Anna personally designs treatment plans`,
    'Hair Salon / Barbershop': `Examples:
• We're a classic barbershop with modern edge since 2012
• Our master barbers have 15+ years experience
• We offer hot towel shaves and beard grooming
• We use premium products from Baxter of California
• We're known for our fade cuts and attention to detail
• Owner Jake creates a relaxed, friendly atmosphere`,
    'Dental Practice': `Examples:
• We're a family dental practice serving the community since 1995
• Dr. Smith specializes in gentle, anxiety-free care
• We use the latest digital imaging technology
• We offer same-day emergency appointments
• We're known for our comfortable, modern office
• Our hygienist Maria makes every patient feel at ease`,
    'Medical Practice': `Examples:
• We're a primary care practice with 20+ years in the community
• Dr. Johnson takes time to listen to every patient
• We offer same-day sick visits
• We use a patient portal for easy communication
• We're known for our thorough, personalized care
• Our staff creates a warm, welcoming environment`,
    'Auto Repair / Service': `Examples:
• We're an independent auto shop specializing in European cars
• Our mechanics are ASE certified with 30+ years combined experience
• We offer free loaner cars for major repairs
• We use OEM parts and stand behind our work
• We're known for honest pricing and clear explanations
• Owner Mike personally inspects every vehicle`,
    'Gym / Fitness Studio': `Examples:
• We're a boutique fitness studio since 2018
• Our trainers are nationally certified with specialties in strength and mobility
• We offer small group classes for personalized attention
• We have state-of-the-art equipment and 24/7 access
• We're known for our supportive community
• Owner Sarah creates programs for all fitness levels`,
    'Retail Store': `Examples:
• We're a local boutique curating unique finds since 2010
• We source products from local artisans and small brands
• Our staff knows every product personally
• We offer complimentary gift wrapping
• We're known for our personalized shopping experience
• Owner Emma hand-selects every item in our collection`,
    'E-commerce': `Examples:
• We're an online store focused on quality and fast shipping
• We ship same-day on orders before 2pm
• We have a hassle-free 30-day return policy
• Our customer support responds within 2 hours
• We're known for our carefully curated selection
• Our team personally quality-checks every order`,
    'Professional Services': `Examples:
• We're a CPA firm serving small businesses since 2005
• We specialize in tax planning and bookkeeping
• Our team responds to every inquiry within 24 hours
• We use cloud-based tools for easy collaboration
• We're known for making accounting understandable
• Partner David has 25 years of experience`,
    'Real Estate': `Examples:
• We're local real estate experts with 15 years in this market
• We specialize in first-time homebuyers and families
• We provide neighborhood insights you won't find online
• We're available evenings and weekends
• We're known for our honest, no-pressure approach
• Agent Jennifer treats every client like family`,
    'Home Services': `Examples:
• We're a family-owned plumbing company since 1990
• Our technicians are licensed and background-checked
• We offer same-day service and upfront pricing
• We stand behind our work with a 2-year warranty
• We're known for arriving on time and cleaning up after
• Owner Bob personally follows up on every job`,
    // New business types
    'Veterinary / Pet Services': `Examples:
• We're a full-service vet clinic caring for pets since 2005
• Our vets specialize in dogs, cats, and small animals
• We offer fear-free certified handling
• We have boarding and grooming on-site
• We're known for our gentle approach with anxious pets
• Dr. Wilson treats every pet like her own`,
    'Financial Services': `Examples:
• We're a CPA firm serving small businesses since 2000
• We specialize in tax planning and bookkeeping
• We're QuickBooks ProAdvisor certified
• We offer virtual meetings for convenience
• We're known for making finances understandable
• Partner Mike has 25 years in the industry`,
    'Photography / Creative': `Examples:
• We're a wedding and portrait studio since 2012
• We specialize in natural light photography
• We offer same-day previews and fast turnaround
• We have a cozy studio and travel anywhere
• We're known for capturing genuine moments
• Photographer Amy has an eye for candid shots`,
    'Event Planning': `Examples:
• We're a full-service event planning company since 2008
• We specialize in weddings and corporate events
• We've planned over 200 successful events
• We handle everything from venue to catering
• We're known for stress-free planning
• Coordinator Lisa makes every detail perfect`,
    'Childcare / Education': `Examples:
• We're a licensed daycare center since 2010
• Our teachers are early childhood certified
• We accept children ages 2-5
• We offer outdoor learning and healthy meals
• We're known for our nurturing environment
• Director Sarah ensures every child thrives`,
    'Senior Care': `Examples:
• We're a family-owned assisted living facility since 2005
• Our caregivers are certified with ongoing training
• We offer memory care with specialized programs
• We have a beautiful garden and activity room
• We're known for our compassionate, personalized care
• Director Sarah treats every resident like family`,
    'Therapy / Counseling': `Examples:
• We're a private therapy practice with 15+ years experience
• Our therapists are licensed and specialize in anxiety, depression, and relationships
• We offer in-person and telehealth sessions
• We provide a safe, confidential environment
• We're known for our empathetic, client-centered approach
• Dr. Johnson creates individualized treatment plans`,
  };
  return (
    examples[businessType] ||
    `Examples:
• We're a family-owned business since 1985
• Our team brings 20+ years of combined experience
• We pride ourselves on quality service
• We're known for our attention to detail
• Our manager Sarah ensures every customer is satisfied
• We treat every customer like family`
  );
};

// Placeholder Chip Component - Clickable inline placeholder that turns into input
const PlaceholderChip = ({ placeholder, onReplace }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (value.trim()) {
      onReplace(value.trim());
    }
    setEditing(false);
    setValue('');
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={handleSubmit}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') {
            setEditing(false);
            setValue('');
          }
        }}
        placeholder={placeholder.slice(1, -1)}
        style={{
          display: 'inline-block',
          width: 'auto',
          minWidth: '120px',
          padding: '2px 8px',
          border: '2px solid var(--primary)',
          borderRadius: '4px',
          fontSize: '14px',
          background: 'var(--bg-secondary)',
          outline: 'none',
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'var(--primary-100)',
        border: '1px dashed var(--primary-400)',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '14px',
        color: 'var(--primary-700)',
        cursor: 'pointer',
        margin: '0 2px',
        fontWeight: '500',
      }}
    >
      {placeholder.slice(1, -1)}
      <Edit2 size={12} />
    </button>
  );
};

// Helper to render text with clickable placeholder chips
const renderWithPlaceholders = (text, onPlaceholderChange) => {
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((part, i) => {
    if (part.match(/^\[.+\]$/)) {
      return (
        <PlaceholderChip
          key={i}
          placeholder={part}
          onReplace={newValue => onPlaceholderChange(part, newValue)}
        />
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// AI Context Generator Component - Always visible, no collapse
const AIContextGenerator = ({ field, businessType, businessName, currentValue, onGenerated }) => {
  const [keywords, setKeywords] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState('');
  const [remaining, setRemaining] = useState(null);

  // Load keywords from onboarding if available (only for context field, only if no current value)
  useEffect(() => {
    if (field === 'context' && !currentValue) {
      const savedKeywords = localStorage.getItem('onboardingKeywords');
      if (savedKeywords) {
        setKeywords(savedKeywords);
        localStorage.removeItem('onboardingKeywords'); // Use only once
      }
    }
  }, [field, currentValue]);

  const handleGenerate = async () => {
    // Debug: Log for mobile debugging
    console.log('[Generate] Start', {
      field,
      keywords: keywords?.substring?.(0, 20),
      businessType,
    });

    try {
      if (!keywords || !keywords.trim()) {
        toast.error('Please enter some keywords');
        return;
      }

      setGenerating(true);

      const requestData = {
        keywords: keywords.trim(),
        businessType: businessType || '',
        businessName: businessName || '',
        field,
        structured: field === 'context', // Profile Page gets structured output with placeholders
      };
      console.log('[Generate] Request', requestData);

      const res = await api.post('/personalization/generate-context', requestData);
      console.log('[Generate] Response', res.data);

      setGenerated(res.data.generated);
      setRemaining(res.data.remaining);
    } catch (error) {
      console.error('[Generate] Error', error);
      const errMsg = error.response?.data?.error || error.message || String(error);
      toast.error(`Error: ${errMsg}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleAccept = () => {
    onGenerated(generated);
    setKeywords('');
    setGenerated('');
    toast.success('Text applied!');
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Clean input + Generate button layout */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '6px' }}>
        <input
          type="text"
          className="form-input"
          value={keywords}
          onChange={e => setKeywords(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && keywords.trim() && handleGenerate()}
          placeholder={
            field === 'context'
              ? getContextPlaceholder(businessType)
              : 'e.g. friendly, short, with greeting, casual'
          }
          style={{ flex: 1, fontSize: '14px' }}
        />
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating || !keywords.trim()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background:
              generating || !keywords.trim()
                ? '#d1d5db'
                : 'linear-gradient(135deg, #6366f1, #4f46e5)',
            border: 'none',
            borderRadius: '8px',
            cursor: generating || !keywords.trim() ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: '#ffffff',
            whiteSpace: 'nowrap',
            boxShadow:
              generating || !keywords.trim() ? 'none' : '0 2px 8px rgba(99, 102, 241, 0.3)',
          }}
        >
          {generating ? (
            <GeneratingIndicator text="Generating profile..." staticText />
          ) : (
            <>
              <Sparkles size={16} />
              Generate
              <span
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Recommended
              </span>
            </>
          )}
        </button>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--gray-500)', margin: 0 }}>
        {field === 'context'
          ? 'Enter a few keywords to create a structured profile you can edit'
          : 'Enter a few keywords to create style guidelines'}
      </p>

      {remaining !== null && remaining < 10 && (
        <span style={{ fontSize: '12px', color: 'var(--gray-500)', marginLeft: '8px' }}>
          ({remaining} generations remaining today)
        </span>
      )}

      {generated && (
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--primary-200)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '12px',
          }}
        >
          <label
            style={{
              fontSize: '12px',
              color: 'var(--gray-500)',
              marginBottom: '6px',
              display: 'block',
            }}
          >
            {field === 'context' ? 'Generated text (click [placeholders] to fill in):' : 'Generated style guidelines:'}
          </label>
          <div
            style={{
              fontSize: '14px',
              color: 'var(--gray-800)',
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderWithPlaceholders(generated, (oldPlaceholder, newValue) => {
              setGenerated(prev => prev.replace(oldPlaceholder, newValue));
            })}
          </div>
          <div
            style={{
              background: 'var(--primary-50)',
              border: '1px solid var(--primary-200)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginTop: '10px',
              fontSize: '12px',
              color: 'var(--primary-700)',
            }}
          >
            {generated.includes('[') ? (
              <>Fill in the [brackets] above, then click "Apply & Edit"</>
            ) : (
              <>Click "Apply & Edit" to copy to the edit field below</>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="btn btn-sm"
              style={{ background: 'var(--gray-100)', border: '1px solid var(--gray-200)' }}
            >
              <RefreshCw size={14} style={{ marginRight: '4px' }} />
              Regenerate
            </button>
            <button type="button" onClick={handleAccept} className="btn btn-primary btn-sm">
              <Check size={14} style={{ marginRight: '4px' }} />
              Apply & Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  // Load businessType from localStorage first (draft), then fallback to user data
  const [businessType, setBusinessType] = useState(() => {
    const cached = localStorage.getItem('draft_businessType');
    return cached || user?.businessType || '';
  });
  const [customBusinessType, setCustomBusinessType] = useState('');
  const [city, setCity] = useState(user?.city || ''); // V3.2.1: City field (optional)
  // V4.3.22: Sign-off field for custom response signatures
  const [signOff, setSignOff] = useState(user?.signOff || '');
  const [useBusinessNameAsSignOff, setUseBusinessNameAsSignOff] = useState(() => {
    // If signOff is empty or same as businessName, default to "use business name"
    return !user?.signOff || user?.signOff === user?.businessName;
  });
  const [businessContext, setBusinessContext] = useState(user?.businessContext || '');
  const [responseStyle, setResponseStyle] = useState(user?.responseStyle || '');
  const [askUserEnabled, setAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false'; // Default ON
  });
  const [includeSignature, setIncludeSignature] = useState(() => {
    const stored = localStorage.getItem('includeSignature');
    if (stored !== null) return stored === 'true';
    return user?.includeSignature ?? true; // Default ON
  });
  // Response Length setting - auto or user override
  const [responseLength, setResponseLength] = useState(() => {
    const stored = localStorage.getItem('responseLength');
    return stored || 'auto'; // Default: auto (AI calculates)
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [apiKeys, setApiKeys] = useState([]);
  // Scraped data banner dismiss state (V4.0: Task 1.1.3)
  const [scrapedBannerDismissed, setScrapedBannerDismissed] = useState(() => {
    return localStorage.getItem('scraped_banner_dismissed') === 'true';
  });

  // Track if user has visited settings before (for CTA display)
  // Read the value BEFORE this visit - don't update state immediately
  const [isFirstVisit] = useState(() => {
    const wasVisitedBefore = localStorage.getItem('has_visited_settings') === 'true';
    // Mark as visited for NEXT time (but this render still sees the old value)
    localStorage.setItem('has_visited_settings', 'true');
    return !wasVisitedBefore;
  });

  // Quick Adds collapse states for Settings (persistent instructions)
  const [showContextQuickAdds, setShowContextQuickAdds] = useState(false);
  const [showStyleQuickAdds, setShowStyleQuickAdds] = useState(false);

  // Context Quick Adds (Facts + USPs) for Business Context
  const contextQuickAdds = [
    { id: 'family', emoji: '👨‍👩‍👧', label: 'Family-owned', snippet: 'We are a family-owned business', category: 'facts' },
    { id: 'since', emoji: '🎂', label: 'Since 19XX', snippet: 'Established in [year]', category: 'facts' },
    { id: 'award', emoji: '🏆', label: 'Award-winning', snippet: 'Award-winning [type]', category: 'facts' },
    { id: 'local', emoji: '📍', label: 'Local favorite', snippet: 'A local favorite in [city]', category: 'facts' },
    { id: 'known', emoji: '⭐', label: 'Known for...', snippet: 'Known for our [specialty]', category: 'usps' },
    { id: 'specialize', emoji: '🎯', label: 'We specialize', snippet: 'We specialize in [area]', category: 'usps' },
    { id: 'focus', emoji: '💪', label: 'Our focus', snippet: 'Our focus is on [quality/service]', category: 'usps' },
    { id: 'setapart', emoji: '🌟', label: 'Sets us apart', snippet: 'What sets us apart is [unique value]', category: 'usps' },
  ];

  // Style Quick Adds (persistent - from existing quickTemplates)
  const styleQuickAdds = [
    { id: 'contact', emoji: '📞', label: 'Contact Info', snippet: '<always>\n- Include our contact info\n</always>' },
    { id: 'no-fluff', emoji: '🚫', label: 'No Fluff', snippet: '<never>\n- Use fluffy or generic language\n- Say "we appreciate your feedback"\n</never>' },
    { id: 'short', emoji: '✂️', label: 'Short & Sweet', snippet: '<style>\n- Keep responses under 3 sentences\n- Be concise and direct\n</style>' },
    { id: 'use-name', emoji: '👋', label: 'Use Name', snippet: '<always>\n- Address the reviewer by name if available\n</always>' },
    { id: 'no-sorry', emoji: '🚫', label: 'No "Sorry"', snippet: '<never>\nNEVER say: sorry, apologies, regret, unfortunately\n</never>' },
    { id: 'sign-name', emoji: '✍️', label: 'Sign Name', snippet: '<always>\n- Sign off with team name\n</always>' },
    { id: 'invite-back', emoji: '📅', label: 'Invite Back', snippet: '<always>\n- For positive reviews, end with a warm invitation to visit again\n</always>' },
    { id: 'offer', emoji: '🎁', label: 'Make Offer', snippet: '<always>\n- For negative reviews, offer to make things right or discuss further\n</always>' },
  ];

  // Check if context Quick Add is active
  const isContextQuickAddActive = (template) => {
    const coreText = template.snippet.replace(/\[.*?\]/g, '').trim();
    return businessContext.includes(coreText) || businessContext.includes(template.snippet);
  };

  // Check if style Quick Add is active
  const isStyleQuickAddActive = (template) => {
    const coreText = template.snippet.replace(/<\/?[a-z]+>/g, '').replace(/\n/g, ' ').replace(/- /g, '').trim();
    return responseStyle.includes(coreText) || responseStyle.includes(template.snippet);
  };

  // Add context Quick Add
  const addContextQuickAdd = (template) => {
    if (isContextQuickAddActive(template)) return;
    setBusinessContext(prev => prev.trim() ? `${prev.trim()}\n${template.snippet}` : template.snippet);
  };

  // Remove context Quick Add
  const removeContextQuickAdd = (template) => {
    setBusinessContext(prev => {
      // Try to remove the full snippet first
      let result = prev.replace(template.snippet, '');
      // Also try without the placeholder brackets
      const coreText = template.snippet.replace(/\[.*?\]/g, '').trim();
      if (result === prev) {
        result = prev.replace(coreText, '');
      }
      // Clean up extra newlines
      return result.replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '').trim();
    });
  };

  // Add style Quick Add
  const addStyleQuickAdd = (template) => {
    if (isStyleQuickAddActive(template)) return;
    setResponseStyle(prev => prev.trim() ? `${prev.trim()}\n\n${template.snippet}` : template.snippet);
  };

  // Remove style Quick Add
  const removeStyleQuickAdd = (template) => {
    setResponseStyle(prev => {
      // Try to remove the full snippet first
      let result = prev.replace(template.snippet, '');
      // Also try core text without tags
      const coreText = template.snippet.replace(/<\/?[a-z]+>/g, '').replace(/\n/g, ' ').replace(/- /g, '').trim();
      if (result === prev) {
        result = prev.replace(coreText, '');
      }
      // Clean up extra newlines
      return result.replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '').trim();
    });
  };
  const [loadingKeys, setLoadingKeys] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Grouped business types for react-select
  const businessTypeOptions = [
    {
      label: 'Most Common',
      options: [
        { value: 'Restaurant', label: 'Restaurant' },
        { value: 'Dental Practice', label: 'Dental Practice' },
        { value: 'Medical Practice', label: 'Medical Practice' },
        { value: 'Hair Salon / Barbershop', label: 'Hair Salon / Barbershop' },
        { value: 'Auto Repair / Service', label: 'Auto Repair / Service' },
        { value: 'Real Estate', label: 'Real Estate' },
        { value: 'Home Services', label: 'Home Services' },
      ],
    },
    {
      label: 'Food & Hospitality',
      options: [
        { value: 'Cafe / Coffee Shop', label: 'Cafe / Coffee Shop' },
        { value: 'Bar / Nightclub', label: 'Bar / Nightclub' },
        { value: 'Hotel / Accommodation', label: 'Hotel / Accommodation' },
      ],
    },
    {
      label: 'Health & Beauty',
      options: [
        { value: 'Spa / Wellness', label: 'Spa / Wellness' },
        { value: 'Gym / Fitness Studio', label: 'Gym / Fitness Studio' },
        { value: 'Veterinary / Pet Services', label: 'Veterinary / Pet Services' },
        { value: 'Senior Care', label: 'Senior Care' },
        { value: 'Therapy / Counseling', label: 'Therapy / Counseling' },
      ],
    },
    {
      label: 'Professional',
      options: [
        { value: 'Professional Services', label: 'Professional Services' },
        { value: 'Financial Services', label: 'Financial Services' },
        { value: 'Retail Store', label: 'Retail Store' },
        { value: 'E-commerce', label: 'E-commerce' },
      ],
    },
    {
      label: 'Lifestyle',
      options: [
        { value: 'Photography / Creative', label: 'Photography / Creative' },
        { value: 'Event Planning', label: 'Event Planning' },
        { value: 'Childcare / Education', label: 'Childcare / Education' },
      ],
    },
    {
      label: 'Other',
      options: [{ value: 'Other', label: 'Other (Custom)' }],
    },
  ];

  // Flat list for validation
  const businessTypes = businessTypeOptions.flatMap(g => g.options.map(o => o.value));
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const loadApiKeys = async () => {
    setLoadingKeys(true);
    try {
      const res = await api.get('/keys');
      setApiKeys(res.data.keys || []);
    } catch (e) {
    } finally {
      setLoadingKeys(false);
    }
  };
  const createApiKey = async () => {
    setCreatingKey(true);
    try {
      const res = await api.post('/keys', { name: newKeyName || 'API Key' });
      setGeneratedKey(res.data.key);
      setNewKeyName('');
      loadApiKeys();
      toast.success('API key created!');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setCreatingKey(false);
    }
  };
  const deleteApiKey = async keyId => {
    if (!window.confirm('Delete?')) return;
    try {
      await api.delete(`/keys/${keyId}`);
      setApiKeys(apiKeys.filter(k => k.id !== keyId));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed');
    }
  };
  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  useEffect(() => {
    if (user) {
      setBusinessName(user.businessName || '');
      if (user.plan === 'unlimited' && user.subscriptionStatus === 'active') loadApiKeys();
      // Check if user has a custom business type (not in preset list)
      if (user.businessType && businessTypes.includes(user.businessType)) {
        setBusinessType(user.businessType);
        setCustomBusinessType('');
      } else if (user.businessType) {
        // Custom type - show "Other" dropdown + custom value in input
        setBusinessType('Other');
        setCustomBusinessType(user.businessType);
      } else {
        setBusinessType('');
        setCustomBusinessType('');
      }
      setCity(user.city || ''); // V3.2.1
      // V4.3.22: Sign-off field
      setSignOff(user.signOff || '');
      setUseBusinessNameAsSignOff(!user.signOff || user.signOff === user.businessName);
      setBusinessContext(user.businessContext || '');
      setResponseStyle(user.responseStyle || '');
    }
  }, [user]);

  // Core save function (used by both auto-save and manual save)
  const doSave = async (showToast = false) => {
    setSaving(true);
    setSaveStatus('saving');

    const finalBusinessType = businessType === 'Other' ? customBusinessType.trim() : businessType;

    try {
      const res = await api.put('/auth/profile', {
        businessName,
        businessType: finalBusinessType,
        city, // V3.2.1: City field
        signOff: useBusinessNameAsSignOff ? null : signOff, // V4.3.22: null = use businessName
        businessContext,
        responseStyle,
        includeSignature: true, // Always include signature
      });
      updateUser(res.data.user);
      localStorage.removeItem('draft_businessType');
      setSaveStatus('saved');
      if (showToast) {
        toast.success('Settings saved!');
      }
      // Reset to idle after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('error');
      if (showToast) {
        toast.error('Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when fields change (debounced 1.5 seconds)
  useEffect(() => {
    // Skip initial mount to avoid saving on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(() => {
      doSave(false);
    }, 1500);

    // Cleanup on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [businessName, businessType, customBusinessType, city, signOff, useBusinessNameAsSignOff, businessContext, responseStyle]);

  // Manual save handler (for the button)
  const handleSave = async e => {
    e.preventDefault();
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    await doSave(true);
  };

  return (
    <div
      className="container"
      style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '800px' }}
    >
      <div style={{ marginBottom: '32px' }}>
        <Link
          to="/dashboard"
          onClick={(e) => {
            // If no personalization set, redirect to dashboard with onboarding trigger
            if (!businessContext?.trim() && !responseStyle?.trim()) {
              e.preventDefault();
              navigate('/dashboard?showOnboarding=true');
            }
          }}
          style={{
            color: 'var(--primary-600)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          ← Back to Dashboard
        </Link>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }}>
            <Settings size={28} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Business Settings
          </h1>
          {/* Auto-save status indicator */}
          <span
            style={{
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color:
                saveStatus === 'saving'
                  ? 'var(--gray-500)'
                  : saveStatus === 'saved'
                    ? 'var(--green-600)'
                    : saveStatus === 'error'
                      ? 'var(--red-500)'
                      : 'transparent',
              transition: 'color 0.2s',
            }}
          >
            {saveStatus === 'saving' && (
              <>
                <Loader size={14} className="spin" />
                Saving...
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <Check size={14} />
                Saved
              </>
            )}
            {saveStatus === 'error' && <>Save failed</>}
          </span>
        </div>
        <p style={{ color: 'var(--gray-600)' }}>
          Add details about your business to get more personalized responses
          <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--gray-400)' }}>
            (auto-saves as you type)
          </span>
        </p>
        <span
          style={{
            display: 'inline-block',
            marginTop: '8px',
            background: 'var(--primary-100)',
            color: 'var(--primary-700)',
            padding: '3px 10px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
          }}
        >
          Synced with Dashboard & Extension
        </span>
      </div>

      {/* V4.0 Task 1.1.3: Info banner for scraped/pre-filled data */}
      {user?.dataSource === 'playwright' && !scrapedBannerDismissed && (
        <div
          style={{
            background: 'var(--blue-50)',
            border: '1px solid var(--blue-200)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <Info size={20} style={{ color: 'var(--blue-600)', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--blue-800)', fontWeight: '500' }}>
              We pre-filled your business details
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--blue-700)' }}>
              Based on publicly available data from your demo, we've pre-filled some fields to save you time.
              Feel free to review and edit these details to make them perfect.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setScrapedBannerDismissed(true);
              localStorage.setItem('scraped_banner_dismissed', 'true');
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              color: 'var(--blue-400)',
              flexShrink: 0,
            }}
            title="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Building size={20} />
            Basic Information
          </h2>

          <div className="form-group">
            <label className="form-label">Business Name</label>
            <input
              type="text"
              className="form-input"
              value={businessName}
              onChange={e => setBusinessName(e.target.value)}
              placeholder="e.g., Mario's Italian Restaurant"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Business Type</label>
            <Select
              options={businessTypeOptions}
              value={
                businessType
                  ? businessTypeOptions
                      .flatMap(g => g.options)
                      .find(o => o.value === businessType) || null
                  : null
              }
              onChange={option => {
                const newType = option?.value || '';
                setBusinessType(newType);
                // Cache in localStorage as draft
                if (newType) {
                  localStorage.setItem('draft_businessType', newType);
                } else {
                  localStorage.removeItem('draft_businessType');
                }
                if (newType !== 'Other') {
                  setCustomBusinessType('');
                }
              }}
              placeholder="Search or select type..."
              isClearable
              isSearchable
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? 'var(--primary)' : 'var(--gray-200)',
                  boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                  '&:hover': { borderColor: 'var(--primary)' },
                  borderRadius: '8px',
                  padding: '2px',
                  backgroundColor: 'var(--bg-primary)',
                }),
                menu: base => ({
                  ...base,
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--gray-200)',
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? 'var(--primary)'
                    : state.isFocused
                      ? 'var(--bg-tertiary)'
                      : 'var(--bg-primary)',
                  color: state.isSelected ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                }),
                singleValue: base => ({
                  ...base,
                  color: 'var(--text-primary)',
                }),
                input: base => ({
                  ...base,
                  color: 'var(--text-primary)',
                }),
                groupHeading: base => ({
                  ...base,
                  fontWeight: '600',
                  color: 'var(--gray-500)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                }),
              }}
            />
            {businessType === 'Other' && (
              <input
                type="text"
                className="form-input"
                placeholder="Enter your business type..."
                value={customBusinessType}
                onChange={e => setCustomBusinessType(e.target.value)}
                style={{ marginTop: '8px' }}
              />
            )}
          </div>

          {/* V3.2.1: City field (optional) */}
          <div className="form-group" style={{ marginTop: '16px' }}>
            <label className="form-label">
              City <span style={{ color: 'var(--gray-400)', fontWeight: 'normal' }}>(optional)</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder="e.g., Munich, Berlin, Vienna"
            />
            <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
              Helps with local references in your responses
            </p>
          </div>

          {/* V4.3.22: Sign-off field */}
          <div className="form-group" style={{ marginTop: '16px' }}>
            <label className="form-label">
              Sign-off Name <span style={{ color: 'var(--gray-400)', fontWeight: 'normal' }}>(optional)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: 'var(--gray-600)' }}>
                <input
                  type="checkbox"
                  checked={useBusinessNameAsSignOff}
                  onChange={e => {
                    setUseBusinessNameAsSignOff(e.target.checked);
                    if (e.target.checked) {
                      setSignOff(businessName);
                    }
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Use Business Name
              </label>
            </div>
            <input
              type="text"
              className="form-input"
              value={useBusinessNameAsSignOff ? businessName : signOff}
              onChange={e => setSignOff(e.target.value)}
              placeholder="e.g., Marco, The Team, Management"
              disabled={useBusinessNameAsSignOff}
              style={{
                backgroundColor: useBusinessNameAsSignOff ? 'var(--gray-100)' : 'white',
                cursor: useBusinessNameAsSignOff ? 'not-allowed' : 'text'
              }}
            />
            <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
              How responses are signed (e.g., "Marco" or "Your Business")
            </p>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Business Context
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
            Tell us about your business so ReviewResponder can mention specific details in responses
          </p>

          <AIContextGenerator
            field="context"
            businessType={businessType}
            businessName={businessName}
            currentValue={businessContext}
            onGenerated={text => setBusinessContext(prev => prev.trim() ? `${prev.trim()}\n\n${text}` : text)}
          />

          <div className="form-group">
            <label className="form-label">About Your Business</label>
            <textarea
              className="form-textarea"
              value={businessContext}
              onChange={e => setBusinessContext(e.target.value)}
              placeholder={getTextareaPlaceholder(businessType)}
              rows={8}
            />
            <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '8px' }}>
              The more details you provide, the more personalized your responses will be
            </p>

            {/* Collapsed Quick Adds for Business Context */}
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setShowContextQuickAdds(!showContextQuickAdds)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: 'none',
                  padding: '8px 0',
                  fontSize: '13px',
                  color: 'var(--primary-600)',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                <span style={{
                  transform: showContextQuickAdds ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                }}>▶</span>
                + Quick Add
              </button>

              {showContextQuickAdds && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  background: 'var(--gray-50)',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)',
                }}>
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)', margin: '0 0 12px 0' }}>
                    Click to add to your profile
                  </p>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                      Facts
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {contextQuickAdds.filter(t => t.category === 'facts').map(template => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => addContextQuickAdd(template)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            background: isContextQuickAddActive(template) ? 'var(--primary-100)' : 'var(--card-bg)',
                            border: `1px solid ${isContextQuickAddActive(template) ? 'var(--primary-300)' : 'var(--gray-200)'}`,
                            borderRadius: '16px',
                            cursor: isContextQuickAddActive(template) ? 'default' : 'pointer',
                            color: isContextQuickAddActive(template) ? 'var(--primary-700)' : 'var(--gray-700)',
                            opacity: isContextQuickAddActive(template) ? 0.7 : 1,
                          }}
                        >
                          <span>{template.emoji}</span>
                          <span>{template.label}</span>
                          {isContextQuickAddActive(template) && <span>✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                      USPs
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {contextQuickAdds.filter(t => t.category === 'usps').map(template => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => addContextQuickAdd(template)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            background: isContextQuickAddActive(template) ? 'var(--primary-100)' : 'var(--card-bg)',
                            border: `1px solid ${isContextQuickAddActive(template) ? 'var(--primary-300)' : 'var(--gray-200)'}`,
                            borderRadius: '16px',
                            cursor: isContextQuickAddActive(template) ? 'default' : 'pointer',
                            color: isContextQuickAddActive(template) ? 'var(--primary-700)' : 'var(--gray-700)',
                            opacity: isContextQuickAddActive(template) ? 0.7 : 1,
                          }}
                        >
                          <span>{template.emoji}</span>
                          <span>{template.label}</span>
                          {isContextQuickAddActive(template) && <span>✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Response Style Preferences
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
            Any specific instructions for how you want responses written
          </p>

          <AIContextGenerator
            field="style"
            businessType={businessType}
            businessName={businessName}
            currentValue={responseStyle}
            onGenerated={text => setResponseStyle(prev => prev.trim() ? `${prev.trim()}\n\n${text}` : text)}
          />

          <div className="form-group">
            <label className="form-label">Style Instructions</label>
            <textarea
              className="form-textarea"
              value={responseStyle}
              onChange={e => setResponseStyle(e.target.value)}
              placeholder={`<always>
- Sign off with "The [Business Name] Team"
- Invite to contact us at (555) 123-4567
</always>
<never>
- Offer discounts or compensation
- Admit fault or liability
</never>
<style>
- Casual and warm tone
- Max 3 sentences
</style>`}
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
            <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
              Use XML tags for clearer structure: {'<always>'}, {'<never>'}, {'<style>'}
            </p>

            {/* Collapsed Quick Adds for Response Style */}
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setShowStyleQuickAdds(!showStyleQuickAdds)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: 'none',
                  padding: '8px 0',
                  fontSize: '13px',
                  color: 'var(--primary-600)',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                <span style={{
                  transform: showStyleQuickAdds ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                }}>▶</span>
                + Quick Add
              </button>

              {showStyleQuickAdds && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  background: 'var(--gray-50)',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)',
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {styleQuickAdds.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => addStyleQuickAdd(template)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          background: isStyleQuickAddActive(template) ? 'var(--primary-100)' : 'var(--card-bg)',
                          border: `1px solid ${isStyleQuickAddActive(template) ? 'var(--primary-300)' : 'var(--gray-200)'}`,
                          borderRadius: '16px',
                          cursor: isStyleQuickAddActive(template) ? 'default' : 'pointer',
                          color: isStyleQuickAddActive(template) ? 'var(--primary-700)' : 'var(--gray-700)',
                          opacity: isStyleQuickAddActive(template) ? 0.7 : 1,
                        }}
                      >
                        <span>{template.emoji}</span>
                        <span>{template.label}</span>
                        {isStyleQuickAddActive(template) && <span>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Human-in-the-Loop Mode */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Human-in-the-Loop Mode
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
            Make every response truly yours with one-click personalization
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              background: askUserEnabled ? 'var(--primary-50)' : 'var(--gray-50)',
              borderRadius: '8px',
              border: `1px solid ${askUserEnabled ? 'var(--primary-200)' : 'var(--gray-200)'}`,
              transition: 'all 0.2s ease',
            }}
          >
            <input
              type="checkbox"
              id="askUserEnabled"
              checked={askUserEnabled}
              onChange={(e) => {
                setAskUserEnabled(e.target.checked);
                localStorage.setItem('askUser_enabled', e.target.checked);
              }}
              style={{
                width: '20px',
                height: '20px',
                marginTop: '2px',
                accentColor: 'var(--primary-500)',
                cursor: 'pointer',
              }}
            />
            <div style={{ flex: 1 }}>
              <label
                htmlFor="askUserEnabled"
                style={{
                  fontWeight: '500',
                  fontSize: '15px',
                  color: 'var(--gray-700)',
                  cursor: 'pointer',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Craft your own response
              </label>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', margin: 0, lineHeight: '1.5' }}>
                AI creates the framework, you pick the perfect words. Click
                <span style={{ color: 'var(--primary-500)', fontWeight: '500' }}> [blue options] </span>
                to personalize details like dish names, greetings, or specific offers.
                <strong style={{ color: 'var(--primary-600)' }}> Your voice, zero guesswork!</strong>
              </p>
            </div>
          </div>

          <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '12px' }}>
            Example: "Sorry your <span style={{ color: 'var(--primary-500)' }}>[Margherita pizza]</span> arrived cold"
            — click to select the actual dish from your menu.
          </p>
        </div>

        {/* Manual save button - backup for auto-save */}
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <button
            type="submit"
            className="btn"
            style={{
              background: 'var(--gray-100)',
              border: '1px solid var(--gray-200)',
              color: 'var(--gray-600)',
              padding: '10px 24px',
            }}
            disabled={saving}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <Save size={16} style={{ marginRight: '6px' }} />
                Save Now
              </>
            )}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '8px' }}>
            Changes are auto-saved. Use this button if you want to save immediately.
          </p>
        </div>
      </form>

      {/* API Key Management - Only for Unlimited Plan */}
      {user?.plan === 'unlimited' && user?.subscriptionStatus === 'active' && (
        <div className="card" style={{ marginTop: '24px' }}>
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Key size={20} />
            API Access
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
            Generate API keys to integrate ReviewResponder with your applications.{' '}
            <Link to="/api-docs" style={{ color: 'var(--primary-600)' }}>
              View API Documentation
            </Link>
          </p>

          {generatedKey && (
            <div
              style={{
                background: 'var(--success-50)',
                border: '1px solid var(--success-200)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <p style={{ fontWeight: '600', color: 'var(--success-700)', marginBottom: '8px' }}>
                New API Key Created!
              </p>
              <p style={{ fontSize: '12px', color: 'var(--success-600)', marginBottom: '8px' }}>
                Copy this key now. It won't be shown again.
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <code
                  style={{
                    flex: 1,
                    background: 'var(--bg-secondary)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    wordBreak: 'break-all',
                    color: 'var(--text-primary)',
                  }}
                >
                  {generatedKey}
                </code>
                <button
                  onClick={() => copyToClipboard(generatedKey)}
                  className="btn btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <button
                onClick={() => setGeneratedKey(null)}
                style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'var(--success-600)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Key name (optional)"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              style={{ flex: 1 }}
            />
            <button onClick={createApiKey} className="btn btn-primary" disabled={creatingKey}>
              {creatingKey ? 'Creating...' : 'Generate Key'}
            </button>
          </div>

          {loadingKeys ? (
            <p>Loading keys...</p>
          ) : apiKeys.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
              No API keys yet. Generate one to get started.
            </p>
          ) : (
            <div
              style={{
                border: '1px solid var(--gray-200)',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {apiKeys.map((key, idx) => (
                <div
                  key={key.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: idx < apiKeys.length - 1 ? '1px solid var(--gray-200)' : 'none',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '500' }}>{key.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                      {key.key_prefix}... | {key.requests_today}/100 today | {key.requests_total}{' '}
                      total
                    </div>
                  </div>
                  <button
                    onClick={() => deleteApiKey(key.id)}
                    className="btn btn-sm"
                    style={{ color: 'var(--error-600)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Start Generating CTA - Shows different content based on first visit */}
      <div
        style={{
          marginTop: '32px',
          padding: '24px',
          background: 'linear-gradient(135deg, var(--primary-50), var(--primary-100))',
          border: '2px solid var(--primary-200)',
          borderRadius: '12px',
          textAlign: 'center',
        }}
      >
        {isFirstVisit ? (
          <>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: 'var(--primary-700)' }}>
              Ready to craft your first response?
            </h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '16px' }}>
              {businessContext?.trim()
                ? `Personalized for ${businessName || 'your business'}`
                : 'Try it now - works even without customization'}
            </p>
            <Link
              to="/dashboard"
              className="btn btn-primary"
              style={{
                padding: '14px 36px',
                fontSize: '16px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Sparkles size={18} />
              Craft Your First Response
            </Link>
          </>
        ) : (
          <Link
            to="/dashboard"
            className="btn btn-primary"
            style={{
              padding: '16px 48px',
              fontSize: '18px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </Link>
        )}
      </div>

      {/* Chrome Extension Promo */}
      <div
        style={{
          marginTop: '24px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, var(--gray-50), #f0f9ff)',
          border: '1px solid var(--gray-200)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}
      >
        <Chrome size={20} style={{ color: 'var(--primary)' }} />
        <span style={{ fontSize: '14px', color: 'var(--gray-700)' }}>
          Want to respond faster?{' '}
          <a
            href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary)', fontWeight: '600', textDecoration: 'none' }}
          >
            Add to Chrome →
          </a>
        </span>
      </div>
    </div>
  );
};

// Team Management Page (Pro & Unlimited)
const TeamPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const loadTeamData = async () => {
    setLoading(true);
    try {
      const [teamRes, myTeamRes] = await Promise.all([
        api.get('/team').catch(() => null),
        api.get('/team/my-team').catch(() => null),
      ]);
      setTeamData(teamRes?.data || null);
      setMyTeam(myTeamRes?.data || null);
    } catch (e) {
      console.error('Load team error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeamData();
  }, []);

  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const handleInvite = async e => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await api.post('/team/invite', { email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      // Show invite URL in case email doesn't arrive
      if (res.data.inviteUrl) {
        setLastInviteUrl(res.data.inviteUrl);
      }
      setInviteEmail('');
      loadTeamData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async memberId => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api.delete(`/team/${memberId}`);
      toast.success('Member removed');
      loadTeamData();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await api.put(`/team/${memberId}/role`, { role: newRole });
      toast.success('Role updated');
      loadTeamData();
    } catch (e) {
      toast.error('Failed to update role');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this team? You will lose access to the shared subscription.'))
      return;
    setLeaving(true);
    try {
      await api.post('/team/leave');
      toast.success('You have left the team');
      loadTeamData();
    } catch (e) {
      toast.error('Failed to leave team');
    } finally {
      setLeaving(false);
    }
  };

  const canManageTeam = ['professional', 'unlimited'].includes(user?.plan);
  const maxMembers = user?.plan === 'unlimited' ? 10 : user?.plan === 'professional' ? 3 : 0;

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <div className="spinner" />
        <p style={{ color: 'var(--gray-500)', marginTop: '16px' }}>Loading team...</p>
      </div>
    );
  }

  // User is a team member (belongs to someone else's team)
  if (myTeam?.isTeamMember) {
    return (
      <div
        className="container"
        style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '800px' }}
      >
        <Link
          to="/dashboard"
          style={{
            color: 'var(--primary-600)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          ← Back to Dashboard
        </Link>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
          <Users size={28} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Team Membership
        </h1>
        <div className="card" style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'var(--primary-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Users size={28} style={{ color: 'var(--primary-600)' }} />
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600' }}>
                {myTeam.teamOwner.businessName || myTeam.teamOwner.email}
              </h3>
              <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>Your team owner</p>
            </div>
          </div>
          <div
            style={{
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: 'var(--gray-600)' }}>Your Role</span>
              <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{myTeam.role}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--gray-600)' }}>Team Usage</span>
              <span style={{ fontWeight: '500' }}>
                {myTeam.teamUsage?.used || 0} / {myTeam.teamUsage?.limit || 0}
              </span>
            </div>
          </div>
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="btn btn-secondary"
            style={{ color: 'var(--error-600)' }}
          >
            {leaving ? 'Leaving...' : 'Leave Team'}
          </button>
        </div>
      </div>
    );
  }

  // User is not on a Pro/Unlimited plan - show upgrade prompt
  if (!canManageTeam) {
    return (
      <div
        className="container"
        style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '800px' }}
      >
        <Link
          to="/dashboard"
          style={{
            color: 'var(--primary-600)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          ← Back to Dashboard
        </Link>
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <Users size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
            Team Features
          </h2>
          <p
            style={{
              color: 'var(--gray-600)',
              marginBottom: '24px',
              maxWidth: '400px',
              margin: '0 auto 24px',
            }}
          >
            Invite team members to share your subscription. Pro plan allows 3 members, Unlimited
            allows 10.
          </p>
          <Link to="/pricing" className="btn btn-primary">
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  // User can manage team (Pro or Unlimited)
  return (
    <div
      className="container"
      style={{ paddingTop: '40px', paddingBottom: '60px', maxWidth: '800px' }}
    >
      <Link
        to="/dashboard"
        style={{
          color: 'var(--primary-600)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginBottom: '16px',
          fontSize: '14px',
        }}
      >
        ← Back to Dashboard
      </Link>
      <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
        <Users size={28} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Team Management
      </h1>
      <p style={{ color: 'var(--gray-600)', marginBottom: '32px' }}>
        Invite team members to generate responses using your subscription
      </p>

      {/* Invite Form */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          Invite Team Member
        </h3>
        <form onSubmit={handleInvite} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="email"
            placeholder="Email address"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            className="form-input"
            style={{ flex: '1', minWidth: '200px' }}
            required
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={inviting || teamData?.members?.length >= maxMembers}
          >
            {inviting ? 'Inviting...' : 'Invite'}
          </button>
        </form>
        <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '12px' }}>
          {teamData?.members?.length || 0} / {maxMembers} team members used
          {user?.plan === 'professional' && (
            <span>
              {' '}
              ·{' '}
              <Link to="/pricing" style={{ color: 'var(--primary-600)' }}>
                Upgrade for more
              </Link>
            </span>
          )}
        </p>
        {lastInviteUrl && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              background: 'var(--primary-50)',
              border: '1px solid var(--primary-200)',
              borderRadius: '8px',
            }}
          >
            <p
              style={{
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--primary-700)',
                marginBottom: '8px',
              }}
            >
              📧 Email not arriving? Share this invite link directly:
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={lastInviteUrl}
                readOnly
                className="form-input"
                style={{ flex: '1', fontSize: '12px', fontFamily: 'monospace' }}
                onClick={e => e.target.select()}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(lastInviteUrl);
                  toast.success('Link copied!');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() => setLastInviteUrl(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--gray-400)',
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Team Members List */}
      <div className="card">
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Team Members</h3>
        {!teamData?.members?.length ? (
          <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
            No team members yet. Invite someone to get started!
          </p>
        ) : (
          <div
            style={{ border: '1px solid var(--gray-200)', borderRadius: '8px', overflow: 'hidden' }}
          >
            {teamData.members.map((member, idx) => (
              <div
                key={member.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  borderBottom:
                    idx < teamData.members.length - 1 ? '1px solid var(--gray-200)' : 'none',
                  background:
                    member.status === 'pending' ? 'var(--gray-50)' : 'var(--bg-secondary)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background:
                        member.status === 'active' ? 'var(--primary-100)' : 'var(--gray-200)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Users
                      size={18}
                      style={{
                        color:
                          member.status === 'active' ? 'var(--primary-600)' : 'var(--gray-400)',
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>{member.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                      {member.status === 'pending'
                        ? 'Invitation pending...'
                        : `Joined ${new Date(member.acceptedAt).toLocaleDateString()}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span
                    style={{
                      fontSize: '13px',
                      color: 'var(--gray-600)',
                      background: 'var(--gray-100)',
                      padding: '4px 10px',
                      borderRadius: '4px',
                    }}
                  >
                    Member
                  </span>
                  <button
                    onClick={() => handleRemove(member.id)}
                    className="btn btn-sm"
                    style={{ color: 'var(--error-600)', padding: '6px' }}
                    title="Remove member"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Join Team Page (for accepting invitations)
const JoinTeamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invitation token provided');
        setLoading(false);
        return;
      }
      try {
        const res = await api.get(`/team/invite/${token}`);
        setInvitation(res.data);
      } catch (e) {
        setError(e.response?.data?.error || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      sessionStorage.setItem('pendingTeamInvite', token);
      toast.info('Please log in or create an account to join the team');
      navigate('/login');
      return;
    }
    setAccepting(true);
    try {
      const res = await api.post('/team/accept', { token });
      toast.success(res.data.message);
      navigate('/team');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    const pendingToken = sessionStorage.getItem('pendingTeamInvite');
    if (user && pendingToken) {
      sessionStorage.removeItem('pendingTeamInvite');
      api
        .post('/team/accept', { token: pendingToken })
        .then(res => {
          toast.success(res.data.message);
          navigate('/team');
        })
        .catch(e => toast.error(e.response?.data?.error || 'Failed to accept invitation'));
    }
  }, [user, navigate]);

  if (loading)
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p>Validating invitation...</p>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <AlertCircle size={48} style={{ color: 'var(--error-600)', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
            Invalid Invitation
          </h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>{error}</p>
          <Link to="/" className="btn btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );

  return (
    <div className="auth-container">
      <div className="card auth-card" style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'var(--primary-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <Users size={32} style={{ color: 'var(--primary-600)' }} />
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
          Team Invitation
        </h2>
        <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
          <strong>{invitation?.invitedBy}</strong> invited you to join their ReviewResponder team as
          a <strong style={{ textTransform: 'capitalize' }}>{invitation?.role}</strong>.
        </p>
        {user ? (
          user.email.toLowerCase() === invitation?.invitedEmail?.toLowerCase() ? (
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          ) : (
            <div>
              <p style={{ color: 'var(--error-600)', marginBottom: '16px' }}>
                This invitation was sent to {invitation?.invitedEmail}. You are logged in as{' '}
                {user.email}.
              </p>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.reload();
                }}
                className="btn btn-secondary"
                style={{ width: '100%' }}
              >
                Log out and try again
              </button>
            </div>
          )
        ) : (
          <div>
            <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '16px' }}>
              Log in with <strong>{invitation?.invitedEmail}</strong> to accept
            </p>
            <button onClick={handleAccept} className="btn btn-primary" style={{ width: '100%' }}>
              Log In to Accept
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== PUBLIC BLOG PAGES ==============

// Blog List Page - Public SEO Blog
const BlogListPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  // Language toggle (de/en) for article previews - persisted in localStorage
  const [language, setLanguageState] = useState(() => {
    try { return localStorage.getItem('blog-language') || 'de'; } catch { return 'de'; }
  });
  const setLanguage = (lang) => {
    setLanguageState(lang);
    try { localStorage.setItem('blog-language', lang); } catch {}
  };

  // Get translated field or fallback to original
  const getField = (article, field) => {
    if (language === 'en' && article[`${field}_en`]) {
      return article[`${field}_en`];
    }
    return article[field];
  };

  const handleGoogleSignIn = async credentialResponse => {
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome! Redirecting to generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Google sign in failed. Please try again.');
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [page, selectedCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await fetch(`${API_URL.replace('/api', '')}/api/public/blog?${params}`);
      const data = await response.json();
      setArticles(data.articles || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL.replace('/api', '')}/api/public/blog/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)' }}>
      <SEO
        title="Blog - Review Management Tips & Guides"
        description="Expert guides on review management, reputation building, and customer feedback strategies. Learn how to respond to reviews and grow your business."
        canonical="/blog"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'ReviewResponder Blog',
          description: 'Expert guides on review management and customer feedback strategies',
          url: 'https://tryreviewresponder.com/blog',
        }}
      />
      {/* Hero Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
          padding: '80px 20px 60px',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <h1 style={{ fontSize: '42px', fontWeight: '700', marginBottom: '16px' }}>
          Review Management Blog
        </h1>
        {/* Language Toggle - Segmented Control */}
        <div style={{
          display: 'inline-flex',
          borderRadius: '24px',
          border: '2px solid rgba(255,255,255,0.3)',
          background: 'rgba(255,255,255,0.1)',
          padding: '4px',
          marginTop: '8px',
        }}>
          <button
            onClick={() => setLanguage('de')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              borderRadius: '20px',
              border: 'none',
              background: language === 'de' ? 'white' : 'transparent',
              color: language === 'de' ? '#4F46E5' : 'rgba(255,255,255,0.8)',
              fontSize: '14px',
              fontWeight: language === 'de' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontSize: '16px' }}>🇩🇪</span>
            DE
          </button>
          <button
            onClick={() => setLanguage('en')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              borderRadius: '20px',
              border: 'none',
              background: language === 'en' ? 'white' : 'transparent',
              color: language === 'en' ? '#4F46E5' : 'rgba(255,255,255,0.8)',
              fontSize: '14px',
              fontWeight: language === 'en' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontSize: '16px' }}>🇬🇧</span>
            EN
          </button>
        </div>
      </div>

      <div
        className="container"
        style={{ maxWidth: '1200px', padding: '40px 20px', margin: '0 auto' }}
      >
        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '32px' }}>
          <button
            onClick={() => {
              setSelectedCategory('');
              setPage(1);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: 'none',
              background: !selectedCategory ? 'var(--primary)' : 'var(--card-bg)',
              color: !selectedCategory ? 'white' : 'var(--gray-600)',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            All Posts
          </button>
          {categories.map(cat => (
            <button
              key={cat.category}
              onClick={() => {
                setSelectedCategory(cat.category);
                setPage(1);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                background: selectedCategory === cat.category ? 'var(--primary)' : 'var(--card-bg)',
                color: selectedCategory === cat.category ? 'white' : 'var(--gray-600)',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              {cat.category} ({cat.count})
            </button>
          ))}
        </div>

        {/* Article Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>Loading...</div>
        ) : articles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--gray-500)' }}>
            <p style={{ fontSize: '18px', marginBottom: '16px' }}>No articles yet.</p>
            <p>Check back soon for expert review management tips!</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '24px',
            }}
          >
            {articles.map(article => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <article
                  style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    height: '100%',
                    padding: '24px',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'var(--primary)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {article.category}
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--gray-400)' }}>
                      {article.read_time_minutes} min read
                    </span>
                  </div>
                  <h2
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      lineHeight: '1.3',
                    }}
                  >
                    {getField(article, 'title')}
                  </h2>
                  <p
                    style={{
                      fontSize: '14px',
                      color: 'var(--gray-500)',
                      lineHeight: '1.6',
                      marginBottom: '16px',
                    }}
                  >
                    {getField(article, 'meta_description')}
                  </p>
                  <div
                    style={{
                      fontSize: '13px',
                      color: 'var(--gray-400)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span>{article.published_at && formatDate(article.published_at)}</span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '48px',
            }}
          >
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid var(--gray-200)',
                background: 'var(--bg-secondary)',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                opacity: page === 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <span style={{ padding: '10px 20px', display: 'flex', alignItems: 'center' }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid var(--gray-200)',
                background: 'var(--bg-secondary)',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                opacity: page === totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        )}

        {/* CTA Section */}
        <div
          style={{
            marginTop: '60px',
            padding: '40px 32px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            borderRadius: '16px',
            textAlign: 'center',
            color: 'white',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Craft Your Own Review Responses
          </h2>
          <p
            style={{
              fontSize: '13px',
              opacity: 0.85,
              marginBottom: '20px',
            }}
          >
            Based on principles from Industrial and Organizational Psychology
          </p>
          {user ? (
            <Link
              to="/generator"
              style={{
                display: 'inline-block',
                padding: '12px 28px',
                background: 'var(--bg-secondary)',
                color: 'var(--primary)',
                borderRadius: '8px',
                fontWeight: '600',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              Go to Generator
            </Link>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="signup_with"
                shape="rectangular"
                size="large"
              />
              <Link
                to="/register"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '13px',
                  textDecoration: 'none',
                }}
              >
                or continue with email
              </Link>
              <span style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                20/month free
              </span>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Social Share Component
const SocialShareButtons = ({ title, url, vertical = false }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: 'Twitter',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: '#000',
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: '#0077B5',
    },
    {
      name: 'Facebook',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: '#1877F2',
    },
    {
      name: 'Copy Link',
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      ),
      onClick: () => {
        navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      },
      color: '#6B7280',
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: '12px',
        alignItems: 'center',
      }}
    >
      {!vertical && (
        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--gray-500)' }}>
          Share:
        </span>
      )}
      {shareLinks.map(link => (
        <button
          key={link.name}
          onClick={() => {
            if (link.onClick) {
              link.onClick();
            } else {
              window.open(link.url, '_blank', 'width=600,height=400');
            }
          }}
          title={link.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '1px solid var(--gray-200)',
            background: 'var(--bg-secondary)',
            color: link.color,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseOver={e => {
            e.currentTarget.style.background = link.color;
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.borderColor = link.color;
          }}
          onMouseOut={e => {
            e.currentTarget.style.background = 'var(--bg-secondary)';
            e.currentTarget.style.color = link.color;
            e.currentTarget.style.borderColor = 'var(--gray-200)';
          }}
        >
          {link.icon}
        </button>
      ))}
    </div>
  );
};

// Table of Contents Component
const TableOfContents = ({ content }) => {
  const [isOpen, setIsOpen] = useState(true);

  // Extract headings from markdown content
  const headings = [];
  if (content) {
    const lines = content.split('\n');
    lines.forEach((line, index) => {
      const h2Match = line.match(/^## (.+)$/);
      const h3Match = line.match(/^### (.+)$/);
      if (h2Match) {
        headings.push({ level: 2, text: h2Match[1], id: `heading-${index}` });
      } else if (h3Match) {
        headings.push({ level: 3, text: h3Match[1], id: `heading-${index}` });
      }
    });
  }

  if (headings.length < 3) return null;

  return (
    <div
      style={{
        background: 'var(--gray-50)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '32px',
        border: '1px solid var(--gray-200)',
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-800)' }}>
          Table of Contents
        </span>
        <ChevronDown
          size={20}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
            color: 'var(--gray-500)',
          }}
        />
      </button>
      {isOpen && (
        <nav style={{ marginTop: '16px' }}>
          {headings.map((heading, i) => (
            <a
              key={i}
              href={`#${heading.id}`}
              style={{
                display: 'block',
                padding: '6px 0',
                paddingLeft: heading.level === 3 ? '16px' : '0',
                fontSize: heading.level === 3 ? '14px' : '15px',
                color: heading.level === 3 ? 'var(--gray-500)' : 'var(--gray-700)',
                textDecoration: 'none',
                borderLeft: heading.level === 3 ? '2px solid var(--gray-200)' : 'none',
              }}
              onMouseOver={e => (e.currentTarget.style.color = 'var(--primary)')}
              onMouseOut={e =>
                (e.currentTarget.style.color =
                  heading.level === 3 ? 'var(--gray-500)' : 'var(--gray-700)')
              }
            >
              {heading.text}
            </a>
          ))}
        </nav>
      )}
    </div>
  );
};

// Blog Article Page - Single Article View
const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStickyShare, setShowStickyShare] = useState(false);
  const [isWideScreen, setIsWideScreen] = useState(false);
  // Language toggle state (de = German original, en = English translation) - persisted in localStorage
  const [language, setLanguageState] = useState(() => {
    try { return localStorage.getItem('blog-language') || 'de'; } catch { return 'de'; }
  });
  const setLanguage = (lang) => {
    setLanguageState(lang);
    try { localStorage.setItem('blog-language', lang); } catch {}
  };
  const [translating, setTranslating] = useState(false);
  const [translationCache, setTranslationCache] = useState(null);

  // Toggle language (DE <-> EN) with lazy translation
  const toggleLanguage = async () => {
    if (language === 'en') {
      // Switch back to German
      setLanguage('de');
      return;
    }

    // Switch to English - check if we have translation
    if (translationCache || article?.content_en) {
      setLanguage('en');
      return;
    }

    // Need to fetch/generate translation
    setTranslating(true);
    try {
      const response = await axios.post(`${API_URL}/public/blog/${slug}/translate`);
      if (response.data.ok) {
        setTranslationCache({
          title_en: response.data.title_en,
          content_en: response.data.content_en,
          meta_description_en: response.data.meta_description_en
        });
        setLanguage('en');
      }
    } catch (err) {
      console.error('Translation error:', err);
      toast.error('Translation failed. Please try again.');
    }
    setTranslating(false);
  };

  // Get current content based on language
  const getCurrentTitle = () => {
    if (language === 'en') {
      return translationCache?.title_en || article?.title_en || article?.title;
    }
    return article?.title;
  };

  const getCurrentContent = () => {
    if (language === 'en') {
      return translationCache?.content_en || article?.content_en || article?.content;
    }
    return article?.content;
  };

  const handleGoogleSignIn = async credentialResponse => {
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome! Redirecting to generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Google sign in failed. Please try again.');
    }
  };

  // Handle responsive sticky share (SSR-safe)
  useEffect(() => {
    const checkWidth = () => setIsWideScreen(window.innerWidth > 1200);
    checkWidth(); // Initial check
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Show sticky share buttons on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyShare(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO meta tags management with proper cleanup
  useEffect(() => {
    if (!article) return;

    const articleUrl = `https://tryreviewresponder.com/blog/${slug}`;
    const originalTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute('content') || '';

    // Set page title
    document.title = `${article.title} | ReviewResponder Blog`;

    // Set meta description
    if (metaDesc) {
      metaDesc.setAttribute('content', article.meta_description);
    }

    // Create canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = articleUrl;

    // Create Open Graph meta tags
    const ogTags = [
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: article.title },
      { property: 'og:description', content: article.meta_description },
      { property: 'og:url', content: articleUrl },
      { property: 'og:site_name', content: 'ReviewResponder' },
      { property: 'article:published_time', content: article.published_at },
      { property: 'article:section', content: article.category },
    ];

    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: article.title },
      { name: 'twitter:description', content: article.meta_description },
    ];

    const createdOgTags = [];
    [...ogTags, ...twitterTags].forEach(tag => {
      const attr = tag.property ? 'property' : 'name';
      const value = tag.property || tag.name;
      let meta = document.querySelector(`meta[${attr}="${value}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
        createdOgTags.push(meta);
      }
      meta.setAttribute('content', tag.content);
    });

    // Create Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'blog-article-schema';
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.meta_description,
      author: {
        '@type': 'Organization',
        name: 'ReviewResponder',
        url: 'https://tryreviewresponder.com',
      },
      publisher: {
        '@type': 'Organization',
        name: 'ReviewResponder',
        logo: {
          '@type': 'ImageObject',
          url: 'https://tryreviewresponder.com/logo.png',
        },
      },
      datePublished: article.published_at,
      dateModified: article.updated_at || article.published_at,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
      articleSection: article.category,
      wordCount: article.word_count,
    });
    document.head.appendChild(schemaScript);

    // Cleanup function
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDesc);
      schemaScript.remove();
      createdOgTags.forEach(tag => tag.remove());
      // Keep canonical but reset
      if (canonical) canonical.href = 'https://tryreviewresponder.com';
    };
  }, [article, slug]);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL.replace('/api', '')}/api/public/blog/${slug}`);
      if (!response.ok) {
        throw new Error('Article not found');
      }
      const data = await response.json();
      setArticle(data.article);
      setRelated(data.related || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Enhanced Markdown renderer with proper list wrapping
  // v2.2 - Added table support + inline list detection (2026-02-01)
  const renderMarkdown = content => {
    if (!content) return '';

    // Pre-process: Add newlines before markdown elements (Gemini often outputs without line breaks)
    let normalizedContent = content
      // STEP -1: Fix inline dash lists (common from manual articles: "- item1 - item2" → separate lines)
      // Only match " - " pattern after a word character (not at start, not after newline)
      .replace(/(\S)\s+-\s+(?=[A-Za-z0-9])/g, '$1\n- ')
      // STEP 0a: Fix bullet points missing space after asterisk (*SMS → * SMS)
      // Only at start of line, not mid-text (to avoid breaking bold **)
      .replace(/^(\*+)([A-Za-z])/gm, '$1 $2')
      // STEP 0b: Fix numbered lists missing space (1.The → 1. The)
      .replace(/^(\d+\.)([A-Za-z])/gm, '$1 $2')
      // STEP 0c: Fix colons missing space before next word (Examples:The → Examples: The)
      .replace(/:([A-Z])/g, ': $1')
      // STEP 0d: Fix words stuck together after periods in mid-text (vs.Email → vs. Email)
      .replace(/(\.)([A-Z][a-z])/g, '$1 $2')
      // STEP 0e: Fix common stuck words (specific patterns that Gemini produces)
      // Pattern: common verbs/prepositions stuck to next word
      .replace(/\b(Should|Take|Scale|Do|How|Why|What|When|Where|Is|Are|Was|Were|Can|Could|Would|Will|May|Might|Must|Has|Have|Had)(You|Your|It|The|This|That|We|They|A|An|To)\b/g, '$1 $2')
      // STEP 1: Add newline before # headers when preceded by non-newline, non-hash char
      // Using [^\n#] to avoid matching within ### sequences
      .replace(/([^\n#])(#{1,3}\s)/g, '$1\n$2')
      // STEP 2: Fix numbered headers missing space (### 1.Title → ### 1. Title)
      .replace(/^(#{1,3}\s*\d+\.)([A-Z])/gm, '$1 $2')
      // STEP 3: Add newline AFTER header content
      // Case 1: Header ends with ) and text follows immediately (like "Complaint)Scenario:")
      .replace(/(#{1,3}\s[^#\n]+?\))([A-Z])/g, '$1\n\n$2')
      // Case 2: Header ends with punctuation, next sentence starts with capital
      // BUT exclude numbered items like "### 1." - check that char before punctuation is NOT a digit
      .replace(/(#{1,3}\s[^#\n]+?[^\d][.!?])(\s*)([A-Z])/g, '$1\n\n$3')
      // Case 3: Header ends without punctuation - detect sentence starters after header
      // V4: Three-tier approach based on header length
      // VERY SHORT headers (5-14 chars): Split on "Negative" (for "# Conclusion Negative...")
      .replace(/(#{1,3}\s[^#\n]{5,14})(\s)(Negative |According |Studies |Research |Furthermore |Moreover |Additionally |However, )/g, '$1\n\n$3')
      // SHORT headers (15-29 chars): Only split on rare words that NEVER appear in headers
      // V5: Removed "Negative" - it appears IN headers like "Why Shoppers Hunt for Negative Feedback"
      .replace(/(#{1,3}\s[^#\n]{15,29})(\s)(According |Studies show |Research |Data shows |Furthermore |Moreover |Additionally |However, |Remember: |Note: |Warning: |Scenario:|Goal:|Template:)/g, '$1\n\n$3')
      // LONG headers (30-39 chars): Only split on STRONG sentence starters
      // V8: Use callback to check for continuation patterns (Than, unclosed parens)
      .replace(/(#{1,3}\s[^#\n]{30,39})(\s)(Your |They |You |If |When |Most |Many |Some |As |By |For |But |And |Or |So |Don't |Never |Always |According |Furthermore |However, )/g, (match, header, space, word) => {
        // Don't split if header ends with "Than" (comparison title like "More Than Your")
        if (/Than$/i.test(header.trim())) return match;
        // Don't split if header has unclosed parenthesis
        const openP = (header.match(/\(/g) || []).length;
        const closeP = (header.match(/\)/g) || []).length;
        if (openP > closeP) return match;
        return header + '\n\n' + word;
      })
      // EXTRA-LONG headers (40-80 chars): Also split on "The " - at this length, very likely body text
      // V8: Same callback protection for continuation patterns
      .replace(/(#{1,3}\s[^#\n]{40,80})(\s)(Your |The |They |You |If |When |Most |Many |Some |As |By |In |On |For |But |And |Or |So |Don't |Never |Always |According |Furthermore |However, )/g, (match, header, space, word) => {
        // Don't split if header ends with "Than" (comparison title)
        if (/Than$/i.test(header.trim())) return match;
        // Don't split if header has unclosed parenthesis "(Before You Ask)"
        const openP = (header.match(/\(/g) || []).length;
        const closeP = (header.match(/\)/g) || []).length;
        if (openP > closeP) return match;
        return header + '\n\n' + word;
      })
      // Case 3b: Header followed by markdown link (common in Sources sections)
      .replace(/(#{1,3}\s[^#\n\[]{5,50})(\s*)(\[)/g, '$1\n\n$3')
      // Case 4: Header followed by numbered list (1. 2. etc)
      .replace(/(#{1,3}\s[^#\n]+?)(\s+)(1\.\s)/g, '$1\n\n$3')
      // Add newline before ACTUAL list items - only after sentence endings (not mid-text)
      // Must be * or - followed by space and word character (not ** for bold)
      .replace(/([.!?:]\s*)(\*\s+[a-zA-Z])/g, '$1\n$2')
      .replace(/([.!?:]\s*)(-\s+[a-zA-Z])/g, '$1\n$2')
      .replace(/([.!?:]\s*)(\d+\.\s+[a-zA-Z])/g, '$1\n$2')
      // Add newline before blockquotes (only after sentence end)
      .replace(/([.!?]\s*)(>\s)/g, '$1\n$2')
      // Normalize multiple spaces to single
      .replace(/  +/g, ' ');

    const pStyle = 'margin-bottom: 20px; line-height: 1.8; color: var(--gray-700);';
    const liStyle = 'margin-bottom: 8px; line-height: 1.7;';
    const ulStyle = 'margin: 16px 0; padding-left: 24px; list-style-type: disc;';
    const olStyle = 'margin: 16px 0; padding-left: 24px; list-style-type: decimal;';
    const codeBlockStyle =
      'background: var(--gray-100); padding: 16px; border-radius: 8px; overflow-x: auto; font-family: monospace; font-size: 14px; margin: 16px 0;';
    const inlineCodeStyle =
      'background: var(--gray-100); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em;';

    // Process content line by line for proper list handling
    const lines = normalizedContent.split('\n');
    const processedLines = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let inCodeBlock = false;
    let codeBlockContent = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // Handle code blocks (```)
      if (line.trim().startsWith('```')) {
        if (!inCodeBlock) {
          // Start code block
          inCodeBlock = true;
          codeBlockContent = [];
        } else {
          // End code block
          inCodeBlock = false;
          processedLines.push(
            `<pre style="${codeBlockStyle}"><code>${codeBlockContent
              .join('\n')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')}</code></pre>`
          );
        }
        continue;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        continue;
      }

      // Check if line is an unordered list item
      const unorderedMatch = line.match(/^[*-]\s+(.*)$/);
      // Check if line is an ordered list item
      const orderedMatch = line.match(/^\d+\.\s+(.*)$/);

      if (unorderedMatch) {
        // Close ordered list if open
        if (inOrderedList) {
          processedLines.push('</ol>');
          inOrderedList = false;
        }
        // Open unordered list if not already open
        if (!inUnorderedList) {
          processedLines.push(`<ul style="${ulStyle}">`);
          inUnorderedList = true;
        }
        processedLines.push(`<li style="${liStyle}">${unorderedMatch[1]}</li>`);
      } else if (orderedMatch) {
        // Close unordered list if open
        if (inUnorderedList) {
          processedLines.push('</ul>');
          inUnorderedList = false;
        }
        // Open ordered list if not already open
        if (!inOrderedList) {
          processedLines.push(`<ol style="${olStyle}">`);
          inOrderedList = true;
        }
        processedLines.push(`<li style="${liStyle}">${orderedMatch[1]}</li>`);
      } else {
        // Close any open lists
        if (inUnorderedList) {
          processedLines.push('</ul>');
          inUnorderedList = false;
        }
        if (inOrderedList) {
          processedLines.push('</ol>');
          inOrderedList = false;
        }
        processedLines.push(line);
      }
    }

    // Close any remaining open lists
    if (inUnorderedList) processedLines.push('</ul>');
    if (inOrderedList) processedLines.push('</ol>');

    let html = processedLines.join('\n');

    // Now apply other markdown transformations

    // TABLE SUPPORT (v2.2): Convert markdown tables to HTML tables
    // Detect tables: lines starting with | and containing |
    const tableStyle = 'width: 100%; border-collapse: collapse; margin: 24px 0; font-size: 15px;';
    const thStyle = 'border: 1px solid var(--gray-300); padding: 12px; background: var(--gray-100); text-align: left; font-weight: 600;';
    const tdStyle = 'border: 1px solid var(--gray-300); padding: 12px; text-align: left;';

    // Process tables line by line
    const tableLines = html.split('\n');
    const tableProcessed = [];
    let inTable = false;
    let tableRows = [];
    let isHeaderRow = true;

    for (let i = 0; i < tableLines.length; i++) {
      const line = tableLines[i].trim();

      // Check if line is a table row (starts and ends with |, or contains multiple |)
      const isTableRow = line.startsWith('|') && line.includes('|', 1);
      // Check if line is a separator row (like |---|---|)
      const isSeparator = /^\|[\s\-:|]+\|$/.test(line) || /^\|?[\s\-:|]+\|?$/.test(line) && line.includes('-');

      if (isTableRow && !isSeparator) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
          isHeaderRow = true;
        }

        // Parse table cells
        const cells = line.split('|').filter((cell, idx, arr) => idx > 0 && idx < arr.length - 1 || (idx === 0 && cell.trim()) || (idx === arr.length - 1 && cell.trim()));
        const cleanCells = cells.map(c => c.trim()).filter(c => c);

        if (cleanCells.length > 0) {
          if (isHeaderRow) {
            const headerHtml = cleanCells.map(cell => `<th style="${thStyle}">${cell}</th>`).join('');
            tableRows.push(`<tr>${headerHtml}</tr>`);
            isHeaderRow = false;
          } else {
            const rowHtml = cleanCells.map(cell => `<td style="${tdStyle}">${cell}</td>`).join('');
            tableRows.push(`<tr>${rowHtml}</tr>`);
          }
        }
      } else if (isSeparator && inTable) {
        // Skip separator row, just mark that header is done
        isHeaderRow = false;
      } else {
        // Not a table row - close table if we were in one
        if (inTable && tableRows.length > 0) {
          tableProcessed.push(`<table style="${tableStyle}"><thead>${tableRows[0]}</thead><tbody>${tableRows.slice(1).join('')}</tbody></table>`);
          inTable = false;
          tableRows = [];
        }
        tableProcessed.push(tableLines[i]);
      }
    }

    // Close any remaining table
    if (inTable && tableRows.length > 0) {
      tableProcessed.push(`<table style="${tableStyle}"><thead>${tableRows[0]}</thead><tbody>${tableRows.slice(1).join('')}</tbody></table>`);
    }

    html = tableProcessed.join('\n');

    html = html
      // Remove horizontal rules (*** or ---)
      .replace(/^\*{3,}$/gm, '<hr style="border: none; border-top: 1px solid var(--gray-200); margin: 32px 0;" />')
      .replace(/^-{3,}$/gm, '<hr style="border: none; border-top: 1px solid var(--gray-200); margin: 32px 0;" />')
      // Blockquotes
      .replace(
        /^>\s*(.*$)/gm,
        '<blockquote style="border-left: 4px solid var(--primary-color); padding-left: 16px; margin: 16px 0; color: var(--gray-600); font-style: italic;">$1</blockquote>'
      )
      // Headers - shifted down one level for blog articles (title is already H1)
      // ### → h4, ## → h3, # → h2 (SEO: only one H1 per page)
      .replace(
        /^### (.*$)/gm,
        '<h4 style="font-size: 18px; font-weight: 600; margin: 24px 0 12px; color: var(--gray-800);">$1</h4>'
      )
      .replace(
        /^## (.*$)/gm,
        '<h3 style="font-size: 20px; font-weight: 600; margin: 32px 0 16px; color: var(--gray-800);">$1</h3>'
      )
      .replace(
        /^# (.*$)/gm,
        '<h2 style="font-size: 24px; font-weight: 700; margin: 40px 0 20px; color: var(--gray-900);">$1</h2>'
      )
      // Inline code (`code`) - BEFORE bold/italic to avoid conflicts
      .replace(/`([^`]+)`/g, `<code style="${inlineCodeStyle}">$1</code>`)
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Links [text](url)
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--primary); text-decoration: underline;">$1</a>'
      )
      // Paragraphs - replace double newlines
      .replace(/\n\n/g, `</p><p style="${pStyle}">`);

    // Wrap in proper paragraph tags (skip if starts with block element)
    if (!html.match(/^<(h[1-6]|blockquote|ul|ol|pre|hr)/)) {
      html = `<p style="${pStyle}">` + html;
    }
    if (!html.match(/<\/(h[1-6]|blockquote|ul|ol|pre)>$/) && !html.match(/<hr[^>]*\/>$/)) {
      html = html + '</p>';
    }

    // Clean up empty paragraphs and fix paragraph around block elements
    html = html
      .replace(/<p[^>]*>\s*<\/p>/g, '')
      .replace(/<p[^>]*>\s*(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)\s*<\/p>/g, '$1')
      .replace(/<p[^>]*>\s*(<blockquote)/g, '$1')
      .replace(/(<\/blockquote>)\s*<\/p>/g, '$1')
      .replace(/<p[^>]*>\s*(<ul)/g, '$1')
      .replace(/(<\/ul>)\s*<\/p>/g, '$1')
      .replace(/<p[^>]*>\s*(<ol)/g, '$1')
      .replace(/(<\/ol>)\s*<\/p>/g, '$1')
      .replace(/<p[^>]*>\s*(<pre)/g, '$1')
      .replace(/(<\/pre>)\s*<\/p>/g, '$1')
      .replace(/<p[^>]*>\s*(<hr)/g, '$1')
      .replace(/(\/>\s*)<\/p>/g, '$1')
      // Remove orphaned # symbols (Gemini sometimes outputs these)
      .replace(/<p[^>]*>\s*#\s*<\/p>/g, '')
      .replace(/^#\s*$/gm, '');

    return html;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div>Loading article...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>Article Not Found</h1>
        <Link to="/blog" style={{ color: 'var(--primary)' }}>
          Back to Blog
        </Link>
      </div>
    );
  }

  const articleUrl = `https://tryreviewresponder.com/blog/${slug}`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Sticky Social Share Bar (Desktop) - Uses state for SSR compatibility */}
      <div
        style={{
          position: 'fixed',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 100,
          opacity: showStickyShare ? 1 : 0,
          visibility: showStickyShare ? 'visible' : 'hidden',
          transition: 'opacity 0.3s, visibility 0.3s',
          display: isWideScreen ? 'block' : 'none',
        }}
      >
        <SocialShareButtons title={getCurrentTitle()} url={articleUrl} vertical />
      </div>

      {/* Article Header */}
      <div style={{ background: 'var(--gray-50)', padding: '80px 20px 60px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Link
            to="/blog"
            style={{
              color: 'var(--primary)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '24px',
            }}
          >
            &#8592; Back to Blog
          </Link>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '16px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--primary)',
                textTransform: 'uppercase',
                background: 'rgba(79, 70, 229, 0.1)',
                padding: '4px 12px',
                borderRadius: '4px',
              }}
            >
              {article.category}
            </span>
            <span style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
              {article.read_time_minutes} min read
            </span>
            {/* Language Toggle - Segmented Control */}
            <div style={{
              display: 'inline-flex',
              borderRadius: '20px',
              border: '1px solid var(--gray-300)',
              background: 'var(--bg-secondary)',
              padding: '3px',
              opacity: translating ? 0.7 : 1,
            }}>
              <button
                onClick={() => setLanguage('de')}
                disabled={translating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  border: 'none',
                  background: language === 'de' ? 'var(--primary)' : 'transparent',
                  color: language === 'de' ? 'white' : 'var(--gray-500)',
                  fontSize: '13px',
                  fontWeight: language === 'de' ? '600' : '500',
                  cursor: translating ? 'wait' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: '14px' }}>🇩🇪</span>
                DE
              </button>
              <button
                onClick={toggleLanguage}
                disabled={translating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  border: 'none',
                  background: language === 'en' ? 'var(--primary)' : 'transparent',
                  color: language === 'en' ? 'white' : 'var(--gray-500)',
                  fontSize: '13px',
                  fontWeight: language === 'en' ? '600' : '500',
                  cursor: translating ? 'wait' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {translating ? (
                  <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                ) : (
                  <span style={{ fontSize: '14px' }}>🇬🇧</span>
                )}
                EN
              </button>
            </div>
          </div>
          <h1
            style={{
              fontSize: '42px',
              fontWeight: '700',
              lineHeight: '1.2',
              marginBottom: '20px',
              color: 'var(--gray-900)',
            }}
          >
            {getCurrentTitle()}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                color: 'var(--gray-500)',
              }}
            >
              <span>{article.published_at && formatDate(article.published_at)}</span>
            </div>
            <SocialShareButtons title={getCurrentTitle()} url={articleUrl} />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 20px' }}>
        {/* Table of Contents */}
        <TableOfContents content={getCurrentContent()} />

        <div
          dangerouslySetInnerHTML={{ __html: renderMarkdown(getCurrentContent()) }}
          style={{ fontSize: '17px' }}
        />

        {/* Share After Reading */}
        <div
          style={{
            marginTop: '48px',
            padding: '24px',
            background: 'var(--gray-50)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <p style={{ fontWeight: '600', marginBottom: '4px' }}>Found this helpful?</p>
            <p style={{ fontSize: '14px', color: 'var(--gray-500)' }}>Share it with your network</p>
          </div>
          <SocialShareButtons title={getCurrentTitle()} url={articleUrl} />
        </div>

      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <div style={{ background: 'var(--gray-50)', padding: '60px 20px' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '32px',
                textAlign: 'center',
              }}
            >
              Related Articles
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '24px',
              }}
            >
              {related.map(rel => (
                <Link
                  key={rel.slug}
                  to={`/blog/${rel.slug}`}
                  style={{
                    background: 'var(--bg-secondary)',
                    padding: '24px',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    color: 'inherit',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: 'var(--gray-800)',
                    }}
                  >
                    {rel.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '12px' }}>
                    {rel.meta_description}
                  </p>
                  <span style={{ fontSize: '13px', color: 'var(--primary)', fontWeight: '500' }}>
                    Read more &#8594;
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div style={{ padding: '60px 20px', background: 'var(--bg-secondary)' }}>
        <div
          style={{
            maxWidth: '700px',
            margin: '0 auto',
            padding: '40px 32px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            borderRadius: '16px',
            textAlign: 'center',
            color: 'white',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Craft Your Own Review Responses
          </h2>
          <p style={{ fontSize: '13px', opacity: 0.85, marginBottom: '20px' }}>
            Based on principles from Industrial and Organizational Psychology
          </p>
          {user ? (
            <Link
              to="/generator"
              style={{
                display: 'inline-block',
                padding: '12px 28px',
                background: 'var(--bg-secondary)',
                color: 'var(--primary)',
                borderRadius: '8px',
                fontWeight: '600',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              Go to Generator
            </Link>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="signup_with"
                shape="rectangular"
                size="large"
              />
              <Link
                to="/register"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '13px',
                  textDecoration: 'none',
                }}
              >
                or continue with email
              </Link>
              <span style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                20/month free
              </span>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Video Demo Landing Page - Minimal friction for video traffic
// First Principles: Direct path from video → instant demo → signup
const VideoDemoPage = () => {
  const [reviewText, setReviewText] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // Human-in-the-Loop: You fill in details (bracket system)
  const [askUserEnabled, setAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false';
  });

  // Track UTM source
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') || 'direct';
    sessionStorage.setItem('video_source', source);
  }, []);

  const generateResponse = async () => {
    if (!reviewText.trim() || reviewText.length < 20) {
      toast.error('Please paste a review (at least 20 characters)');
      return;
    }
    setLoading(true);
    try {
      const fetchHeaders = { 'Content-Type': 'application/json' };
      if (localStorage.getItem('dev_bypass') === 'true') {
        fetchHeaders['x-dev-bypass'] = 'berend-dev-2026';
      }
      const res = await fetch(`${API_URL}/public/try`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify({ reviewText, tone: 'professional', askUserEnabled }),
      });
      const data = await res.json();
      if (data.error) {
        setShowSignup(true);
        toast.success('Sign up for 20 free responses per month!');
      } else {
        setResponse(data.response);
        // Increment generation count for tier-based loading messages
        const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
        const newCount = genCount + 1;
        localStorage.setItem('generation_count', String(newCount));
        checkMilestoneAchievement(newCount);
        // After showing response, prompt for signup
        setTimeout(() => setShowSignup(true), 3000);
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = e => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    navigate(`/register?email=${encodeURIComponent(email)}&source=video-demo`);
  };

  // Google Sign In handler
  const handleGoogleSignIn = async credentialResponse => {
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('Welcome! Redirecting to generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Google sign in failed. Please try again.');
    }
  };

  const copyResponse = () => {
    if (!response) return;
    navigator.clipboard.writeText(response);
    toast.success('Copied!');
    setShowSignup(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--gray-400)',
            textDecoration: 'none',
            fontSize: '14px',
            marginBottom: '24px',
          }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        {/* Hero - Ultra Simple */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Respond to YOUR Review</h1>
          <p className="text-xl text-gray-300">Paste it below. Get a response in 15 seconds.</p>
        </div>

        {/* Step 1: Paste Review */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Step 1: Paste your worst review
          </label>
          <textarea
            value={reviewText}
            onChange={e => setReviewText(e.target.value)}
            placeholder="Paste the negative review here... (e.g., 'The service was terrible, we waited 45 minutes...')"
            className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          />
          {/* Human-in-the-Loop Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginTop: '16px',
            padding: '10px 14px',
            background: askUserEnabled ? 'rgba(249, 115, 22, 0.15)' : 'rgba(107, 114, 128, 0.2)',
            borderRadius: '8px',
            border: `1px solid ${askUserEnabled ? 'rgba(249, 115, 22, 0.4)' : 'rgba(107, 114, 128, 0.3)'}`
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              flex: 1
            }}>
              <input
                type="checkbox"
                checked={askUserEnabled}
                onChange={(e) => {
                  setAskUserEnabled(e.target.checked);
                  localStorage.setItem('askUser_enabled', e.target.checked);
                }}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#f97316'
                }}
              />
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#e5e7eb'
              }}>Craft your own response</span>
            </label>
            {askUserEnabled && (
              <span style={{
                fontSize: '11px',
                color: 'var(--gray-400)',
                whiteSpace: 'nowrap'
              }}>Click [brackets] to personalize</span>
            )}
          </div>

          <button
            onClick={generateResponse}
            disabled={loading || !reviewText.trim()}
            className="w-full mt-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <GeneratingIndicator />
            ) : (
              <>Craft Your Response</>
            )}
          </button>

          {/* Google Sign In - Directly visible */}
          {!user && !showSignup && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '8px' }}>
                20 free/month
              </span>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => toast.error('Google Sign In failed')}
                  text="signup_with"
                  shape="rectangular"
                  width="280"
                />
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Response */}
        {response && (
          <div className="bg-green-900/30 border border-green-500/50 rounded-xl p-6 mb-6 animate-fade-in">
            <div className="flex justify-between items-start mb-3">
              <label className="block text-sm font-medium text-green-400">
                Your Response (ready to post!)
              </label>
              <button
                onClick={copyResponse}
                className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copy
              </button>
            </div>
            {/* Response Display - Interactive brackets or plain text */}
            {askUserEnabled && response && /\[[^\]]+\]/.test(response) ? (
              <div style={{ color: 'white' }}>
                <InteractiveResponse
                  response={response}
                  onResponseChange={(newResponse) => setResponse(newResponse)}
                />
              </div>
            ) : (
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="w-full bg-transparent text-white text-lg leading-relaxed resize-none border-0 focus:ring-0 focus:outline-none"
                style={{ minHeight: '100px' }}
              />
            )}
            <p className="text-green-400 text-sm mt-4 font-medium">
              ✓ Human-sounding, not AI fluff
            </p>
          </div>
        )}

        {/* Step 3: Signup (appears after response) */}
        {showSignup && (
          <div className="bg-orange-500/20 border border-orange-500/50 rounded-xl p-6 animate-fade-in">
            <h3 className="text-xl font-bold text-white mb-2">Get 20 Free Responses Every Month</h3>
            <p className="text-gray-300 mb-4">No credit card required. Cancel anytime.</p>

            {/* Google Sign In - PRIMARY */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google sign in failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '16px 0',
              color: 'var(--gray-400)'
            }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(156, 163, 175, 0.3)' }} />
              <span style={{ fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(156, 163, 175, 0.3)' }} />
            </div>

            {/* Email form - SECONDARY */}
            <form onSubmit={handleSignup} className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg transition-all"
              >
                Start Free
              </button>
            </form>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500 mb-4">20 responses/month free</p>
        </div>

        {/* About Founder - Compact Dark */}
        <div style={{ marginTop: '48px', textAlign: 'center', paddingTop: '32px', borderTop: '1px solid rgba(156, 163, 175, 0.2)' }}>
          <img
            src="/berend.jpg"
            alt="Berend"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              marginBottom: 10,
              objectFit: 'cover',
              objectPosition: 'center 30%',
              border: '2px solid rgba(251, 146, 60, 0.3)',
            }}
          />
          <p style={{ fontSize: '0.9rem', lineHeight: 1.5, color: '#e5e7eb', marginBottom: 6 }}>
            "Built to write responses WITH you, not FOR you."
          </p>
          <p style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
            — Berend · Founder
          </p>
          <Link
            to="/about-berend"
            style={{
              display: 'inline-block',
              marginTop: 10,
              color: '#fb923c',
              textDecoration: 'none',
              fontSize: '0.8rem',
              fontWeight: '500',
            }}
          >
            Read my story →
          </Link>
        </div>
      </div>
    </div>
  );
};

// Demo Landing Page - Personalized demo for cold outreach
const DemoPage = () => {
  const { token } = useParams();
  const { user, setUser } = useAuth();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  // Live Preview State
  const [liveReview, setLiveReview] = useState('');
  const [liveResponse, setLiveResponse] = useState('');
  const [liveLoading, setLiveLoading] = useState(false);
  const [selectedTone, setSelectedTone] = useState('professional');
  const [finalLiveResponse, setFinalLiveResponse] = useState(''); // After bracket selections
  // Human-in-the-Loop: You fill in details (bracket system)
  const [liveAskUserEnabled, setLiveAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false';
  });

  // Track final demo responses after bracket selections (one per demo item)
  const [finalDemoResponses, setFinalDemoResponses] = useState({});

  // Show all responses by default (no email gate)
  const [showAllResponses] = useState(true);

  // Detect UI language from first demo response
  const demoLang = demo?.demos?.[0]?.ai_response
    ? detectResponseLanguage(demo.demos[0].ai_response)
    : 'en';
  const t = DEMO_PAGE_TEXTS[demoLang] || DEMO_PAGE_TEXTS.en;

  // Email Modal State (for expired demos CTA)
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [captureEmail, setCaptureEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDemo();
  }, [token]);

  // Ctrl+Enter keyboard shortcut to generate (Demo Page)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlOrCmd && e.key === 'Enter') {
        e.preventDefault();
        // Only trigger if not already loading and has text
        if (!liveLoading && liveReview.trim().length >= 10) {
          const generateBtn = document.querySelector('[data-action="demo-generate"]');
          if (generateBtn) generateBtn.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [liveLoading, liveReview]);

  // Calculate days until demo expires (7 days from creation)
  const getDaysRemaining = () => {
    if (!demo?.created_at) return 7;
    const created = new Date(demo.created_at);
    const expires = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diff = Math.ceil((expires - now) / (24 * 60 * 60 * 1000));
    return Math.max(0, Math.min(7, diff));
  };

  // Live Preview - Generate AI response for user's review
  const generateLiveResponse = async () => {
    if (!liveReview.trim() || liveLoading) return;
    if (liveReview.trim().length < 10) {
      setLiveResponse('Please enter at least 10 characters.');
      return;
    }
    setLiveLoading(true);
    try {
      const liveHeaders = { 'Content-Type': 'application/json' };
      if (localStorage.getItem('dev_bypass') === 'true') {
        liveHeaders['x-dev-bypass'] = 'berend-dev-2026';
      }
      const response = await fetch(`${API_URL}/public/try`, {
        method: 'POST',
        headers: liveHeaders,
        body: JSON.stringify({
          reviewText: liveReview,
          tone: selectedTone,
          askUserEnabled: liveAskUserEnabled,
          context: {
            businessName: demo?.business_name,
            city: demo?.city,
            businessType: demo?.business_type || 'restaurant',
          },
        }),
      });

      // Handle rate limit (429) BEFORE parsing JSON
      if (response.status === 429) {
        setLiveResponse('');
        toast.success('Love the enthusiasm! Sign up for 20 free responses/month 👇');
        setTimeout(() => {
          document
            .getElementById('demo-cta-section')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
        return;
      }

      const data = await response.json();
      if (data.error) {
        // Rate limit reached - scroll to CTA section (fallback check)
        if (data.error === 'Daily limit reached' || data.message?.includes('free tries')) {
          setLiveResponse('');
          toast.success('Love the enthusiasm! Sign up for 20 free responses/month 👇');
          setTimeout(() => {
            document
              .getElementById('demo-cta-section')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        } else {
          setLiveResponse(data.error);
        }
      } else {
        setLiveResponse(data.response || 'Unable to generate response. Please try again.');
      }
    } catch (err) {
      setLiveResponse('Unable to generate response. Sign up for free to try!');
    } finally {
      setLiveLoading(false);
    }
  };

  const fetchDemo = async () => {
    try {
      // Skip view tracking for admin users (flag set permanently after first admin login)
      const isAdminUser = localStorage.getItem('isAdminUser') === 'true';
      const previewParam = isAdminUser ? '?preview=true' : '';

      const response = await fetch(
        `${API_URL.replace('/api', '')}/api/public/demo/${token}${previewParam}`
      );
      if (!response.ok) {
        throw new Error('Demo not found');
      }
      const data = await response.json();
      setDemo(data);
      document.title = `${data.business_name} - Review Responses Demo | ReviewResponder`;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Copy response directly (no email gate - pure value!)
  const copyResponse = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Handle email submit for signup modal
  const handleEmailSubmit = async e => {
    e.preventDefault();
    if (!captureEmail.trim() || !captureEmail.includes('@')) return;

    setEmailSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/auto-create-demo-user`, {
        email: captureEmail.trim(),
        source: 'demo_page',
        business_name: demo?.business_name,
      });

      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        setShowEmailModal(false);
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Logged in! Taking you to Generator...'
        );
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Failed to create account. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async credentialResponse => {
    setEmailSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });

      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        setShowEmailModal(false);
        toast.success(
          res.data.isNewUser
            ? '🎉 Account created! Taking you to Generator...'
            : '✓ Logged in! Taking you to Generator...'
        );
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Google Sign In failed. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const GoogleStars = ({ rating }) => (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <svg
          key={star}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={star <= rating ? '#FBBC04' : '#E8EAED'}
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'var(--bg-primary)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ marginBottom: '16px' }} />
          <p style={{ color: 'var(--text-muted)' }}>Loading your personalized demo...</p>
        </div>
      </div>
    );
  }

  if (error || !demo) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', padding: '40px 20px', maxWidth: '400px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--bg-tertiary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-muted)"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '12px',
            }}
          >
            Demo Not Found
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px', lineHeight: '1.6' }}>
            This demo link may have expired or the URL is incorrect.
          </p>
          <a href="/" className="btn btn-primary">
            Visit ReviewResponder
          </a>
        </div>
      </div>
    );
  }

  // EXPIRED DEMO: Show special page for expired demos
  if (demo.expired) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', padding: '40px 20px', maxWidth: '500px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '12px',
            }}
          >
            Demo Expired
          </h1>
          <p
            style={{
              color: 'var(--text-muted)',
              marginBottom: '8px',
              lineHeight: '1.6',
              fontSize: '16px',
            }}
          >
            Your personalized demo for{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{demo.business_name}</strong> has
            expired after 7 days.
          </p>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', lineHeight: '1.6' }}>
            Sign up free to craft fresh responses for your reviews. No credit card required.
          </p>
          <button
            onClick={() => {
              if (user) {
                window.location.href = '/generator';
              } else {
                setShowEmailModal(true);
              }
            }}
            className="btn btn-primary"
            style={{
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, var(--primary) 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            {user ? 'Go to Dashboard' : 'Start Free – No Credit Card'}
          </button>
          <p style={{ marginTop: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>
            20 responses/month free forever
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Floating Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div
          style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <a
            href="/"
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
            </div>
            <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
              ReviewResponder
            </span>
          </a>
          <button
            onClick={() => window.location.href = '/generator'}
            className="btn btn-primary"
            style={{ padding: '10px 20px', fontSize: '14px', cursor: 'pointer' }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>

      {/* Hero - Ultra Simple */}
      <div style={{ padding: '48px 20px 32px', textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            fontWeight: '800',
            color: 'var(--text-primary)',
            marginBottom: '12px',
            lineHeight: '1.2',
          }}
        >
          {t.greeting} {demo.owner_name || demo.business_name}!
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--text-muted)', maxWidth: '400px', margin: '0 auto' }}>
          {t.subheadline} <span style={{ color: 'var(--primary)', fontWeight: '600' }}>{t.brackets}</span> {t.subheadlineSuffix}
        </p>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px 48px' }}>
        {/* Demo Cards - All visible, no gates */}
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}
        >
          {demo.demos &&
            demo.demos.map((item, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--card-bg)',
                  borderRadius: '16px',
                  border: '1px solid var(--border-color)',
                  overflow: 'visible',
                  boxShadow: 'var(--card-shadow)',
                }}
              >
                {/* Review Header - Google Style */}
                <div
                  style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-light)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    {/* Avatar */}
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: `linear-gradient(135deg, ${['#4285f4', '#ea4335', '#fbbc04', '#34a853', '#ff6d00'][index % 5]} 0%, ${['#34a853', '#fbbc04', '#ea4335', '#4285f4', '#e91e63'][index % 5]} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '18px',
                      }}
                    >
                      {(item.review.author || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            fontSize: '15px',
                          }}
                        >
                          {item.review.author || t.anonymous}
                        </span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#4285f4">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <GoogleStars rating={item.review.rating} />
                        {item.review.date && (
                          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            {item.review.date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      lineHeight: '1.7',
                      fontSize: '15px',
                      marginTop: '12px',
                    }}
                  >
                    {item.review.text}
                  </p>
                  {/* View on Google Link - use direct review_link if available, fallback to general reviews page */}
                  {(item.review.review_link || demo.google_reviews_url) && (
                    <a
                      href={item.review.review_link || demo.google_reviews_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginTop: '12px',
                        fontSize: '13px',
                        color: '#4285f4',
                        textDecoration: 'none',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#4285f4">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                      {item.review.review_link
                        ? 'View This Review on Google'
                        : `View on Google (by ${item.review.author || 'Anonymous'})`}
                    </a>
                  )}
                </div>

                {/* AI Response */}
                <div
                  style={{
                    padding: '20px 24px',
                    background:
                      'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                      </div>
                      <span style={{ fontWeight: '600', color: '#059669', fontSize: '14px' }}>
                        {demo?.business_name || t.yourResponse}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => copyResponse(finalDemoResponses[index] || item.ai_response, index)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          background: copiedIndex === index ? '#059669' : 'var(--bg-secondary)',
                          border: copiedIndex === index ? 'none' : '1px solid var(--border-color)',
                          color: copiedIndex === index ? 'white' : 'var(--text-secondary)',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500',
                          transition: 'all 0.2s',
                        }}
                      >
                        {copiedIndex === index ? (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                            {t.copied}
                          </>
                        ) : (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="9" y="9" width="13" height="13" rx="2" />
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                            {t.copy}
                          </>
                        )}
                      </button>
                      {(item.review.review_link || demo.google_reviews_url) && (
                        <a
                          href={item.review.review_link || demo.google_reviews_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            background: '#4285f4',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            textDecoration: 'none',
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                          </svg>
                          {t.replyOnGoogle}
                        </a>
                      )}
                    </div>
                  </div>
                  {/* Response Display - Interactive brackets or editable textarea (like Generator) */}
                  <div style={{ color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '15px' }}>
                    {/\[[^\]]+:[^\]]+\|[^\]]+\]/.test(finalDemoResponses[index] || item.ai_response) ? (
                      <InteractiveResponse
                        response={item.ai_response}
                        hideHint={true}
                        onResponseChange={(newResponse) => {
                          setFinalDemoResponses(prev => ({
                            ...prev,
                            [index]: newResponse
                          }));
                        }}
                      />
                    ) : (
                      <textarea
                        value={finalDemoResponses[index] || item.ai_response}
                        onChange={(e) => {
                          setFinalDemoResponses(prev => ({
                            ...prev,
                            [index]: e.target.value
                          }));
                        }}
                        style={{
                          width: '100%',
                          minHeight: '120px',
                          padding: '12px',
                          fontSize: '15px',
                          lineHeight: '1.7',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          background: 'var(--bg-primary)',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Try your own review - minimal */}
        <div
          style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '48px',
            border: '1px solid var(--border-color)',
          }}
        >
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
            {t.orTryYourOwn}
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
            }}
          >
            {/* Input */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px',
                }}
              >
                {t.yourReview}
              </label>
              <textarea
                value={liveReview}
                onChange={e => setLiveReview(e.target.value)}
                placeholder={t.pasteReviewPlaceholder}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '14px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              {/* Tone Buttons - Horizontal Pills (like Landing Page) */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                  gap: '8px',
                  marginTop: '12px',
                }}
              >
                {['professional', 'friendly', 'formal', 'apologetic'].map(t => (
                  <button
                    key={t}
                    onClick={() => setSelectedTone(t)}
                    style={{
                      padding: '10px 12px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      border: selectedTone === t ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                      background: selectedTone === t ? 'var(--primary-50)' : 'var(--bg-primary)',
                      color: selectedTone === t ? 'var(--primary)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Human-in-the-Loop Toggle - You fill in details */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                padding: '10px 14px',
                background: liveAskUserEnabled ? 'var(--primary-50)' : 'var(--bg-secondary)',
                borderRadius: '8px',
                border: `1px solid ${liveAskUserEnabled ? 'var(--primary-200)' : 'var(--border-color)'}`
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    checked={liveAskUserEnabled}
                    onChange={(e) => {
                      setLiveAskUserEnabled(e.target.checked);
                      localStorage.setItem('askUser_enabled', e.target.checked);
                    }}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: 'var(--primary)'
                    }}
                  />
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: 'var(--text-primary)'
                  }}>{t.craftYourOwnResponse}</span>
                </label>
                {liveAskUserEnabled && (
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap'
                  }}>{t.clickBracketsHint}</span>
                )}
              </div>

              {/* Clear Button */}
              {liveReview && (
                <button
                  onClick={() => {
                    setLiveReview('');
                    setLiveResponse('');
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {t.clear}
                </button>
              )}
              <button
                onClick={generateLiveResponse}
                disabled={!liveReview.trim() || liveLoading}
                data-action="demo-generate"
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: liveLoading
                    ? 'var(--gray-400)'
                    : 'linear-gradient(135deg, var(--primary) 0%, #7c3aed 100%)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: liveLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {liveLoading ? (
                  <GeneratingIndicator />
                ) : (
                  <>
                    <Zap size={18} />
                    {t.craftYourResponse}
                  </>
                )}
              </button>
            </div>

            {/* Output */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px',
                }}
              >
                {t.craftResponse}
              </label>
              <div
                style={{
                  minHeight: '120px',
                  padding: liveResponse ? '0' : '14px',
                  borderRadius: '12px',
                  border: liveResponse ? 'none' : '1px solid var(--border-color)',
                  background: liveResponse
                    ? 'transparent'
                    : 'var(--bg-secondary)',
                  color: liveResponse ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontSize: '15px',
                  lineHeight: '1.6',
                }}
              >
                {liveResponse ? (
                  /\[[^\]]+:[^\]]+\|[^\]]+\]/.test(finalLiveResponse || liveResponse) ? (
                    <InteractiveResponse
                      response={liveResponse}
                      hideHint={true}
                      onResponseChange={(assembled) => setFinalLiveResponse(assembled)}
                    />
                  ) : (
                    <textarea
                      value={finalLiveResponse || liveResponse}
                      onChange={(e) => setFinalLiveResponse(e.target.value)}
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '12px',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        background: 'var(--bg-primary)',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                  )
                ) : (
                  t.responseWillAppear
                )}
              </div>
              {liveResponse && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(finalLiveResponse || liveResponse);
                    toast.success(t.copied);
                  }}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontWeight: '500',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <Copy size={16} />
                  {t.copyResponse}
                </button>
              )}

              {/* Google Sign In - Directly visible */}
              {!user && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                    {t.freePerMonth}
                  </span>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                      onSuccess={handleGoogleSignIn}
                      onError={() => toast.error('Google Sign In failed')}
                      text="signup_with"
                      shape="rectangular"
                      width="280"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Sign In Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px',
                color: 'var(--text-primary)',
              }}
            >
              Sign in to continue
            </h3>
            {/* Google Sign In - PRIMARY */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>
            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '16px 0',
                gap: '12px',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
            {/* Email form - SECONDARY */}
            <form onSubmit={handleEmailSubmit}>
              <input
                type="email"
                value={captureEmail}
                onChange={e => setCaptureEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '15px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={emailSubmitting}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  opacity: emailSubmitting ? 0.7 : 1,
                }}
              >
                {emailSubmitting ? 'Creating account...' : 'Continue with Email'}
              </button>
            </form>
            <button
              onClick={() => setShowEmailModal(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                marginTop: '16px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* About Founder - Compact */}
      <div style={{ padding: '40px 20px', background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          <img
            src="/berend.jpg"
            alt="Berend"
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              marginBottom: 12,
              objectFit: 'cover',
              objectPosition: 'center 30%',
              border: '2px solid var(--primary-light, #e8f4f8)',
            }}
          />
          <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--text-primary)', marginBottom: 8 }}>
            {t.founderQuote}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {t.founderTitle}
          </p>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <Link
              to="/about-berend"
              style={{
                color: 'var(--primary)',
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: '500',
              }}
            >
              {t.readMyStory}
            </Link>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{t.questions}</span>
              <a
                href="https://calendly.com/berend-jakob-mainz/quick-call"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                }}
              >
                {t.bookCall}
              </a>
              <a
                href={`mailto:${t.founderEmail}`}
                style={{
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                }}
              >
                → {t.founderEmail}
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Support Page
const SupportPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setSending(true);

    try {
      await api.post('/support/contact', { name, email, subject, message });
      setSent(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.error('Failed to send message. Please email us directly.');
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    {
      q: 'What languages are supported?',
      a: 'The tool automatically detects the review language and responds in the same language. Works with any language - German, Spanish, French, Chinese, Japanese, Arabic, and more.',
    },
    {
      q: 'How does the free trial work?',
      a: 'You get 20 free responses/month when you sign up. No credit card required.',
    },
    {
      q: 'Can I cancel my subscription anytime?',
      a: 'Yes! You can cancel your subscription at any time from your dashboard. No questions asked, no hidden fees.',
    },
    {
      q: 'How fast are responses created?',
      a: 'Paste a review and get a response in seconds. You tweak it, then post.',
    },
    {
      q: 'Do you store my review data?',
      a: 'We store your response history so you can access it later. We never share your data with third parties and you can delete your data anytime.',
    },
    {
      q: 'Which platforms are supported?',
      a: 'Works for Google Reviews, Yelp, TripAdvisor, Facebook, and any other review platform. Just paste the review and craft your response with psychology-based guidance.',
    },
  ];

  if (sent) {
    return (
      <div className="auth-container">
        <div className="card auth-card" style={{ textAlign: 'center' }}>
          <Check size={48} style={{ color: 'var(--success)', marginBottom: '16px' }} />
          <h1 className="auth-title">Message Sent!</h1>
          <p className="auth-subtitle">We'll get back to you within 24 hours.</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: '20px' }}>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '12px' }}>
            How can we help?
          </h1>
          <p style={{ color: 'var(--gray-600)' }}>Check our FAQ or send us a message</p>
        </div>

        <div className="faq-contact-grid">
          <div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <HelpCircle size={20} />
              Frequently Asked Questions
            </h2>

            {faqs.map((faq, i) => (
              <div key={i} className="card" style={{ marginBottom: '12px', padding: '16px' }}>
                <h3
                  style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: 'var(--gray-800)',
                  }}
                >
                  {faq.q}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--gray-600)', lineHeight: '1.6' }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>

          <div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Mail size={20} />
              Contact Us
            </h2>

            <div className="card">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <select
                    className="form-select"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Question</option>
                    <option value="billing">Billing & Subscription</option>
                    <option value="technical">Technical Issue</option>
                    <option value="feedback">Feedback & Suggestions</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-textarea"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="How can we help you?"
                    rows={5}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  disabled={sending}
                >
                  {sending ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>

              <div
                style={{
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid var(--gray-200)',
                  textAlign: 'center',
                }}
              >
                <p style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
                  Or email us directly at
                  <br />
                  <a
                    href="mailto:support@tryreviewresponder.com"
                    style={{ color: 'var(--primary-600)', fontWeight: '500' }}
                  >
                    support@tryreviewresponder.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Reusable Chrome Extension How-To Component for Landing Pages
const ChromeExtensionHowTo = ({ platform = 'Google Maps', color = '#4285f4' }) => (
  <div
    style={{
      marginTop: '60px',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '16px',
      padding: '40px',
      border: '1px solid #bae6fd',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
      <Chrome size={28} style={{ color }} />
      <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
        One-Click with Chrome Extension
      </h2>
    </div>
    <ol style={{ lineHeight: '2.2', color: 'var(--gray-700)', marginBottom: '24px' }}>
      <li>
        <strong>Install the Chrome Extension</strong> (free)
      </li>
      <li>
        <strong>Open {platform}</strong> and find any review
      </li>
      <li>
        <strong>Click the ReviewResponder button</strong> that appears
      </li>
      <li>
        <strong>Done!</strong> Your response is ready to post
      </li>
    </ol>
    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
      <a
        href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
      >
        <Chrome size={18} />
        Add to Chrome
      </a>
      <span style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
        Works on: Google Maps, Yelp, TripAdvisor, Facebook, Booking.com, Trustpilot
      </span>
    </div>
  </div>
);

// Compact Hero Video for Industry Landing Pages
const HeroVideoCompact = () => (
  <div
    style={{
      maxWidth: '480px',
      margin: '0 auto 20px',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      background: '#000',
    }}
  >
    <video
      autoPlay
      muted
      loop
      playsInline
      style={{
        width: '100%',
        display: 'block',
      }}
    >
      <source src="/videos/smart-brackets-demo.mp4" type="video/mp4" />
    </video>
  </div>
);

// Reusable Demo Video Section Component for Landing Pages
const DemoVideoSection = ({ variant = 'extension' }) => {
  // variant: 'extension' (Chrome demo) or 'brackets' (Smart Brackets demo)
  const isExtension = variant === 'extension';

  return (
    <div
      style={{
        marginTop: '60px',
        background: isExtension
          ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
          : 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
        borderRadius: '16px',
        padding: '40px',
        border: isExtension ? '1px solid #bae6fd' : '1px solid #e9d5ff',
        textAlign: 'center',
      }}
    >
      <h2 style={{ fontSize: '24px', fontWeight: '700', margin: '0 0 12px 0' }}>
        {isExtension ? 'See the Chrome Extension in Action' : 'Craft Your Own Response'}
      </h2>
      <p style={{ color: 'var(--gray-600)', marginBottom: '24px', fontSize: '15px' }}>
        {isExtension
          ? 'Respond to reviews directly on Google Maps, Yelp, TripAdvisor & more'
          : 'Click brackets to personalize - make every response truly yours'}
      </p>

      <div
        style={{
          maxWidth: '640px',
          margin: '0 auto',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: isExtension
            ? '0 10px 40px rgba(59, 130, 246, 0.15)'
            : '0 10px 40px rgba(147, 51, 234, 0.15)',
          background: '#000',
        }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: '100%',
            display: 'block',
          }}
        >
          <source src={isExtension ? '/videos/extension-demo.mp4' : '/videos/smart-brackets-demo.mp4'} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};

// Enhanced Free Tool Generator - two-column layout with star rating, quick adds, usage counter
const FreeToolGenerator = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);
  const [tone, setTone] = useState('professional');
  const [recommendedTone, setRecommendedTone] = useState(null);
  const [hasManuallyChangedTone, setHasManuallyChangedTone] = useState(false);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [editedResponse, setEditedResponse] = useState(null);
  const [remainingTries, setRemainingTries] = useState(null);
  const [activeQuickAdds, setActiveQuickAdds] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [captureEmail, setCaptureEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [askUserEnabled, setAskUserEnabled] = useState(() => {
    return localStorage.getItem('askUser_enabled') !== 'false';
  });

  const sampleReview = 'Great service overall and the staff was friendly! There was a small mix-up with our order but they handled it professionally.';

  const quickAdds = [
    { id: 'no-fluff', label: 'No Fluff', instruction: 'Never use generic phrases like "valued customer" or "we appreciate your feedback"' },
    { id: 'short', label: 'Short & Sweet', instruction: 'Keep response under 50 words' },
    { id: 'push-back', label: 'Push Back', instruction: 'Respectfully disagree with the critique, stand your ground' },
    { id: 'use-name', label: 'Use Name', instruction: 'Use the reviewer name if mentioned in the review' },
  ];

  const toggleQuickAdd = (id) => {
    setActiveQuickAdds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Ctrl+Enter keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      if (ctrlOrCmd && e.key === 'Enter') {
        e.preventDefault();
        if (!loading && reviewText.trim().length >= 10) {
          const btn = document.querySelector('[data-action="freetool-generate"]');
          if (btn) btn.click();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [loading, reviewText]);

  const handleGenerate = async () => {
    if (!reviewText.trim() || reviewText.trim().length < 10) {
      setError('Please enter a review (at least 10 characters)');
      return;
    }
    setLoading(true);
    setError('');
    setResponse('');
    setEditedResponse(null);

    // TEST MODE
    const params = new URLSearchParams(location.search);
    if (params.get('test_limit') === 'true') {
      setTimeout(() => {
        setLoading(false);
        toast.success('Love it? Sign up for 20 free responses/month!');
        setShowEmailModal(true);
      }, 500);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (localStorage.getItem('dev_bypass') === 'true') {
        headers['x-dev-bypass'] = 'berend-dev-2026';
      }

      const customInstructions = activeQuickAdds
        .filter(id => id !== 'short')
        .map(id => quickAdds.find(q => q.id === id)?.instruction)
        .filter(Boolean)
        .join('\n');

      const res = await axios.post(
        `${API_URL}/public/try`,
        {
          reviewText: reviewText.trim(),
          tone,
          reviewRating: rating || undefined,
          responseLength: activeQuickAdds.includes('short') ? 'short' : 'auto',
          context: {},
          customInstructions: customInstructions || undefined,
          askUserEnabled,
          source: 'free_tool',
        },
        { headers }
      );
      setResponse(res.data.response);
      if (res.data.remainingTries !== undefined) {
        setRemainingTries(res.data.remainingTries);
      }

      const genCount = parseInt(localStorage.getItem('generation_count') || '0', 10);
      const newCount = genCount + 1;
      localStorage.setItem('generation_count', String(newCount));
      checkMilestoneAchievement(newCount);

      if (!localStorage.getItem('first_wow_moment')) {
        fireConfetti();
        localStorage.setItem('first_wow_moment', 'true');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        if (err.response?.data?.upgrade) {
          setError(`${err.response?.data?.message || 'Monthly limit reached.'} Upgrade to get more responses!`);
        } else {
          toast.success('Love it? Sign up for 20 free responses/month!');
          setShowEmailModal(true);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to generate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const textToCopy = editedResponse !== null ? editedResponse : response;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!captureEmail.trim() || !captureEmail.includes('@')) return;
    setEmailSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/auto-create-demo-user`, {
        email: captureEmail.trim(),
        source: 'free_tool',
        landing_page: location.pathname,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        localStorage.setItem('instant_demo_email', 'true');
        localStorage.setItem('captured_email', captureEmail.trim().toLowerCase());
        setShowEmailModal(false);
        const textToCopy = editedResponse !== null ? editedResponse : response;
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success(res.data.isNewUser ? 'Account created! Taking you to Generator...' : 'Logged in! Taking you to Generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      console.error('Auto-create account error:', err);
      toast.error('Failed to create account. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleGoogleSignIn = async (credentialResponse) => {
    setEmailSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        localStorage.setItem('instant_demo_email', 'true');
        setShowEmailModal(false);
        const textToCopy = editedResponse !== null ? editedResponse : response;
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success(res.data.isNewUser ? 'Account created! Taking you to Generator...' : 'Logged in! Taking you to Generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      console.error('Google Sign In error:', err);
      toast.error('Google Sign In failed. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: '24px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '16px',
          padding: '24px',
        }}
      >
        {/* LEFT COLUMN - Input */}
        <div>
          {/* Star Rating */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
              Star Rating (optional)
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => {
                    setRating(star);
                    if (!hasManuallyChangedTone) {
                      const suggested = star <= 2 ? 'apologetic' : star === 3 ? 'professional' : 'friendly';
                      setTone(suggested);
                      setRecommendedTone(suggested);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                  }}
                >
                  <Star
                    size={24}
                    fill={star <= rating ? '#f59e0b' : 'none'}
                    color={star <= rating ? '#f59e0b' : 'var(--gray-400)'}
                  />
                </button>
              ))}
              {rating > 0 && (
                <button
                  onClick={() => { setRating(0); setRecommendedTone(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: '12px', color: 'var(--gray-400)' }}
                >
                  clear
                </button>
              )}
            </div>
          </div>

          {/* Review Textarea */}
          <div style={{ marginBottom: '12px' }}>
            <textarea
              value={reviewText}
              onChange={e => setReviewText(e.target.value)}
              placeholder="Paste a customer review here..."
              style={{
                width: '100%',
                minHeight: '140px',
                padding: '12px',
                fontSize: '15px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
            />
            {!reviewText && (
              <button
                onClick={() => setReviewText(sampleReview)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Sparkles size={12} />
                Try with an example review
              </button>
            )}
            {reviewText.trim() && (
              <button
                onClick={() => { setReviewText(''); setResponse(''); setError(''); setEditedResponse(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--gray-500)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>

          {/* Tone Selector */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
              Tone
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['professional', 'friendly', 'formal', 'apologetic'].map(t => (
                <button
                  key={t}
                  onClick={() => { setTone(t); setHasManuallyChangedTone(true); }}
                  style={{
                    padding: '8px 4px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: tone === t ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                    background: tone === t ? 'var(--primary-50)' : 'var(--card-bg)',
                    color: tone === t ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontWeight: '500',
                    textTransform: 'capitalize',
                    position: 'relative',
                  }}
                >
                  {t}
                  {t === recommendedTone && (
                    <span style={{ fontSize: '9px', marginLeft: '3px', opacity: 0.7 }}>suggested</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Add Chips */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
              Quick options
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {quickAdds.map(qa => (
                <button
                  key={qa.id}
                  onClick={() => toggleQuickAdd(qa.id)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderRadius: '20px',
                    border: activeQuickAdds.includes(qa.id) ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                    background: activeQuickAdds.includes(qa.id) ? 'var(--primary-50)' : 'var(--card-bg)',
                    color: activeQuickAdds.includes(qa.id) ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  {qa.label}
                </button>
              ))}
            </div>
            <Link
              to="/register"
              style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '6px', display: 'inline-block' }}
            >
              12 more with free account &rarr;
            </Link>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading || !reviewText.trim()}
            className="btn btn-primary"
            data-action="freetool-generate"
            style={{
              width: '100%',
              padding: '14px 20px',
              fontSize: '15px',
              fontWeight: '600',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'var(--primary)',
              opacity: loading || !reviewText.trim() ? 0.7 : 1,
            }}
          >
            {loading ? (
              <GeneratingIndicator />
            ) : (
              <>
                <Sparkles size={16} />
                Generate Response
              </>
            )}
          </button>
          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>Ctrl+Enter</span>
          </div>
        </div>

        {/* RIGHT COLUMN - Output */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Usage Counter */}
          <div style={{
            textAlign: 'center',
            padding: '8px 16px',
            background: 'var(--primary-50)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}>
            <Zap size={14} />
            {remainingTries !== null ? `${remainingTries} of 5 free tries remaining today` : '5 free tries per day'}
          </div>

          {/* Response Area */}
          <div style={{ flex: 1, minHeight: '200px' }}>
            {error && (
              <div
                style={{
                  padding: '16px',
                  background: error.includes('Upgrade') || error.includes('limit') ? 'var(--warning-bg)' : 'var(--error-bg)',
                  border: error.includes('Upgrade') || error.includes('limit') ? '1px solid var(--warning-border)' : '1px solid var(--error-border)',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  textAlign: 'center',
                }}
              >
                <p style={{
                  color: error.includes('Upgrade') || error.includes('limit') ? 'var(--warning-text)' : 'var(--error-text)',
                  fontSize: '14px',
                  marginBottom: (error.includes('Upgrade') || error.includes('limit')) ? '16px' : '0',
                }}>
                  {error}
                </p>
                {(error.includes('Upgrade') || error.includes('limit')) && !user && (
                  <div>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                      Sign up for 20 free responses/month:
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <GoogleLogin
                        onSuccess={handleGoogleSignIn}
                        onError={() => toast.error('Google Sign In failed')}
                        text="signup_with"
                        shape="rectangular"
                        width="280"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {response ? (
              <div
                className="response-reveal"
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--primary)',
                  borderRadius: '12px',
                  padding: '20px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Sparkles size={16} style={{ color: 'var(--primary)' }} />
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Your Response
                  </span>
                </div>
                {askUserEnabled && response && /\[[^\]]+\]/.test(response) ? (
                  <InteractiveResponse
                    response={response}
                    onResponseChange={(newResponse) => {
                      setResponse(newResponse);
                      setEditedResponse(newResponse);
                    }}
                  />
                ) : (
                  <textarea
                    value={editedResponse !== null ? editedResponse : response}
                    onChange={e => setEditedResponse(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '12px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-secondary)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                )}
                <div style={{
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginTop: '12px',
                }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleCopy}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: copied ? '#10B981' : 'var(--card-bg)',
                        color: copied ? 'white' : 'var(--text-primary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {copied ? (<><Check size={14} /> Copied!</>) : (<><Copy size={14} /> Copy</>)}
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--card-bg)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: loading ? 0.7 : 1,
                      }}
                    >
                      <RefreshCw size={14} /> Regenerate
                    </button>
                  </div>
                  {user ? (
                    <Link
                      to="/settings"
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', borderRadius: '6px', background: 'var(--primary)' }}
                    >
                      Customize responses
                    </Link>
                  ) : (
                    <GoogleLogin
                      onSuccess={handleGoogleSignIn}
                      onError={() => toast.error('Google Sign In failed')}
                      text="signup_with"
                      shape="rectangular"
                      size="medium"
                    />
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                border: '2px dashed var(--border-color)',
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center',
              }}>
                <MessageSquare size={32} style={{ color: 'var(--gray-300)', marginBottom: '12px' }} />
                <p style={{ color: 'var(--gray-400)', fontSize: '14px', margin: 0 }}>
                  Your AI-crafted response will appear here
                </p>
                <p style={{ color: 'var(--gray-300)', fontSize: '12px', marginTop: '8px' }}>
                  Paste a review and click Generate
                </p>
              </div>
            )}
          </div>

          {/* Google Sign In below output */}
          {!user && !response && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--gray-400)', display: 'block', marginBottom: '8px' }}>
                20 free/month with account
              </span>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSignIn}
                  onError={() => toast.error('Google Sign In failed')}
                  text="signup_with"
                  shape="rectangular"
                  width="240"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Gate Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: 'var(--text-primary)' }}>
              Sign in to continue
            </h3>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="continue_with"
                shape="rectangular"
                width="300"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0', gap: '12px' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', gap: '8px' }}>
              <input
                type="email"
                value={captureEmail}
                onChange={e => setCaptureEmail(e.target.value)}
                placeholder="or enter email"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  fontSize: '13px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type="submit"
                disabled={emailSubmitting}
                style={{
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  opacity: emailSubmitting ? 0.7 : 1,
                }}
              >
                {emailSubmitting ? '...' : 'Go'}
              </button>
            </form>
            <button
              onClick={() => {
                setShowEmailModal(false);
                if (response) {
                  navigator.clipboard.writeText(response);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                marginTop: '16px',
                textDecoration: 'underline',
              }}
            >
              No thanks, just copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// SEO Landing Page Component - Free AI Review Response Generator
const FreeToolPage = () => {
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/free-review-response-generator',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  const faqs = [
    {
      q: 'Is this review response generator really free?',
      a: 'Yes, completely free. You can generate up to 5 review responses per day without creating an account. No credit card required. For more responses, sign up for a free account to get 20 responses per month.',
    },
    {
      q: 'How does AI generate review responses?',
      a: 'Our AI analyzes the sentiment, context, and key points of each review, then crafts a personalized response matching your chosen tone. It uses advanced language models trained on millions of professional business responses.',
    },
    {
      q: 'Can I use this for Google, Yelp, and TripAdvisor reviews?',
      a: 'Absolutely. This tool works with reviews from any platform including Google Maps, Yelp, TripAdvisor, Booking.com, Facebook, Trustpilot, and more. Simply paste the review text and get your response.',
    },
    {
      q: 'What languages are supported?',
      a: 'The AI automatically detects the language of the review and responds in the same language. It supports all major languages including English, German, French, Spanish, Italian, Dutch, Portuguese, and many more.',
    },
    {
      q: 'Can I edit the AI-generated response?',
      a: 'Yes! Every response is fully editable. You can also use our bracket system to choose between different response options - like selecting your preferred greeting or how to address specific concerns.',
    },
    {
      q: 'Is my data secure?',
      a: 'We do not store your review text or generated responses. Each generation is processed in real-time and not saved to any database. Your privacy is our priority.',
    },
    {
      q: 'What types of businesses can use this?',
      a: 'We support 40+ industries including restaurants, hotels, dental practices, salons, auto shops, law firms, real estate agents, gyms, veterinarians, and many more. Each industry gets tailored responses.',
    },
    {
      q: 'How is this different from ChatGPT?',
      a: 'Unlike general AI tools, ReviewResponder is purpose-built for review responses. It understands review context, avoids generic corporate language, includes your business personality, and offers smart bracket options for personalization.',
    },
  ];

  const freeToolSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'ReviewResponder - Free AI Review Response Generator',
        description:
          'AI-powered tool to generate professional responses to customer reviews on any platform.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '150',
          bestRating: '5',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      },
    ],
  };

  return (
    <div>
      <SEO
        title="Free AI Review Response Generator | Write Perfect Replies"
        description="Generate professional review responses instantly with AI. Free tool for Google, Yelp, TripAdvisor reviews. No signup required. 40+ industries supported."
        canonical="/free-review-response-generator"
        structuredData={freeToolSchema}
      />

      {/* HERO SECTION */}
      <section
        style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Zap size={14} fill="currentColor" />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              100% Free - No Signup Required
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Free AI Review Response Generator
          </h1>

          <p
            style={{
              fontSize: '20px',
              opacity: 0.9,
              marginBottom: '40px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '0 auto 40px',
            }}
          >
            Write perfect, professional replies to any customer review in seconds. No signup needed.
          </p>

          <FreeToolGenerator />

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginTop: '32px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={16} /> 5 Free/Day
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Layers size={16} /> 40+ Industries
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> No Credit Card
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE MAPS</span>
            <span>YELP</span>
            <span>TRIPADVISOR</span>
            <span>BOOKING.COM</span>
            <span>TRUSTPILOT</span>
            <span>FACEBOOK</span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{
            fontSize: '28px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '40px',
            color: 'var(--text-primary)',
          }}
        >
          How It Works
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '32px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <FileText size={28} style={{ color: 'var(--primary)' }} />
            </div>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}
            >
              1. Paste Review
            </h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '15px' }}>
              Copy any customer review from Google, Yelp, TripAdvisor, or any platform
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <Sparkles size={28} style={{ color: 'var(--primary)' }} />
            </div>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}
            >
              2. Choose Your Tone
            </h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '15px' }}>
              Select professional, friendly, formal, or apologetic tone
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <Copy size={28} style={{ color: 'var(--primary)' }} />
            </div>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}
            >
              3. Copy & Reply
            </h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '15px' }}>
              Get your AI-crafted response instantly. Edit, copy, and post
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ padding: '60px 20px', background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '40px',
              color: 'var(--text-primary)',
            }}
          >
            Why Businesses Choose ReviewResponder
          </h2>
          <div
            style={{
              display: 'grid',
              gap: '24px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            }}
          >
            <div className="card" style={{ padding: '24px' }}>
              <Globe size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Works With Any Platform
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Google, Yelp, TripAdvisor, Booking.com, Trustpilot, Facebook, and dozens more. One tool for all your reviews.
              </p>
            </div>
            <div className="card" style={{ padding: '24px' }}>
              <Sparkles size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Smart Tone Matching
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                AI detects review sentiment and adjusts the response tone. Empathetic for complaints, grateful for praise.
              </p>
            </div>
            <div className="card" style={{ padding: '24px' }}>
              <Layers size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                40+ Industry Templates
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Restaurant, hotel, dental, salon, auto shop, law firm, real estate, and many more. Tailored language for your field.
              </p>
            </div>
            <div className="card" style={{ padding: '24px' }}>
              <MessageSquare size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Auto-Detect Language
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Reply in the reviewer's language automatically. Supports English, German, French, Spanish, Italian, and more.
              </p>
            </div>
            <div className="card" style={{ padding: '24px' }}>
              <Edit3 size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Human-in-the-Loop
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Smart brackets let you choose between response options. Pick the greeting, tone, and details that fit your style.
              </p>
            </div>
            <div className="card" style={{ padding: '24px' }}>
              <Zap size={32} style={{ color: 'var(--primary)', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Instant Results
              </h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Get your personalized response in under 5 seconds. No waiting, no loading screens, no account required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container" style={{ padding: '60px 20px', maxWidth: '800px' }}>
        <h2
          style={{
            fontSize: '28px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '40px',
            color: 'var(--text-primary)',
          }}
        >
          Frequently Asked Questions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="card"
              style={{
                padding: '0',
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: 0,
                    color: 'var(--text-primary)',
                  }}
                >
                  {faq.q}
                </h3>
                <ChevronDown
                  size={20}
                  style={{
                    color: 'var(--text-secondary)',
                    flexShrink: 0,
                    transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </div>
              {openFaq === i && (
                <div
                  style={{
                    padding: '0 20px 16px',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.6',
                    fontSize: '15px',
                  }}
                >
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          padding: '60px 20px',
          background: 'var(--bg-secondary)',
          textAlign: 'center',
        }}
      >
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}
          >
            Want Unlimited Responses?
          </h2>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '18px',
              lineHeight: '1.6',
              marginBottom: '32px',
            }}
          >
            Sign up free and get 20 responses/month. Upgrade anytime for unlimited.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <Link
              to="/register"
              className="btn btn-primary"
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '8px',
              }}
            >
              Sign Up Free
            </Link>
            <Link
              to="/pricing"
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '8px',
                border: '2px solid var(--border-color)',
                color: 'var(--text-primary)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              See Pricing <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// SEO Landing Page Component - Google Review Response Tool
const GoogleReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Save UTM parameters to sessionStorage for registration
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/google-review-response-generator',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Google Review Response Generator | AI-Powered Reply Tool"
        description="Generate professional Google review responses instantly. AI-powered tool helps craft thoughtful replies to Google Maps & Business reviews. Free to try."
        canonical="/google-review-response-generator"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Google Review Response Tool',
          description: 'AI-powered tool to craft thoughtful responses to Google reviews',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Star size={14} fill="currentColor" />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Google Maps Integration
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Google Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#4285F4" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Craft thoughtful Google review responses with psychology-based guidance. Your voice,
            your words - just faster. Boost your local SEO and show customers you care.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE MAPS</span>
            <span>YELP</span>
            <span>TRIPADVISOR</span>
            <span>BOOKING.COM</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Use ReviewResponder?
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Edit3 size={32} style={{ color: 'var(--primary-600)', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Your Voice, Your Way
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI gives you smart options — you make the final call. Keep your authentic tone in
              every response.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#34a853', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Psychology-Based Guidance
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Get smart suggestions for every situation. Choose what fits your style and the
              review's tone.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#4285f4', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Boost Local SEO
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active review responses signal to Google that your business is engaged, improving
              local search rankings.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="Google Maps" color="#4285f4" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#4285F4" source="google_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page Component - Yelp Review Reply Tool
const YelpReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Save UTM parameters to sessionStorage for registration
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/yelp-review-reply-tool',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Yelp Review Reply Tool | AI Response Generator"
        description="Create professional Yelp review responses instantly. AI-powered tool for business owners to craft thoughtful replies. Boost your Yelp reputation. Free trial."
        canonical="/yelp-review-reply-tool"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Yelp Review Reply Tool',
          description: 'AI-powered tool to craft thoughtful responses to Yelp reviews',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #d32323 0%, #ff5a5f 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Star size={14} fill="currentColor" />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Yelp Business Owners
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Yelp Review Reply Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#d32323" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Never let a Yelp review go unanswered. Craft thoughtful responses with psychology-based
            guidance - from 5-star praise to 1-star complaints.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>YELP</span>
            <span>GOOGLE MAPS</span>
            <span>TRIPADVISOR</span>
            <span>TRUSTPILOT</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Responding to Yelp Reviews Matters
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#d32323', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              97% Read Responses
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              97% of consumers who read reviews also read business responses —{' '}
              <a
                href="https://www.brightlocal.com/research/local-consumer-review-survey/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                BrightLocal
              </a>
              . Make yours count.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <MessageSquare size={32} style={{ color: '#d32323', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Turn Negatives Positive
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              A thoughtful response to a negative Yelp review can convert critics into loyal
              customers.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Clock size={32} style={{ color: '#d32323', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Respond in 3 Seconds
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stop agonizing over the perfect response. Craft your reply in seconds.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Perfect Responses for Every Yelp Review Type
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>5-Star Reviews:</strong> Thank customers and encourage return visits
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>3-4 Star Reviews:</strong> Acknowledge feedback and show commitment to
                improvement
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>1-2 Star Reviews:</strong> Apologize professionally and offer to make things
                right
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#D32323" source="yelp_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// All Industries Page - Comprehensive listing of all supported industries
const IndustriesPage = () => {
  const industryCategories = [
    {
      title: 'Hospitality & Food',
      icon: <Utensils size={24} />,
      color: '#f59e0b',
      industries: [
        { name: 'Restaurants', path: '/restaurant-review-responses' },
        { name: 'Hotels', path: '/hotel-review-management' },
        { name: 'Coffee Shops', path: '/coffee-shop-review-responses' },
        { name: 'Bars & Nightclubs', path: '/bar-nightclub-review-responses' },
        { name: 'Airbnb Hosts', path: '/airbnb-host-review-responses' },
      ],
    },
    {
      title: 'Healthcare',
      icon: <Heart size={24} />,
      color: '#ef4444',
      industries: [
        { name: 'Dentists', path: '/dentist-review-responses' },
        { name: 'Medical Practices', path: '/medical-practice-reviews' },
        { name: 'Chiropractors', path: '/chiropractor-reviews' },
        { name: 'Dermatologists', path: '/dermatologist-reviews' },
        { name: 'Therapists', path: '/therapy-counselor-reviews' },
        { name: 'Veterinarians', path: '/veterinarian-review-responses' },
        { name: 'Senior Care', path: '/senior-care-review-responses' },
      ],
    },
    {
      title: 'Home Services',
      icon: <Home size={24} />,
      color: '#3b82f6',
      industries: [
        { name: 'Plumbers', path: '/plumber-review-responses' },
        { name: 'Electricians', path: '/electrician-review-responses' },
        { name: 'HVAC', path: '/hvac-review-responses' },
        { name: 'Roofing', path: '/roofing-review-responses' },
        { name: 'Landscaping', path: '/landscaping-review-responses' },
        { name: 'Cleaning Services', path: '/cleaning-service-review-responses' },
        { name: 'Contractors', path: '/angie-list-contractor-reviews' },
      ],
    },
    {
      title: 'Professional Services',
      icon: <Briefcase size={24} />,
      color: '#8b5cf6',
      industries: [
        { name: 'Law Firms', path: '/law-firm-review-responses' },
        { name: 'Real Estate Agents', path: '/real-estate-agent-reviews' },
        { name: 'Accountants', path: '/accountant-review-responses' },
        { name: 'Insurance Agents', path: '/insurance-agent-review-responses' },
      ],
    },
    {
      title: 'Beauty & Wellness',
      icon: <Scissors size={24} />,
      color: '#ec4899',
      industries: [
        { name: 'Salons & Spas', path: '/salon-spa-review-responses' },
        { name: 'Barbers', path: '/barber-barbershop-reviews' },
        { name: 'Massage Therapists', path: '/massage-therapist-reviews' },
        { name: 'Personal Trainers', path: '/personal-trainer-reviews' },
        { name: 'Gyms & Fitness', path: '/gym-fitness-review-responses' },
      ],
    },
    {
      title: 'Automotive & Retail',
      icon: <Car size={24} />,
      color: '#06b6d4',
      industries: [
        { name: 'Auto Shops', path: '/auto-shop-reviews' },
        { name: 'Retail Stores', path: '/retail-store-review-responses' },
        { name: 'E-Commerce', path: '/ecommerce-review-responses' },
      ],
    },
    {
      title: 'Events & Creative',
      icon: <Camera size={24} />,
      color: '#f97316',
      industries: [
        { name: 'Photographers', path: '/photographer-review-responses' },
        { name: 'Wedding Vendors', path: '/wedding-vendor-review-responses' },
      ],
    },
    {
      title: 'Family & Pets',
      icon: <Dog size={24} />,
      color: '#22c55e',
      industries: [
        { name: 'Pet Services', path: '/pet-service-review-responses' },
        { name: 'Daycare', path: '/daycare-review-responses' },
      ],
    },
    {
      title: 'B2B & Software',
      icon: <Building size={24} />,
      color: '#6366f1',
      industries: [
        { name: 'G2', path: '/g2-review-responses' },
        { name: 'Capterra', path: '/capterra-review-responses' },
        { name: 'Glassdoor', path: '/glassdoor-review-responses' },
        { name: 'App Store', path: '/appstore-app-reviews' },
        { name: 'Indeed', path: '/indeed-company-reviews' },
      ],
    },
  ];

  const platformCategories = [
    {
      title: 'Review Platforms',
      industries: [
        { name: 'Google Reviews', path: '/google-review-response-generator' },
        { name: 'Yelp Reviews', path: '/yelp-review-reply-tool' },
        { name: 'TripAdvisor', path: '/tripadvisor-review-responses' },
        { name: 'Booking.com', path: '/booking-review-generator' },
        { name: 'Facebook', path: '/facebook-review-responses' },
        { name: 'Trustpilot', path: '/trustpilot-review-responses' },
        { name: 'BBB', path: '/bbb-review-responses' },
        { name: 'Healthgrades', path: '/healthgrades-review-responses' },
        { name: 'Zocdoc', path: '/zocdoc-review-responses' },
        { name: 'Zillow', path: '/zillow-realtor-reviews' },
        { name: 'Thumbtack', path: '/thumbtack-reviews' },
        { name: 'Negative Reviews', path: '/negative-review-responses' },
      ],
    },
  ];

  return (
    <div>
      <SEO
        title="All Industries We Serve | Review Response Tool"
        description="AI review response generator for 50+ industries. Restaurants, hotels, dentists, contractors, salons & more. Find your industry and start responding professionally."
        canonical="/industries"
      />
      <section
        style={{
          background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%)',
          padding: '80px 0 60px',
          color: 'white',
        }}
      >
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <h1 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '16px' }}>
            All Industries We Serve
          </h1>
          <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '32px' }}>
            Professional review response guidance for 50+ industries. Find your business type
            below.
          </p>
          <Link
            to="/register"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--bg-secondary)',
              color: 'var(--primary-600)',
              padding: '14px 28px',
              borderRadius: '8px',
              fontWeight: '600',
              textDecoration: 'none',
            }}
          >
            Sign Up Free <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 0' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '40px', textAlign: 'center' }}>
          Industries by Category
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
          }}
        >
          {industryCategories.map((category) => (
            <div
              key={category.title}
              className="card"
              style={{ padding: '24px', border: '1px solid var(--border-color)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    background: `${category.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: category.color,
                  }}
                >
                  {category.icon}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{category.title}</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {category.industries.map((ind) => (
                  <Link
                    key={ind.path}
                    to={ind.path}
                    style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      padding: '6px 0',
                      borderBottom: '1px solid var(--border-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {ind.name}
                    <ChevronRight size={14} style={{ opacity: 0.5 }} />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ background: 'var(--bg-secondary)', padding: '60px 0' }}>
        <div className="container">
          <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '40px', textAlign: 'center' }}>
            Supported Review Platforms
          </h2>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              justifyContent: 'center',
            }}
          >
            {platformCategories[0].industries.map((platform) => (
              <Link
                key={platform.path}
                to={platform.path}
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  padding: '10px 20px',
                  borderRadius: '100px',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {platform.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 0', textAlign: 'center' }}>
        <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
          Don't See Your Industry?
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
          ReviewResponder works for any business that receives customer reviews. Try it free today!
        </p>
        <Link
          to="/local-business-reviews"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--primary-600)',
            color: 'white',
            padding: '14px 28px',
            borderRadius: '8px',
            fontWeight: '600',
            textDecoration: 'none',
          }}
        >
          Local Business Reviews <ArrowRight size={18} />
        </Link>
      </section>

      <Footer />
    </div>
  );
};

// SEO Landing Page Component - Restaurant Review Responses
const RestaurantReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Save UTM parameters to sessionStorage for registration
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/restaurant-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Restaurant Review Response Tool | Craft Your Replies"
        description="AI-powered response generator for restaurant reviews. Craft professional replies to Google, Yelp & TripAdvisor reviews in seconds. Free to try."
        canonical="/restaurant-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Restaurant Review Response Tool',
          description: 'Psychology-based tool for restaurants to craft thoughtful review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Utensils size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Restaurant Owners
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Restaurant Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#ef4444" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Running a restaurant is hard enough. Craft your review responses with psychology-based guidance so you can
            focus on what matters - great food and happy customers.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE MAPS</span>
            <span>YELP</span>
            <span>TRIPADVISOR</span>
            <span>OPENTABLE</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Restaurants Need a Review Response Tool
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Clock size={32} style={{ color: '#f59e0b', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              You're Too Busy
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Between managing staff, inventory, and customers, who has time to write thoughtful
              review responses?
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Star size={32} style={{ color: '#f59e0b', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Reviews Make or Break You
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              94% of diners choose restaurants based on reviews —{' '}
              <a
                href="https://www.brightlocal.com/research/local-consumer-review-survey/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                BrightLocal
              </a>
              . Your responses show you care.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <MessageSquare size={32} style={{ color: '#f59e0b', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Every Platform Covered
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Google, Yelp, TripAdvisor, OpenTable, Uber Eats - respond to reviews from any
              platform.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Restaurant Review Scenarios We Handle
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"The food was amazing!"</strong> - Thank them and invite them back
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Service was slow"</strong> - Apologize and explain how you'll improve
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Food was cold"</strong> - Show genuine concern and offer to make it right
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Loved the atmosphere"</strong> - Highlight what makes your restaurant
                special
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#ef4444" source="restaurant_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page Component - Hotel Review Management
const HotelReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Save UTM parameters to sessionStorage for registration
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/hotel-review-management',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Hotel Review Management Tool | Guest Review Responses"
        description="AI-powered hotel review response generator. Craft professional replies for Booking.com, TripAdvisor & Google guest reviews. Save time, boost reputation."
        canonical="/hotel-review-management"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Hotel Review Management Tool',
          description: 'AI-powered tool for hotels to craft thoughtful guest review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Building size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Hotels & B&Bs
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Hotel Review Management Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#6366f1" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Manage guest reviews across all platforms with psychology-based guidance. From Booking.com
            to TripAdvisor, respond professionally and boost your hotel's reputation.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>BOOKING.COM</span>
            <span>TRIPADVISOR</span>
            <span>EXPEDIA</span>
            <span>GOOGLE</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Hotels Choose AI for Review Management
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Globe size={32} style={{ color: '#0ea5e9', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Multi-Language Support
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Respond to international guests in their language. Matches the review language
              automatically.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Clock size={32} style={{ color: '#0ea5e9', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              24/7 Response Ready
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Late-night review? Craft your response anytime. Never let a guest review wait.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#0ea5e9', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Improve OTA Rankings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active review responses improve your ranking on Booking.com and other OTAs.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Hospitality-Specific Response Styles
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>Warm & Welcoming:</strong> Perfect for positive reviews - thank guests and
                invite them back
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>Professional & Apologetic:</strong> Address complaints about rooms, service,
                or amenities
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>Constructive:</strong> Acknowledge mixed reviews and highlight improvements
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>Multi-language:</strong> Respond to German, French, Spanish, Chinese guests
                in their language
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#6366f1" source="hotel_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page Component - Local Business Review Responses (Google Ads)
const LocalBusinessReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Save UTM parameters to sessionStorage for registration
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/local-business-reviews',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Review Response Tool for Local Businesses"
        description="AI review response generator for local businesses. Perfect for plumbers, salons, dentists, auto shops & more. Craft professional replies in seconds."
        canonical="/local-business-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Local Business Review Response Tool',
          description: 'AI-powered tool for local businesses to craft thoughtful review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Store size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Local Businesses
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Craft Review Responses for Local Businesses
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#059669" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            You're busy running your business. Craft your review responses with psychology-based guidance -
            from glowing 5-stars to tough complaints. Your voice, your way.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE MAPS</span>
            <span>YELP</span>
            <span>FACEBOOK</span>
            <span>NEXTDOOR</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Perfect for Every Local Business
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '16px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            marginBottom: '48px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '12px',
            }}
          >
            <Wrench size={24} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: '500' }}>Plumbers</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '12px',
            }}
          >
            <Scissors size={24} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: '500' }}>Salons</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '12px',
            }}
          >
            <Heart size={24} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: '500' }}>Dentists</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '12px',
            }}
          >
            <Car size={24} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: '500' }}>Auto Shops</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px',
              background: 'var(--gray-50)',
              borderRadius: '12px',
            }}
          >
            <MapPin size={24} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: '500' }}>Any Local Biz</span>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Clock size={32} style={{ color: '#10b981', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              3 Seconds Per Review
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stop spending minutes on each response. Craft professional replies in seconds.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Star size={32} style={{ color: '#10b981', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Boost Your Rating
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews shows customers you care. Many unhappy reviewers update their
              rating after a thoughtful response.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#10b981', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Handle Complaints Like a Pro
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Turn angry reviewers into loyal customers with thoughtful, apologetic responses that
              address their concerns.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Local Business Review Scenarios
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Great service, highly recommend!"</strong> - Thank them warmly, mention
                your team's dedication
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Had to wait too long"</strong> - Apologize sincerely, explain improvements
                you're making
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Price was too high"</strong> - Highlight the value and quality they
                received
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#10b981', marginTop: '2px' }} />
              <div>
                <strong>"Problem wasn't fixed properly"</strong> - Offer to make it right, provide
                contact info
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#2196F3" source="local_business_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Negative Review Responses (Highest purchase intent!)
const NegativeReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/negative-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="How to Respond to Negative Reviews | AI Response Generator"
        description="Turn negative reviews into opportunities. AI-powered tool to craft professional responses to 1-star and 2-star reviews. Protect your reputation. Free trial."
        canonical="/negative-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Negative Review Response Tool',
          description: 'AI-powered tool to craft thoughtful responses to negative customer reviews',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <AlertCircle size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Damage Control
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            How to Respond to Negative Reviews
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#B71C1C" industry="negative" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Got a bad review? Craft a professional, empathetic response in seconds that shows
            you care.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE MAPS</span>
            <span>YELP</span>
            <span>TRIPADVISOR</span>
            <span>FACEBOOK</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Responding to Negative Reviews Matters
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#D32F2F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Turn Critics Into Fans
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              A thoughtful response to a negative review can change minds. Many customers give
              businesses another chance after seeing a professional reply.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#D32F2F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Others Are Watching
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              94% of consumers read review responses —{' '}
              <a
                href="https://www.brightlocal.com/research/local-consumer-review-survey/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                BrightLocal
              </a>
              . How you handle criticism shapes how new customers see you.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#D32F2F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Protect Your Rating
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Quick, professional responses often lead to updated reviews. Many critics upgrade
              their rating after a good experience.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            The Right Way to Respond to Negative Reviews
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#D32F2F', marginTop: '2px' }} />
              <div>
                <strong>Respond within 24 hours</strong> - Speed shows you take feedback seriously
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#D32F2F', marginTop: '2px' }} />
              <div>
                <strong>Acknowledge their feelings</strong> - Start with empathy, not defensiveness
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#D32F2F', marginTop: '2px' }} />
              <div>
                <strong>Take responsibility</strong> - Even if you disagree, own what you can
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#D32F2F', marginTop: '2px' }} />
              <div>
                <strong>Offer a solution</strong> - Invite them back or provide contact info to
                resolve
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#D32F2F', marginTop: '2px' }} />
              <div>
                <strong>Keep it professional</strong> - Never argue or get defensive publicly
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#F44336" source="negative_review_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - TripAdvisor Review Responses
const TripAdvisorReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/tripadvisor-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="TripAdvisor Review Response Tool | AI Reply Generator"
        description="Generate professional TripAdvisor review responses instantly. AI-powered tool for hotels, restaurants & attractions. Boost your ranking with thoughtful replies."
        canonical="/tripadvisor-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - TripAdvisor Review Response Tool',
          description: 'AI-powered tool to craft thoughtful TripAdvisor review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #003580 0%, #0066cc 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <MapPin size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              TripAdvisor Integration
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            TripAdvisor Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#00AF87" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Stand out on TripAdvisor with thoughtful responses. Craft professional replies to guest
            reviews and boost your ranking in the world's largest travel platform.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>HOTELS</span>
            <span>RESTAURANTS</span>
            <span>ATTRACTIONS</span>
            <span>VACATION RENTALS</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why TripAdvisor Reviews Matter for Your Business
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              463M Monthly Visitors
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              TripAdvisor is the world's largest travel platform. Your reviews are seen by millions
              of potential guests every month.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Higher Ranking
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Businesses that respond to reviews rank higher in TripAdvisor search results.
              Engagement matters for visibility.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Traveller's Choice
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active review management helps you earn TripAdvisor badges and awards that attract
              more bookings.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="TripAdvisor" color="#00AF87" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#00AF87" source="tripadvisor_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Booking.com Review Responses
const BookingReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/booking-review-generator',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Booking.com Review Response Tool | Hotel Guest Reviews"
        description="AI-powered Booking.com review response generator. Craft professional replies to guest reviews for hotels and vacation rentals. Improve your score. Free trial."
        canonical="/booking-review-generator"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Booking.com Review Response Tool',
          description: 'AI-powered tool to craft thoughtful Booking.com guest review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #003580 0%, #0066ff 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Globe size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Booking.com Partner
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Booking.com Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#003580" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Improve your Booking.com rating. Craft your guest review responses with psychology-based guidance.
            Your voice, your way - and watch your bookings increase.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>HOTELS</span>
            <span>APARTMENTS</span>
            <span>HOSTELS</span>
            <span>VACATION RENTALS</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Booking.com Reviews Are Critical
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Star size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Rating = Visibility
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Properties with higher review scores rank better in Booking.com search. Every 0.1
              point increase matters for bookings.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <MessageSquare size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Response Rate Tracked
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Booking.com shows your response rate to potential guests. High engagement builds trust
              and increases conversions.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#003580', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Earn Badges
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active review management helps you earn Booking.com badges like "Traveller Review
              Awards" that boost credibility.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="Booking.com" color="#003580" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#003580" source="booking_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Facebook Review Responses
const FacebookReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/facebook-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Facebook Review Response Tool | Business Page Reviews"
        description="AI Facebook review response generator. Craft professional replies to recommendations and reviews on your business page. Build trust with customers. Free trial."
        canonical="/facebook-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Facebook Review Response Tool',
          description: 'AI-powered tool to craft thoughtful Facebook business review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #1877f2 0%, #0a66c2 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <MessageSquare size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Facebook Business
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Facebook Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#1877F2" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build trust on Facebook with thoughtful responses. Craft professional replies to
            recommendations and reviews to grow your local business presence.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>FACEBOOK</span>
            <span>INSTAGRAM</span>
            <span>META BUSINESS</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Facebook Reviews Matter for Local Business
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#1877f2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              3 Billion Users
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Facebook is the world's largest social network. Your business reviews are visible to
              friends and family of your customers.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Star size={32} style={{ color: '#1877f2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Recommendations Matter
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Facebook's recommendation system shows reviews to people searching for local
              businesses. Engaged pages get more visibility.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <MessageSquare size={32} style={{ color: '#1877f2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Social Proof
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to Facebook reviews shows you're an active, caring business. This builds
              trust with potential customers.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="Facebook" color="#1877f2" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#1877F2" source="facebook_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Dentist Review Responses
const DentistReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/dentist-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Dentist Review Response Tool | Dental Practice Reviews"
        description="AI review response generator for dentists and dental practices. Reply to Google, Yelp & Healthgrades patient reviews professionally. HIPAA-friendly. Free trial."
        canonical="/dentist-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Dentist Review Response Tool',
          description: 'AI-powered tool for dental practices to craft thoughtful patient review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Dental Practices
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Dentist Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#4CAF50" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build patient trust with thoughtful review responses. Craft replies that maintain
            HIPAA compliance awareness.
          </p>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE</span>
            <span>YELP</span>
            <span>HEALTHGRADES</span>
            <span>ZOCDOC</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Dental Practices
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Patients Research Online
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              77% of patients use online reviews when choosing a new dentist —{' '}
              <a href="https://www.amraandelma.com/dentist-marketing-statistics/" target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>source</a>.
              Your responses show you care.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              HIPAA-Aware Responses
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responses avoid confirming patient relationships or treatment details, helping you
              stay compliant.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Grow Your Practice
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Dental practices that respond to reviews see more new patient appointments. Active
              engagement builds trust.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Dental Review Scenarios
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Dr. Smith was amazing!"</strong> - Thank them warmly, mention your
                commitment to patient care
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Wait time was too long"</strong> - Apologize for the inconvenience, explain
                your commitment to quality care
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Procedure was painful"</strong> - Show empathy, invite them to discuss
                concerns privately
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Front desk was rude"</strong> - Take it seriously, commit to addressing the
                issue
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#2E7D32" source="dentist_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Medical Practice Review Responses
const MedicalPracticeReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/medical-practice-reviews',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Medical Practice Review Response Tool | Healthcare Reviews"
        description="HIPAA-friendly AI review response generator for doctors, clinics and healthcare providers. Craft professional patient review replies. Protect your reputation."
        canonical="/medical-practice-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Medical Practice Review Response Tool',
          description: 'AI-powered tool for healthcare providers to craft thoughtful patient review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Healthcare Providers
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Medical Practice Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#C62828" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Manage your medical practice's online reputation with psychology-based guidance. Craft
            professional, HIPAA-aware responses that build patient trust without compromising privacy.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GOOGLE</span>
            <span>HEALTHGRADES</span>
            <span>VITALS</span>
            <span>ZOCDOC</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Medical Practices
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#E53935', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Patients Choose Based on Reviews
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              94% of patients use online reviews to evaluate providers —{' '}
              <a
                href="https://www.softwareadvice.com/resources/how-patients-use-online-reviews/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                Software Advice
              </a>
              . Your responses shape their decision.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#E53935', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              HIPAA Compliance Matters
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responses don't confirm patient relationships or disclose PHI, helping you respond
              safely.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Clock size={32} style={{ color: '#E53935', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Save Staff Time
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Your staff is busy with patient care. Craft responses in seconds that you can
              review and post quickly.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Safe Review Response Guidelines
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E53935', marginTop: '2px' }} />
              <div>
                <strong>Never confirm patient status</strong> - Don't acknowledge someone is/was
                your patient
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E53935', marginTop: '2px' }} />
              <div>
                <strong>Avoid treatment details</strong> - Keep responses general about your
                practice's approach
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E53935', marginTop: '2px' }} />
              <div>
                <strong>Take complaints offline</strong> - Invite concerned reviewers to contact you
                directly
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E53935', marginTop: '2px' }} />
              <div>
                <strong>Stay professional</strong> - Never argue or get defensive, even with unfair
                reviews
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#C62828" source="medical_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Salon & Spa Review Responses
const SalonSpaReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/salon-spa-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Salon & Spa Review Response Tool | Beauty Business Reviews"
        description="AI review response generator for salons and spas. Hair salons, nail salons, day spas - craft professional replies to client reviews. Boost your reputation."
        canonical="/salon-spa-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Salon & Spa Review Response Tool',
          description: 'AI-powered tool for beauty businesses to craft thoughtful client review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Scissors size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Beauty Businesses
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Salon & Spa Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#C2185B" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your beauty business reputation with thoughtful responses. Craft professional replies
            to client reviews and attract more bookings.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>HAIR SALONS</span>
            <span>NAIL SALONS</span>
            <span>DAY SPAS</span>
            <span>MED SPAS</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Salons & Spas
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Star size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Visual Results Matter
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Clients research salons extensively before booking. Reviews help them trust you with
              their look and self-care.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Personal Connection
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              The beauty industry is personal. Responding to reviews shows you value each client
              relationship.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              More Bookings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Salons with more reviews and responses rank higher in local search, bringing in new
              clients naturally.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Salon & Spa Review Scenarios
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E91E63', marginTop: '2px' }} />
              <div>
                <strong>"Love my new hair color!"</strong> - Thank them, credit your stylist's
                expertise
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E91E63', marginTop: '2px' }} />
              <div>
                <strong>"Color faded after one wash"</strong> - Apologize, invite back for a touch-up
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E91E63', marginTop: '2px' }} />
              <div>
                <strong>"Most relaxing massage ever"</strong> - Express gratitude, invite them back
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#E91E63', marginTop: '2px' }} />
              <div>
                <strong>"Nail polish chipped next day"</strong> - Apologize, offer a redo or
                discount
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#E91E63" source="salon_spa_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Auto Shop Review Responses
const AutoShopReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/auto-shop-reviews',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
  }, [location.search]);

  return (
    <div>
      <SEO
        title="Auto Shop Review Response Tool | Mechanic & Car Dealer Reviews"
        description="AI review response generator for auto shops. Mechanics, auto repair, tire shops, dealerships - craft professional customer review replies. Build trust."
        canonical="/auto-shop-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Auto Shop Review Response Tool',
          description: 'AI-powered tool for automotive businesses to craft thoughtful customer review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #FF6F00 0%, #E65100 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Car size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              For Auto Shops
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Auto Shop Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#E65100" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build trust in an industry where customers are skeptical. Craft professional responses
            that turn first-timers into loyal customers.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>AUTO REPAIR</span>
            <span>TIRE SHOPS</span>
            <span>OIL CHANGE</span>
            <span>DEALERSHIPS</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Are Critical for Auto Shops
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#FF6F00', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Trust Is Everything
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Customers worry about being overcharged or upsold. Reviews and your responses prove
              you're honest and fair.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#FF6F00', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Local Search Dominance
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              When someone's car breaks down, they search "auto repair near me." More reviews =
              higher ranking = more calls.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FF6F00', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Repeat Business
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews builds relationships. Happy customers come back for oil changes,
              tires, and recommend you to friends.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Auto Shop Review Scenarios
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF6F00', marginTop: '2px' }} />
              <div>
                <strong>"Fixed my car right the first time"</strong> - Thank them, mention your
                commitment to quality work
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF6F00', marginTop: '2px' }} />
              <div>
                <strong>"Price was higher than quoted"</strong> - Explain transparently, offer to
                discuss
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF6F00', marginTop: '2px' }} />
              <div>
                <strong>"Took longer than expected"</strong> - Apologize, explain the thoroughness
                of your work
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF6F00', marginTop: '2px' }} />
              <div>
                <strong>"Same problem came back"</strong> - Take responsibility, offer to make it
                right
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#455A64" source="auto_shop_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Trustpilot Review Responses
const TrustpilotReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/trustpilot-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

  }, [location.search]);

  return (
    <div>
      <SEO
        title="Trustpilot Review Response Tool | Craft Your Replies"
        description="Craft your Trustpilot review responses with psychology-based guidance. Get a structure, add your voice. Boost your TrustScore authentically."
        canonical="/trustpilot-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Trustpilot Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful Trustpilot review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #00b67a 0%, #005128 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Star size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Trustpilot Integration
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Trustpilot Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#00B67A" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build trust on the world's leading review platform. Craft thoughtful Trustpilot responses
            with psychology-based guidance.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>E-COMMERCE</span>
            <span>SaaS</span>
            <span>SERVICES</span>
            <span>RETAIL</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Trustpilot Reviews Matter for Your Business
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#00b67a', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              100M+ Reviews
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Trustpilot hosts over 100 million reviews. Customers check your rating before buying.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#00b67a', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              TrustScore Impact
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews can improve your TrustScore and build customer confidence.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#00b67a', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Google Integration
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Trustpilot reviews appear in Google Ads and organic search, boosting your visibility.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="Trustpilot" color="#00b67a" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#00B67A" source="trustpilot_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Airbnb Host Review Responses
const AirbnbReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/airbnb-host-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Airbnb Host Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Airbnb guest review responses with psychology-based guidance. Host like a Superhost with authentic, thoughtful replies.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Airbnb Host Review Response Tool',
      description: 'Psychology-based tool for Airbnb hosts to craft thoughtful guest review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #FF5A5F 0%, #C13584 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Home size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Airbnb Host Tool
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Airbnb Host Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF5A5F" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Respond to guest reviews like a Superhost. Craft warm, professional responses that
            encourage future bookings.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>APARTMENTS</span>
            <span>VACATION HOMES</span>
            <span>UNIQUE STAYS</span>
            <span>EXPERIENCES</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Review Responses Matter for Airbnb Hosts
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#FF5A5F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Superhost Status
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Engaging with guest reviews shows Airbnb you're a responsive, caring host.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FF5A5F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              More Bookings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Guests read your responses to previous reviews before booking. Make a great
              impression.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#FF5A5F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Guest Loyalty
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Thoughtful responses encourage repeat stays and recommendations to friends.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Tips for Responding to Airbnb Reviews
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF5A5F', marginTop: '2px' }} />
              <div>
                <strong>Thank every guest</strong> - Even for short stays, appreciation matters
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF5A5F', marginTop: '2px' }} />
              <div>
                <strong>Be personal</strong> - Reference specific details from their stay
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF5A5F', marginTop: '2px' }} />
              <div>
                <strong>Address concerns gracefully</strong> - Show you care about improving
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#FF5A5F', marginTop: '2px' }} />
              <div>
                <strong>Invite them back</strong> - End with a warm welcome for future stays
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF5A5F" source="airbnb_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Real Estate Review Responses
const RealEstateReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/real-estate-agent-reviews',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Real Estate Agent Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your real estate review responses with psychology-based guidance. Reply to client reviews on Zillow, Realtor.com, and Google authentically.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Real Estate Agent Review Response Tool',
      description: 'Psychology-based tool for real estate agents to craft thoughtful client review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Home size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Real Estate
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Real Estate Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#2d5a87" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your realtor reputation with thoughtful responses. Craft professional replies to
            client reviews on Zillow, Realtor.com, and Google.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>ZILLOW</span>
            <span>REALTOR.COM</span>
            <span>GOOGLE</span>
            <span>YELP</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Real Estate Agents
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#2d5a87', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Buyers Research Online
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Home buyers and sellers research agents online before making contact. Your reviews matter.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#2d5a87', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Win More Listings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Strong reviews help you win listing appointments against competing agents.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#2d5a87', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Build Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews shows you care about client relationships beyond the sale.
            </p>
          </div>
        </div>

        <ChromeExtensionHowTo platform="Zillow, Realtor.com, or Google" color="#2d5a87" />

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#8E44AD" source="real_estate_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Gym/Fitness Review Responses
const GymReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/gym-fitness-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Gym & Fitness Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your gym and fitness center review responses with psychology-based guidance. Attract more members with authentic, thoughtful replies.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Gym & Fitness Review Response Tool',
      description: 'Psychology-based tool for gyms and fitness centers to craft thoughtful member review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Zap size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Fitness Industry
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Gym & Fitness Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#ff6b35" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Keep members motivated and attract new ones. Craft professional responses to gym
            reviews that show you care about every member's fitness journey.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>GYMS</span>
            <span>CROSSFIT</span>
            <span>YOGA STUDIOS</span>
            <span>PERSONAL TRAINING</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Fitness Businesses
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#ff6b35', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Member Acquisition
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              People looking for a gym read reviews to learn about culture and community. Your responses showcase who you are.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#ff6b35', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Reduce Churn
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to negative reviews can turn unhappy members into loyal advocates.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#ff6b35', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Community Building
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Engaging with reviews creates a sense of community that keeps members coming back.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Gym Review Topics
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#ff6b35', marginTop: '2px' }} />
              <div>
                <strong>"Equipment was broken"</strong> - Acknowledge, explain maintenance schedule
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#ff6b35', marginTop: '2px' }} />
              <div>
                <strong>"Too crowded"</strong> - Suggest off-peak hours, mention expansion plans
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#ff6b35', marginTop: '2px' }} />
              <div>
                <strong>"Great trainers!"</strong> - Thank them, share with your team
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#ff6b35', marginTop: '2px' }} />
              <div>
                <strong>"Cancellation issues"</strong> - Apologize, offer direct contact for
                resolution
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF5722" source="gym_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Veterinarian/Pet Services Review Responses
const VetReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/veterinarian-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Veterinarian Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your veterinarian review responses with psychology-based guidance. Reply to pet owner reviews with compassion and authenticity.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Veterinarian Review Response Tool',
      description: 'Psychology-based tool for veterinarians to craft thoughtful pet owner review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Veterinary Care
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Veterinarian Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#4CAF50" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Show pet owners you care as much about their furry friends as they do. Craft
            compassionate, professional responses to veterinary reviews.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>VET CLINICS</span>
            <span>ANIMAL HOSPITALS</span>
            <span>PET GROOMING</span>
            <span>PET BOARDING</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Veterinary Practices
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Pet Parent Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Pet owners are deeply emotional about their animals. Your responses show you
              understand.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              New Patient Growth
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Pet owners trust reviews when choosing a vet. Thoughtful responses attract new patients.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Handle Sensitive Topics
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              From billing concerns to end-of-life care, respond with empathy and professionalism.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Veterinary Review Topics
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Long wait times"</strong> - Explain emergency protocols, offer appointment
                tips
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Expensive treatment"</strong> - Explain value, mention payment plans
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"Compassionate care"</strong> - Thank them, share with your team
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#4CAF50', marginTop: '2px' }} />
              <div>
                <strong>"End-of-life support"</strong> - Respond with deep empathy and care
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#4CAF50" source="vet_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Law Firm Review Responses
const LawFirmReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/law-firm-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Law Firm Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your law firm review responses with psychology-based guidance. Reply to client reviews while maintaining attorney ethics authentically.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Law Firm Review Response Tool',
      description:
        'Psychology-based tool for law firms and attorneys to craft thoughtful client review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Shield size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Legal Industry
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Law Firm Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#16213e" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Respond to client reviews professionally while maintaining attorney-client privilege.
            Craft ethical, thoughtful responses that build trust.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>PERSONAL INJURY</span>
            <span>FAMILY LAW</span>
            <span>CRIMINAL DEFENSE</span>
            <span>CORPORATE</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Law Firms
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#16213e', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Client Acquisition
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              82% of people who found attorneys online used reviews —{' '}
              <a
                href="https://www.findlaw.com/lawyer-marketing/blog/the-2024-u-s-consumer-legal-needs-survey-what-attorneys-need-to-know/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                FindLaw 2024
              </a>
              . Your reputation matters.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#16213e', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Ethical Compliance
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft responses that respect confidentiality and bar association guidelines.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#16213e', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Competitive Edge
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stand out from other firms by showing you care about client feedback.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Best Practices for Attorney Review Responses
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#16213e', marginTop: '2px' }} />
              <div>
                <strong>Never confirm representation</strong> - Protect client confidentiality
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#16213e', marginTop: '2px' }} />
              <div>
                <strong>Keep it general</strong> - Thank without revealing case details
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#16213e', marginTop: '2px' }} />
              <div>
                <strong>Address concerns offline</strong> - Invite private conversation
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#16213e', marginTop: '2px' }} />
              <div>
                <strong>Stay professional</strong> - Never argue publicly with reviewers
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#34495E" source="law_firm_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - E-Commerce Review Responses
const EcommerceReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/ecommerce-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'E-Commerce Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your e-commerce review responses with psychology-based guidance. Reply to customer reviews on Shopify, Etsy, eBay and more authentically.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - E-Commerce Review Response Tool',
      description: 'Psychology-based tool for e-commerce businesses to craft thoughtful customer review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <ShoppingCart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              E-Commerce
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            E-Commerce Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#7c3aed" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Turn product reviews into sales opportunities. Craft professional responses that
            address concerns, thank happy customers, and boost your store rating.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>SHOPIFY</span>
            <span>ETSY</span>
            <span>EBAY</span>
            <span>GOOGLE SHOPPING</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for E-Commerce
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#7c3aed', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Conversion Boost
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Products with seller responses see 18% higher conversion rates. Engagement drives
              sales.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#7c3aed', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Customer Retention
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to negative reviews can turn complainers into repeat customers.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#7c3aed', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Algorithm Ranking
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active sellers rank higher in marketplace search results. Reviews boost visibility.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common E-Commerce Review Topics
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#7c3aed', marginTop: '2px' }} />
              <div>
                <strong>"Product not as described"</strong> - Apologize, offer exchange or refund
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#7c3aed', marginTop: '2px' }} />
              <div>
                <strong>"Shipping was slow"</strong> - Explain delays, offer solutions for next time
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#7c3aed', marginTop: '2px' }} />
              <div>
                <strong>"Love this product!"</strong> - Thank them, suggest complementary products
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#7c3aed', marginTop: '2px' }} />
              <div>
                <strong>"Arrived damaged"</strong> - Express concern, provide replacement process
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#9C27B0" source="ecommerce_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Coffee Shop/Café Review Responses
const CoffeeShopReviewPage = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/coffee-shop-review-responses',
    };
    if (utmParams.utm_source) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }

    document.title =
      'Coffee Shop Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your coffee shop and cafe review responses with psychology-based guidance. Build your local reputation with authentic replies.'
      );

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Coffee Shop Review Response Tool',
      description: 'Psychology-based tool for coffee shops and cafes to craft thoughtful customer review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #6f4e37 0%, #8b5a2b 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Coffee size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Coffee & Cafes
            </span>
          </div>

          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Coffee Shop Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#6f4e37" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Brew the perfect response every time. Craft warm, friendly replies that reflect your
            coffee shop's unique personality and keep customers coming back.
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '32px',
              opacity: 0.7,
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1px',
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '32px',
              alignItems: 'center',
            }}
          >
            <span>COFFEE SHOPS</span>
            <span>CAFES</span>
            <span>BAKERIES</span>
            <span>TEA HOUSES</span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Coffee Shops
        </h2>

        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <MapPin size={32} style={{ color: '#6f4e37', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Local Discovery
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              "Coffee near me" searches lead to Google reviews. Your rating determines if they walk
              in.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#6f4e37', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Community Building
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews creates a sense of community that makes your cafe a local
              favorite.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#6f4e37', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Regular Customers
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Personalized responses turn one-time visitors into daily regulars who know you by
              name.
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: '60px',
            background: 'var(--gray-50)',
            borderRadius: '16px',
            padding: '40px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
            Common Coffee Shop Review Topics
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#6f4e37', marginTop: '2px' }} />
              <div>
                <strong>"Coffee was cold"</strong> - Apologize, invite them back for a fresh cup
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#6f4e37', marginTop: '2px' }} />
              <div>
                <strong>"No WiFi/power outlets"</strong> - Explain your concept, suggest
                alternatives
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#6f4e37', marginTop: '2px' }} />
              <div>
                <strong>"Best latte in town!"</strong> - Thank them warmly, share barista's
                appreciation
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <CheckCircle size={20} style={{ color: '#6f4e37', marginTop: '2px' }} />
              <div>
                <strong>"Too expensive"</strong> - Explain sourcing, mention value options
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#6D4C41" source="coffee_shop_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - G2 Review Responses
const G2ReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/g2-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="G2 Review Response Tool | Craft Your Replies"
        description="Craft your G2 review responses with psychology-based guidance. Reply to B2B software reviews authentically and win more enterprise deals."
        canonical="/g2-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - G2 Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful G2 software review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #FF492C 0%, #cc3a23 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Award size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              B2B Software
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            G2 Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF492C" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Win more enterprise deals with professional G2 review responses. Show prospects you care
            about customer success.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why G2 Reviews Matter for SaaS Companies
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#FF492C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Buyer Research
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              89% of B2B buyers check review sites before buying software —{' '}
              <a
                href="https://research.g2.com/2024-buyer-behavior-report"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                G2 2024
              </a>
              . Your responses shape buying decisions.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FF492C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Grid Rankings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Active engagement improves your G2 Grid position and category leadership.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#FF492C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Enterprise Deals
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Procurement teams check G2 reviews. Professional responses close deals faster.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF492C" source="g2_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Capterra Review Responses
const CapterraReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/capterra-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Capterra Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Capterra review responses with psychology-based guidance. Reply to software reviews authentically and attract more leads.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Capterra Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Capterra software review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #044D80 0%, #023350 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Layers size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Software Reviews
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Capterra Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#044D80" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Stand out on Capterra by crafting authentic, thoughtful responses. Convert more software
            buyers into customers.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Capterra Reviews Drive Software Sales
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#044D80', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              5M+ Monthly Visitors
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Capterra attracts millions of software buyers researching solutions every month.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#044D80', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Comparison Shopping
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Buyers compare software side-by-side. Responsive vendors win more trials.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#044D80', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Lead Generation
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Higher ratings and active engagement drive more qualified leads from Capterra.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#044D80" source="capterra_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Glassdoor Review Responses
const GlassdoorReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/glassdoor-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Glassdoor Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Glassdoor review responses with psychology-based guidance. Reply to employee reviews authentically and improve your employer brand.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Glassdoor Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Glassdoor employee review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #0CAA41 0%, #087830 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Users size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Employer Brand
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Glassdoor Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#0CAA41" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build a stronger employer brand with authentic, thoughtful responses. Attract top
            talent by showing you value feedback.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Glassdoor Reviews Matter for Recruiting
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#0CAA41', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Candidate Research
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              86% of job seekers read company reviews before applying —{' '}
              <a
                href="https://www.glassdoor.com/blog/most-important-employer-branding-statistics/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                Glassdoor
              </a>
              . Your responses shape their impression.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#0CAA41', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Hiring Cost Reduction
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Strong employer brands reduce cost-per-hire by up to 50%.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#0CAA41', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Talent Retention
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews shows you listen, improving employee satisfaction.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#0CAA41" source="glassdoor_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - BBB Review Responses
const BBBReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/bbb-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'BBB Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Better Business Bureau review responses with psychology-based guidance. Reply to BBB complaints authentically and build trust.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - BBB Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Better Business Bureau review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #005A8C 0%, #003d5c 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Shield size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              BBB Accredited
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            BBB Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#005A8C" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Maintain your BBB rating with authentic, thoughtful responses. Handle complaints
            effectively and build consumer trust.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why BBB Reviews Matter for Your Business
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#005A8C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Trust Signal
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              BBB accreditation is a powerful trust signal. Consumers check BBB before major
              purchases.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#005A8C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Rating Protection
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Timely, professional responses help maintain your A+ rating.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#005A8C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Complaint Resolution
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              BBB tracks complaint resolution rates. Professional responses improve your metrics.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#005A8C" source="bbb_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Plumber Review Responses
const PlumberReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/plumber-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Plumber Review Response Tool | Plumbing Service Reviews"
        description="AI review response generator for plumbers and plumbing companies. Craft professional replies to customer reviews. Get more jobs with great reputation."
        canonical="/plumber-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Plumber Review Response Tool',
          description: 'AI-powered tool to craft thoughtful plumbing service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Wrench size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Plumbing Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Plumber Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#1E88E5" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your plumbing reputation with authentic, thoughtful responses. Turn satisfied
            customers into referral sources.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Plumbers
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Wrench size={32} style={{ color: '#1E88E5', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Emergency Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Customers need plumbers fast. Good reviews mean they call you first for emergencies.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#1E88E5', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Local SEO Boost
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responded reviews improve your Google Maps ranking for local searches.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#1E88E5', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Word of Mouth
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Professional responses show reliability, driving referrals from happy homeowners.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#1976D2" source="plumber_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Electrician Review Responses
const ElectricianReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/electrician-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Electrician Review Response Tool | Craft Your Replies"
        description="Craft your electrical service review responses with psychology-based guidance. Build trust and get more electrical jobs with authentic replies."
        canonical="/electrician-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Electrician Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful electrical service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #FFC107 0%, #FFA000 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Zap size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Electrical Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Electrician Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FFA000" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Power up your electrical business with authentic, thoughtful responses. Show
            customers why you're the trusted choice.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Electricians
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Zap size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Safety Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Electrical work requires trust. Positive reviews show you're licensed, insured, and
              reliable.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Competitive Edge
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stand out from competitors with professional, thoughtful review responses.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Commercial Contracts
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Business clients check reviews before hiring. Great responses win bigger contracts.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FFC107" source="electrician_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - HVAC Review Responses
const HVACReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/hvac-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="HVAC Review Response Tool | Craft Your Replies"
        description="Craft your HVAC service review responses with psychology-based guidance. Build trust and grow your HVAC business with authentic replies."
        canonical="/hvac-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - HVAC Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful HVAC service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #00ACC1 0%, #00838F 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Thermometer size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              HVAC Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            HVAC Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#00ACC1" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Keep your HVAC business cool under pressure with authentic, thoughtful responses.
            Turn every review into a growth opportunity.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for HVAC Companies
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Thermometer size={32} style={{ color: '#00ACC1', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Seasonal Demand
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Peak season means more reviews. Stay on top of customer feedback when it matters most.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#00ACC1', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Service Agreements
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Good reviews help sell maintenance contracts and recurring service plans.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#00ACC1', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Emergency Calls
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              When AC breaks in summer, customers call the highest-rated HVAC company.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#0288D1" source="hvac_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Roofing Review Responses
const RoofingReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/roofing-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Roofing Review Response Tool | Craft Your Replies"
        description="Craft your roofing review responses with psychology-based guidance. Build trust and win more roofing contracts with authentic replies."
        canonical="/roofing-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Roofing Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful roofing service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Home size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Roofing Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Roofing Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#795548" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Raise the roof on your reputation with authentic, thoughtful responses. Win more
            high-value roofing contracts.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Roofers
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Home size={32} style={{ color: '#795548', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              High-Value Projects
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Roofing is a big investment. Homeowners research extensively before choosing a
              contractor.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#795548', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Storm Season
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              After storms, everyone needs a roofer. The best-reviewed companies get called first.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#795548', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Insurance Work
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Insurance companies recommend highly-rated contractors. Reviews drive referrals.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#795548" source="roofing_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Landscaping Review Responses
const LandscapingReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/landscaping-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Landscaping Review Response Tool | Craft Your Replies"
        description="Craft your landscaping review responses with psychology-based guidance. Build trust and grow your landscaping business with authentic replies."
        canonical="/landscaping-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Landscaping Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful landscaping service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Trees size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Landscaping Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Landscaping Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#4CAF50" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Help your landscaping business flourish with authentic, thoughtful responses. Turn
            happy customers into year-round clients.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Landscapers
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Trees size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Visual Results
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Landscaping transforms homes. Reviews with photos drive new business.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Recurring Revenue
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Good reviews help convert one-time projects into monthly maintenance contracts.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#4CAF50', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Neighborhood Effect
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              One beautiful yard leads to referrals from neighbors. Reviews amplify this effect.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#4CAF50" source="landscaping_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Cleaning Service Review Responses
const CleaningReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/cleaning-service-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Cleaning Service Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your cleaning service review responses with psychology-based guidance. Build trust and grow your cleaning business with authentic replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Cleaning Service Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful cleaning service review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Sparkles size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Cleaning Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Cleaning Service Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#9C27B0" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Keep your cleaning business sparkling with authentic, thoughtful responses. Turn
            one-time clients into recurring customers.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Cleaning Services
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Sparkles size={32} style={{ color: '#9C27B0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Trust in Your Home
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Clients invite you into their homes. Reviews build the trust needed for that
              relationship.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#9C27B0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Recurring Bookings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Great reviews convert one-time deep cleans into weekly or bi-weekly service plans.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#9C27B0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Commercial Contracts
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Office managers check reviews before hiring. Professional responses win business
              accounts.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#00BCD4" source="cleaning_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Healthgrades Review Responses
const HealthgradesReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/healthgrades-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Healthgrades Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Healthgrades review responses with psychology-based guidance. Build your medical practice reputation with authentic, HIPAA-aware replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Healthgrades Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Healthgrades patient review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #00A99D 0%, #007d74 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Healthcare
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Healthgrades Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#0077C8" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build patient trust on Healthgrades with authentic, thoughtful responses. Attract
            more patients with a stellar online reputation.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> HIPAA-Aware
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Healthgrades Reviews Matter
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#00A99D', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Patient Decisions
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              77% of patients use reviews as their first step in finding a doctor —{' '}
              <a href="https://www.sixthcitymarketing.com/2024/02/28/dental-marketing-stats/" target="_blank" rel="noopener noreferrer" style={{ color: '#00A99D' }}>source</a>.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#00A99D', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Practice Growth
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Higher ratings mean more new patient appointments and practice revenue.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#00A99D', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              HIPAA Compliant
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft responses that maintain patient privacy and HIPAA compliance.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#0077C8" source="healthgrades_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Zocdoc Review Responses
const ZocdocReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/zocdoc-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Zocdoc Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Zocdoc review responses with psychology-based guidance. Build patient trust and fill your appointment calendar authentically.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Zocdoc Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Zocdoc patient review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #FFA000 0%, #FF8F00 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Calendar size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Healthcare Booking
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Zocdoc Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF7F50" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Turn Zocdoc reviews into new patient bookings with authentic, thoughtful responses.
            Stand out in the competitive healthcare market.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> HIPAA-Aware
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Zocdoc Reviews Drive Bookings
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Calendar size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Direct Bookings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Zocdoc patients book appointments directly. Reviews influence their choice.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Search Rankings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Engaged profiles rank higher in Zocdoc search results.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#FFA000', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Patient Retention
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Thoughtful responses show you care, encouraging patients to return.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF7F50" source="zocdoc_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Photographer Review Responses
const PhotographerReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/photographer-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Photographer Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your photography review responses with psychology-based guidance. Book more sessions and grow your photography business with authentic replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Photographer Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful photography business review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #424242 0%, #212121 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Camera size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Photography
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Photographer Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#424242" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Capture more bookings with authentic, thoughtful responses. Let your reputation
            shine as bright as your photos.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Photographers
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Camera size={32} style={{ color: '#424242', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Portfolio + Reviews
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Clients love your photos, but reviews tell them about the experience.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#424242', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Referral Power
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Thoughtful responses encourage clients to refer friends and family.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#424242', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Premium Pricing
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Great reviews justify premium rates. Show why you're worth every penny.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#607D8B" source="photographer_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Wedding Vendor Review Responses
const WeddingReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/wedding-vendor-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Wedding Vendor Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your wedding vendor review responses with psychology-based guidance. Book more weddings and grow your business with authentic replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Wedding Vendor Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful wedding vendor review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Wedding Industry
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Wedding Vendor Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#E91E63" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Say "I do" to more bookings with authentic, thoughtful responses. Build the
            reputation that makes couples choose you.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Wedding Vendors
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Heart size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Emotional Decisions
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Weddings are emotional. Couples choose vendors who show they care through reviews.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              The Knot & WeddingWire
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Top-rated vendors on wedding platforms get more inquiries and bookings.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#E91E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Premium Events
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Great reviews help you attract higher-budget weddings and premium clients.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#EC407A" source="wedding_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Pet Service Review Responses
const PetServiceReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/pet-service-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Pet Service Review Response Tool | Craft Your Replies"
        description="Craft your pet service review responses with psychology-based guidance. Build trust and grow your pet business with authentic replies."
        canonical="/pet-service-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Pet Service Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful pet service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #8D6E63 0%, #6D4C41 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Dog size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Pet Services
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Pet Service Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#8D6E63" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Fetch more customers with authentic, thoughtful responses. Show pet parents why
            their fur babies are in good hands.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Pet Businesses
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Dog size={32} style={{ color: '#8D6E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Pet Parent Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Pet owners are protective. Reviews help them trust you with their beloved pets.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#8D6E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Recurring Revenue
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Happy pet parents become regular customers. Reviews build that loyalty.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#8D6E63', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Word of Mouth
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Pet owners talk at dog parks. Great reviews become referral conversations.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#8BC34A" source="pet_service_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Daycare Review Responses
const DaycareReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/daycare-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Daycare Review Response Tool | Craft Your Replies"
        description="Craft your daycare review responses with psychology-based guidance. Build trust and enroll more families with authentic replies."
        canonical="/daycare-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Daycare Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful daycare and childcare review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #FF7043 0%, #E64A19 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Baby size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Childcare
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Daycare Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF7043" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Nurture your reputation with authentic, thoughtful responses. Help parents feel
            confident choosing your childcare center.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Daycares
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Baby size={32} style={{ color: '#FF7043', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Parent Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Childcare is parents' biggest decision. Reviews build the trust needed to enroll.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#FF7043', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Waitlist Demand
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Top-rated daycares have waitlists. Great reviews create that demand.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#FF7043', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Community Reputation
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Parents talk. Your online reputation becomes your local reputation.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FFEB3B" source="daycare_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Accountant Review Responses
const AccountantReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/accountant-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Accountant Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your accountant review responses with psychology-based guidance. Build trust and grow your CPA practice with authentic replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Accountant Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful accounting and CPA review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #37474F 0%, #263238 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Calculator size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Accounting
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Accountant Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#37474F" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Balance your books and your reputation with authentic, thoughtful responses. Build
            client trust during tax season and beyond.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Accountants
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Calculator size={32} style={{ color: '#37474F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Financial Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Clients share sensitive financial data. Reviews establish the trust needed.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#37474F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Tax Season Prep
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              New clients search for CPAs before tax season. Your reviews are waiting.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#37474F', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Business Referrals
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Business owners recommend accountants. Great reviews get you referred.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#3F51B5" source="accountant_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Insurance Agent Review Responses
const InsuranceReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/insurance-agent-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Insurance Agent Review Response Tool | Craft Your Replies | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your insurance agent review responses with psychology-based guidance. Build trust and write more policies with authentic replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Insurance Agent Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful insurance agent review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Shield size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Insurance
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Insurance Agent Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#1565C0" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Protect your reputation like you protect your clients. Craft authentic
            responses that build trust and close more policies.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Insurance Agents
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Shield size={32} style={{ color: '#1565C0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Trust is Everything
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Insurance is a trust business. Reviews prove you deliver on your promises.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#1565C0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Local Competition
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stand out from other local agents with superior online presence.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#1565C0', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Multi-Policy Clients
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Happy clients bundle policies. Great reviews encourage multi-policy business.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#00796B" source="insurance_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Senior Care Review Responses
const SeniorCareReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/senior-care-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Senior Care Review Response Tool | Craft Your Replies"
        description="Craft your senior care review responses with psychology-based guidance. Build trust with families through thoughtful, authentic replies."
        canonical="/senior-care-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Senior Care Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful senior care and assisted living review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <HeartHandshake size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Senior Care
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Senior Care Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#7B1FA2" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Show families the compassionate care you provide with authentic, thoughtful
            responses. Build trust during difficult decisions.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Senior Care
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <HeartHandshake size={32} style={{ color: '#7B1FA2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Family Decisions
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Choosing senior care is emotional. Reviews help families feel confident in their
              choice.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#7B1FA2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Occupancy Rates
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Higher ratings mean higher occupancy. Reviews directly impact your business.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#7B1FA2', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Community Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Healthcare professionals and social workers recommend facilities with great reviews.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#009688" source="senior_care_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// App Store Review Response Page
const AppStoreReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/appstore-app-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'App Store Review Response Tool | iOS & Android App Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your App Store and Google Play review responses with psychology-based guidance. Boost your app rating with thoughtful replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - App Store Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful iOS and Android app review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Smartphone size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              App Developers
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            App Store Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#007AFF" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Boost your app rating with authentic, thoughtful responses. Reply to iOS App Store
            and Google Play reviews in seconds.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} /> Save 5+ Hours/Week
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> iOS & Android
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps App Developers
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 122, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Smartphone size={24} style={{ color: '#007AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Multi-Platform Support
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Reply to reviews on iOS App Store, Google Play Store, and other app marketplaces from
              one place.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 122, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Star size={24} style={{ color: '#007AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Improve App Ratings
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Turn negative reviews into positive experiences. Professional responses can lead users
              to update their ratings.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 122, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Zap size={24} style={{ color: '#007AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Bug Report Handling
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft professional responses to bug reports and feature requests. Show users you
              care about their feedback.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#007AFF" source="appstore_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Indeed Company Review Response Page
const IndeedReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/indeed-company-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Indeed Company Review Response Tool | Employer Brand Management | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Indeed company review responses with psychology-based guidance. Manage your employer brand and attract top talent.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Indeed Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Indeed company review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #2164F3 0%, #1a4fc9 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Briefcase size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              HR & Recruiting
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Indeed Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#2164F3" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your employer brand with authentic, thoughtful responses. Turn employee
            feedback into recruitment opportunities.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} /> Save 5+ Hours/Week
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> Employer Branding
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps HR Teams
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(33, 100, 243, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Briefcase size={24} style={{ color: '#2164F3' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Employer Brand Management
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Respond professionally to employee reviews. Show candidates that you value feedback
              and care about your workplace culture.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(33, 100, 243, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Users size={24} style={{ color: '#2164F3' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Attract Top Talent
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Candidates read reviews before applying. Professional responses to negative feedback
              show you take employee concerns seriously.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(33, 100, 243, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <MessageSquare size={24} style={{ color: '#2164F3' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Consistent Messaging
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Maintain a consistent employer voice across all review platforms. AI ensures
              professional, on-brand responses every time.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#2164F3" source="indeed_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Zillow Realtor Review Response Page
const ZillowReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/zillow-realtor-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Zillow Review Response Tool | Realtor Review Management | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your Zillow review responses with psychology-based guidance. Real estate agents can build trust with thoughtful client replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Zillow Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful Zillow realtor review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #006AFF 0%, #0052cc 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Home size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Real Estate Agents
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Zillow Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#006AFF" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your realtor reputation with authentic, thoughtful responses. Turn client
            reviews into more listings and sales.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} /> Save 5+ Hours/Week
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> Realtor Focused
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Real Estate Agents
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 106, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Home size={24} style={{ color: '#006AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Zillow Profile Optimization
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Boost your Zillow rating with professional responses. Active engagement shows
              potential clients you care about their experience.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 106, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#006AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              More Listings & Sales
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Most home buyers read agent reviews before choosing a realtor —{' '}
              <a
                href="https://www.brightlocal.com/research/local-consumer-review-survey/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--gray-500)', fontSize: '14px' }}
              >
                BrightLocal
              </a>
              . Stand out with professional responses.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 106, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <MessageSquare size={24} style={{ color: '#006AFF' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Handle Difficult Reviews
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Real estate transactions can be emotional. Craft professional responses to
              challenging feedback in seconds.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#006AFF" source="zillow_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Therapy/Counselor Review Response Page
const TherapyReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/therapy-counselor-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Therapist Review Response Tool | Mental Health Practice Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your therapy practice review responses with psychology-based guidance. HIPAA-aware approach for psychologists, counselors, and therapists.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Therapist Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful therapy and counseling practice review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #7C3AED 0%, #5b21b6 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Mental Health Professionals
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Therapist Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#7C3AED" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build your therapy practice with authentic, thoughtful responses. Privacy-aware
            replies that respect client confidentiality.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} /> Save 5+ Hours/Week
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> Privacy-Aware
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Therapists
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Shield size={24} style={{ color: '#7C3AED' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Privacy-First Responses
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft responses that maintain client confidentiality. Never acknowledge treatment
              details or confirm client status.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Heart size={24} style={{ color: '#7C3AED' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Compassionate Tone
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI understands the sensitivity of mental health reviews. Responses are empathetic,
              professional, and appropriate for healthcare.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Users size={24} style={{ color: '#7C3AED' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Grow Your Practice
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              People seeking therapy often research providers online. Professional responses help
              build trust with potential clients.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#7B68EE" source="therapy_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Thumbtack Review Response Page
const ThumbtackReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/thumbtack-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Thumbtack Review Response Tool | Service Pro Reviews"
        description="Craft your Thumbtack review responses with psychology-based guidance. Service professionals can build trust and get more leads."
        canonical="/thumbtack-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Thumbtack Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful Thumbtack service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #009FD9 0%, #007ab3 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Hammer size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Service Professionals
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Thumbtack Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#009FD9" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Get more Thumbtack leads with authentic, thoughtful responses. Turn positive reviews
            into more bookings.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} /> Save 5+ Hours/Week
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={16} /> More Leads
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Thumbtack Pros
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 159, 217, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Hammer size={24} style={{ color: '#009FD9' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Built for Service Pros
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Whether you're a plumber, photographer, or personal trainer - craft responses that
              match your profession and expertise.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 159, 217, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#009FD9' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Increase Your Hire Rate
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Thumbtack customers check reviews before hiring. Professional responses show you're
              reliable and care about customer satisfaction.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 159, 217, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Zap size={24} style={{ color: '#009FD9' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Quick & Professional
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Respond to reviews between jobs without spending hours writing. Craft professional
              replies in seconds.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#009FD9" source="thumbtack_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Angi (Angie's List) Contractor Reviews
const AngiReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/angie-list-contractor-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Angi Review Response Tool | Contractor Review Replies"
        description="Craft your Angi (Angie's List) review responses with psychology-based guidance. Contractors can build trust and get more leads."
        canonical="/angie-list-contractor-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Angi Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful Angi contractor review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #00A651 0%, #007336 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Hammer size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Home Service Contractors
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Angi Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF6153" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Win more jobs on Angi with authentic, thoughtful review responses. Build your
            contractor reputation and get more leads.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Award size={16} /> Top Pro Status
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={16} /> More Leads
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Angi Contractors
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 166, 81, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Hammer size={24} style={{ color: '#00A651' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Built for Contractors
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Plumbers, electricians, roofers, landscapers - craft responses that match your trade
              and expertise.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 166, 81, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Award size={24} style={{ color: '#00A651' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Boost Your Rating
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responding to reviews shows professionalism and can help you achieve Top Pro status on
              Angi.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 166, 81, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Clock size={24} style={{ color: '#00A651' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>Save Time</h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stop spending hours writing review responses. Craft professional replies between
              jobs in seconds.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF6153" source="angi_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Chiropractor Reviews
const ChiropractorReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/chiropractor-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Chiropractor Review Response Tool | Chiropractic Practice Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your chiropractor review responses with psychology-based guidance. Grow your chiropractic practice with thoughtful patient replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Chiropractor Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful chiropractic practice review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #5C6BC0 0%, #3949AB 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Activity size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Chiropractic Practices
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Chiropractor Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#5C6BC0" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build patient trust with authentic, thoughtful review responses. Grow your
            chiropractic practice with better online reputation.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={16} /> HIPAA Aware
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={16} /> More Patients
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Chiropractors
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(92, 107, 192, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Activity size={24} style={{ color: '#5C6BC0' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Healthcare Focused
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI understands chiropractic care terminology and patient sensitivity. Responses are
              professional and HIPAA-aware.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(92, 107, 192, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#5C6BC0' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Attract New Patients
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Nearly 90% of patients read online reviews before choosing a healthcare provider —{' '}
              <a href="https://www.brightlocal.com/research/local-consumer-review-survey/" target="_blank" rel="noopener noreferrer" style={{ color: '#5C6BC0' }}>BrightLocal</a>.
              Professional responses build trust.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(92, 107, 192, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Shield size={24} style={{ color: '#5C6BC0' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Handle Negative Reviews
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Turn negative feedback into positive outcomes with thoughtful, professional responses.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#388E3C" source="chiropractor_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Barber/Barbershop Reviews
const BarberReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/barber-barbershop-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Barbershop Review Response Tool | Barber Shop Reviews"
        description="Craft your barbershop review responses with psychology-based guidance. Build client loyalty with thoughtful replies."
        canonical="/barber-barbershop-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Barbershop Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful barbershop review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Scissors size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Barbershops & Barbers
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Barbershop Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#D32F2F" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Keep your chair full with authentic, thoughtful review responses. Build loyalty and
            attract new clients to your barbershop.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Scissors size={16} /> Built for Barbers
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={16} /> More Clients
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Barbers
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(211, 47, 47, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Scissors size={24} style={{ color: '#D32F2F' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Built for Barbershops
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI understands barbershop culture and terminology. Responses sound authentic, not
              robotic.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(211, 47, 47, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Users size={24} style={{ color: '#D32F2F' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Build Client Loyalty
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Thank regulars for their reviews and turn first-timers into repeat clients with
              thoughtful responses.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(211, 47, 47, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Clock size={24} style={{ color: '#D32F2F' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>Save Time</h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Respond between cuts instead of after closing. Craft professional replies in
              seconds.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#5D4037" source="barber_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Dermatologist Reviews
const DermatologistReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/dermatologist-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Dermatologist Review Response Tool | Dermatology Practice Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your dermatologist review responses with psychology-based guidance. Grow your dermatology practice with thoughtful patient replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Dermatologist Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful dermatology practice review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #26A69A 0%, #00897B 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Sparkles size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Dermatology Practices
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Dermatologist Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#26A69A" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Build patient confidence with authentic, thoughtful review responses. Grow your
            dermatology practice with better online reputation.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={16} /> HIPAA Aware
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={16} /> More Patients
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Dermatologists
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(38, 166, 154, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Sparkles size={24} style={{ color: '#26A69A' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Medical & Cosmetic
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI handles both medical dermatology and cosmetic procedure reviews with appropriate
              professional tone.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(38, 166, 154, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Shield size={24} style={{ color: '#26A69A' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              HIPAA Compliant
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Responses never reveal patient information. Professional, private, and compliant with
              healthcare regulations.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(38, 166, 154, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#26A69A' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Build Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Patients research extensively before choosing a dermatologist. Professional responses
              establish credibility.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#1E88E5" source="dermatologist_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Massage Therapist Reviews
const MassageTherapistReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/massage-therapist-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Massage Therapist Review Response Tool | Spa & Massage Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your massage therapist review responses with psychology-based guidance. Grow your practice with thoughtful client replies.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Massage Therapist Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful massage therapy review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #8E24AA 0%, #6A1B9A 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Heart size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Massage & Wellness
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Massage Therapist Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#8E24AA" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Keep your schedule full with authentic, thoughtful review responses. Build client
            relationships and attract new bookings.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={16} /> Warm & Personal
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={16} /> More Bookings
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Massage Therapists
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(142, 36, 170, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Heart size={24} style={{ color: '#8E24AA' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Warm & Personal
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft responses that feel genuine and caring - matching the personal nature of
              massage therapy.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(142, 36, 170, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Users size={24} style={{ color: '#8E24AA' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Client Retention
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Personal thank you responses turn one-time clients into regulars. Build lasting
              relationships.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(142, 36, 170, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#8E24AA' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Grow Your Practice
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              New clients check reviews before booking. Professional responses build trust and
              attract bookings.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#9C27B0" source="massage_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Personal Trainer Reviews
const PersonalTrainerReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/personal-trainer-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    document.title =
      'Personal Trainer Review Response Tool | Fitness Trainer Reviews | ReviewResponder';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Craft your personal trainer review responses with psychology-based guidance. Build client trust and grow your fitness business.'
      );
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ReviewResponder - Personal Trainer Review Response Tool',
      description: 'Psychology-based tool to craft thoughtful personal trainer and fitness review responses',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [location.search]);
  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #FF5722 0%, #E64A19 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Zap size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Personal Training & Fitness
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Personal Trainer Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF5722" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Fill your training schedule with authentic, thoughtful review responses. Build your
            fitness brand and attract new clients.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={16} /> Fitness Focused
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={16} /> More Clients
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Personal Trainers
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 87, 34, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Zap size={24} style={{ color: '#FF5722' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Fitness Industry Focus
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              AI understands personal training terminology and client transformation stories.
              Responses celebrate achievements.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 87, 34, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Users size={24} style={{ color: '#FF5722' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Build Your Brand
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Professional responses showcase your expertise. Turn happy clients into brand
              ambassadors.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 87, 34, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#FF5722' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Attract New Clients
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              People read reviews before hiring a personal trainer. Stand out with thoughtful, personal
              responses.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF5722" source="personal_trainer_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Angi (formerly Angie's List) Contractor Review Page
const AngiListReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/angie-list-contractor-reviews',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Angi Review Response Tool | Contractor Reviews"
        description="Craft your Angi (Angie's List) review responses with psychology-based guidance. Build trust and win more home service jobs."
        canonical="/angie-list-contractor-reviews"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Angi Contractor Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful Angi and home service review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #FF6F00 0%, #E65100 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Sparkles size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Home Service Contractors
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Angi Review Response Tool
          </h1>

          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#FF6153" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Win more home service jobs with authentic, thoughtful review responses. Stand out
            from competitors on Angi (formerly Angie's List).
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Wrench size={16} /> All Trades
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={16} /> More Jobs
            </span>
          </div>
        </div>
      </section>
      <section className="container" style={{ padding: '80px 0' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', marginBottom: '48px' }}
        >
          How ReviewResponder Helps Contractors
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '64px',
          }}
        >
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 111, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Sparkles size={24} style={{ color: '#FF6F00' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Professional Tone
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Craft responses that show your expertise and professionalism - key factors for
              homeowners choosing contractors.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 111, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <Clock size={24} style={{ color: '#FF6F00' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>Save Time</h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Stop spending evenings writing review responses. Craft professional replies in
              seconds between jobs.
            </p>
          </div>
          <div className="card">
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(255, 111, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              <TrendingUp size={24} style={{ color: '#FF6F00' }} />
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Win More Bids
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Homeowners compare contractors by reviews. Active, professional responses set you
              apart from competition.
            </p>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%)',
            borderRadius: '24px',
          }}
        >
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#FF6153" source="angilist_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Extension Page - Redirects to Chrome Web Store
const ExtensionPage = () => {
  useEffect(() => {
    // Redirect to Chrome Web Store
    window.location.href = 'https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae';
  }, []);

  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <Chrome size={48} style={{ color: 'var(--primary-600)', marginBottom: '16px' }} />
      <h1 style={{ fontSize: '24px', marginBottom: '16px' }}>Redirecting to Chrome Web Store...</h1>
      <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
        If you're not redirected automatically,{' '}
        <a
          href="https://chrome.google.com/webstore/detail/reviewresponder/fbjciclgikjilkiogfjjkfnogjlhndae"
          style={{ color: 'var(--primary-600)' }}
        >
          click here
        </a>
      </p>
    </div>
  );
};

// Pricing Page
const PricingPage = () => {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  // Translation state for testimonials
  const [translatedIds, setTranslatedIds] = useState(new Set());
  const [translatingIds, setTranslatingIds] = useState(new Set());
  const [translationCache, setTranslationCache] = useState({});

  // Toggle translation for a testimonial
  const toggleTranslation = async (index, testimonialId, hasExistingTranslation) => {
    if (translatedIds.has(index)) {
      // Switch back to original
      setTranslatedIds(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    } else {
      // Show translation
      if (hasExistingTranslation || translationCache[index]) {
        setTranslatedIds(prev => new Set(prev).add(index));
      } else {
        // Need to fetch translation
        setTranslatingIds(prev => new Set(prev).add(index));
        try {
          const response = await api.post(`/testimonials/${testimonialId}/translate`);
          setTranslationCache(prev => ({ ...prev, [index]: response.data.comment_en }));
          setTranslatedIds(prev => new Set(prev).add(index));
        } catch (err) {
          console.error('Translation failed:', err);
        } finally {
          setTranslatingIds(prev => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
        }
      }
    }
  };

  useEffect(() => {
    // Fetch testimonials
    api
      .get('/testimonials')
      .then(res => setTestimonials(res.data.testimonials || []))
      .catch(err => console.warn('[Testimonials] Failed:', err.message));
  }, []);

  const openBillingPortal = async () => {
    try {
      const res = await api.post('/billing/portal');
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to open billing portal');
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async credentialResponse => {
    try {
      const res = await axios.post(`${API_URL}/auth/google`, {
        credential: credentialResponse.credential,
      });
      if (res.data.token && res.data.user) {
        localStorage.setItem('token', res.data.token);
        setUser(res.data.user);
        toast.success('✓ Logged in! Taking you to Generator...');
        setTimeout(() => navigate('/generator'), 800);
      }
    } catch (err) {
      toast.error('Google Sign In failed. Please try again.');
    }
  };

  // FAQ data
  const faqs = [
    {
      q: 'Can I switch plans anytime?',
      a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and we prorate the billing.',
    },
    {
      q: 'What happens if I exceed my response limit?',
      a: "You'll receive a notification when you reach 80% of your limit. After reaching your limit, you can upgrade or wait for the next billing cycle.",
    },
    {
      q: 'Is there a free trial?',
      a: 'Yes! The Free plan gives you 20 responses per month forever - no credit card required. Try it out and upgrade when ready.',
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept credit cards (Visa, Mastercard, Amex), PayPal, Apple Pay, Google Pay, and Link via our secure Stripe payment processor.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Absolutely! No contracts, no hidden fees. Cancel with one click from your dashboard. Plus, we offer a 30-day money-back guarantee.',
    },
    {
      q: 'Do you offer refunds?',
      a: "Yes! We have a 30-day money-back guarantee. If you're not satisfied, contact us for a full refund.",
    },
  ];

  // Feature comparison data
  const features = [
    { name: 'Responses', free: '20', starter: '300', pro: '800', unlimited: 'Unlimited' },
    { name: 'Chrome Extension', free: true, starter: true, pro: true, unlimited: true },
    { name: 'All Tone Options', free: true, starter: true, pro: true, unlimited: true },
    { name: 'Auto-Detect Language', free: true, starter: true, pro: true, unlimited: true },
    { name: 'Response History', free: false, starter: true, pro: true, unlimited: true },
    { name: 'CSV/PDF Export', free: false, starter: true, pro: true, unlimited: true },
    { name: 'Bulk Generation (20)', free: false, starter: false, pro: true, unlimited: true },
    { name: 'Analytics Dashboard', free: false, starter: false, pro: true, unlimited: true },
    { name: 'Team Members', free: '-', starter: '-', pro: '3', unlimited: '10' },
    { name: 'API Access', free: false, starter: false, pro: false, unlimited: true },
    { name: 'Priority Support', free: false, starter: false, pro: true, unlimited: true },
    { name: 'Priority Feature Requests', free: false, starter: false, pro: false, unlimited: true },
  ];

  return (
    <div style={{ paddingBottom: '100px' }}>
      <SEO
        title="Pricing - Simple, Transparent Plans"
        description="Choose the plan that fits your business. Free tier with 20 responses/month, Starter for growing businesses, Pro for power users, Unlimited for agencies."
        canonical="/pricing"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder',
          description: 'AI-powered review response generator for businesses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: [
            { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Starter', price: '9', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Pro', price: '29', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Unlimited', price: '49', priceCurrency: 'USD' },
          ],
        }}
      />
      {/* Hero Section */}
      <div className="pricing-section">
        <div className="container">
          <div className="pricing-header">
            <h1 className="pricing-title">Simple, Transparent Pricing</h1>
            <p style={{ color: 'var(--gray-600)', maxWidth: '600px', margin: '0 auto' }}>
              No hidden fees. No surprises. Choose the plan that fits your business.
            </p>
          </div>

          <PricingCards />

          {user && user.plan !== 'free' && (
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <button onClick={openBillingPortal} className="btn btn-secondary">
                Manage Subscription
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Trust Badges */}
      <div className="container" style={{ marginTop: '40px', marginBottom: '60px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '32px',
            alignItems: 'center',
            padding: '24px',
            background: 'var(--gray-50)',
            borderRadius: '12px',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)' }}
          >
            <Shield size={20} style={{ color: 'var(--success)' }} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>SSL Secured</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)' }}
          >
            <CreditCard size={20} style={{ color: 'var(--primary-600)' }} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Powered by Stripe</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)' }}
          >
            <RefreshCw size={20} style={{ color: 'var(--warning)' }} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>30-Day Money Back</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)' }}
          >
            <Check size={20} style={{ color: 'var(--success)' }} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Cancel Anytime</span>
          </div>
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="container" style={{ marginBottom: '60px' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '28px', fontWeight: '700', marginBottom: '32px' }}
        >
          Compare All Features
        </h2>
        <div className="table-scroll-container">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                <th
                  style={{
                    padding: '16px',
                    paddingTop: '32px',
                    textAlign: 'left',
                    fontWeight: '600',
                    verticalAlign: 'bottom',
                  }}
                >
                  Feature
                </th>
                <th
                  style={{
                    padding: '16px',
                    paddingTop: '32px',
                    textAlign: 'center',
                    fontWeight: '600',
                    verticalAlign: 'bottom',
                  }}
                >
                  Free
                </th>
                <th
                  style={{
                    padding: '16px',
                    paddingTop: '32px',
                    textAlign: 'center',
                    fontWeight: '600',
                    verticalAlign: 'bottom',
                  }}
                >
                  Starter
                </th>
                <th
                  style={{
                    padding: '16px',
                    paddingTop: '32px',
                    textAlign: 'center',
                    fontWeight: '600',
                    background: 'var(--primary-50)',
                    verticalAlign: 'bottom',
                  }}
                >
                  Pro
                </th>
                <th
                  style={{
                    padding: '16px',
                    paddingTop: '32px',
                    textAlign: 'center',
                    fontWeight: '600',
                    verticalAlign: 'bottom',
                  }}
                >
                  Unlimited
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '14px 16px', color: 'var(--gray-700)' }}>{feature.name}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    {typeof feature.free === 'boolean' ? (
                      feature.free ? (
                        <Check size={18} style={{ color: 'var(--success)' }} />
                      ) : (
                        <X size={18} style={{ color: 'var(--gray-300)' }} />
                      )
                    ) : (
                      <span style={{ fontWeight: '500' }}>{feature.free}</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    {typeof feature.starter === 'boolean' ? (
                      feature.starter ? (
                        <Check size={18} style={{ color: 'var(--success)' }} />
                      ) : (
                        <X size={18} style={{ color: 'var(--gray-300)' }} />
                      )
                    ) : (
                      <span style={{ fontWeight: '500' }}>{feature.starter}</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '14px 16px',
                      textAlign: 'center',
                      background: 'var(--primary-50)',
                    }}
                  >
                    {typeof feature.pro === 'boolean' ? (
                      feature.pro ? (
                        <Check size={18} style={{ color: 'var(--success)' }} />
                      ) : (
                        <X size={18} style={{ color: 'var(--gray-300)' }} />
                      )
                    ) : (
                      <span style={{ fontWeight: '500' }}>{feature.pro}</span>
                    )}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    {typeof feature.unlimited === 'boolean' ? (
                      feature.unlimited ? (
                        <Check size={18} style={{ color: 'var(--success)' }} />
                      ) : (
                        <X size={18} style={{ color: 'var(--gray-300)' }} />
                      )
                    ) : (
                      <span style={{ fontWeight: '500' }}>{feature.unlimited}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <div className="container" style={{ marginBottom: '60px' }}>
          <h2
            style={{
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '32px',
              color: 'var(--gray-700)',
            }}
          >
            See what our customers have to say
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              maxWidth: '900px',
              margin: '0 auto',
            }}
          >
            {testimonials.slice(0, 3).map((t, idx) => {
              const isTranslated = translatedIds.has(idx);
              const isTranslating = translatingIds.has(idx);
              const hasExistingTranslation = !!(t.comment_en || translationCache[idx]);
              const displayComment = isTranslated
                ? (t.comment_en || translationCache[idx] || t.comment)
                : t.comment;

              return (
              <div key={idx} className="card" style={{ padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s}
                        size={16}
                        fill={s <= t.rating ? '#f59e0b' : 'none'}
                        color={s <= t.rating ? '#f59e0b' : '#d1d5db'}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => toggleTranslation(idx, t.id, hasExistingTranslation)}
                    disabled={isTranslating}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      color: isTranslating ? 'var(--gray-400)' : (isTranslated ? 'var(--primary-600)' : 'var(--gray-500)'),
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isTranslating ? 'wait' : 'pointer',
                    }}
                  >
                    {isTranslating ? (
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Globe size={12} />
                    )}
                    {isTranslating ? '...' : (isTranslated ? 'Original' : 'English')}
                  </button>
                </div>
                {t.comment && (
                  <p
                    style={{
                      color: 'var(--gray-700)',
                      fontStyle: 'italic',
                      marginBottom: '16px',
                      lineHeight: '1.6',
                    }}
                  >
                    "{displayComment}"
                  </p>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'var(--primary-100)',
                      color: 'var(--primary-600)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  >
                    {(t.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {t.user_name || 'Verified User'}
                  </span>
                </div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="container" style={{ marginBottom: '60px' }}>
        <h2
          style={{ textAlign: 'center', fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}
        >
          Frequently Asked Questions
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray-600)', marginBottom: '32px' }}>
          Got questions? We've got answers.
        </p>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="card"
              style={{
                marginBottom: '12px',
                padding: '0',
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
            >
              <div
                style={{
                  padding: '18px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '500',
                }}
              >
                <span>{faq.q}</span>
                <ChevronRight
                  size={20}
                  style={{
                    color: 'var(--gray-400)',
                    transform: expandedFaq === idx ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </div>
              {expandedFaq === idx && (
                <div
                  style={{
                    padding: '0 20px 18px',
                    color: 'var(--gray-600)',
                    lineHeight: '1.6',
                    borderTop: '1px solid var(--gray-100)',
                    paddingTop: '16px',
                  }}
                >
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="container">
        <div
          className="card"
          style={{
            background: 'linear-gradient(135deg, var(--primary-600), var(--primary-700))',
            textAlign: 'center',
            padding: '48px 24px',
          }}
        >
          <h2 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '24px' }}>
            Ready to Craft Psychology-Based Review Responses?
          </h2>
          {user ? (
            <Link
              to="/generator"
              className="btn"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--primary-600)',
                fontWeight: '600',
                padding: '14px 32px',
                fontSize: '16px',
              }}
            >
              Go to Generator →
            </Link>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSignIn}
                onError={() => toast.error('Google Sign In failed')}
                text="signup_with"
                shape="rectangular"
                size="large"
              />
              <Link
                to="/register"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '13px',
                  textDecoration: 'none',
                }}
              >
                or continue with email
              </Link>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                20/month free
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--gray-200)',
          padding: '12px 16px',
          display: 'none',
          zIndex: 1000,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
        }}
        className="mobile-sticky-cta"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>Start Free</div>
            <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>20 responses/month</div>
          </div>
          <Link
            to="/register"
            className="btn btn-primary"
            style={{ padding: '10px 24px', fontSize: '14px' }}
          >
            Sign Up
          </Link>
        </div>
      </div>

      <Footer />

      <style>
        {`
          @media (max-width: 768px) {
            .mobile-sticky-cta {
              display: block !important;
            }
          }
        `}
      </style>
    </div>
  );
};

// Claim Discount Page - Personalized landing for discount links
const ClaimDiscountPage = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  // Static discount info for fallback (direct codes like EARLY50)
  // expiryHours: custom expiry time (default 48h if not specified)
  const staticDiscountInfo = {
    EARLY50: { percent: 50, label: 'Early Access', duration: '12 months' },
    HUNTLAUNCH: { percent: 60, label: 'Product Hunt Special', duration: '12 months' },
    DEMOFOLLOWUP: { percent: 30, label: 'Demo Special', duration: '3 months' },
    SAVE20: { percent: 20, label: 'Special Offer', duration: '12 months' },
    FLASH50: { percent: 50, label: 'Flash Sale', duration: 'first month', expiryHours: 2 },
    COMEBACK20: { percent: 20, label: 'Welcome Back', duration: 'first month', expiryHours: 24 },
    HOTLEAD40: { percent: 40, label: 'Special Offer', duration: 'first month', expiryHours: 48 },
  };

  // Check if code is a token (8 chars) or a discount code
  const isToken = code && code.length === 8 && !staticDiscountInfo[code.toUpperCase()];

  useEffect(() => {
    const fetchLinkData = async () => {
      if (isToken) {
        // Fetch personalized link data from backend
        try {
          const res = await api.get(`/discount-links/${code}`);
          setLinkData(res.data);
          setIsExpired(res.data.isExpired);
        } catch (error) {
          setLinkData({ valid: false });
        }
      } else {
        // Use static discount info
        const upperCode = code?.toUpperCase();
        const staticDiscount = staticDiscountInfo[upperCode];
        if (staticDiscount) {
          // Get or set expiry from localStorage
          const storageKey = `discount_expiry_${upperCode}`;
          let expiresAt = localStorage.getItem(storageKey);
          if (!expiresAt) {
            // Use custom expiryHours from discount config, default to 48h
            const hours = staticDiscount.expiryHours || 48;
            expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
            localStorage.setItem(storageKey, expiresAt);
          }
          setLinkData({
            valid: true,
            discountCode: upperCode,
            discount: staticDiscount,
            expiresAt,
            isExpired: new Date(expiresAt) < new Date(),
          });
        } else {
          setLinkData({ valid: false });
        }
      }
      setLoading(false);
    };

    fetchLinkData();
  }, [code, isToken]);

  // Timer effect
  useEffect(() => {
    if (!linkData?.expiresAt || linkData.isExpired) return;

    const updateTimer = () => {
      const now = Date.now();
      const expiryTime = new Date(linkData.expiresAt).getTime();
      const diff = expiryTime - now;

      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [linkData]);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-color)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  // Invalid link
  if (!linkData?.valid) {
    return (
      <div
        style={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔗</div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '12px',
              color: 'var(--text-primary)',
            }}
          >
            Link Not Found
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
            This discount link is not valid or has expired.
          </p>
          <Link
            to="/pricing"
            style={{
              display: 'inline-block',
              padding: '14px 32px',
              background: 'var(--primary)',
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
            }}
          >
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  const { discount, discountCode, recipientName, businessName, source } = linkData;
  const firstName = recipientName?.split(' ')[0];

  // Calculate yearly savings (based on Starter plan $29/mo)
  const yearlySavings = Math.round((29 * 12 * discount.percent) / 100);

  // Source-aware headline
  const getHeadline = () => {
    if (source === 'demo') {
      return firstName ? `${firstName}, loved the demo?` : 'Loved the demo?';
    }
    if (source === 'outreach') {
      return firstName ? `${firstName}, managing reviews is hard` : 'Managing reviews is hard';
    }
    if (source === 'drip') {
      return firstName ? `${firstName}, ready to save time?` : 'Ready to save time?';
    }
    return firstName ? `${firstName}, this one's for you` : 'A special offer for you';
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        paddingTop: '40px',
        paddingBottom: '80px',
      }}
    >
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '0 24px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Link
            to="/"
            style={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <MessageSquare size={28} style={{ color: 'var(--primary)' }} />
            <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
              ReviewResponder
            </span>
          </Link>
        </div>

        {/* Main Card */}
        <div
          style={{
            background: 'var(--card-bg)',
            borderRadius: '16px',
            border: '1px solid var(--border-color)',
            padding: '40px 32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'var(--primary)',
              color: 'white',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              marginBottom: '24px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {discount.label}
          </div>

          {/* Headline - Source-aware */}
          <h1
            style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              lineHeight: '1.3',
            }}
          >
            {getHeadline()}
          </h1>

          {/* Discount Display */}
          <div
            style={{
              display: 'inline-block',
              padding: '20px 40px',
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
              borderRadius: '12px',
              marginBottom: '24px',
            }}
          >
            <span
              style={{
                fontSize: '56px',
                fontWeight: '800',
                color: 'white',
                lineHeight: '1',
              }}
            >
              {discount.percent}%
            </span>
            <span
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.9)',
                marginLeft: '8px',
                textTransform: 'uppercase',
              }}
            >
              OFF
            </span>
            <p
              style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.8)',
                margin: '8px 0 0 0',
              }}
            >
              for {discount.duration}
            </p>
          </div>

          {/* Business name */}
          {businessName && (
            <div
              style={{
                padding: '12px 20px',
                background: 'var(--bg-tertiary)',
                borderRadius: '8px',
                marginBottom: '24px',
                display: 'inline-block',
              }}
            >
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 4px 0' }}>
                Reserved for
              </p>
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                }}
              >
                {businessName}
              </p>
            </div>
          )}

          {/* Value proposition with savings */}
          <p
            style={{
              fontSize: '16px',
              color: 'var(--text-secondary)',
              marginBottom: '32px',
              lineHeight: '1.6',
            }}
          >
            {businessName
              ? `Respond to ${businessName}'s reviews in seconds, not hours.`
              : 'Respond to reviews in seconds, not hours.'}{' '}
            <strong style={{ color: 'var(--primary)' }}>Save ${yearlySavings}/year</strong> on your
            subscription.
          </p>

          {/* Timer */}
          {!isExpired ? (
            <div style={{ marginBottom: '32px' }}>
              <p
                style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: 'var(--text-muted)',
                  marginBottom: '12px',
                }}
              >
                Offer expires in
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                {[
                  { value: timeLeft.hours, label: 'hrs' },
                  { value: timeLeft.minutes, label: 'min' },
                  { value: timeLeft.seconds, label: 'sec' },
                ].map((item, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '60px',
                        height: '60px',
                        background: 'var(--bg-tertiary)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: '700',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: 'var(--text-muted)',
                        marginTop: '6px',
                      }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                marginBottom: '32px',
                padding: '12px 20px',
                background: 'var(--error-bg)',
                borderRadius: '8px',
                color: 'var(--error-text)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Clock size={18} />
              <span style={{ fontWeight: '500' }}>This offer has expired</span>
            </div>
          )}

          {/* CTA Button */}
          <Link
            to={isExpired ? '/pricing' : `/pricing?discount=${discountCode}`}
            className="btn btn-primary"
            style={{
              display: 'block',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '10px',
              textDecoration: 'none',
              background: isExpired ? 'var(--gray-400)' : 'var(--primary)',
              marginBottom: '24px',
            }}
          >
            {isExpired ? 'View Regular Pricing' : `Claim ${discount.percent}% Off Now`}
          </Link>

          {/* Discount code */}
          <div
            style={{
              padding: '16px 24px',
              background: 'var(--bg-tertiary)',
              borderRadius: '8px',
              border: '1px dashed var(--border-color)',
            }}
          >
            <p
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500',
              }}
            >
              Your discount code
            </p>
            <p
              style={{
                fontSize: '20px',
                fontWeight: '700',
                fontFamily: 'monospace',
                color: 'var(--primary)',
                margin: 0,
                letterSpacing: '2px',
              }}
            >
              {discountCode}
            </p>
          </div>
        </div>

        {/* Trust indicators below card */}
        <div
          style={{
            marginTop: '32px',
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          {[
            { icon: <Zap size={16} />, text: 'Instant responses' },
            { icon: <Globe size={16} />, text: 'Any language' },
            { icon: <Shield size={16} />, text: 'Cancel anytime' },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-muted)',
                fontSize: '13px',
              }}
            >
              {item.icon}
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Analytics Page (Pro/Unlimited Only)
const AnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for charts
  const TONE_COLORS = {
    professional: '#4F46E5',
    friendly: '#10B981',
    formal: '#6366F1',
    apologetic: '#F59E0B',
  };

  const PLATFORM_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get('/analytics');
      setAnalytics(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('upgrade');
      } else {
        setError('Failed to load analytics');
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  // Upgrade required view
  if (error === 'upgrade') {
    return (
      <div className="container" style={{ marginTop: '40px', textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: '600px', margin: '0 auto', padding: '60px 40px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, var(--primary-100), var(--primary-200))',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <BarChart2 size={40} style={{ color: 'var(--primary-600)' }} />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>
            Analytics Dashboard
          </h1>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px', fontSize: '16px' }}>
            Get detailed insights into your review response patterns with beautiful charts and
            statistics.
          </p>

          <div
            style={{
              background: 'var(--gray-50)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              textAlign: 'left',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
              What you'll get:
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Total responses over time (line chart)',
                'Responses by tone distribution (pie chart)',
                'Platform breakdown',
                'Weekly comparison insights',
                'Average responses per day',
              ].map((feature, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                    color: 'var(--gray-700)',
                  }}
                >
                  <Check size={18} style={{ color: 'var(--secondary)' }} />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div
            style={{
              background: 'linear-gradient(135deg, var(--primary-50), var(--primary-100))',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <p style={{ fontSize: '14px', color: 'var(--primary-700)' }}>
              <Lock size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              Available on <strong>Pro</strong> and <strong>Unlimited</strong> plans
            </p>
          </div>

          <Link to="/pricing" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
            Upgrade to Unlock Analytics
          </Link>
          <Link
            to="/dashboard"
            style={{ display: 'block', marginTop: '16px', color: 'var(--gray-500)' }}
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="container" style={{ marginTop: '40px' }}>
        <div className="loading">
          <div className="spinner"></div>
          <p style={{ marginTop: '16px', color: 'var(--gray-500)' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && error !== 'upgrade') {
    return (
      <div className="container" style={{ marginTop: '40px', textAlign: 'center' }}>
        <AlertCircle size={48} style={{ color: 'var(--danger)' }} />
        <h2 style={{ marginTop: '16px' }}>Failed to load analytics</h2>
        <button onClick={fetchAnalytics} className="btn btn-primary" style={{ marginTop: '16px' }}>
          Try Again
        </button>
      </div>
    );
  }

  // Format date for display
  const formatDate = dateStr => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Capitalize first letter
  const capitalize = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  return (
    <div className="container" style={{ marginTop: '40px', marginBottom: '60px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>
            <BarChart2
              size={28}
              style={{ verticalAlign: 'middle', marginRight: '12px', color: 'var(--primary-600)' }}
            />
            Analytics Dashboard
          </h1>
          <p style={{ color: 'var(--gray-500)' }}>
            Track your review response patterns and insights
          </p>
        </div>
        <Link to="/dashboard" className="btn btn-secondary">
          ← Back to Dashboard
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid" style={{ marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-label">Total Responses</div>
          <div className="stat-value primary">{analytics?.totalResponses || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">This Week</div>
          <div className="stat-value">{analytics?.insights?.thisWeek || 0}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '8px',
              color: analytics?.insights?.weeklyChange >= 0 ? 'var(--secondary)' : 'var(--danger)',
              fontSize: '13px',
            }}
          >
            {analytics?.insights?.weeklyChange >= 0 ? (
              <TrendingUp size={16} />
            ) : (
              <TrendingDown size={16} />
            )}
            {Math.abs(analytics?.insights?.weeklyChange || 0)} vs last week
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg per Day</div>
          <div className="stat-value">{analytics?.insights?.avgPerDay || '0'}</div>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '8px' }}>
            Last 30 days
          </p>
        </div>
        <div className="stat-card">
          <div className="stat-label">Most Used Tone</div>
          <div className="stat-value" style={{ fontSize: '20px', textTransform: 'capitalize' }}>
            {analytics?.insights?.mostUsedTone || 'N/A'}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="analytics-charts-grid" style={{ marginBottom: '32px' }}>
        {/* Responses Over Time */}
        <div className="card">
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <TrendingUp size={20} style={{ color: 'var(--primary-600)' }} />
            Responses Over Time
          </h3>
          {analytics?.overTime?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.overTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip
                  labelFormatter={formatDate}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
                <Line
                  type="monotone"
                  dataKey="responses"
                  stroke="#4F46E5"
                  strokeWidth={2}
                  dot={{ fill: '#4F46E5', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '250px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-400)',
              }}
            >
              No data available yet
            </div>
          )}
        </div>

        {/* Responses by Tone */}
        <div className="card">
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <PieChart size={20} style={{ color: 'var(--primary-600)' }} />
            Responses by Tone
          </h3>
          {analytics?.byTone?.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ResponsiveContainer width="60%" height={250}>
                <RechartsPieChart>
                  <Pie
                    data={analytics.byTone}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {analytics.byTone.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          TONE_COLORS[entry.name] || PLATFORM_COLORS[index % PLATFORM_COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                </RechartsPieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {analytics.byTone.map((entry, index) => (
                  <div
                    key={entry.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '3px',
                        background:
                          TONE_COLORS[entry.name] ||
                          PLATFORM_COLORS[index % PLATFORM_COLORS.length],
                      }}
                    />
                    <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                      {entry.name}
                    </span>
                    <span
                      style={{ fontSize: '14px', color: 'var(--gray-500)', marginLeft: 'auto' }}
                    >
                      {entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                height: '250px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-400)',
              }}
            >
              No data available yet
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="analytics-charts-grid">
        {/* Responses by Platform */}
        <div className="card">
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Globe size={20} style={{ color: 'var(--primary-600)' }} />
            Responses by Platform
          </h3>
          {analytics?.byPlatform?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.byPlatform} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={capitalize}
                  width={80}
                />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Bar dataKey="value" fill="#4F46E5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-400)',
              }}
            >
              No data available yet
            </div>
          )}
        </div>

        {/* Responses by Rating */}
        <div className="card">
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Star size={20} style={{ color: 'var(--primary-600)' }} />
            Responses by Review Rating
          </h3>
          {analytics?.byRating?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.byRating.sort((a, b) => a.rating - b.rating)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="rating"
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={v => `${v} ★`}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip
                  labelFormatter={v => `${v} Star Reviews`}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                />
                <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-400)',
              }}
            >
              No rating data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Affiliate Landing Page (Public)
const AffiliateLandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [affiliateStatus, setAffiliateStatus] = useState(null);
  const [formData, setFormData] = useState({
    website: '',
    marketingChannels: '',
    audienceSize: '',
    payoutMethod: 'paypal',
    payoutEmail: '',
  });

  useEffect(() => {
    if (user) {
      api
        .get('/affiliate/stats')
        .then(res => {
          if (res.data.isAffiliate) {
            setAffiliateStatus(res.data);
          }
        })
        .catch(err => console.warn('[AffiliateStats] Failed:', err.message));
    }
  }, [user]);

  const handleApply = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/affiliate/apply', formData);
      toast.success(res.data.message);
      fireConfetti();
      navigate('/affiliate/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to apply');
    } finally {
      setLoading(false);
    }
  };

  if (affiliateStatus?.isAffiliate) {
    return (
      <div
        className="container"
        style={{ paddingTop: '60px', paddingBottom: '60px', textAlign: 'center' }}
      >
        <div className="card" style={{ maxWidth: '600px', margin: '0 auto', padding: '48px' }}>
          <Award size={56} style={{ color: 'var(--primary-600)', marginBottom: '20px' }} />
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
            You're Already an Affiliate!
          </h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            Welcome back! Check your dashboard to see your earnings and stats.
          </p>
          <Link to="/affiliate/dashboard" className="btn btn-primary">
            Go to Affiliate Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section
        style={{
          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #8B5CF6 100%)',
          color: 'white',
          padding: '80px 0',
          textAlign: 'center',
        }}
      >
        <div className="container">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '24px',
            }}
          >
            <Award size={18} /> Affiliate Partner Program
          </div>
          <h1
            style={{ fontSize: '48px', fontWeight: '800', marginBottom: '20px', lineHeight: '1.2' }}
          >
            Earn 20% Recurring
            <br />
            Commission
          </h1>
          <p style={{ fontSize: '20px', opacity: '0.9', maxWidth: '600px', margin: '0 auto 32px' }}>
            Join our affiliate program and earn 20% on every payment from customers you refer. Not
            just once - on every single payment, forever.
          </p>
          {user ? (
            <a
              href="#apply"
              className="btn btn-primary"
              style={{
                background: 'var(--bg-secondary)',
                color: '#4F46E5',
                padding: '16px 32px',
                fontSize: '18px',
              }}
            >
              Apply Now
            </a>
          ) : (
            <Link
              to="/register"
              className="btn btn-primary"
              style={{
                background: 'var(--bg-secondary)',
                color: '#4F46E5',
                padding: '16px 32px',
                fontSize: '18px',
              }}
            >
              Sign Up to Become an Affiliate
            </Link>
          )}
        </div>
      </section>

      <section style={{ padding: '60px 0', background: 'var(--gray-50)' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '24px',
              maxWidth: '900px',
              margin: '0 auto',
            }}
          >
            {[
              { value: '20%', label: 'Commission Rate' },
              { value: 'Recurring', label: 'Lifetime Earnings' },
              { value: '$50', label: 'Min. Payout' },
              { value: '30 Days', label: 'Cookie Duration' },
            ].map(s => (
              <div key={s.label} className="card" style={{ textAlign: 'center', padding: '24px' }}>
                <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--primary-600)' }}>
                  {s.value}
                </div>
                <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2
            style={{
              fontSize: '32px',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '48px',
            }}
          >
            How It Works
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
            {[
              {
                num: '1',
                title: 'Sign Up',
                desc: 'Create a free account and apply to our affiliate program. Approval is instant!',
              },
              {
                num: '2',
                title: 'Share Your Link',
                desc: 'Get your unique affiliate link and share it with your audience via blog, social media, or email.',
              },
              {
                num: '3',
                title: 'Earn Commission',
                desc: 'Earn 20% of every payment made by customers you refer - recurring, for life!',
              },
            ].map(s => (
              <div key={s.num} style={{ textAlign: 'center' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: '700',
                    margin: '0 auto 16px',
                  }}
                >
                  {s.num}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {s.title}
                </h3>
                <p style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '60px 0', background: 'var(--gray-50)' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '32px',
            }}
          >
            Potential Earnings
          </h2>
          <div className="card">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Plan</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>
                    Monthly Price
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>
                    Your Commission
                  </th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>
                    Yearly Earnings
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { plan: 'Starter', price: '$29/mo', comm: '$5.80/mo', yearly: '$69.60/year' },
                  { plan: 'Pro', price: '$49/mo', comm: '$9.80/mo', yearly: '$117.60/year' },
                  { plan: 'Unlimited', price: '$99/mo', comm: '$19.80/mo', yearly: '$237.60/year' },
                ].map((r, i) => (
                  <tr
                    key={r.plan}
                    style={{ borderBottom: i < 2 ? '1px solid var(--gray-100)' : 'none' }}
                  >
                    <td style={{ padding: '16px', fontWeight: '500' }}>{r.plan}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>{r.price}</td>
                    <td
                      style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: 'var(--primary-600)',
                        fontWeight: '600',
                      }}
                    >
                      {r.comm}
                    </td>
                    <td
                      style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: 'var(--secondary)',
                        fontWeight: '700',
                      }}
                    >
                      {r.yearly}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              style={{
                padding: '16px',
                background: 'var(--primary-50)',
                borderRadius: '8px',
                marginTop: '16px',
                textAlign: 'center',
              }}
            >
              <strong>Example:</strong> Refer 10 Pro customers ={' '}
              <strong style={{ color: 'var(--primary-600)' }}>$1,176/year</strong> in passive
              income!
            </div>
          </div>
        </div>
      </section>

      {user && (
        <section id="apply" style={{ padding: '80px 0' }}>
          <div className="container" style={{ maxWidth: '600px' }}>
            <h2
              style={{
                fontSize: '28px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Apply to Become an Affiliate
            </h2>
            <p style={{ color: 'var(--gray-600)', textAlign: 'center', marginBottom: '32px' }}>
              Fill out the form below. Approval is typically instant!
            </p>
            <div className="card">
              <form onSubmit={handleApply}>
                <div className="form-group">
                  <label className="form-label">Website URL (Optional)</label>
                  <input
                    type="url"
                    className="form-input"
                    value={formData.website}
                    onChange={e => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">How will you promote ReviewResponder?</label>
                  <select
                    className="form-input"
                    value={formData.marketingChannels}
                    onChange={e => setFormData({ ...formData, marketingChannels: e.target.value })}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="blog">Blog / Website</option>
                    <option value="social">Social Media</option>
                    <option value="youtube">YouTube</option>
                    <option value="email">Email Newsletter</option>
                    <option value="podcast">Podcast</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Estimated Audience Size</label>
                  <select
                    className="form-input"
                    value={formData.audienceSize}
                    onChange={e => setFormData({ ...formData, audienceSize: e.target.value })}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="small">Small (Under 1,000)</option>
                    <option value="medium">Medium (1,000 - 10,000)</option>
                    <option value="large">Large (10,000 - 100,000)</option>
                    <option value="enterprise">Enterprise (100,000+)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Preferred Payout Method</label>
                  <select
                    className="form-input"
                    value={formData.payoutMethod}
                    onChange={e => setFormData({ ...formData, payoutMethod: e.target.value })}
                    required
                  >
                    <option value="paypal">PayPal</option>
                    <option value="bank">Bank Transfer</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payout Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.payoutEmail}
                    onChange={e => setFormData({ ...formData, payoutEmail: e.target.value })}
                    placeholder="payments@youremail.com"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', padding: '14px' }}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {!user && (
        <section
          style={{
            padding: '80px 0',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <div className="container">
            <h2 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '16px' }}>
              Ready to Start Earning?
            </h2>
            <p style={{ fontSize: '18px', opacity: '0.9', marginBottom: '24px' }}>
              Create a free account to apply for our affiliate program.
            </p>
            <Link
              to="/register"
              className="btn btn-primary"
              style={{
                background: 'var(--bg-secondary)',
                color: '#4F46E5',
                padding: '16px 32px',
                fontSize: '18px',
              }}
            >
              Sign Up Now
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

// Affiliate Dashboard Page (Protected)
const AffiliateDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [settingsForm, setSettingsForm] = useState({ payoutMethod: 'paypal', payoutEmail: '' });
  const [updatingSettings, setUpdatingSettings] = useState(false);

  const loadData = async () => {
    try {
      const [statsRes, payoutsRes] = await Promise.all([
        api.get('/affiliate/stats'),
        api.get('/affiliate/payouts'),
      ]);
      setData(statsRes.data);
      setPayouts(payoutsRes.data);
      if (statsRes.data.affiliate) {
        setSettingsForm({
          payoutMethod: statsRes.data.affiliate.payoutMethod || 'paypal',
          payoutEmail: statsRes.data.affiliate.payoutEmail || '',
        });
      }
    } catch (error) {
      console.error('Load affiliate data error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCopyLink = () => {
    if (data?.links?.affiliateLink) {
      navigator.clipboard.writeText(data.links.affiliateLink);
      setCopied(true);
      toast.success('Affiliate link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      const res = await api.post('/affiliate/payout');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleUpdateSettings = async e => {
    e.preventDefault();
    setUpdatingSettings(true);
    try {
      await api.put('/affiliate/settings', settingsForm);
      toast.success('Settings updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const chartData =
    data?.clicksChart
      ?.slice(0, 30)
      .reverse()
      .map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        clicks: parseInt(d.clicks),
      })) || [];

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <div className="spinner" />
        <p style={{ color: 'var(--gray-500)', marginTop: '16px' }}>
          Loading affiliate dashboard...
        </p>
      </div>
    );
  }

  if (!data?.isAffiliate) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '48px' }}>
          <Award size={48} style={{ color: 'var(--gray-400)', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
            Not an Affiliate Yet
          </h2>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            Apply to our affiliate program and start earning 20% recurring commission on every
            referral.
          </p>
          <Link to="/affiliate" className="btn btn-primary">
            Apply Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>
            <Award
              size={28}
              style={{ verticalAlign: 'middle', marginRight: '12px', color: 'var(--primary-600)' }}
            />
            Affiliate Dashboard
          </h1>
          <p style={{ color: 'var(--gray-500)' }}>Track your earnings, clicks, and conversions</p>
        </div>
        <Link to="/dashboard" className="btn btn-secondary">
          ← Back to Dashboard
        </Link>
      </div>

      <div
        className="card"
        style={{
          marginBottom: '24px',
          background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--primary-600)',
                marginBottom: '4px',
              }}
            >
              Your Affiliate Link
            </div>
            <div style={{ fontSize: '16px', fontFamily: 'monospace', color: 'var(--gray-700)' }}>
              {data?.links?.affiliateLink}
            </div>
          </div>
          <button
            onClick={handleCopyLink}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--gray-600)' }}>
          Affiliate Code: <strong>{data?.affiliate?.code}</strong> | Status:{' '}
          <strong
            style={{
              color: data?.affiliate?.status === 'approved' ? 'var(--secondary)' : 'var(--warning)',
            }}
          >
            {data?.affiliate?.status}
          </strong>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-label">Total Clicks</div>
          <div className="stat-value primary">{data?.stats?.totalClicks || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Conversions</div>
          <div className="stat-value">{data?.stats?.totalConversions || 0}</div>
          <div style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
            {data?.stats?.conversionRate || 0}% rate
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Earned</div>
          <div className="stat-value" style={{ color: 'var(--secondary)' }}>
            ${data?.stats?.totalEarned?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Balance</div>
          <div className="stat-value">${data?.stats?.pendingBalance?.toFixed(2) || '0.00'}</div>
          {(data?.stats?.pendingBalance || 0) >= 50 && (
            <button
              onClick={handleRequestPayout}
              disabled={requestingPayout}
              className="btn btn-primary"
              style={{ marginTop: '12px', fontSize: '13px', padding: '8px 16px' }}
            >
              {requestingPayout ? 'Processing...' : 'Request Payout'}
            </button>
          )}
          {(data?.stats?.pendingBalance || 0) < 50 && (
            <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '8px' }}>
              Min. $50 for payout
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '1px solid var(--gray-200)',
          paddingBottom: '8px',
        }}
      >
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'conversions', label: 'Conversions', icon: Users },
          { id: 'payouts', label: 'Payouts', icon: CreditCard },
          { id: 'materials', label: 'Marketing Materials', icon: Download },
          { id: 'settings', label: 'Settings', icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 16px',
              borderRadius: '8px',
              background: activeTab === tab.id ? 'var(--primary-100)' : 'transparent',
              color: activeTab === tab.id ? 'var(--primary-700)' : 'var(--gray-600)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
            }}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="card">
          <h3
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <TrendingUp size={20} style={{ color: 'var(--primary-600)' }} /> Clicks Over Time (Last
            30 Days)
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Bar dataKey="clicks" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-400)',
              }}
            >
              No click data yet. Share your affiliate link to get started!
            </div>
          )}
        </div>
      )}

      {activeTab === 'conversions' && (
        <div className="card">
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
            Recent Conversions
          </h3>
          {data?.recentConversions?.length > 0 ? (
            <div className="table-scroll-container">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                      Customer
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Plan
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Payment
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Commission
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentConversions.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '12px' }}>{c.email}</td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          textTransform: 'capitalize',
                        }}
                      >
                        {c.plan}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        ${c.amount?.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          color: 'var(--secondary)',
                          fontWeight: '600',
                        }}
                      >
                        ${c.commission?.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background:
                              c.status === 'paid'
                                ? 'var(--secondary-light)'
                                : c.status === 'approved'
                                  ? 'var(--primary-100)'
                                  : 'var(--gray-100)',
                            color:
                              c.status === 'paid'
                                ? 'var(--secondary)'
                                : c.status === 'approved'
                                  ? 'var(--primary-700)'
                                  : 'var(--gray-600)',
                          }}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--gray-400)' }}>
              No conversions yet. Keep sharing your affiliate link!
            </div>
          )}
        </div>
      )}

      {activeTab === 'payouts' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Payout History</h3>
            <div style={{ fontSize: '14px', color: 'var(--gray-600)' }}>
              Total Paid Out:{' '}
              <strong style={{ color: 'var(--secondary)' }}>
                ${payouts?.totalPaid?.toFixed(2) || '0.00'}
              </strong>
            </div>
          </div>
          {payouts?.payouts?.length > 0 ? (
            <div className="table-scroll-container">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>
                      Amount
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Method
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      Requested
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      Processed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.payouts.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '12px', fontWeight: '600' }}>
                        ${p.amount?.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          textTransform: 'capitalize',
                        }}
                      >
                        {p.method}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background:
                              p.status === 'completed'
                                ? 'var(--secondary-light)'
                                : p.status === 'processing'
                                  ? 'var(--warning-light)'
                                  : 'var(--gray-100)',
                            color:
                              p.status === 'completed'
                                ? 'var(--secondary)'
                                : p.status === 'processing'
                                  ? 'var(--warning)'
                                  : 'var(--gray-600)',
                          }}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {new Date(p.requestedAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {p.processedAt ? new Date(p.processedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--gray-400)' }}>
              No payouts yet. Earn at least $50 in commissions to request your first payout.
            </div>
          )}
        </div>
      )}

      {activeTab === 'materials' && (
        <div className="card">
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
            Marketing Materials
          </h3>
          <p style={{ color: 'var(--gray-600)', marginBottom: '24px' }}>
            Use these resources to promote ReviewResponder to your audience.
          </p>
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Your Affiliate Link
            </h4>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="text"
                className="form-input"
                value={data?.links?.affiliateLink || ''}
                readOnly
                style={{ fontFamily: 'monospace', flex: 1 }}
              />
              <button onClick={handleCopyLink} className="btn btn-primary">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Email Template
            </h4>
            <div
              style={{
                background: 'var(--gray-50)',
                padding: '16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {`Subject: Tool for Review Responses

Hi [Name],

I've been using ReviewResponder to write review responses faster.

It helps write review responses faster - you just paste the review and tweak the result.

Try it here: ${data?.links?.affiliateLink}

Best,
[Your Name]`}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `Subject: Tool for Review Responses\n\nHi [Name],\n\nI've been using ReviewResponder to write review responses faster.\n\nIt helps write review responses faster - you just paste the review and tweak the result.\n\nTry it here: ${data?.links?.affiliateLink}\n\nBest,\n[Your Name]`
                );
                toast.success('Email template copied!');
              }}
              className="btn btn-secondary"
              style={{ marginTop: '12px' }}
            >
              <Copy size={16} /> Copy Email Template
            </button>
          </div>
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Social Media Post
            </h4>
            <div
              style={{
                background: 'var(--gray-50)',
                padding: '16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
            >
              {`Struggling with customer reviews? Check out ReviewResponder - craft your responses with psychology-based guidance. Built by a psych student who gets it. ${data?.links?.affiliateLink}`}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `Struggling with customer reviews? Check out ReviewResponder - craft your responses with psychology-based guidance. Built by a psych student who gets it. ${data?.links?.affiliateLink}`
                );
                toast.success('Social post copied!');
              }}
              className="btn btn-secondary"
              style={{ marginTop: '12px' }}
            >
              <Copy size={16} /> Copy Social Post
            </button>
          </div>
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Key Selling Points
            </h4>
            <ul style={{ paddingLeft: '20px', color: 'var(--gray-600)' }}>
              <li style={{ marginBottom: '8px' }}>
                Craft your responses with psychology-based guidance
              </li>
              <li style={{ marginBottom: '8px' }}>Any language with auto-detect</li>
              <li style={{ marginBottom: '8px' }}>
                4 tone options: Professional, Friendly, Formal, Apologetic
              </li>
              <li style={{ marginBottom: '8px' }}>
                Works with Google, Yelp, TripAdvisor, and more
              </li>
              <li style={{ marginBottom: '8px' }}>Chrome extension for quick access</li>
              <li style={{ marginBottom: '8px' }}>Free plan with 20 responses to try</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="card" style={{ maxWidth: '500px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
            Payout Settings
          </h3>
          <form onSubmit={handleUpdateSettings}>
            <div className="form-group">
              <label className="form-label">Payout Method</label>
              <select
                className="form-input"
                value={settingsForm.payoutMethod}
                onChange={e => setSettingsForm({ ...settingsForm, payoutMethod: e.target.value })}
              >
                <option value="paypal">PayPal</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Payout Email</label>
              <input
                type="email"
                className="form-input"
                value={settingsForm.payoutEmail}
                onChange={e => setSettingsForm({ ...settingsForm, payoutEmail: e.target.value })}
                placeholder="payments@youremail.com"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={updatingSettings}>
              {updatingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// Admin Panel Page (Protected with Admin Key)
// Simplified Admin Page - Only Users and Email tabs
const AdminPage = () => {
  // Core state
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState('users');

  // Users tab state
  const [usersData, setUsersData] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSort, setUsersSort] = useState({ column: 'created_at', order: 'desc' });
  const [usersFilter, setUsersFilter] = useState('real');

  // Email tab state
  const [emailData, setEmailData] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Use the same API_URL as the rest of the app (already includes /api)
  // Remove /api suffix if present to build admin URLs correctly
  const API_BASE = API_URL.replace(/\/api$/, '');

  const authenticate = async keyToUse => {
    const key = keyToUse || adminKey;
    if (!key || !key.trim()) {
      toast.error('Please enter an admin key');
      return;
    }
    setLoading(true);
    try {
      console.log('Authenticating with key:', key.substring(0, 8) + '...');
      // Use /api/admin/users to verify admin access (simplified admin dashboard)
      const res = await axios.get(`${API_BASE}/api/admin/users`, {
        headers: { 'X-Admin-Key': key },
        params: { limit: 1 }, // Just verify access, don't load all data yet
      });
      setIsAuthenticated(true);
      setAdminKey(key);
      localStorage.setItem('adminKey', key);
      toast.success('Admin authenticated');
      // Load users data for the default tab
      loadUsersData(key);
    } catch (err) {
      console.error('Auth error:', err.response?.data || err.message);
      toast.error(err.response?.data?.error || 'Invalid admin key');
      localStorage.removeItem('adminKey');
      setAdminKey('');
    } finally {
      setLoading(false);
    }
  };

  const getHeaders = () => ({ 'X-Admin-Key': adminKey });


  const loadUsersData = async (key, sortOverride, filterOverride) => {
    const keyToUse = key || adminKey;
    if (!keyToUse) return;
    setUsersLoading(true);
    try {
      const sort = sortOverride || usersSort;
      const filter = filterOverride || usersFilter;
      const res = await axios.get(`${API_BASE}/api/admin/users`, {
        headers: { 'X-Admin-Key': keyToUse },
        params: {
          sort: sort.column,
          order: sort.order,
          filter: filter,
          limit: 200,
        },
      });
      setUsersData(res.data);
    } catch (err) {
      console.error('Users load error:', err);
      toast.error('Failed to load users data');
    } finally {
      setUsersLoading(false);
    }
  };

  const toggleUserVerified = async (userId, currentVerified) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/${userId}/verify`,
        { verified: !currentVerified },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      // Update local state
      setUsersData(prev => ({
        ...prev,
        users: prev.users.map(u =>
          u.id === userId ? { ...u, is_verified_real: !currentVerified, is_test_account: false } : u
        ),
        summary: {
          ...prev.summary,
          verified: prev.summary.verified + (currentVerified ? -1 : 1),
        },
      }));
      toast.success(currentVerified ? 'User unverified' : 'User verified as real');
    } catch (err) {
      console.error('Toggle verify error:', err);
      toast.error('Failed to update verification');
    }
  };

  const toggleFriendAccount = async (userId, currentIsFriend) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/${userId}/friend`,
        { isFriend: !currentIsFriend },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      // Update local state
      setUsersData(prev => {
        const updatedUsers = prev.users.map(u =>
          u.id === userId ? { ...u, is_friend_account: !currentIsFriend } : u
        );
        // Recalculate real users (response_count >= 1 AND NOT excluded)
        const realCount = updatedUsers.filter(u => u.response_count >= 1 && !u.is_test_account && !u.is_friend_account).length;
        return {
          ...prev,
          users: updatedUsers,
          summary: {
            ...prev.summary,
            real: realCount,
            friend: (prev.summary.friend || 0) + (currentIsFriend ? -1 : 1),
          },
        };
      });
      toast.success(currentIsFriend ? 'Friend status removed' : 'Marked as friend account');
    } catch (err) {
      console.error('Toggle friend error:', err);
      toast.error('Failed to update friend status');
    }
  };

  const handleUsersSort = column => {
    const newSort = {
      column,
      order: usersSort.column === column && usersSort.order === 'desc' ? 'asc' : 'desc',
    };
    setUsersSort(newSort);
    loadUsersData(adminKey, newSort, usersFilter);
  };

  const handleUsersFilter = newFilter => {
    setUsersFilter(newFilter);
    loadUsersData(adminKey, usersSort, newFilter);
  };


  const loadEmailData = async key => {
    const keyToUse = key || adminKey;
    if (!keyToUse) return;
    setEmailLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email-dashboard`, {
        headers: { 'X-Admin-Key': keyToUse },
      });
      setEmailData(res.data);
    } catch (err) {
      console.error('Email load error:', err);
      toast.error('Failed to load email data');
    } finally {
      setEmailLoading(false);
    }
  };


  // Check for saved admin key on mount
  useEffect(() => {
    const savedKey = localStorage.getItem('adminKey');
    if (savedKey && savedKey.trim()) {
      setAdminKey(savedKey);
      authenticate(savedKey);
    }
  }, []);

  // Load data when tab changes (simplified - only users and email tabs remain)
  useEffect(() => {
    if (isAuthenticated && adminKey) {
      if (activeAdminTab === 'users' && !usersData) {
        loadUsersData();
      }
      if (activeAdminTab === 'email' && !emailData) {
        loadEmailData();
      }
    }
  }, [activeAdminTab, isAuthenticated, adminKey]);

  if (!isAuthenticated) {
    return (
      <div
        className="page-container"
        style={{ maxWidth: '400px', margin: '100px auto', padding: '20px' }}
      >
        <div className="card">
          <h1
            style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            Admin Login
          </h1>
          <div className="form-group">
            <label className="form-label">Admin Key</label>
            <input
              type="password"
              className="form-input"
              value={adminKey}
              onChange={e => setAdminKey(e.target.value)}
              placeholder="Enter admin secret key"
              onKeyDown={e => e.key === 'Enter' && authenticate(adminKey)}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => authenticate(adminKey)}
            disabled={loading}
          >
            {loading ? 'Authenticating...' : 'Login'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container"
      style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
        }}
      >
        <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Admin Dashboard</h1>
        <button
          className="btn btn-secondary"
          onClick={() => {
            localStorage.removeItem('adminKey');
            setIsAuthenticated(false);
            setAdminKey('');
          }}
        >
          <LogOut size={16} /> Logout
        </button>
      </div>

      {/* Admin Tabs - Users, Email, and Queue link */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          className={`btn ${activeAdminTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveAdminTab('users')}
        >
          Users
        </button>
        <button
          className={`btn ${activeAdminTab === 'email' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveAdminTab('email')}
        >
          Email
        </button>
        <a
          href="/admin/queue"
          className="btn btn-secondary"
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Send size={14} /> Email Queue
        </a>
      </div>


      {/* Users Tab */}
      {activeAdminTab === 'users' && (
        <div>
          {usersLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading users...</div>
          ) : usersData ? (
            <>
              {/* Summary Cards - Simplified */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px',
                }}
              >
                {/* REAL USERS - Fresh Start */}
                <div className="card" style={{ textAlign: 'center', padding: '16px', border: '3px solid #10B981', background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#10B981' }}>
                    {usersData.summary?.real || 0}
                  </div>
                  <div style={{ color: '#10B981', fontSize: '14px', fontWeight: '600' }}>Real Users</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '11px' }}>response_count ≥ 1</div>
                </div>
                {/* PAYING */}
                <div className="card" style={{ textAlign: 'center', padding: '16px', border: '2px solid #8B5CF6' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#8B5CF6' }}>
                    {usersData.summary?.realPaying || 0}
                  </div>
                  <div style={{ color: '#8B5CF6', fontSize: '14px', fontWeight: '600' }}>Paying</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '11px' }}>€€€</div>
                </div>
                {/* ARCHIVE */}
                <div className="card" style={{ textAlign: 'center', padding: '16px', background: '#F3F4F6', border: '1px dashed var(--gray-300)' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--gray-400)' }}>
                    {usersData.summary?.total || 0}
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '14px', fontWeight: '600' }}>📦 Archive</div>
                  <div style={{ color: 'var(--gray-400)', fontSize: '11px' }}>Alte Accounts</div>
                </div>
              </div>

              {/* Users Table */}
              <div className="card">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <h3 style={{ fontSize: '18px', fontWeight: '600' }}>
                    Users ({usersData.users?.length || 0})
                  </h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['real', 'paying', 'archive'].map(f => (
                      <button
                        key={f}
                        className={`btn ${usersFilter === f ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleUsersFilter(f === 'archive' ? 'all' : f)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          background: f === 'archive' && usersFilter === 'all' ? 'var(--gray-600)' : undefined,
                          color: f === 'archive' && usersFilter === 'all' ? 'white' : undefined,
                        }}
                      >
                        {f === 'archive' ? '📦 Archive' : f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                    <button
                      className="btn btn-secondary"
                      onClick={() => loadUsersData()}
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      ↻
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ padding: '10px 8px', textAlign: 'left' }}>Email</th>
                        <th
                          style={{ padding: '10px 8px', textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleUsersSort('response_count')}
                        >
                          Responses {usersSort.column === 'response_count' && (usersSort.order === 'desc' ? '▼' : '▲')}
                        </th>
                        <th style={{ padding: '10px 8px', textAlign: 'left' }}>Plan</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center' }}>Status</th>
                        <th
                          style={{ padding: '10px 8px', textAlign: 'left', cursor: 'pointer' }}
                          onClick={() => handleUsersSort('last_response_at')}
                        >
                          Last Active {usersSort.column === 'last_response_at' && (usersSort.order === 'desc' ? '▼' : '▲')}
                        </th>
                        <th
                          style={{ padding: '10px 8px', textAlign: 'left', cursor: 'pointer' }}
                          onClick={() => handleUsersSort('created_at')}
                        >
                          Registered {usersSort.column === 'created_at' && (usersSort.order === 'desc' ? '▼' : '▲')}
                        </th>
                        <th style={{ padding: '10px 8px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersData.users?.map(user => (
                        <tr
                          key={user.id}
                          style={{
                            borderBottom: '1px solid var(--border-color)',
                            background: user.is_friend_account
                              ? 'rgba(252, 231, 243, 0.5)'
                              : user.is_verified_real
                                ? 'rgba(6, 182, 212, 0.08)'
                                : user.is_test_account
                                  ? 'rgba(251, 191, 36, 0.1)'
                                  : 'transparent',
                          }}
                        >
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {user.email}
                                {user.is_verified_real && (
                                  <span style={{ color: '#06B6D4', fontSize: '14px' }} title="Verified Real">✓</span>
                                )}
                                {user.is_friend_account && (
                                  <span
                                    style={{
                                      padding: '1px 4px',
                                      borderRadius: '3px',
                                      fontSize: '9px',
                                      fontWeight: '600',
                                      background: '#EC4899',
                                      color: 'white',
                                    }}
                                    title="Friend account - excluded from real user metrics"
                                  >
                                    FRIEND
                                  </span>
                                )}
                                {user.created_via_magic_link && (
                                  <span
                                    style={{
                                      padding: '1px 4px',
                                      borderRadius: '3px',
                                      fontSize: '9px',
                                      fontWeight: '600',
                                      background: '#7C3AED',
                                      color: 'white',
                                    }}
                                  >
                                    MAGIC
                                  </span>
                                )}
                              </div>
                              {user.business_name && (
                                <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                                  {user.business_name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                            <span
                              style={{
                                fontWeight: '600',
                                color: user.response_count > 0 ? '#10B981' : 'var(--gray-400)',
                                fontSize: '14px',
                              }}
                            >
                              {user.response_count}
                            </span>
                          </td>
                          <td style={{ padding: '10px 8px' }}>
                            <span
                              style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500',
                                background:
                                  user.subscription_plan === 'free'
                                    ? '#E5E7EB'
                                    : user.subscription_plan === 'starter'
                                      ? '#DBEAFE'
                                      : user.subscription_plan === 'pro'
                                        ? '#D1FAE5'
                                        : '#EDE9FE',
                                color:
                                  user.subscription_plan === 'free'
                                    ? '#374151'
                                    : user.subscription_plan === 'starter'
                                      ? '#1D4ED8'
                                      : user.subscription_plan === 'pro'
                                        ? '#065F46'
                                        : '#5B21B6',
                              }}
                            >
                              {user.subscription_plan}
                            </span>
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                            <span
                              style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600',
                                background: user.is_friend_account
                                  ? '#FCE7F3'
                                  : user.is_verified_real
                                    ? '#CFFAFE'
                                    : user.is_test_account
                                      ? '#FEF3C7'
                                      : parseInt(user.response_count) >= 1
                                        ? '#D1FAE5'
                                        : '#E5E7EB',
                                color: user.is_friend_account
                                  ? '#BE185D'
                                  : user.is_verified_real
                                    ? '#0E7490'
                                    : user.is_test_account
                                      ? '#92400E'
                                      : parseInt(user.response_count) >= 1
                                        ? '#065F46'
                                        : '#6B7280',
                              }}
                            >
                              {user.is_friend_account ? 'FRIEND' : user.is_verified_real ? 'VERIFIED' : user.is_test_account ? 'TEST' : parseInt(user.response_count) >= 1 ? 'REAL' : 'GHOST'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 8px', color: 'var(--gray-500)', fontSize: '12px' }}>
                            {user.last_response_at
                              ? new Date(user.last_response_at).toLocaleDateString()
                              : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', color: 'var(--gray-500)', fontSize: '12px' }}>
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => toggleUserVerified(user.id, user.is_verified_real)}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  background: user.is_verified_real ? '#FEE2E2' : '#D1FAE5',
                                  color: user.is_verified_real ? '#991B1B' : '#065F46',
                                }}
                              >
                                {user.is_verified_real ? 'Unverify' : 'Verify'}
                              </button>
                              <button
                                onClick={() => toggleFriendAccount(user.id, user.is_friend_account)}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  background: user.is_friend_account ? '#FCE7F3' : '#F3E8FF',
                                  color: user.is_friend_account ? '#BE185D' : '#7C3AED',
                                }}
                                title={user.is_friend_account ? 'Remove friend status (will count as real user)' : 'Mark as friend (excluded from real user metrics)'}
                              >
                                {user.is_friend_account ? 'Unfriend' : 'Friend'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--gray-500)', marginBottom: '16px' }}>Failed to load users</p>
              <button className="btn btn-primary" onClick={() => loadUsersData()}>
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Email Tab - Simple Provider Overview */}
      {activeAdminTab === 'email' && (
        <div>
          {/* Provider Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            {/* Brevo */}
            <div className="card" style={{ border: '2px solid #0066FF' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#0066FF' }}></span>
                  Brevo
                </h3>
                <a
                  href="https://app.brevo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Dashboard →
                </a>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#0066FF' }}>0</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Sent Today</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--gray-400)' }}>/ 300</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Daily Limit</div>
                </div>
              </div>
            </div>

            {/* Resend */}
            <div className="card" style={{ border: '2px solid #000000' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#000000' }}></span>
                  Resend
                </h3>
                <a
                  href="https://resend.com/emails"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Dashboard →
                </a>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#000000' }}>0</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Sent Today</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--gray-400)' }}>/ 100</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Daily Limit</div>
                </div>
              </div>
            </div>

            {/* Gmail */}
            <div className="card" style={{ border: '2px solid #EA4335' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#EA4335' }}></span>
                  Gmail
                </h3>
                <a
                  href="https://mail.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Dashboard →
                </a>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#EA4335' }}>0</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Sent Today</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--gray-400)' }}>/ 500</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Daily Limit</div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Summary */}
          <div className="card" style={{ background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)', border: '1px solid #BAE6FD' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '4px' }}>Total Daily Capacity</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#0369A1' }}>900 Emails/Day</div>
              </div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#0066FF' }}>300</div>
                  <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Brevo</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#000000' }}>100</div>
                  <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Resend</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#EA4335' }}>500</div>
                  <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Gmail</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// SEO Landing Page - Bar & Nightclub Review Responses
const BarNightclubReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/bar-nightclub-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Bar & Nightclub Review Response Tool | Craft Your Replies"
        description="Craft your bar and nightclub review responses with psychology-based guidance. Build reputation and attract more patrons with authentic replies."
        canonical="/bar-nightclub-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Bar & Nightclub Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful bar and nightclub review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #4A148C 0%, #311B92 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Utensils size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Bars & Nightclubs
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Bar & Nightclub Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#4A148C" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Turn every review into an opportunity. Craft professional responses that showcase your
            atmosphere, service, and unforgettable experiences.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Bars & Nightclubs
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Sparkles size={32} style={{ color: '#4A148C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              First Impressions Count
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Guests check reviews before choosing where to spend their night. Your responses shape
              their expectations.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#4A148C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Boost Weekend Traffic
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Higher ratings mean more foot traffic. Professional responses turn critics into
              regulars.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#4A148C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Stand Out in Nightlife
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              In a competitive scene, engaged venues win. Show guests you care about their
              experience.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#7C4DFF" source="bar_nightclub_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// SEO Landing Page - Retail Store Review Responses
const RetailStoreReviewPage = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      landing_page: '/retail-store-review-responses',
    };
    if (utmParams.utm_source) sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }, [location.search]);
  return (
    <div>
      <SEO
        title="Retail Store Review Response Tool | Craft Your Replies"
        description="Craft your retail store review responses with psychology-based guidance. Build trust and drive more foot traffic with authentic replies."
        canonical="/retail-store-review-responses"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewResponder - Retail Store Review Response Tool',
          description: 'Psychology-based tool to craft thoughtful retail store review responses',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section
        style={{
          background: 'linear-gradient(135deg, #00695C 0%, #004D40 100%)',
          padding: '60px 0 60px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          className="container"
          style={{ textAlign: 'center', maxWidth: '900px', position: 'relative', zIndex: 1 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              padding: '6px 16px',
              borderRadius: '100px',
              marginBottom: '32px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Store size={14} />
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Retail Stores
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em',
            }}
          >
            Retail Store Review Response Tool
          </h1>
          <HeroVideoCompact />

          <InstantDemoWidget primaryColor="#00695C" />

          <p
            style={{
              fontSize: '18px',
              opacity: 0.9,
              marginTop: '32px',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '32px auto 40px',
            }}
          >
            Transform customer feedback into loyalty. Craft professional responses that highlight
            your service, selection, and shopping experience.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '24px',
              marginBottom: '48px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Star size={16} /> 20 Free/Month
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} /> Auto-Detect Language
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Chrome size={16} /> One-Click Chrome
            </span>
          </div>
        </div>
      </section>

      <section className="container" style={{ padding: '60px 20px', maxWidth: '900px' }}>
        <h2
          style={{ fontSize: '28px', fontWeight: '700', textAlign: 'center', marginBottom: '40px' }}
        >
          Why Reviews Matter for Retail Stores
        </h2>
        <div
          style={{
            display: 'grid',
            gap: '24px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <div className="card" style={{ padding: '24px' }}>
            <Users size={32} style={{ color: '#00695C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Customer Trust
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Shoppers research before visiting. Your review responses build confidence and set
              expectations.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <TrendingUp size={32} style={{ color: '#00695C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Drive Foot Traffic
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              Higher ratings mean more visitors. Professional responses convert online browsers into
              in-store customers.
            </p>
          </div>
          <div className="card" style={{ padding: '24px' }}>
            <Award size={32} style={{ color: '#00695C', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Stand Out Locally
            </h3>
            <p style={{ color: 'var(--gray-600)', lineHeight: '1.6' }}>
              In a competitive retail landscape, engaged stores win. Show customers you value their
              feedback.
            </p>
          </div>
        </div>
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: '16px', fontSize: '14px' }}>
            20 responses/month free
          </p>
          <LandingEmailCapture buttonColor="#F44336" source="retail_landing" />
        </div>
      </section>
      <Footer />
    </div>
  );
};

// Main App
// ============================================
// EASTER EGG: 404 Not Found Page with Review Humor
// ============================================
const NotFoundPage = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const jokes = [
    "Just like that 1-star review that disappeared after you wrote the perfect response.",
    "Gone faster than a Karen who 'will never come back' but returns next Tuesday.",
    "As missing as the 'manager' that angry reviewer demanded to speak to.",
    "Vanished like your motivation after reading 'The food was cold. 1 star.'",
    "404: Page not found. Unlike that review from 2019 you still think about at 3am.",
    "This page left no review. Probably still waiting for the check.",
    "Error 404: This page ghosted harder than customers who promise to 'leave a great review!'",
    "Not here. Much like the reviewer who complained about 'waiting 2 hours' for a 15-minute meal.",
    "This page gave us 5 stars... then took them back because the parking was too far.",
    "Missing. Just like the proofreading in 'WORST EXPERIANCE EVER!!!!1'",
  ];

  const randomJoke = jokes[Math.floor(Math.random() * jokes.length)];

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="text-center max-w-md">
        <div className="text-8xl mb-4">⭐</div>
        <h1 className={`text-6xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>404</h1>
        <h2 className={`text-2xl font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          This page doesn't exist.
        </h2>
        <p className={`text-lg mb-8 italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {randomJoke}
        </p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Home className="w-5 h-5" />
          Back to 5-Star Territory
        </button>
      </div>
    </div>
  );
};

// ============================================
// EASTER EGG: HTTP 418 I'm a Teapot Page
// ============================================
const CoffeePage = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const [teapotClicked, setTeapotClicked] = useState(false);

  const handleTeapotClick = () => {
    if (!teapotClicked) {
      setTeapotClicked(true);
      // Mini confetti burst
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors: ['#8B4513', '#D2691E', '#F4A460'], // Coffee/tea colors
      });
      toast('☕ You found the easter egg within the easter egg!', {
        icon: '🎉',
        duration: 3000,
      });
    }
  };

  const teapotAscii = `
       ;,'
   _o_    ;:;'
 ,-.'---\`.__ ;
((j\`=====',-'
 \`-\\     /
    \`-=-'
  `;

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="text-center max-w-lg">
        <div className={`text-6xl font-bold mb-2 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
          Error 418
        </div>
        <h1 className={`text-3xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          I'm a Teapot ☕
        </h1>

        <div
          onClick={handleTeapotClick}
          className={`cursor-pointer select-none transition-transform hover:scale-110 ${teapotClicked ? 'animate-bounce' : ''}`}
          title="Click me!"
        >
          <pre className={`text-4xl leading-tight inline-block ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
            {teapotAscii}
          </pre>
        </div>

        <p className={`text-lg mt-6 mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          The server refuses to brew coffee.
        </p>
        <p className={`text-sm mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          RFC 2324 • Hyper Text Coffee Pot Control Protocol
        </p>

        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
        >
          <Coffee className="w-5 h-5" />
          Maybe Try Water Instead
        </Link>
      </div>
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

// App Content (inside Router for useLocation to work)
const AppContent = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // ============================================
  // EASTER EGG: Konami Code (↑↑↓↓←→←→BA)
  // ============================================
  useEffect(() => {
    const konamiCode = [
      'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
      'KeyB', 'KeyA'
    ];
    let konamiIndex = 0;

    const handleKonami = (e) => {
      if (e.code === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          // Success! Fire confetti and show toast
          fireConfetti();
          toast.success('🎮 You found the secret! Achievement unlocked!', {
            duration: 4000,
            icon: '🥚',
          });
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener('keydown', handleKonami);
    return () => window.removeEventListener('keydown', handleKonami);
  }, []);

  // ============================================
  // EASTER EGG: Console Message for Developers
  // ============================================
  useEffect(() => {
    console.log('%c👋 Hey Developer!', 'font-size: 24px; font-weight: bold; color: #6366f1;');
    console.log('%cLooking for bugs or just curious?', 'font-size: 14px; color: #8b5cf6;');
    console.log('%c🔧 Built with React, Node.js & lots of coffee ☕', 'font-size: 12px; color: #a1a1aa;');
    console.log('%c💡 Pro tip: Try the Konami Code! ↑↑↓↓←→←→BA', 'font-size: 11px; color: #71717a; font-style: italic;');
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" />
        <Navbar />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/claim/:code" element={<ClaimDiscountPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/blog"
            element={
              <BlogErrorBoundary>
                <BlogListPage />
              </BlogErrorBoundary>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <BlogErrorBoundary>
                <BlogArticlePage />
              </BlogErrorBoundary>
            }
          />
          <Route path="/demo/:token" element={<DemoPage />} />
          <Route path="/video-demo" element={<VideoDemoPage />} />
          <Route path="/demo-video" element={<DemoVideoPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/about-berend" element={<AboutBerend />} />
          <Route path="/brain" element={<BrainExplorer />} />
          <Route path="/brain/architecture" element={<ArchitectureExplorer />} />
          <Route path="/brain/deep-dive" element={<BrainDeepDive />} />
          <Route path="/extension" element={<ExtensionPage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/free-review-response-generator" element={<FreeToolPage />} />
          <Route path="/google-review-response-generator" element={<GoogleReviewPage />} />
          <Route path="/yelp-review-reply-tool" element={<YelpReviewPage />} />
          <Route path="/restaurant-review-responses" element={<RestaurantReviewPage />} />
          <Route path="/hotel-review-management" element={<HotelReviewPage />} />
          <Route path="/local-business-reviews" element={<LocalBusinessReviewPage />} />
          <Route path="/negative-review-responses" element={<NegativeReviewPage />} />
          <Route path="/tripadvisor-review-responses" element={<TripAdvisorReviewPage />} />
          <Route path="/booking-review-generator" element={<BookingReviewPage />} />
          <Route path="/facebook-review-responses" element={<FacebookReviewPage />} />
          <Route path="/dentist-review-responses" element={<DentistReviewPage />} />
          <Route path="/medical-practice-reviews" element={<MedicalPracticeReviewPage />} />
          <Route path="/salon-spa-review-responses" element={<SalonSpaReviewPage />} />
          <Route path="/auto-shop-reviews" element={<AutoShopReviewPage />} />
          <Route path="/trustpilot-review-responses" element={<TrustpilotReviewPage />} />
          <Route path="/airbnb-host-review-responses" element={<AirbnbReviewPage />} />
          <Route path="/real-estate-agent-reviews" element={<RealEstateReviewPage />} />
          <Route path="/gym-fitness-review-responses" element={<GymReviewPage />} />
          <Route path="/veterinarian-review-responses" element={<VetReviewPage />} />
          <Route path="/law-firm-review-responses" element={<LawFirmReviewPage />} />
          <Route path="/ecommerce-review-responses" element={<EcommerceReviewPage />} />
          <Route path="/coffee-shop-review-responses" element={<CoffeeShopReviewPage />} />
                    <Route path="/g2-review-responses" element={<G2ReviewPage />} />
          <Route path="/capterra-review-responses" element={<CapterraReviewPage />} />
          <Route path="/glassdoor-review-responses" element={<GlassdoorReviewPage />} />
          <Route path="/bbb-review-responses" element={<BBBReviewPage />} />
          <Route path="/plumber-review-responses" element={<PlumberReviewPage />} />
          <Route path="/electrician-review-responses" element={<ElectricianReviewPage />} />
          <Route path="/hvac-review-responses" element={<HVACReviewPage />} />
          <Route path="/roofing-review-responses" element={<RoofingReviewPage />} />
          <Route path="/landscaping-review-responses" element={<LandscapingReviewPage />} />
          <Route path="/cleaning-service-review-responses" element={<CleaningReviewPage />} />
          <Route path="/healthgrades-review-responses" element={<HealthgradesReviewPage />} />
          <Route path="/zocdoc-review-responses" element={<ZocdocReviewPage />} />
          <Route path="/photographer-review-responses" element={<PhotographerReviewPage />} />
          <Route path="/wedding-vendor-review-responses" element={<WeddingReviewPage />} />
          <Route path="/pet-service-review-responses" element={<PetServiceReviewPage />} />
          <Route path="/daycare-review-responses" element={<DaycareReviewPage />} />
          <Route path="/accountant-review-responses" element={<AccountantReviewPage />} />
          <Route path="/insurance-agent-review-responses" element={<InsuranceReviewPage />} />
          <Route path="/senior-care-review-responses" element={<SeniorCareReviewPage />} />
          <Route path="/appstore-app-reviews" element={<AppStoreReviewPage />} />
          <Route path="/indeed-company-reviews" element={<IndeedReviewPage />} />
          <Route path="/zillow-realtor-reviews" element={<ZillowReviewPage />} />
          <Route path="/therapy-counselor-reviews" element={<TherapyReviewPage />} />
          <Route path="/thumbtack-reviews" element={<ThumbtackReviewPage />} />
          <Route path="/chiropractor-reviews" element={<ChiropractorReviewPage />} />
          <Route path="/barber-barbershop-reviews" element={<BarberReviewPage />} />
          <Route path="/dermatologist-reviews" element={<DermatologistReviewPage />} />
          <Route path="/angie-list-contractor-reviews" element={<AngiListReviewPage />} />
          <Route path="/massage-therapist-reviews" element={<MassageTherapistReviewPage />} />
          <Route path="/personal-trainer-reviews" element={<PersonalTrainerReviewPage />} />
          <Route path="/bar-nightclub-review-responses" element={<BarNightclubReviewPage />} />
          <Route path="/retail-store-review-responses" element={<RetailStoreReviewPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generator"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            }
          />
          <Route path="/join-team" element={<JoinTeamPage />} />
          <Route path="/affiliate" element={<AffiliateLandingPage />} />
          <Route
            path="/affiliate/dashboard"
            element={
              <ProtectedRoute>
                <AffiliateDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-docs"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyApiDocsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/queue"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyAdminEmailQueue />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Easter Egg: HTTP 418 I'm a Teapot */}
          <Route path="/coffee" element={<CoffeePage />} />
          {/* Catch-all route for 404 - show fun 404 page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
